import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Users, 
  Bot, 
  Send, 
  Search, 
  Trash2, 
  Save, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw, 
  RefreshCcw,
  Key, 
  Sparkles, 
  Eye, 
  EyeOff,
  ArrowLeft,
  Crown,
  Database,
  User,
  Lock,
  LogOut,
  Activity,
  Check,
  Layers,
  Settings,
  Server,
  Wifi,
  Radio,
  Cpu,
  Power,
  Zap,
  Globe,
  Mail,
  Calendar,
  Clock,
  Copy,
  Plus,
  Download,
  ExternalLink,
  Sliders,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Play,
  Square,
  Edit3,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Info,
  Terminal,
  BarChart2,
  LayoutGrid,
  List,
  Wallet,
  ChevronRight,
  Target
} from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export default function AdminDashboardPage({ onBackToTerminal }) {
  const { user, tier, logout, changePassword } = useAuth();
  
  const [activeSection, setActiveSection] = useState('users'); // 'users' | 'connection' | 'ai' | 'telegram' | 'email_oauth' | 'storage' | 'account'
  
  // Users Management State
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('ALL');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersMsg, setUsersMsg] = useState({ type: '', text: '' });

  // Feeder & Server Connection State
  const [systemStatus, setSystemStatus] = useState({
    is_mt5_available: true,
    connected: true,
    server: 'Exness-MT5Trial14',
    account: '416296152',
    mode: 'High-Speed Python Gateway + MQL5 EA Bridge',
    balance: 0,
    equity: 0,
    currency: 'USD'
  });
  const [mt5Form, setMt5Form] = useState({
    account: '',
    password: '',
    server: 'Exness-MT5Real',
    path: ''
  });
  const [connectingMT5, setConnectingMT5] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [connMsg, setConnMsg] = useState({ type: '', text: '' });

  // AI & System Settings State
  const [settings, setSettings] = useState({
    gemini_api_key: '',
    openai_api_key: '',
    default_ai_model: 'gemini-1.5-flash',
    telegram_bot_token: '',
    telegram_chat_id: '',
    google_client_id: '',
    smtp_host: 'smtp.gmail.com',
    smtp_port: '587',
    smtp_user: '',
    smtp_password: '',
    smtp_sender_name: 'MINO TRADING PRO',
    smtp_from_email: '',
    system_name: 'MINO TRADING PRO',
    default_mid_same_pct: 50,
    default_entry_mode: 'Limit 1',
    hma_period: 24,
    hma_slope_filter: true,
    hma_max_slope: 3.0,
    hma_min_body_pct: 60,
    hma_lookback_bars: 100,
    hma_max_bars_from_extreme: 35,
    hma_max_risk_pips: 80,
    hma_r1: 1.5,
    hma_r2: 2.0,
    hma_sl_buffer_pips: 5.0,
    hma_cooldown_bars: 5,
    hma_show_line: true,
    hma_show_base_box: false,
    hma_allow_floating_breakout: false,
    hma_candle_strength_filter: true,
    hma_min_candle_body_ratio: 35,
    hma_max_rejection_wick_ratio: 40,
    hma_dynamic_limit_enable: true,
    hma_limit_threshold_pips: 60,
    hma_limit_pullback_pct: 50,
    hma_limit_cancel_bars: 20,
    hma_dynamic_be_enable: false,
    hma_be_trigger_pct: 75,
    hma_be_lock_pips: 5,
    hma_near_tp_cut_enable: false,
    hma_near_tp_threshold_pct: 85,
    hma_close_on_opposite: true,
    hma_cascade_filter: false,
    hma_bear_ratio_thresh: 70,
    hma_session_filter_enable: false,
    hma_session_mode: 'ALL',
    hma_session_start: '06:00',
    hma_session_end: '19:30',
    hma_sl_buffer_mode: 'ATR',
    hma_sl_buffer_min_pips: 5.0,
    hma_sl_buffer_max_pips: 30.0,
    hma_sl_atr_mult: 1.0,
    hma_ema200_sw_filter_enable: false,
    hma_ema200_sw_max_dist_pips: 300.0,
    hma_adaptive_period_enable: false,
    hma_adaptive_min_period: 21,
    hma_adaptive_max_period: 35,
    volman_ema_period: 20,
    volman_pivot_length: 3,
    volman_r1: 1.5,
    volman_sl_buffer_pips: 5.0,
    volman_sl_filter_enable: true,
    volman_min_risk_pips: 3.0,
    volman_max_risk_pips: 150.0,
    volman_cooldown_bars: 5,
    volman_enable_bb: true,
    volman_enable_tb: true,
    volman_breakout_candle_filter: true,
    volman_min_body_ratio: 50,
    volman_min_body_beyond_pct: 30,
    volman_max_rejection_wick: 25,
    volman_show_trendlines: false,
    volman_show_barriers: false,
    volman_show_buildup: false,
    volman_show_ema: true,
    volman_dynamic_limit_enable: false,
    volman_limit_threshold_pips: 80,
    volman_limit_pullback_pct: 40,
    volman_limit_cancel_bars: 20,
    orb_asia_enable: true,
    orb_london_enable: true,
    orb_ny_enable: false,
    orb_entry_mode: 'SMART_HYBRID',
    orb_rr: 3.0,
    orb_sl_buffer_mode: 'FIXED',
    orb_sl_buffer_pips: 2.0,
    orb_sl_atr_mult: 1.0,
    orb_sl_buffer_min_pips: 2.0,
    orb_sl_buffer_max_pips: 15.0,
    orb_sl_filter_enable: false,
    orb_min_sl_pips: 3.0,
    orb_max_sl_pips: 150.0,
    orb_max_break_pips: 15.0,
    orb_max_limit_bars: 6,
    orb_cancel_on_tp: true,
    orb_cancel_on_sl: true,
    orb_ema200_filter: false,
    orb_ema200_mode: 'OFF',
    orb_max_zone_pips: 250.0,
    orb_min_zone_pips: 10.0,
    orb_dynamic_be_enable: false,
    orb_be_trigger_pct: 75.0,
    orb_be_lock_pips: 5.0,
    orb_timeout_exit_enable: true,
    orb_revert_enable: false,
    orb_revert_entry_mode: 'LIMIT_50_BOX',
    orb_revert_rr: 1.5,
    orb_revert_cancel_bars: 6,
    orb_revert_sl_buffer_pips: 2.0,
    orb_dynamic_limit_enable: false,
    orb_limit_threshold_pips: 100.0,
    orb_limit_pullback_pct: 50.0,
    orb_limit_cancel_bars: 10,
    smc_fvg_filter_enable: false,
    smc_revert_enable: false,
    smc_revert_entry_mode: 'MARKET',
    smc_revert_rr: 1.5,
    smc_revert_cancel_bars: 20,
    smc_revert_sl_buffer_pips: 10.0,
    // Liquidity Sweep Pro
    sweep_m1_anchor_m5: true,
    sweep_zone_limit_enable: true,
    sweepZoneLimitEnable: true,
    sweep_entry_mode: 'RETEST',
    sweep_hybrid_threshold_pips: 80.0,
    sweep_hybrid_limit_mode: 'RETEST',
    sweep_hybrid_zone_pct: 50.0,
    sweep_hybrid_pullback_pct: 50.0,
    sweep_lookback: 5,
    sweep_opt: 'Only Outbreaks & Retest',
    sweep_extend: true,
    sweep_max_bars: 300,
    sweep_rr: 2.0,
    sweep_sl_buffer_pips: 1.5,
    sweep_min_risk_pips: 3.0,
    sweep_max_risk_pips: 150.0,
    sweepCooldownBars: 3,
    sweep_trend_filter_enable: false,
    sweep_trend_ema_period: 200,
    sweep_session_filter_enable: false,
    sweep_session_start: '00:00',
    sweep_session_end: '23:59',
    sweep_limit_cancel_bars: 30,
    sweep_highest_peak_filter: false,
    sweep_peak_lookback: 30,
    sweep_max_wait_bars: 20,
    sweep_close_on_opposite: false,
    sweep_dynamic_be_enable: false,
    sweep_be_trigger_mode: 'PCT_TP',
    sweep_be_trigger_pct: 50.0,
    sweep_be_trigger_pips: 40.0,
    sweep_be_lock_pips: 2.0,
  });
  const [showGeminiKey, setShowGeminiKey] = useState(false);
  const [showOpenAIKey, setShowOpenAIKey] = useState(false);
  const [showSmtpPwd, setShowSmtpPwd] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState({ type: '', text: '' });

  // Telegram Test State
  const [testingTg, setTestingTg] = useState(false);
  const [tgMsg, setTgMsg] = useState({ type: '', text: '' });

  // Email Test State
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTestMsg, setEmailTestMsg] = useState({ type: '', text: '' });

  // AI Live Test State
  const [testingAI, setTestingAI] = useState(false);
  const [aiTestResult, setAiTestResult] = useState(null);

  // Account Password State
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdMsg, setPwdMsg] = useState({ type: '', text: '' });
  const [pwdLoading, setPwdLoading] = useState(false);

  // EA Management State
  const [eaSubTab, setEaSubTab] = useState('accounts'); // 'accounts' | 'strategies' | 'positions' | 'history' | 'ledger'
  const [eaAccounts, setEaAccounts] = useState([]);
  const [eaStrategies, setEaStrategies] = useState([]);
  const [eaOrders, setEaOrders] = useState([]);
  const [eaPositions, setEaPositions] = useState([]);
  const [eaTransactions, setEaTransactions] = useState([]);
  const [eaSettlements, setEaSettlements] = useState([]);
  const [eaStats, setEaStats] = useState({
    total_accounts: 0,
    active_accounts: 0,
    total_balance: 0,
    total_equity: 0,
    total_free_margin: 0,
    total_floating_pnl: 0,
    total_deposit: 0,
    total_withdraw: 0,
    total_account_realized: 0,
    total_open_trades: 0,
    total_orders: 0,
    closed_orders: 0,
    win_orders: 0,
    loss_orders: 0,
    win_rate: 0,
    total_realized_pnl: 0,
    running_orders: 0,
    ledger_deposits: 0,
    ledger_withdrawals: 0,
    net_cash_flow: 0,
    deposit_transactions_count: 0,
    withdraw_transactions_count: 0,
    commission_generated: 0,
    commission_paid: 0,
    commission_pending: 0,
    pending_settlements: 0,
    paid_settlements: 0
  });
  const [loadingEa, setLoadingEa] = useState(false);
  const [eaMsg, setEaMsg] = useState({ type: '', text: '' });
  const [showKeyMap, setShowKeyMap] = useState({});
  const [selectedEaAccount, setSelectedEaAccount] = useState('ALL');
  const [accountViewMode, setAccountViewMode] = useState('cards');
  const [showGuide, setShowGuide] = useState(false);
  const [copiedKey, setCopiedKey] = useState('');
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [historyFilterSym, setHistoryFilterSym] = useState('ALL');
  const [historyFilterStatus, setHistoryFilterStatus] = useState('ALL');
  const [historyFilterPeriod, setHistoryFilterPeriod] = useState('ALL'); // 'ALL' | 'TODAY' | 'YESTERDAY' | '7D' | '30D' | 'THIS_MONTH' | 'CUSTOM'
  const [historyStartDate, setHistoryStartDate] = useState(''); // 'YYYY-MM-DD'
  const [historyEndDate, setHistoryEndDate] = useState(''); // 'YYYY-MM-DD'
  const [ledgerFilterAcc, setLedgerFilterAcc] = useState('ALL');
  const [ledgerFilterType, setLedgerFilterType] = useState('ALL');

  // Modals state
  const [showAddAccModal, setShowAddAccModal] = useState(false);
  const [editingAcc, setEditingAcc] = useState(null);
  const [accForm, setAccForm] = useState({
    name: '',
    login: '',
    server: '',
    magic_number: 888888,
    is_active: true,
    profit_share_pct: 30.0,
    client_notes: ''
  });

  const getDefaultStrategyParams = (strat) => {
    if (strat === 'matrix' || strat === 'signal') {
      return {
        emaFast: 9,
        emaSlow: 21,
        rsiPeriod: 14,
        tpMult: 1.8,
        slMult: 1.2,
        minConfidence: 35,
        slFilterEnable: true,
        slMin: 20.0,
        slMax: 150.0,
        cooldownBars: 25,
        timeoutExitEnable: true,
        maxHoldingBars: 30,
        sessionFilterEnable: true,
        sessionStart: '06:00',
        sessionEnd: '03:30',
        ema200FilterEnable: false,
        ema200DistFilterEnable: false,
        ema200OuterPips: 200.0,
        ema200InnerPips: 100.0,
        entryMode: settings.default_entry_mode || 'Limit 1',
        midSamePct: settings.default_mid_same_pct ?? 50.0,
        antiStreakEnable: true,
        antiStreakBars: 30,
      };
    }
    if (strat === 'smc') {
      return {
        windowBars: 100,
        lenPH: 3,
        lenPL: 3,
        minGapBars: 2,
        r1: 1.5,
        r2: 2.0,
        entryMode: settings.default_entry_mode || 'Limit 1',
        midSamePct: settings.default_mid_same_pct ?? 50.0,
        fibPct: 35.0,
        slFilterEnable: true,
        slMin: 10.0,
        slMax: 150.0,
        slExtraOn: true,
        slExtraVal: 5.0,
        pendingCancelOn: true,
        pendingCancelN: 40,
        revEnable: true,
        revR: 2.0,
        htfFilterEnable: true,
        htfTimeframe: 'AUTO',
        htfBufferPips: 10.0,
        htfMaxAgeDays: 7,
        htfMode: 'touch',
        fvgFilterEnable: settings.smc_fvg_filter_enable !== false ? (settings.smc_fvg_filter_enable === true || settings.smc_fvg_filter_enable === 'true') : false,
        smcRevertEnable: settings.smc_revert_enable !== undefined ? (settings.smc_revert_enable === true || settings.smc_revert_enable === 'true') : false,
        smcRevertEntryMode: settings.smc_revert_entry_mode || 'MARKET',
        smcRevertRr: settings.smc_revert_rr ?? 1.5,
        smcRevertCancelBars: settings.smc_revert_cancel_bars ?? 20,
        smcRevertSlBufferPips: settings.smc_revert_sl_buffer_pips ?? 10.0,
      };
    }
    if (strat === 'bob_volman' || strat === 'volman') {
      return {
        volmanEmaPeriod: settings.volman_ema_period ?? 20,
        volmanPivotLength: settings.volman_pivot_length ?? 3,
        volmanR1: settings.volman_r1 ?? 1.5,
        volmanSlBufferPips: settings.volman_sl_buffer_pips ?? 5.0,
        volmanSlFilterEnable: settings.volman_sl_filter_enable !== false,
        volmanMinRiskPips: settings.volman_min_risk_pips ?? 3.0,
        volmanMaxRiskPips: settings.volman_max_risk_pips ?? 150.0,
        volmanCooldownBars: settings.volman_cooldown_bars ?? 5,
        volmanEnableBB: settings.volman_enable_bb !== false,
        volmanEnableTB: settings.volman_enable_tb !== false,
        volmanEnableSB: settings.volman_enable_sb === true,
        volmanBreakoutCandleFilter: settings.volman_breakout_candle_filter !== false,
        volmanMinBodyRatio: settings.volman_min_body_ratio ?? 50,
        volmanMinBodyBeyondPct: settings.volman_min_body_beyond_pct ?? 30,
        volmanMaxRejectionWick: settings.volman_max_rejection_wick ?? 25,
        volmanDynamicLimitEnable: settings.volman_dynamic_limit_enable === true,
        volmanLimitThresholdPips: settings.volman_limit_threshold_pips ?? 80,
        volmanLimitPullbackPct: settings.volman_limit_pullback_pct ?? 40,
        volmanLimitCancelBars: settings.volman_limit_cancel_bars ?? 20,
        volmanMaFilterEnable: settings.volman_ma_filter_enable === true || settings.volman_ma_filter_enable === 'true',
        volmanMaPeriod: settings.volman_ma_period ?? 200,
        volmanMaType: settings.volman_ma_type || 'EMA',
      };
    }
    if (strat === 'orb' || strat === 'orb_session' || strat === 'orb_session_pro') {
      return {
        orbAsiaEnable: settings.orb_asia_enable !== false,
        orbLondonEnable: settings.orb_london_enable !== false,
        orbNyEnable: settings.orb_ny_enable === true,
        orbEntryMode: settings.orb_entry_mode || 'SMART_HYBRID',
        orbRr: settings.orb_rr ?? 3.0,
        orbSlBufferMode: settings.orb_sl_buffer_mode || 'FIXED',
        orbSlBufferPips: settings.orb_sl_buffer_pips ?? 2.0,
        orbSlAtrMult: settings.orb_sl_atr_mult ?? 1.0,
        orbSlBufferMinPips: settings.orb_sl_buffer_min_pips ?? 2.0,
        orbSlBufferMaxPips: settings.orb_sl_buffer_max_pips ?? 15.0,
        orbSlFilterEnable: settings.orb_sl_filter_enable === true,
        orbMinSlPips: settings.orb_min_sl_pips ?? 3.0,
        orbMaxSlPips: settings.orb_max_sl_pips ?? 150.0,
        orbMaxBreakPips: settings.orb_max_break_pips ?? 15.0,
        orbMaxLimitBars: settings.orb_max_limit_bars ?? 6,
        orbCancelOnTp: settings.orb_cancel_on_tp !== false,
        orbCancelOnSl: settings.orb_cancel_on_sl !== false,
        orbEma200Filter: settings.orb_ema200_filter === true,
        orbEma200Mode: settings.orb_ema200_mode || (settings.orb_ema200_filter ? 'STRICT' : 'OFF'),
        orbMaxZonePips: settings.orb_max_zone_pips ?? 250.0,
        orbMinZonePips: settings.orb_min_zone_pips ?? 10.0,
        orbDynamicBeEnable: settings.orb_dynamic_be_enable === true,
        orbBeTriggerPct: settings.orb_be_trigger_pct ?? 75.0,
        orbBeLockPips: settings.orb_be_lock_pips ?? 5.0,
        orbTimeoutExitEnable: settings.orb_timeout_exit_enable !== false,
        orbRevertEnable: settings.orb_revert_enable === true,
        orbRevertEntryMode: settings.orb_revert_entry_mode || 'LIMIT_50_BOX',
        orbRevertRr: settings.orb_revert_rr ?? 1.5,
        orbRevertCancelBars: settings.orb_revert_cancel_bars ?? 6,
        orbRevertSlBufferPips: settings.orb_revert_sl_buffer_pips ?? 2.0,
        orbDynamicLimitEnable: settings.orb_dynamic_limit_enable !== undefined ? (settings.orb_dynamic_limit_enable === true || settings.orb_dynamic_limit_enable === 'true') : false,
        orbLimitThresholdPips: settings.orb_limit_threshold_pips ?? 100.0,
        orbLimitPullbackPct: settings.orb_limit_pullback_pct ?? 50.0,
        orbLimitCancelBars: settings.orb_limit_cancel_bars ?? 10,
      };
    }
    if (strat === 'liquidity_sweep' || strat === 'sweep' || strat === 'sweep_pro' || strat === 'liquidity_sweep_pro') {
      return {
        sweepM1AnchorM5: settings.sweep_m1_anchor_m5 !== false,
        sweepLookback: settings.sweep_lookback ?? 5,
        sweepOpt: 'Only Outbreaks & Retest',
        sweepExtend: settings.sweep_extend !== false,
        sweepMaxBars: settings.sweep_max_bars ?? 300,
        sweepEntryMode: settings.sweep_entry_mode || 'RETEST',
        sweepRr: settings.sweep_rr ?? 2.0,
        sweepSlBufferPips: settings.sweep_sl_buffer_pips ?? 1.5,
        sweepMinRiskPips: settings.sweep_min_risk_pips ?? 3.0,
        sweepMaxRiskPips: settings.sweep_max_risk_pips ?? 150.0,
        sweepCooldownBars: settings.sweep_cooldown_bars ?? 3,
        sweepMinSwingPips: 18.0,
        sweepCompareBars: 25,
        sweepLimitCancelBars: settings.sweep_limit_cancel_bars ?? 30,
        sweepHighestPeakFilter: settings.sweep_highest_peak_filter === true || settings.sweep_highest_peak_filter === 'true',
        sweepPeakLookback: settings.sweep_peak_lookback ?? 30,
        sweepZoneLimitEnable: settings.sweep_zone_limit_enable !== false && settings.sweep_zone_limit_enable !== 'false',
        sweep_zone_limit_enable: settings.sweep_zone_limit_enable !== false && settings.sweep_zone_limit_enable !== 'false',
        sweepHybridThresholdPips: settings.sweep_hybrid_threshold_pips !== undefined ? parseFloat(settings.sweep_hybrid_threshold_pips) : 80.0,
        sweepHybridLimitMode: settings.sweep_hybrid_limit_mode || 'RETEST',
        sweepHybridZonePct: settings.sweep_hybrid_zone_pct !== undefined ? parseFloat(settings.sweep_hybrid_zone_pct) : 50.0,
        sweepHybridPullbackPct: settings.sweep_hybrid_pullback_pct !== undefined ? parseFloat(settings.sweep_hybrid_pullback_pct) : 50.0,
        sweepMaxWaitBars: settings.sweep_max_wait_bars ?? 5,
        sweepTrendFilterEnable: settings.sweep_trend_filter_enable === true || settings.sweep_trend_filter_enable === 'true',
        sweepTrendEmaPeriod: settings.sweep_trend_ema_period ?? 200,
        sweepSessionFilterEnable: settings.sweep_session_filter_enable === true || settings.sweep_session_filter_enable === 'true',
        sweepSessionStart: settings.sweep_session_start || '00:00',
        sweepSessionEnd: settings.sweep_session_end || '23:59',
        sweepCloseOnOpposite: settings.sweep_close_on_opposite === true || settings.sweep_close_on_opposite === 'true',
        sweepDynamicBeEnable: settings.sweep_dynamic_be_enable === true || settings.sweep_dynamic_be_enable === 'true',
        sweepBeTriggerMode: settings.sweep_be_trigger_mode || 'PCT_TP',
        sweepBeTriggerPct: settings.sweep_be_trigger_pct ?? 50.0,
        sweepBeTriggerPips: settings.sweep_be_trigger_pips ?? 40.0,
        sweepBeLockPips: settings.sweep_be_lock_pips ?? 2.0,
      };
    }
    // Default HMA
    return {
      period: settings.hma_period ?? 24,
      lookbackBars: settings.hma_lookback_bars ?? 100,
      maxBarsFromExtreme: settings.hma_max_bars_from_extreme ?? 35,
      hmaSlopeFilter: settings.hma_slope_filter !== false,
      maxHmaSlope: settings.hma_max_slope ?? 3.0,
      r1: settings.hma_r1 ?? 1.5,
      r2: settings.hma_r2 ?? 2.0,
      minBodyPct: settings.hma_min_body_pct ?? 60,
      allowFloatingBreakout: settings.hma_allow_floating_breakout === true,
      slBufferMode: settings.hma_sl_buffer_mode ?? "ATR",
      slBufferMinPips: settings.hma_sl_buffer_min_pips ?? 5.0,
      slBufferMaxPips: settings.hma_sl_buffer_max_pips ?? 30.0,
      slAtrMult: settings.hma_sl_atr_mult ?? 1.0,
      slBufferPips: settings.hma_sl_buffer_pips ?? 5.0,
      minRiskPips: settings.hma_min_risk_pips ?? 3.0,
      maxRiskPips: settings.hma_max_risk_pips ?? 80.0,
      hmaCooldownBars: settings.hma_cooldown_bars ?? 5,
      candleStrengthFilter: settings.hma_candle_strength_filter !== false,
      minCandleBodyRatio: settings.hma_min_candle_body_ratio ?? 35,
      maxRejectionWickRatio: settings.hma_max_rejection_wick_ratio ?? 40,
      dynamicLimitEnable: settings.hma_dynamic_limit_enable !== false,
      limitThresholdPips: settings.hma_limit_threshold_pips ?? 60.0,
      limitPullbackPct: settings.hma_limit_pullback_pct ?? 50.0,
      limitCancelBars: settings.hma_limit_cancel_bars ?? 20,
      dynamicBeEnable: settings.hma_dynamic_be_enable === true,
      beTriggerPct: settings.hma_be_trigger_pct ?? 75.0,
      beLockPips: settings.hma_be_lock_pips ?? 5.0,
      nearTpCutEnable: settings.hma_near_tp_cut_enable === true,
      nearTpThresholdPct: settings.hma_near_tp_threshold_pct ?? 85.0,
      closeOnOpposite: settings.hma_close_on_opposite !== false,
      hma_close_on_opposite: settings.hma_close_on_opposite !== false,
      cascadeFilter: settings.hma_cascade_filter === true,
      bearRatioThresh: settings.hma_bear_ratio_thresh ?? 70.0,
      sessionFilterEnable: settings.hma_session_filter_enable === true,
      sessionMode: settings.hma_session_mode || 'ALL',
      sessionStart: settings.hma_session_start || '06:00',
      sessionEnd: settings.hma_session_end || '19:30',
      ema200SwFilterEnable: settings.hma_ema200_sw_filter_enable !== undefined ? (settings.hma_ema200_sw_filter_enable === true || settings.hma_ema200_sw_filter_enable === 'true') : true,
      ema200SwMaxDistPips: settings.hma_ema200_sw_max_dist_pips !== undefined ? parseFloat(settings.hma_ema200_sw_max_dist_pips) : 420.0,
    };
  };

  const [showAddStratModal, setShowAddStratModal] = useState(false);
  const [editingStrat, setEditingStrat] = useState(null);
  const [stratForm, setStratForm] = useState({
    account_id: '',
    strategy: 'hma',
    symbol: 'XAUUSD',
    timeframe: 'M5',
    lot_mode: 'FIXED',
    lot_size: 0.01,
    risk_pct: 1.0,
    max_spread: 40.0,
    tp_mode: 'TP1',
    breakeven_enable: true,
    breakeven_pips: 15.0,
    trailing_enable: false,
    trailing_pips: 20.0,
    session_enable: false,
    session_start: '00:00',
    session_end: '23:59',
    custom_params: getDefaultStrategyParams('hma'),
  });

  const [confirmCloseTicket, setConfirmCloseTicket] = useState(null);
  const [confirmCloseAll, setConfirmCloseAll] = useState(false);

  // Settlement & Profit Sharing Modals
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [settleForm, setSettleForm] = useState({
    account_id: '',
    account_name: '',
    period_start: 0,
    period_end: 0,
    period_profit: 0,
    profit_share_pct: 30,
    payment_note: ''
  });

  const [showProfitShareModal, setShowProfitShareModal] = useState(false);
  const [profitShareForm, setProfitShareForm] = useState({
    account_id: '',
    account_name: '',
    profit_share_pct: 30,
    client_notes: ''
  });

  useEffect(() => {
    loadUsers();
    loadSettings();
    loadSystemStatus();
    const interval = setInterval(loadSystemStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadSystemStatus = async () => {
    try {
      const res = await api.getStatus();
      if (res) {
        setSystemStatus(res);
      }
    } catch (err) {
      console.error('Error fetching system status:', err);
    }
  };

  const handleConnectMT5 = async (e) => {
    if (e) e.preventDefault();
    setConnectingMT5(true);
    setConnMsg({ type: '', text: '' });
    try {
      const res = await api.connectMT5(mt5Form);
      if (res.success) {
        setConnMsg({ type: 'success', text: res.message || 'Kết nối máy chủ MT5 thành công!' });
        loadSystemStatus();
      } else {
        setConnMsg({ type: 'error', text: res.message || 'Kết nối MT5 thất bại.' });
      }
    } catch (err) {
      setConnMsg({ type: 'error', text: err.response?.data?.detail || err.message || 'Lỗi kết nối MT5.' });
    } finally {
      setConnectingMT5(false);
    }
  };

  const handleDisconnectMT5 = async () => {
    setConnMsg({ type: '', text: '' });
    try {
      const res = await api.disconnectMT5();
      setConnMsg({ type: 'success', text: 'Đã ngắt kết nối máy chủ MT5.' });
      loadSystemStatus();
    } catch (err) {
      setConnMsg({ type: 'error', text: err.message || 'Lỗi ngắt kết nối.' });
    }
  };

  const handleTriggerResync = async () => {
    setResyncing(true);
    setConnMsg({ type: '', text: '' });
    try {
      const res = await api.triggerResync('XAUUSD');
      if (res.success) {
        setConnMsg({ type: 'success', text: 'Đã kích hoạt đồng bộ lại toàn bộ nến cho tất cả các cặp tiền & khung thời gian!' });
      } else {
        setConnMsg({ type: 'error', text: res.error || 'Lỗi kích hoạt đồng bộ.' });
      }
    } catch (err) {
      setConnMsg({ type: 'error', text: err.message || 'Lỗi gửi yêu cầu đồng bộ.' });
    } finally {
      setTimeout(() => setResyncing(false), 2000);
    }
  };

  // EA Loaders & Handlers
  const loadEaData = async () => {
    try {
      const [accRes, stratRes, posRes, statsRes, ordRes, txRes, stlRes] = await Promise.allSettled([
        api.adminGetEAAccounts(),
        api.adminGetEAStrategies(),
        api.adminGetEAPositions(),
        api.adminGetEAStats(),
        api.adminGetEAOrders({ limit: 100 }),
        api.adminGetEATransactions({ limit: 150 }),
        api.adminGetEASettlements({ limit: 100 })
      ]);
      if (accRes.status === 'fulfilled' && accRes.value?.success) {
        setEaAccounts(accRes.value.accounts || []);
      }
      if (stratRes.status === 'fulfilled' && stratRes.value?.success) {
        setEaStrategies(stratRes.value.strategies || []);
      }
      if (posRes.status === 'fulfilled' && posRes.value?.success) {
        setEaPositions(posRes.value.positions || []);
      }
      if (statsRes.status === 'fulfilled' && statsRes.value?.success) {
        setEaStats(statsRes.value.stats || {});
      }
      if (ordRes.status === 'fulfilled' && ordRes.value?.success) {
        setEaOrders(ordRes.value.orders || []);
      }
      if (txRes.status === 'fulfilled' && txRes.value?.success) {
        setEaTransactions(txRes.value.transactions || []);
      }
      if (stlRes.status === 'fulfilled' && stlRes.value?.success) {
        setEaSettlements(stlRes.value.settlements || []);
      }
    } catch (err) {
      console.error('Error loading EA data:', err);
    }
  };

  useEffect(() => {
    if (activeSection === 'ea_trading') {
      loadEaData();
      const interval = setInterval(() => {
        loadEaData();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [activeSection]);

  const handleSaveAccount = async (e) => {
    if (e) e.preventDefault();
    if (!accForm.login) {
      setEaMsg({ type: 'error', text: 'Vui lòng nhập mã số Login tài khoản MT5!' });
      return;
    }
    try {
      if (editingAcc) {
        await api.adminUpdateEAAccount(editingAcc.id, accForm);
        setEaMsg({ type: 'success', text: `Cập nhật tài khoản "${accForm.name || accForm.login}" thành công!` });
      } else {
        await api.adminCreateEAAccount(accForm);
        setEaMsg({ type: 'success', text: `Tạo mới tài khoản MT5 thành công!` });
      }
      setShowAddAccModal(false);
      setEditingAcc(null);
      setAccForm({ name: '', login: '', server: '', magic_number: 888888, is_active: true, profit_share_pct: 30.0, client_notes: '' });
      loadEaData();
      setTimeout(() => setEaMsg({ type: '', text: '' }), 4000);
    } catch (err) {
      setEaMsg({ type: 'error', text: err.response?.data?.detail || 'Lỗi lưu thông tin tài khoản MT5.' });
    }
  };

  const handleOpenSettleModal = (acc) => {
    const totRealized = Number(acc.realized_profit || 0);
    const lastSettled = Number(acc.last_settled_profit || 0);
    const unsettledProfit = Math.max(0, totRealized - lastSettled);
    const now = Math.floor(Date.now() / 1000);
    setSettleForm({
      account_id: acc.id,
      account_name: acc.name || acc.login,
      period_start: acc.last_settled_at || acc.created_at || now,
      period_end: now,
      period_profit: unsettledProfit,
      profit_share_pct: Number(acc.profit_share_pct || 30),
      payment_note: `Đối soát lợi nhuận đợt chốt ${new Date(now * 1000).toLocaleDateString('vi-VN')}`
    });
    setShowSettleModal(true);
  };

  const handleSaveSettlement = async (e) => {
    if (e) e.preventDefault();
    try {
      await api.adminCreateEASettlement(settleForm);
      setEaMsg({ type: 'success', text: `Tạo đợt đối soát chia lợi nhuận thành công!` });
      setShowSettleModal(false);
      loadEaData();
      setTimeout(() => setEaMsg({ type: '', text: '' }), 4000);
    } catch (err) {
      setEaMsg({ type: 'error', text: err.response?.data?.detail || 'Lỗi tạo đợt đối soát.' });
    }
  };

  const handleSettlePaymentStatus = async (settlementId, nextStatus) => {
    try {
      await api.adminSettleEAPayment({ settlement_id: settlementId, status: nextStatus });
      setEaMsg({ type: 'success', text: `Đã cập nhật trạng thái đợt đối soát thành "${nextStatus === 'PAID' ? 'Đã Thanh Toán' : nextStatus}".` });
      loadEaData();
      setTimeout(() => setEaMsg({ type: '', text: '' }), 3000);
    } catch (err) {
      setEaMsg({ type: 'error', text: 'Lỗi cập nhật trạng thái.' });
    }
  };

  const handleOpenProfitShareModal = (acc) => {
    setProfitShareForm({
      account_id: acc.id,
      account_name: acc.name || acc.login,
      profit_share_pct: Number(acc.profit_share_pct || 30),
      client_notes: acc.client_notes || ''
    });
    setShowProfitShareModal(true);
  };

  const handleSaveProfitShareConfig = async (e) => {
    if (e) e.preventDefault();
    try {
      await api.adminUpdateProfitShare(profitShareForm.account_id, {
        profit_share_pct: profitShareForm.profit_share_pct,
        client_notes: profitShareForm.client_notes
      });
      setEaMsg({ type: 'success', text: `Cập nhật cấu hình chia sẻ lợi nhuận thành công!` });
      setShowProfitShareModal(false);
      loadEaData();
      setTimeout(() => setEaMsg({ type: '', text: '' }), 3000);
    } catch (err) {
      setEaMsg({ type: 'error', text: 'Lỗi cập nhật cấu hình.' });
    }
  };

  const handleDeleteAccount = async (acc) => {
    if (!window.confirm(`Bạn có chắc chắn muốn xóa tài khoản MT5 "${acc.name} (${acc.login})" không? Toàn bộ cấu hình và lịch sử lệnh của tài khoản này sẽ bị xóa.`)) return;
    try {
      await api.adminDeleteEAAccount(acc.id);
      setEaMsg({ type: 'success', text: `Đã xóa tài khoản "${acc.name || acc.login}".` });
      loadEaData();
      setTimeout(() => setEaMsg({ type: '', text: '' }), 4000);
    } catch (err) {
      setEaMsg({ type: 'error', text: err.response?.data?.detail || 'Lỗi xóa tài khoản MT5.' });
    }
  };

  const handleRegenerateKey = async (accId) => {
    if (!window.confirm("Tạo mới API Key sẽ làm EA đang chạy với API Key cũ bị mất kết nối cho tới khi bạn cập nhật lại API Key mới vào EA. Bạn có chắc muốn tạo lại không?")) return;
    try {
      const res = await api.adminRegenEAKey(accId);
      if (res.success) {
        setEaMsg({ type: 'success', text: 'Đã tạo mới API Key thành công! Vui lòng cập nhật vào EA trên MT5.' });
        loadEaData();
        setTimeout(() => setEaMsg({ type: '', text: '' }), 4000);
      }
    } catch (err) {
      setEaMsg({ type: 'error', text: 'Lỗi tạo lại API Key.' });
    }
  };

  const handleToggleAccountActive = async (acc) => {
    try {
      const nextActive = !acc.is_active;
      await api.adminUpdateEAAccount(acc.id, { is_active: nextActive });
      setEaMsg({ type: 'success', text: `Đã ${nextActive ? 'BẬT' : 'TẠM DỪNG'} giao dịch cho tài khoản "${acc.name || acc.login}".` });
      loadEaData();
      setTimeout(() => setEaMsg({ type: '', text: '' }), 3000);
    } catch (err) {
      setEaMsg({ type: 'error', text: 'Lỗi cập nhật trạng thái tài khoản.' });
    }
  };

  const handleSaveStrategy = async (e) => {
    if (e) e.preventDefault();
    if (!stratForm.account_id) {
      setEaMsg({ type: 'error', text: 'Vui lòng chọn tài khoản MT5!' });
      return;
    }
    try {
      if (editingStrat) {
        await api.adminUpdateEAStrategy(editingStrat.id, stratForm);
        setEaMsg({ type: 'success', text: 'Cập nhật cấu hình chiến lược thành công!' });
      } else {
        await api.adminCreateEAStrategy(stratForm);
        setEaMsg({ type: 'success', text: 'Thêm cấu hình chiến lược mới thành công!' });
      }
      setShowAddStratModal(false);
      setEditingStrat(null);
      loadEaData();
      setTimeout(() => setEaMsg({ type: '', text: '' }), 4000);
    } catch (err) {
      setEaMsg({ type: 'error', text: err.response?.data?.detail || 'Lỗi lưu cấu hình chiến lược.' });
    }
  };

  const handleDeleteStrategy = async (stratId) => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa cấu hình chiến lược này không?")) return;
    try {
      await api.adminDeleteEAStrategy(stratId);
      setEaMsg({ type: 'success', text: 'Đã xóa cấu hình chiến lược.' });
      loadEaData();
      setTimeout(() => setEaMsg({ type: '', text: '' }), 3000);
    } catch (err) {
      setEaMsg({ type: 'error', text: 'Lỗi xóa cấu hình chiến lược.' });
    }
  };

  const handleToggleStrategyActive = async (strat) => {
    try {
      const nextActive = !strat.is_active;
      await api.adminUpdateEAStrategy(strat.id, { is_active: nextActive });
      setEaMsg({ type: 'success', text: `Đã ${nextActive ? 'BẬT' : 'TẮT'} chiến lược ${strat.strategy.toUpperCase()} (${strat.symbol} - ${strat.timeframe}).` });
      loadEaData();
      setTimeout(() => setEaMsg({ type: '', text: '' }), 3000);
    } catch (err) {
      setEaMsg({ type: 'error', text: 'Lỗi cập nhật trạng thái chiến lược.' });
    }
  };

  const handleClosePosition = async (ticket, accountId) => {
    try {
      await api.adminCloseEAPosition(ticket, accountId);
      setEaMsg({ type: 'success', text: `Đã gửi lệnh đóng ticket #${ticket} tới EA. EA sẽ khớp đóng ngay!` });
      setConfirmCloseTicket(null);
      loadEaData();
      setTimeout(() => setEaMsg({ type: '', text: '' }), 4000);
    } catch (err) {
      setEaMsg({ type: 'error', text: err.response?.data?.detail || 'Lỗi gửi lệnh đóng.' });
    }
  };

  const handleCloseAllPositions = async (accountId = null) => {
    try {
      await api.adminCloseAllEAPositions(accountId);
      setEaMsg({ type: 'success', text: 'Đã gửi lệnh khẩn cấp ĐÓNG TOÀN BỘ LỆNH tới EA!' });
      setConfirmCloseAll(false);
      loadEaData();
      setTimeout(() => setEaMsg({ type: '', text: '' }), 4000);
    } catch (err) {
      setEaMsg({ type: 'error', text: err.response?.data?.detail || 'Lỗi gửi lệnh đóng toàn bộ.' });
    }
  };

  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(''), 2500);
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2500);
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await api.adminGetUsers();
      if (res.success) {
        setUsers(res.users || []);
      }
    } catch (err) {
      setUsersMsg({ type: 'error', text: err.response?.data?.detail || 'Không thể tải danh sách người dùng.' });
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await api.adminGetSettings();
      if (res.success && res.settings) {
        const s = res.settings;
        setSettings(prev => ({
          ...prev,
          ...s,
          default_mid_same_pct: s.default_mid_same_pct !== undefined ? parseFloat(s.default_mid_same_pct) : prev.default_mid_same_pct,
          hma_period: s.hma_period !== undefined ? parseInt(s.hma_period) : prev.hma_period,
          hma_slope_filter: s.hma_slope_filter !== undefined ? (s.hma_slope_filter === true || s.hma_slope_filter === 'true') : prev.hma_slope_filter,
          hma_max_slope: s.hma_max_slope !== undefined ? parseFloat(s.hma_max_slope) : prev.hma_max_slope,
          hma_min_body_pct: s.hma_min_body_pct !== undefined ? (parseFloat(s.hma_min_body_pct) <= 1.0 ? parseFloat(s.hma_min_body_pct) * 100 : parseFloat(s.hma_min_body_pct)) : prev.hma_min_body_pct,
          hma_lookback_bars: s.hma_lookback_bars !== undefined ? parseInt(s.hma_lookback_bars) : prev.hma_lookback_bars,
          hma_max_bars_from_extreme: s.hma_max_bars_from_extreme !== undefined ? parseInt(s.hma_max_bars_from_extreme) : prev.hma_max_bars_from_extreme,
          hma_max_risk_pips: s.hma_max_risk_pips !== undefined ? parseFloat(s.hma_max_risk_pips) : prev.hma_max_risk_pips,
          hma_r1: s.hma_r1 !== undefined ? parseFloat(s.hma_r1) : prev.hma_r1,
          hma_r2: s.hma_r2 !== undefined ? parseFloat(s.hma_r2) : prev.hma_r2,
          hma_sl_buffer_pips: s.hma_sl_buffer_pips !== undefined ? parseFloat(s.hma_sl_buffer_pips) : prev.hma_sl_buffer_pips,
          hma_cooldown_bars: s.hma_cooldown_bars !== undefined ? parseInt(s.hma_cooldown_bars) : prev.hma_cooldown_bars,
          hma_show_line: s.hma_show_line !== undefined ? (s.hma_show_line === true || s.hma_show_line === 'true') : prev.hma_show_line,
          hma_show_base_box: s.hma_show_base_box !== undefined ? (s.hma_show_base_box === true || s.hma_show_base_box === 'true') : prev.hma_show_base_box,
          hma_allow_floating_breakout: s.hma_allow_floating_breakout !== undefined ? (s.hma_allow_floating_breakout === true || s.hma_allow_floating_breakout === 'true') : prev.hma_allow_floating_breakout,
          hma_candle_strength_filter: s.hma_candle_strength_filter !== undefined ? (s.hma_candle_strength_filter === true || s.hma_candle_strength_filter === 'true') : prev.hma_candle_strength_filter,
          hma_min_candle_body_ratio: s.hma_min_candle_body_ratio !== undefined ? (parseFloat(s.hma_min_candle_body_ratio) <= 1.0 ? parseFloat(s.hma_min_candle_body_ratio) * 100 : parseFloat(s.hma_min_candle_body_ratio)) : prev.hma_min_candle_body_ratio,
          hma_max_rejection_wick_ratio: s.hma_max_rejection_wick_ratio !== undefined ? (parseFloat(s.hma_max_rejection_wick_ratio) <= 1.0 ? parseFloat(s.hma_max_rejection_wick_ratio) * 100 : parseFloat(s.hma_max_rejection_wick_ratio)) : prev.hma_max_rejection_wick_ratio,
          hma_dynamic_limit_enable: s.hma_dynamic_limit_enable !== undefined ? (s.hma_dynamic_limit_enable === true || s.hma_dynamic_limit_enable === 'true') : prev.hma_dynamic_limit_enable,
          hma_limit_threshold_pips: s.hma_limit_threshold_pips !== undefined ? parseFloat(s.hma_limit_threshold_pips) : prev.hma_limit_threshold_pips,
          hma_limit_pullback_pct: s.hma_limit_pullback_pct !== undefined ? parseFloat(s.hma_limit_pullback_pct) : prev.hma_limit_pullback_pct,
          hma_limit_cancel_bars: s.hma_limit_cancel_bars !== undefined ? parseInt(s.hma_limit_cancel_bars) : prev.hma_limit_cancel_bars,
          hma_dynamic_be_enable: s.hma_dynamic_be_enable !== undefined ? (s.hma_dynamic_be_enable === true || s.hma_dynamic_be_enable === 'true') : prev.hma_dynamic_be_enable,
          hma_be_trigger_pct: s.hma_be_trigger_pct !== undefined ? parseFloat(s.hma_be_trigger_pct) : prev.hma_be_trigger_pct,
          hma_be_lock_pips: s.hma_be_lock_pips !== undefined ? parseFloat(s.hma_be_lock_pips) : prev.hma_be_lock_pips,
          hma_near_tp_cut_enable: s.hma_near_tp_cut_enable !== undefined ? (s.hma_near_tp_cut_enable === true || s.hma_near_tp_cut_enable === 'true') : prev.hma_near_tp_cut_enable,
          hma_near_tp_threshold_pct: s.hma_near_tp_threshold_pct !== undefined ? parseFloat(s.hma_near_tp_threshold_pct) : prev.hma_near_tp_threshold_pct,
          hma_close_on_opposite: s.hma_close_on_opposite !== undefined ? (s.hma_close_on_opposite === true || s.hma_close_on_opposite === 'true') : prev.hma_close_on_opposite,
          hma_cascade_filter: s.hma_cascade_filter !== undefined ? (s.hma_cascade_filter === true || s.hma_cascade_filter === 'true') : prev.hma_cascade_filter,
          hma_bear_ratio_thresh: s.hma_bear_ratio_thresh !== undefined ? parseFloat(s.hma_bear_ratio_thresh) : prev.hma_bear_ratio_thresh,
          hma_session_filter_enable: s.hma_session_filter_enable !== undefined ? (s.hma_session_filter_enable === true || s.hma_session_filter_enable === 'true') : prev.hma_session_filter_enable,
          hma_session_mode: s.hma_session_mode !== undefined ? String(s.hma_session_mode) : prev.hma_session_mode,
          hma_session_start: s.hma_session_start !== undefined ? String(s.hma_session_start) : prev.hma_session_start,
          hma_session_end: s.hma_session_end !== undefined ? String(s.hma_session_end) : prev.hma_session_end,
          hma_sl_buffer_mode: s.hma_sl_buffer_mode !== undefined ? String(s.hma_sl_buffer_mode) : prev.hma_sl_buffer_mode,
          hma_sl_buffer_min_pips: s.hma_sl_buffer_min_pips !== undefined ? parseFloat(s.hma_sl_buffer_min_pips) : prev.hma_sl_buffer_min_pips,
          hma_sl_buffer_max_pips: s.hma_sl_buffer_max_pips !== undefined ? parseFloat(s.hma_sl_buffer_max_pips) : prev.hma_sl_buffer_max_pips,
          hma_sl_atr_mult: s.hma_sl_atr_mult !== undefined ? parseFloat(s.hma_sl_atr_mult) : prev.hma_sl_atr_mult,
          hma_ema200_sw_filter_enable: s.hma_ema200_sw_filter_enable !== undefined ? (s.hma_ema200_sw_filter_enable === true || s.hma_ema200_sw_filter_enable === 'true') : prev.hma_ema200_sw_filter_enable,
          hma_ema200_sw_max_dist_pips: s.hma_ema200_sw_max_dist_pips !== undefined ? parseFloat(s.hma_ema200_sw_max_dist_pips) : prev.hma_ema200_sw_max_dist_pips,
          hma_adaptive_period_enable: s.hma_adaptive_period_enable !== undefined ? (s.hma_adaptive_period_enable === true || s.hma_adaptive_period_enable === 'true') : prev.hma_adaptive_period_enable,
          hma_adaptive_min_period: s.hma_adaptive_min_period !== undefined ? parseInt(s.hma_adaptive_min_period) : prev.hma_adaptive_min_period,
          hma_adaptive_max_period: s.hma_adaptive_max_period !== undefined ? parseInt(s.hma_adaptive_max_period) : prev.hma_adaptive_max_period,
          volman_ema_period: s.volman_ema_period !== undefined ? parseInt(s.volman_ema_period) : prev.volman_ema_period,
          volman_pivot_length: s.volman_pivot_length !== undefined ? parseInt(s.volman_pivot_length) : prev.volman_pivot_length,
          volman_r1: s.volman_r1 !== undefined ? parseFloat(s.volman_r1) : prev.volman_r1,
          volman_sl_buffer_pips: s.volman_sl_buffer_pips !== undefined ? parseFloat(s.volman_sl_buffer_pips) : prev.volman_sl_buffer_pips,
          volman_sl_filter_enable: s.volman_sl_filter_enable !== undefined ? (s.volman_sl_filter_enable === true || s.volman_sl_filter_enable === 'true') : prev.volman_sl_filter_enable,
          volman_min_risk_pips: s.volman_min_risk_pips !== undefined ? parseFloat(s.volman_min_risk_pips) : prev.volman_min_risk_pips,
          volman_max_risk_pips: s.volman_max_risk_pips !== undefined ? parseFloat(s.volman_max_risk_pips) : prev.volman_max_risk_pips,
          volman_cooldown_bars: s.volman_cooldown_bars !== undefined ? parseInt(s.volman_cooldown_bars) : prev.volman_cooldown_bars,
          volman_enable_bb: s.volman_enable_bb !== undefined ? (s.volman_enable_bb === true || s.volman_enable_bb === 'true') : prev.volman_enable_bb,
          volman_enable_tb: s.volman_enable_tb !== undefined ? (s.volman_enable_tb === true || s.volman_enable_tb === 'true') : prev.volman_enable_tb,
          volman_breakout_candle_filter: s.volman_breakout_candle_filter !== undefined ? (s.volman_breakout_candle_filter === true || s.volman_breakout_candle_filter === 'true') : prev.volman_breakout_candle_filter,
          volman_min_body_ratio: s.volman_min_body_ratio !== undefined ? parseFloat(s.volman_min_body_ratio) : prev.volman_min_body_ratio,
          volman_min_body_beyond_pct: s.volman_min_body_beyond_pct !== undefined ? parseFloat(s.volman_min_body_beyond_pct) : prev.volman_min_body_beyond_pct,
          volman_max_rejection_wick: s.volman_max_rejection_wick !== undefined ? parseFloat(s.volman_max_rejection_wick) : prev.volman_max_rejection_wick,
          volman_show_trendlines: s.volman_show_trendlines !== undefined ? (s.volman_show_trendlines === true || s.volman_show_trendlines === 'true') : prev.volman_show_trendlines,
          volman_show_barriers: s.volman_show_barriers !== undefined ? (s.volman_show_barriers === true || s.volman_show_barriers === 'true') : prev.volman_show_barriers,
          volman_show_buildup: s.volman_show_buildup !== undefined ? (s.volman_show_buildup === true || s.volman_show_buildup === 'true') : prev.volman_show_buildup,
          volman_show_ema: s.volman_show_ema !== undefined ? (s.volman_show_ema === true || s.volman_show_ema === 'true') : prev.volman_show_ema,
          volman_dynamic_limit_enable: s.volman_dynamic_limit_enable !== undefined ? (s.volman_dynamic_limit_enable === true || s.volman_dynamic_limit_enable === 'true') : prev.volman_dynamic_limit_enable,
          volman_limit_threshold_pips: s.volman_limit_threshold_pips !== undefined ? parseFloat(s.volman_limit_threshold_pips) : prev.volman_limit_threshold_pips,
          volman_limit_pullback_pct: s.volman_limit_pullback_pct !== undefined ? parseFloat(s.volman_limit_pullback_pct) : prev.volman_limit_pullback_pct,
          volman_limit_cancel_bars: s.volman_limit_cancel_bars !== undefined ? parseInt(s.volman_limit_cancel_bars) : prev.volman_limit_cancel_bars,
          orb_asia_enable: s.orb_asia_enable !== undefined ? (s.orb_asia_enable === true || s.orb_asia_enable === 'true') : prev.orb_asia_enable,
          orb_london_enable: s.orb_london_enable !== undefined ? (s.orb_london_enable === true || s.orb_london_enable === 'true') : prev.orb_london_enable,
          orb_ny_enable: s.orb_ny_enable !== undefined ? (s.orb_ny_enable === true || s.orb_ny_enable === 'true') : prev.orb_ny_enable,
          orb_entry_mode: s.orb_entry_mode !== undefined ? String(s.orb_entry_mode) : prev.orb_entry_mode,
          orb_rr: s.orb_rr !== undefined ? parseFloat(s.orb_rr) : prev.orb_rr,
          orb_sl_buffer_mode: s.orb_sl_buffer_mode !== undefined ? String(s.orb_sl_buffer_mode) : prev.orb_sl_buffer_mode,
          orb_sl_buffer_pips: s.orb_sl_buffer_pips !== undefined ? parseFloat(s.orb_sl_buffer_pips) : prev.orb_sl_buffer_pips,
          orb_sl_atr_mult: s.orb_sl_atr_mult !== undefined ? parseFloat(s.orb_sl_atr_mult) : prev.orb_sl_atr_mult,
          orb_sl_buffer_min_pips: s.orb_sl_buffer_min_pips !== undefined ? parseFloat(s.orb_sl_buffer_min_pips) : prev.orb_sl_buffer_min_pips,
          orb_sl_buffer_max_pips: s.orb_sl_buffer_max_pips !== undefined ? parseFloat(s.orb_sl_buffer_max_pips) : prev.orb_sl_buffer_max_pips,
          orb_sl_filter_enable: s.orb_sl_filter_enable !== undefined ? (s.orb_sl_filter_enable === true || s.orb_sl_filter_enable === 'true') : prev.orb_sl_filter_enable,
          orb_min_sl_pips: s.orb_min_sl_pips !== undefined ? parseFloat(s.orb_min_sl_pips) : prev.orb_min_sl_pips,
          orb_max_sl_pips: s.orb_max_sl_pips !== undefined ? parseFloat(s.orb_max_sl_pips) : prev.orb_max_sl_pips,
          orb_max_break_pips: s.orb_max_break_pips !== undefined ? parseFloat(s.orb_max_break_pips) : prev.orb_max_break_pips,
          orb_max_limit_bars: s.orb_max_limit_bars !== undefined ? parseInt(s.orb_max_limit_bars) : prev.orb_max_limit_bars,
          orb_cancel_on_tp: s.orb_cancel_on_tp !== undefined ? (s.orb_cancel_on_tp === true || s.orb_cancel_on_tp === 'true') : prev.orb_cancel_on_tp,
          orb_cancel_on_sl: s.orb_cancel_on_sl !== undefined ? (s.orb_cancel_on_sl === true || s.orb_cancel_on_sl === 'true') : prev.orb_cancel_on_sl,
          orb_ema200_filter: s.orb_ema200_filter !== undefined ? (s.orb_ema200_filter === true || s.orb_ema200_filter === 'true') : prev.orb_ema200_filter,
          orb_ema200_mode: s.orb_ema200_mode !== undefined ? String(s.orb_ema200_mode) : (s.orb_ema200_filter !== undefined ? ((s.orb_ema200_filter === true || s.orb_ema200_filter === 'true') ? 'STRICT' : 'OFF') : prev.orb_ema200_mode),
          orb_max_zone_pips: s.orb_max_zone_pips !== undefined ? parseFloat(s.orb_max_zone_pips) : prev.orb_max_zone_pips,
          orb_min_zone_pips: s.orb_min_zone_pips !== undefined ? parseFloat(s.orb_min_zone_pips) : prev.orb_min_zone_pips,
          orb_dynamic_be_enable: s.orb_dynamic_be_enable !== undefined ? (s.orb_dynamic_be_enable === true || s.orb_dynamic_be_enable === 'true') : prev.orb_dynamic_be_enable,
          orb_be_trigger_pct: s.orb_be_trigger_pct !== undefined ? parseFloat(s.orb_be_trigger_pct) : prev.orb_be_trigger_pct,
          orb_be_lock_pips: s.orb_be_lock_pips !== undefined ? parseFloat(s.orb_be_lock_pips) : prev.orb_be_lock_pips,
          orb_timeout_exit_enable: s.orb_timeout_exit_enable !== undefined ? (s.orb_timeout_exit_enable === true || s.orb_timeout_exit_enable === 'true') : prev.orb_timeout_exit_enable,
          orb_revert_enable: s.orb_revert_enable !== undefined ? (s.orb_revert_enable === true || s.orb_revert_enable === 'true') : prev.orb_revert_enable,
          orb_revert_entry_mode: s.orb_revert_entry_mode !== undefined ? String(s.orb_revert_entry_mode) : prev.orb_revert_entry_mode,
          orb_revert_rr: s.orb_revert_rr !== undefined ? parseFloat(s.orb_revert_rr) : prev.orb_revert_rr,
          orb_revert_cancel_bars: s.orb_revert_cancel_bars !== undefined ? parseInt(s.orb_revert_cancel_bars) : prev.orb_revert_cancel_bars,
          orb_revert_sl_buffer_pips: s.orb_revert_sl_buffer_pips !== undefined ? parseFloat(s.orb_revert_sl_buffer_pips) : prev.orb_revert_sl_buffer_pips,
          orb_dynamic_limit_enable: s.orb_dynamic_limit_enable !== undefined ? (s.orb_dynamic_limit_enable === true || s.orb_dynamic_limit_enable === 'true') : Boolean(prev.orb_dynamic_limit_enable),
          orb_limit_threshold_pips: s.orb_limit_threshold_pips !== undefined ? parseFloat(s.orb_limit_threshold_pips) : (prev.orb_limit_threshold_pips ?? 100.0),
          orb_limit_pullback_pct: s.orb_limit_pullback_pct !== undefined ? parseFloat(s.orb_limit_pullback_pct) : (prev.orb_limit_pullback_pct ?? 50.0),
          orb_limit_cancel_bars: s.orb_limit_cancel_bars !== undefined ? parseInt(s.orb_limit_cancel_bars) : (prev.orb_limit_cancel_bars ?? 10),
          smc_fvg_filter_enable: s.smc_fvg_filter_enable !== undefined ? (s.smc_fvg_filter_enable === true || s.smc_fvg_filter_enable === 'true') : prev.smc_fvg_filter_enable,
          smc_revert_enable: s.smc_revert_enable !== undefined ? (s.smc_revert_enable === true || s.smc_revert_enable === 'true') : prev.smc_revert_enable,
          smc_revert_entry_mode: s.smc_revert_entry_mode !== undefined ? String(s.smc_revert_entry_mode) : prev.smc_revert_entry_mode,
          smc_revert_rr: s.smc_revert_rr !== undefined ? parseFloat(s.smc_revert_rr) : prev.smc_revert_rr,
          smc_revert_cancel_bars: s.smc_revert_cancel_bars !== undefined ? parseInt(s.smc_revert_cancel_bars) : prev.smc_revert_cancel_bars,
          smc_revert_sl_buffer_pips: s.smc_revert_sl_buffer_pips !== undefined ? parseFloat(s.smc_revert_sl_buffer_pips) : prev.smc_revert_sl_buffer_pips,
          // Liquidity Sweep Pro Settings
          sweep_m1_anchor_m5: s.sweep_m1_anchor_m5 !== undefined ? (s.sweep_m1_anchor_m5 === true || s.sweep_m1_anchor_m5 === 'true') : (prev.sweep_m1_anchor_m5 !== false),
          sweep_zone_limit_enable: s.sweep_zone_limit_enable !== undefined ? (s.sweep_zone_limit_enable === true || s.sweep_zone_limit_enable === 'true') : (prev.sweep_zone_limit_enable !== false),
          sweep_entry_mode: s.sweep_entry_mode || prev.sweep_entry_mode,
          sweep_hybrid_threshold_pips: s.sweep_hybrid_threshold_pips !== undefined ? parseFloat(s.sweep_hybrid_threshold_pips) : prev.sweep_hybrid_threshold_pips,
          sweep_hybrid_limit_mode: s.sweep_hybrid_limit_mode || prev.sweep_hybrid_limit_mode,
          sweep_hybrid_zone_pct: s.sweep_hybrid_zone_pct !== undefined ? parseFloat(s.sweep_hybrid_zone_pct) : prev.sweep_hybrid_zone_pct,
          sweep_hybrid_pullback_pct: s.sweep_hybrid_pullback_pct !== undefined ? parseFloat(s.sweep_hybrid_pullback_pct) : prev.sweep_hybrid_pullback_pct,
          sweep_lookback: s.sweep_lookback !== undefined ? parseInt(s.sweep_lookback) : prev.sweep_lookback,
          sweep_opt: s.sweep_opt || prev.sweep_opt,
          sweep_extend: s.sweep_extend !== undefined ? (s.sweep_extend === true || s.sweep_extend === 'true') : prev.sweep_extend,
          sweep_max_bars: s.sweep_max_bars !== undefined ? parseInt(s.sweep_max_bars) : prev.sweep_max_bars,
          sweep_rr: s.sweep_rr !== undefined ? parseFloat(s.sweep_rr) : prev.sweep_rr,
          sweep_sl_buffer_pips: s.sweep_sl_buffer_pips !== undefined ? parseFloat(s.sweep_sl_buffer_pips) : prev.sweep_sl_buffer_pips,
          sweep_min_risk_pips: s.sweep_min_risk_pips !== undefined ? parseFloat(s.sweep_min_risk_pips) : prev.sweep_min_risk_pips,
          sweep_max_risk_pips: s.sweep_max_risk_pips !== undefined ? parseFloat(s.sweep_max_risk_pips) : prev.sweep_max_risk_pips,
          sweep_cooldown_bars: s.sweep_cooldown_bars !== undefined ? parseInt(s.sweep_cooldown_bars) : prev.sweep_cooldown_bars,
          sweep_trend_filter_enable: s.sweep_trend_filter_enable !== undefined ? (s.sweep_trend_filter_enable === true || s.sweep_trend_filter_enable === 'true') : prev.sweep_trend_filter_enable,
          sweep_trend_ema_period: s.sweep_trend_ema_period !== undefined ? parseInt(s.sweep_trend_ema_period) : prev.sweep_trend_ema_period,
          sweep_session_filter_enable: s.sweep_session_filter_enable !== undefined ? (s.sweep_session_filter_enable === true || s.sweep_session_filter_enable === 'true') : prev.sweep_session_filter_enable,
          sweep_session_start: s.sweep_session_start || prev.sweep_session_start,
          sweep_session_end: s.sweep_session_end || prev.sweep_session_end,
          sweep_limit_cancel_bars: s.sweep_limit_cancel_bars !== undefined ? parseInt(s.sweep_limit_cancel_bars) : prev.sweep_limit_cancel_bars,
          sweep_highest_peak_filter: s.sweep_highest_peak_filter !== undefined ? (s.sweep_highest_peak_filter === true || s.sweep_highest_peak_filter === 'true') : prev.sweep_highest_peak_filter,
          sweep_peak_lookback: s.sweep_peak_lookback !== undefined ? parseInt(s.sweep_peak_lookback) : prev.sweep_peak_lookback,
          sweep_max_wait_bars: s.sweep_max_wait_bars !== undefined ? parseInt(s.sweep_max_wait_bars) : prev.sweep_max_wait_bars,
          sweep_close_on_opposite: s.sweep_close_on_opposite !== undefined ? (s.sweep_close_on_opposite === true || s.sweep_close_on_opposite === 'true') : prev.sweep_close_on_opposite,
          sweep_dynamic_be_enable: s.sweep_dynamic_be_enable !== undefined ? (s.sweep_dynamic_be_enable === true || s.sweep_dynamic_be_enable === 'true') : prev.sweep_dynamic_be_enable,
          sweep_be_trigger_mode: s.sweep_be_trigger_mode || prev.sweep_be_trigger_mode,
          sweep_be_trigger_pct: s.sweep_be_trigger_pct !== undefined ? parseFloat(s.sweep_be_trigger_pct) : prev.sweep_be_trigger_pct,
          sweep_be_trigger_pips: s.sweep_be_trigger_pips !== undefined ? parseFloat(s.sweep_be_trigger_pips) : prev.sweep_be_trigger_pips,
          sweep_be_lock_pips: s.sweep_be_lock_pips !== undefined ? parseFloat(s.sweep_be_lock_pips) : prev.sweep_be_lock_pips,
        }));
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  };

  const handleUpdateUserTier = async (userId, newTier, newStatus, days = null, pro_expires_at = null) => {
    try {
      await api.adminUpdateUserTier(userId, newTier, newStatus, days, pro_expires_at);
      setUsersMsg({ type: 'success', text: `Cập nhật tài khoản & thời hạn PRO thành công.` });
      loadUsers();
      setTimeout(() => setUsersMsg({ type: '', text: '' }), 3000);
    } catch (err) {
      setUsersMsg({ type: 'error', text: err.response?.data?.detail || 'Lỗi cập nhật người dùng.' });
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Bạn có chắc chắn muốn xóa tài khoản "${username}" khỏi hệ thống không?`)) return;
    try {
      await api.adminDeleteUser(userId);
      setUsersMsg({ type: 'success', text: `Đã xóa người dùng "${username}".` });
      setUsers(prev => prev.filter(u => u.id !== userId));
      setTimeout(() => setUsersMsg({ type: '', text: '' }), 3000);
    } catch (err) {
      setUsersMsg({ type: 'error', text: err.response?.data?.detail || 'Lỗi xóa người dùng.' });
    }
  };

  const handleSaveSettings = async (e) => {
    if (e) e.preventDefault();
    setSavingSettings(true);
    setSettingsMsg({ type: '', text: '' });
    try {
      let payload = {};

      if (activeSection === 'hma_config') {
        payload = {
          hma_period: settings.hma_period !== undefined ? parseInt(settings.hma_period) : 24,
          hma_slope_filter: settings.hma_slope_filter !== undefined ? Boolean(settings.hma_slope_filter) : true,
          hma_max_slope: settings.hma_max_slope !== undefined ? parseFloat(settings.hma_max_slope) : 3.0,
          hma_min_body_pct: settings.hma_min_body_pct !== undefined ? (parseFloat(settings.hma_min_body_pct) <= 1.0 ? parseFloat(settings.hma_min_body_pct) * 100 : parseFloat(settings.hma_min_body_pct)) : 60,
          hma_lookback_bars: settings.hma_lookback_bars !== undefined ? parseInt(settings.hma_lookback_bars) : 100,
          hma_max_bars_from_extreme: settings.hma_max_bars_from_extreme !== undefined ? parseInt(settings.hma_max_bars_from_extreme) : 35,
          hma_max_risk_pips: settings.hma_max_risk_pips !== undefined ? parseFloat(settings.hma_max_risk_pips) : 80,
          hma_r1: settings.hma_r1 !== undefined ? parseFloat(settings.hma_r1) : 1.5,
          hma_r2: settings.hma_r2 !== undefined ? parseFloat(settings.hma_r2) : 2.0,
          hma_sl_buffer_pips: settings.hma_sl_buffer_pips !== undefined ? parseFloat(settings.hma_sl_buffer_pips) : 5.0,
          hma_cooldown_bars: settings.hma_cooldown_bars !== undefined ? parseInt(settings.hma_cooldown_bars) : 5,
          hma_show_line: settings.hma_show_line !== undefined ? Boolean(settings.hma_show_line) : true,
          hma_show_base_box: settings.hma_show_base_box !== undefined ? Boolean(settings.hma_show_base_box) : false,
          hma_allow_floating_breakout: settings.hma_allow_floating_breakout !== undefined ? Boolean(settings.hma_allow_floating_breakout) : false,
          hma_candle_strength_filter: settings.hma_candle_strength_filter !== undefined ? Boolean(settings.hma_candle_strength_filter) : true,
          hma_min_candle_body_ratio: settings.hma_min_candle_body_ratio !== undefined ? (parseFloat(settings.hma_min_candle_body_ratio) <= 1.0 ? parseFloat(settings.hma_min_candle_body_ratio) * 100 : parseFloat(settings.hma_min_candle_body_ratio)) : 35,
          hma_max_rejection_wick_ratio: settings.hma_max_rejection_wick_ratio !== undefined ? (parseFloat(settings.hma_max_rejection_wick_ratio) <= 1.0 ? parseFloat(settings.hma_max_rejection_wick_ratio) * 100 : parseFloat(settings.hma_max_rejection_wick_ratio)) : 40,
          hma_dynamic_limit_enable: settings.hma_dynamic_limit_enable !== undefined ? Boolean(settings.hma_dynamic_limit_enable) : true,
          hma_limit_threshold_pips: settings.hma_limit_threshold_pips !== undefined ? parseFloat(settings.hma_limit_threshold_pips) : 60,
          hma_limit_pullback_pct: settings.hma_limit_pullback_pct !== undefined ? parseFloat(settings.hma_limit_pullback_pct) : 50,
          hma_limit_cancel_bars: settings.hma_limit_cancel_bars !== undefined ? parseInt(settings.hma_limit_cancel_bars) : 20,
          hma_dynamic_be_enable: settings.hma_dynamic_be_enable !== undefined ? Boolean(settings.hma_dynamic_be_enable) : false,
          hma_be_trigger_pct: settings.hma_be_trigger_pct !== undefined ? parseFloat(settings.hma_be_trigger_pct) : 75,
          hma_be_lock_pips: settings.hma_be_lock_pips !== undefined ? parseFloat(settings.hma_be_lock_pips) : 5,
          hma_near_tp_cut_enable: settings.hma_near_tp_cut_enable !== undefined ? Boolean(settings.hma_near_tp_cut_enable) : false,
          hma_near_tp_threshold_pct: settings.hma_near_tp_threshold_pct !== undefined ? parseFloat(settings.hma_near_tp_threshold_pct) : 85,
          hma_close_on_opposite: settings.hma_close_on_opposite !== undefined ? Boolean(settings.hma_close_on_opposite) : true,
          hma_cascade_filter: settings.hma_cascade_filter !== undefined ? Boolean(settings.hma_cascade_filter) : false,
          hma_bear_ratio_thresh: settings.hma_bear_ratio_thresh !== undefined ? parseFloat(settings.hma_bear_ratio_thresh) : 70,
          hma_session_filter_enable: settings.hma_session_filter_enable !== undefined ? Boolean(settings.hma_session_filter_enable) : false,
          hma_session_mode: settings.hma_session_mode || 'ALL',
          hma_session_start: settings.hma_session_start || '06:00',
          hma_session_end: settings.hma_session_end || '19:30',
          hma_sl_buffer_mode: settings.hma_sl_buffer_mode || 'ATR',
          hma_sl_buffer_min_pips: settings.hma_sl_buffer_min_pips !== undefined ? parseFloat(settings.hma_sl_buffer_min_pips) : 5.0,
          hma_sl_buffer_max_pips: settings.hma_sl_buffer_max_pips !== undefined ? parseFloat(settings.hma_sl_buffer_max_pips) : 30.0,
          hma_sl_atr_mult: settings.hma_sl_atr_mult !== undefined ? parseFloat(settings.hma_sl_atr_mult) : 1.0,
          hma_ema200_sw_filter_enable: settings.hma_ema200_sw_filter_enable !== undefined ? Boolean(settings.hma_ema200_sw_filter_enable) : false,
          hma_ema200_sw_max_dist_pips: settings.hma_ema200_sw_max_dist_pips !== undefined ? parseFloat(settings.hma_ema200_sw_max_dist_pips) : 300.0,
          hma_adaptive_period_enable: settings.hma_adaptive_period_enable !== undefined ? Boolean(settings.hma_adaptive_period_enable) : false,
          hma_adaptive_min_period: settings.hma_adaptive_min_period !== undefined ? parseInt(settings.hma_adaptive_min_period) : 21,
          hma_adaptive_max_period: settings.hma_adaptive_max_period !== undefined ? parseInt(settings.hma_adaptive_max_period) : 35,
        };
      } else if (activeSection === 'volman_config') {
        payload = {
          volman_ema_period: settings.volman_ema_period !== undefined ? parseInt(settings.volman_ema_period) : 20,
          volman_pivot_length: settings.volman_pivot_length !== undefined ? parseInt(settings.volman_pivot_length) : 3,
          volman_r1: settings.volman_r1 !== undefined ? parseFloat(settings.volman_r1) : 1.5,
          volman_sl_buffer_pips: settings.volman_sl_buffer_pips !== undefined ? parseFloat(settings.volman_sl_buffer_pips) : 5.0,
          volman_sl_filter_enable: settings.volman_sl_filter_enable !== undefined ? (settings.volman_sl_filter_enable === true || settings.volman_sl_filter_enable === 'true') : true,
          volman_min_risk_pips: settings.volman_min_risk_pips !== undefined ? parseFloat(settings.volman_min_risk_pips) : 3.0,
          volman_max_risk_pips: settings.volman_max_risk_pips !== undefined ? parseFloat(settings.volman_max_risk_pips) : 150.0,
          volman_cooldown_bars: settings.volman_cooldown_bars !== undefined ? parseInt(settings.volman_cooldown_bars) : 5,
          volman_enable_bb: settings.volman_enable_bb !== undefined ? Boolean(settings.volman_enable_bb) : true,
          volman_enable_tb: settings.volman_enable_tb !== undefined ? Boolean(settings.volman_enable_tb) : true,
          volman_breakout_candle_filter: settings.volman_breakout_candle_filter !== undefined ? Boolean(settings.volman_breakout_candle_filter) : true,
          volman_min_body_ratio: settings.volman_min_body_ratio !== undefined ? parseFloat(settings.volman_min_body_ratio) : 50,
          volman_min_body_beyond_pct: settings.volman_min_body_beyond_pct !== undefined ? parseFloat(settings.volman_min_body_beyond_pct) : 30,
          volman_max_rejection_wick: settings.volman_max_rejection_wick !== undefined ? parseFloat(settings.volman_max_rejection_wick) : 25,
          volman_show_trendlines: settings.volman_show_trendlines !== undefined ? Boolean(settings.volman_show_trendlines) : false,
          volman_show_barriers: settings.volman_show_barriers !== undefined ? Boolean(settings.volman_show_barriers) : false,
          volman_show_buildup: settings.volman_show_buildup !== undefined ? Boolean(settings.volman_show_buildup) : false,
          volman_show_ema: settings.volman_show_ema !== undefined ? Boolean(settings.volman_show_ema) : true,
          volman_dynamic_limit_enable: settings.volman_dynamic_limit_enable !== undefined ? Boolean(settings.volman_dynamic_limit_enable) : false,
          volman_limit_threshold_pips: settings.volman_limit_threshold_pips !== undefined ? parseFloat(settings.volman_limit_threshold_pips) : 80,
          volman_limit_pullback_pct: settings.volman_limit_pullback_pct !== undefined ? parseFloat(settings.volman_limit_pullback_pct) : 40,
          volman_limit_cancel_bars: settings.volman_limit_cancel_bars !== undefined ? parseInt(settings.volman_limit_cancel_bars) : 20,
          volman_ma_filter_enable: settings.volman_ma_filter_enable !== undefined ? (settings.volman_ma_filter_enable === true || settings.volman_ma_filter_enable === 'true') : false,
          volman_ma_period: settings.volman_ma_period !== undefined ? parseInt(settings.volman_ma_period) : 200,
          volman_ma_type: settings.volman_ma_type || 'EMA',
        };
      } else if (activeSection === 'orb_config') {
        payload = {
          orb_asia_enable: settings.orb_asia_enable !== undefined ? Boolean(settings.orb_asia_enable) : true,
          orb_london_enable: settings.orb_london_enable !== undefined ? Boolean(settings.orb_london_enable) : true,
          orb_ny_enable: settings.orb_ny_enable !== undefined ? Boolean(settings.orb_ny_enable) : false,
          orb_entry_mode: settings.orb_entry_mode || 'SMART_HYBRID',
          orb_rr: settings.orb_rr !== undefined ? parseFloat(settings.orb_rr) : 3.0,
          orb_sl_buffer_mode: settings.orb_sl_buffer_mode || 'FIXED',
          orb_sl_buffer_pips: settings.orb_sl_buffer_pips !== undefined ? parseFloat(settings.orb_sl_buffer_pips) : 2.0,
          orb_sl_atr_mult: settings.orb_sl_atr_mult !== undefined ? parseFloat(settings.orb_sl_atr_mult) : 1.0,
          orb_sl_buffer_min_pips: settings.orb_sl_buffer_min_pips !== undefined ? parseFloat(settings.orb_sl_buffer_min_pips) : 2.0,
          orb_sl_buffer_max_pips: settings.orb_sl_buffer_max_pips !== undefined ? parseFloat(settings.orb_sl_buffer_max_pips) : 15.0,
          orb_sl_filter_enable: settings.orb_sl_filter_enable !== undefined ? Boolean(settings.orb_sl_filter_enable) : false,
          orb_min_sl_pips: settings.orb_min_sl_pips !== undefined ? parseFloat(settings.orb_min_sl_pips) : 3.0,
          orb_max_sl_pips: settings.orb_max_sl_pips !== undefined ? parseFloat(settings.orb_max_sl_pips) : 150.0,
          orb_max_break_pips: settings.orb_max_break_pips !== undefined ? parseFloat(settings.orb_max_break_pips) : 15.0,
          orb_max_limit_bars: settings.orb_max_limit_bars !== undefined ? parseInt(settings.orb_max_limit_bars) : 6,
          orb_cancel_on_tp: settings.orb_cancel_on_tp !== undefined ? Boolean(settings.orb_cancel_on_tp) : true,
          orb_cancel_on_sl: settings.orb_cancel_on_sl !== undefined ? Boolean(settings.orb_cancel_on_sl) : true,
          orb_ema200_filter: settings.orb_ema200_filter !== undefined ? Boolean(settings.orb_ema200_filter) : false,
          orb_ema200_mode: settings.orb_ema200_mode || (settings.orb_ema200_filter ? 'STRICT' : 'OFF'),
          orb_max_zone_pips: settings.orb_max_zone_pips !== undefined ? parseFloat(settings.orb_max_zone_pips) : 250.0,
          orb_min_zone_pips: settings.orb_min_zone_pips !== undefined ? parseFloat(settings.orb_min_zone_pips) : 10.0,
          orb_dynamic_be_enable: settings.orb_dynamic_be_enable !== undefined ? Boolean(settings.orb_dynamic_be_enable) : false,
          orb_be_trigger_pct: settings.orb_be_trigger_pct !== undefined ? parseFloat(settings.orb_be_trigger_pct) : 75.0,
          orb_be_lock_pips: settings.orb_be_lock_pips !== undefined ? parseFloat(settings.orb_be_lock_pips) : 5.0,
          orb_timeout_exit_enable: settings.orb_timeout_exit_enable !== undefined ? Boolean(settings.orb_timeout_exit_enable) : true,
          orb_revert_enable: settings.orb_revert_enable !== undefined ? Boolean(settings.orb_revert_enable) : false,
          orb_revert_entry_mode: settings.orb_revert_entry_mode || 'LIMIT_50_BOX',
          orb_revert_rr: settings.orb_revert_rr !== undefined ? parseFloat(settings.orb_revert_rr) : 1.5,
          orb_revert_cancel_bars: settings.orb_revert_cancel_bars !== undefined ? parseInt(settings.orb_revert_cancel_bars) : 6,
          orb_revert_sl_buffer_pips: settings.orb_revert_sl_buffer_pips !== undefined ? parseFloat(settings.orb_revert_sl_buffer_pips) : 2.0,
          orb_dynamic_limit_enable: settings.orb_dynamic_limit_enable !== undefined ? Boolean(settings.orb_dynamic_limit_enable) : false,
          orb_limit_threshold_pips: settings.orb_limit_threshold_pips !== undefined && settings.orb_limit_threshold_pips !== '' ? parseFloat(settings.orb_limit_threshold_pips) : 100,
          orb_limit_pullback_pct: settings.orb_limit_pullback_pct !== undefined && settings.orb_limit_pullback_pct !== '' ? parseFloat(settings.orb_limit_pullback_pct) : 50,
          orb_limit_cancel_bars: settings.orb_limit_cancel_bars !== undefined && settings.orb_limit_cancel_bars !== '' ? parseInt(settings.orb_limit_cancel_bars) : 10,
        };
      } else if (activeSection === 'sweep_config') {
        payload = {
          sweep_m1_anchor_m5: settings.sweep_m1_anchor_m5 !== false,
          sweep_zone_limit_enable: settings.sweep_zone_limit_enable !== false,
          sweep_entry_mode: settings.sweep_entry_mode || 'RETEST',
          sweep_hybrid_threshold_pips: settings.sweep_hybrid_threshold_pips !== undefined ? parseFloat(settings.sweep_hybrid_threshold_pips) : 150.0,
          sweep_hybrid_limit_mode: settings.sweep_hybrid_limit_mode || 'ZONE_50',
          sweep_hybrid_zone_pct: settings.sweep_hybrid_zone_pct !== undefined ? parseFloat(settings.sweep_hybrid_zone_pct) : 50.0,
          sweep_hybrid_pullback_pct: settings.sweep_hybrid_pullback_pct !== undefined ? parseFloat(settings.sweep_hybrid_pullback_pct) : 50.0,
          sweep_lookback: settings.sweep_lookback !== undefined ? parseInt(settings.sweep_lookback) : 5,
          sweep_opt: 'Only Outbreaks & Retest',
          sweep_extend: (settings.sweep_extend === true || settings.sweep_extend === 'true'),
          sweep_max_bars: settings.sweep_max_bars !== undefined ? parseInt(settings.sweep_max_bars) : 300,
          sweep_rr: settings.sweep_rr !== undefined ? parseFloat(settings.sweep_rr) : 2.0,
          sweep_sl_buffer_pips: settings.sweep_sl_buffer_pips !== undefined ? parseFloat(settings.sweep_sl_buffer_pips) : 1.5,
          sweep_min_risk_pips: settings.sweep_min_risk_pips !== undefined ? parseFloat(settings.sweep_min_risk_pips) : 3.0,
          sweep_max_risk_pips: settings.sweep_max_risk_pips !== undefined ? parseFloat(settings.sweep_max_risk_pips) : 150.0,
          sweep_cooldown_bars: settings.sweep_cooldown_bars !== undefined ? parseInt(settings.sweep_cooldown_bars) : 3,
          sweep_min_swing_pips: settings.sweep_min_swing_pips !== undefined ? parseFloat(settings.sweep_min_swing_pips) : 18.0,
          sweep_compare_bars: settings.sweep_compare_bars !== undefined ? parseInt(settings.sweep_compare_bars) : 25,
          sweep_trend_filter_enable: (settings.sweep_trend_filter_enable === true || settings.sweep_trend_filter_enable === 'true'),
          sweep_trend_ema_period: settings.sweep_trend_ema_period !== undefined ? parseInt(settings.sweep_trend_ema_period) : 200,
          sweep_session_filter_enable: (settings.sweep_session_filter_enable === true || settings.sweep_session_filter_enable === 'true'),
          sweep_session_start: settings.sweep_session_start || '00:00',
          sweep_session_end: settings.sweep_session_end || '23:59',
          sweep_limit_cancel_bars: settings.sweep_limit_cancel_bars !== undefined ? parseInt(settings.sweep_limit_cancel_bars) : 30,
          sweep_highest_peak_filter: (settings.sweep_highest_peak_filter === true || settings.sweep_highest_peak_filter === 'true'),
          sweep_peak_lookback: settings.sweep_peak_lookback !== undefined ? parseInt(settings.sweep_peak_lookback) : 30,
          sweep_max_wait_bars: settings.sweep_max_wait_bars !== undefined ? parseInt(settings.sweep_max_wait_bars) : 5,
          sweep_close_on_opposite: (settings.sweep_close_on_opposite === true || settings.sweep_close_on_opposite === 'true'),
          sweep_dynamic_be_enable: (settings.sweep_dynamic_be_enable === true || settings.sweep_dynamic_be_enable === 'true'),
          sweep_be_trigger_mode: settings.sweep_be_trigger_mode || 'PCT_TP',
          sweep_be_trigger_pct: settings.sweep_be_trigger_pct !== undefined ? parseFloat(settings.sweep_be_trigger_pct) : 50.0,
          sweep_be_trigger_pips: settings.sweep_be_trigger_pips !== undefined ? parseFloat(settings.sweep_be_trigger_pips) : 40.0,
          sweep_be_lock_pips: settings.sweep_be_lock_pips !== undefined ? parseFloat(settings.sweep_be_lock_pips) : 2.0,
        };
      } else if (activeSection === 'smc_config') {
        payload = {
          default_mid_same_pct: settings.default_mid_same_pct !== undefined ? parseFloat(settings.default_mid_same_pct) : 50,
          default_entry_mode: settings.default_entry_mode || 'Limit 1',
          smc_fvg_filter_enable: settings.smc_fvg_filter_enable !== undefined ? Boolean(settings.smc_fvg_filter_enable) : false,
          smc_revert_enable: settings.smc_revert_enable !== undefined ? Boolean(settings.smc_revert_enable) : false,
          smc_revert_entry_mode: settings.smc_revert_entry_mode || 'MARKET',
          smc_revert_rr: settings.smc_revert_rr !== undefined ? parseFloat(settings.smc_revert_rr) : 1.5,
          smc_revert_cancel_bars: settings.smc_revert_cancel_bars !== undefined ? parseInt(settings.smc_revert_cancel_bars) : 20,
          smc_revert_sl_buffer_pips: settings.smc_revert_sl_buffer_pips !== undefined ? parseFloat(settings.smc_revert_sl_buffer_pips) : 10.0,
        };
      } else if (activeSection === 'ai') {
        payload = {
          gemini_api_key: settings.gemini_api_key,
          openai_api_key: settings.openai_api_key,
          default_ai_model: settings.default_ai_model,
          system_name: settings.system_name,
        };
      } else if (activeSection === 'telegram') {
        payload = {
          telegram_bot_token: settings.telegram_bot_token,
          telegram_chat_id: settings.telegram_chat_id,
          telegram_bot_username: settings.telegram_bot_username,
        };
      } else if (activeSection === 'email_oauth') {
        payload = {
          google_client_id: settings.google_client_id,
          smtp_host: settings.smtp_host,
          smtp_port: settings.smtp_port,
          smtp_user: settings.smtp_user,
          smtp_password: settings.smtp_password,
          smtp_sender_name: settings.smtp_sender_name,
          smtp_from_email: settings.smtp_from_email,
        };
      } else {
        payload = { ...settings };
      }

      await api.adminUpdateSettings(payload);

      // Immediately sync to localStorage for instantaneous client reflection
      try {
        let currentLocal = {};
        const saved = localStorage.getItem("minopro_strategy_params");
        if (saved) currentLocal = JSON.parse(saved);
        let updatedLocal = { ...currentLocal };

        if (activeSection === 'hma_config') {
          updatedLocal = {
            ...updatedLocal,
            period: settings.hma_period !== undefined ? parseInt(settings.hma_period) : (updatedLocal.period || 24),
            hmaPeriod: settings.hma_period !== undefined ? parseInt(settings.hma_period) : (updatedLocal.hmaPeriod || 24),
            hma_period: settings.hma_period !== undefined ? parseInt(settings.hma_period) : (updatedLocal.hma_period || 24),
            hmaSlopeFilter: settings.hma_slope_filter !== undefined ? Boolean(settings.hma_slope_filter) : (updatedLocal.hmaSlopeFilter ?? true),
            hma_slope_filter: settings.hma_slope_filter !== undefined ? Boolean(settings.hma_slope_filter) : (updatedLocal.hma_slope_filter ?? true),
            maxHmaSlope: settings.hma_max_slope !== undefined ? parseFloat(settings.hma_max_slope) : (updatedLocal.maxHmaSlope || 3.0),
            hmaMaxSlope: settings.hma_max_slope !== undefined ? parseFloat(settings.hma_max_slope) : (updatedLocal.hmaMaxSlope || 3.0),
            hma_max_slope: settings.hma_max_slope !== undefined ? parseFloat(settings.hma_max_slope) : (updatedLocal.hma_max_slope || 3.0),
            minBodyPct: settings.hma_min_body_pct !== undefined ? (parseFloat(settings.hma_min_body_pct) <= 1.0 ? parseFloat(settings.hma_min_body_pct) * 100 : parseFloat(settings.hma_min_body_pct)) : (updatedLocal.minBodyPct || 60),
            hmaMinBodyPct: settings.hma_min_body_pct !== undefined ? (parseFloat(settings.hma_min_body_pct) <= 1.0 ? parseFloat(settings.hma_min_body_pct) * 100 : parseFloat(settings.hma_min_body_pct)) : (updatedLocal.hmaMinBodyPct || 60),
            hma_min_body_pct: settings.hma_min_body_pct !== undefined ? (parseFloat(settings.hma_min_body_pct) <= 1.0 ? parseFloat(settings.hma_min_body_pct) * 100 : parseFloat(settings.hma_min_body_pct)) : (updatedLocal.hma_min_body_pct || 60),
            lookbackBars: settings.hma_lookback_bars !== undefined ? parseInt(settings.hma_lookback_bars) : (updatedLocal.lookbackBars || 100),
            hmaLookbackBars: settings.hma_lookback_bars !== undefined ? parseInt(settings.hma_lookback_bars) : (updatedLocal.hmaLookbackBars || 100),
            hma_lookback_bars: settings.hma_lookback_bars !== undefined ? parseInt(settings.hma_lookback_bars) : (updatedLocal.hma_lookback_bars || 100),
            maxBarsFromExtreme: settings.hma_max_bars_from_extreme !== undefined ? parseInt(settings.hma_max_bars_from_extreme) : (updatedLocal.maxBarsFromExtreme || 35),
            hmaMaxBarsFromExtreme: settings.hma_max_bars_from_extreme !== undefined ? parseInt(settings.hma_max_bars_from_extreme) : (updatedLocal.hmaMaxBarsFromExtreme || 35),
            hma_max_bars_from_extreme: settings.hma_max_bars_from_extreme !== undefined ? parseInt(settings.hma_max_bars_from_extreme) : (updatedLocal.hma_max_bars_from_extreme || 35),
            maxRiskPips: settings.hma_max_risk_pips !== undefined ? parseFloat(settings.hma_max_risk_pips) : (updatedLocal.maxRiskPips || 80),
            hmaMaxRiskPips: settings.hma_max_risk_pips !== undefined ? parseFloat(settings.hma_max_risk_pips) : (updatedLocal.hmaMaxRiskPips || 80),
            hma_max_risk_pips: settings.hma_max_risk_pips !== undefined ? parseFloat(settings.hma_max_risk_pips) : (updatedLocal.hma_max_risk_pips || 80),
            r1: settings.hma_r1 !== undefined ? parseFloat(settings.hma_r1) : (updatedLocal.r1 || 1.5),
            hmaR1: settings.hma_r1 !== undefined ? parseFloat(settings.hma_r1) : (updatedLocal.hmaR1 || 1.5),
            hma_r1: settings.hma_r1 !== undefined ? parseFloat(settings.hma_r1) : (updatedLocal.hma_r1 || 1.5),
            r2: settings.hma_r2 !== undefined ? parseFloat(settings.hma_r2) : (updatedLocal.r2 || 2.0),
            hmaR2: settings.hma_r2 !== undefined ? parseFloat(settings.hma_r2) : (updatedLocal.hmaR2 || 2.0),
            hma_r2: settings.hma_r2 !== undefined ? parseFloat(settings.hma_r2) : (updatedLocal.hma_r2 || 2.0),
            slBufferPips: settings.hma_sl_buffer_pips !== undefined ? parseFloat(settings.hma_sl_buffer_pips) : (updatedLocal.slBufferPips || 5.0),
            hmaSlBufferPips: settings.hma_sl_buffer_pips !== undefined ? parseFloat(settings.hma_sl_buffer_pips) : (updatedLocal.hmaSlBufferPips || 5.0),
            hma_sl_buffer_pips: settings.hma_sl_buffer_pips !== undefined ? parseFloat(settings.hma_sl_buffer_pips) : (updatedLocal.hma_sl_buffer_pips || 5.0),
            hmaCooldownBars: settings.hma_cooldown_bars !== undefined ? parseInt(settings.hma_cooldown_bars) : (updatedLocal.hmaCooldownBars || 5),
            hma_cooldown_bars: settings.hma_cooldown_bars !== undefined ? parseInt(settings.hma_cooldown_bars) : (updatedLocal.hma_cooldown_bars || 5),
            hmaShowLine: settings.hma_show_line !== undefined ? Boolean(settings.hma_show_line) : (updatedLocal.hmaShowLine ?? true),
            hma_show_line: settings.hma_show_line !== undefined ? Boolean(settings.hma_show_line) : (updatedLocal.hma_show_line ?? true),
            hmaShowBaseBox: settings.hma_show_base_box !== undefined ? Boolean(settings.hma_show_base_box) : (updatedLocal.hmaShowBaseBox ?? false),
            hma_show_base_box: settings.hma_show_base_box !== undefined ? Boolean(settings.hma_show_base_box) : (updatedLocal.hma_show_base_box ?? false),
            allowFloatingBreakout: settings.hma_allow_floating_breakout !== undefined ? Boolean(settings.hma_allow_floating_breakout) : (updatedLocal.allowFloatingBreakout ?? false),
            hmaAllowFloatingBreakout: settings.hma_allow_floating_breakout !== undefined ? Boolean(settings.hma_allow_floating_breakout) : (updatedLocal.hmaAllowFloatingBreakout ?? false),
            hma_allow_floating_breakout: settings.hma_allow_floating_breakout !== undefined ? Boolean(settings.hma_allow_floating_breakout) : (updatedLocal.hma_allow_floating_breakout ?? false),
            candleStrengthFilter: settings.hma_candle_strength_filter !== undefined ? Boolean(settings.hma_candle_strength_filter) : (updatedLocal.candleStrengthFilter ?? true),
            hmaCandleStrengthFilter: settings.hma_candle_strength_filter !== undefined ? Boolean(settings.hma_candle_strength_filter) : (updatedLocal.hmaCandleStrengthFilter ?? true),
            hma_candle_strength_filter: settings.hma_candle_strength_filter !== undefined ? Boolean(settings.hma_candle_strength_filter) : (updatedLocal.hma_candle_strength_filter ?? true),
            minCandleBodyRatio: settings.hma_min_candle_body_ratio !== undefined ? (parseFloat(settings.hma_min_candle_body_ratio) <= 1.0 ? parseFloat(settings.hma_min_candle_body_ratio) * 100 : parseFloat(settings.hma_min_candle_body_ratio)) : (updatedLocal.minCandleBodyRatio || 35),
            hmaMinCandleBodyRatio: settings.hma_min_candle_body_ratio !== undefined ? (parseFloat(settings.hma_min_candle_body_ratio) <= 1.0 ? parseFloat(settings.hma_min_candle_body_ratio) * 100 : parseFloat(settings.hma_min_candle_body_ratio)) : (updatedLocal.hmaMinCandleBodyRatio || 35),
            hma_min_candle_body_ratio: settings.hma_min_candle_body_ratio !== undefined ? (parseFloat(settings.hma_min_candle_body_ratio) <= 1.0 ? parseFloat(settings.hma_min_candle_body_ratio) * 100 : parseFloat(settings.hma_min_candle_body_ratio)) : (updatedLocal.hma_min_candle_body_ratio || 35),
            maxRejectionWickRatio: settings.hma_max_rejection_wick_ratio !== undefined ? (parseFloat(settings.hma_max_rejection_wick_ratio) <= 1.0 ? parseFloat(settings.hma_max_rejection_wick_ratio) * 100 : parseFloat(settings.hma_max_rejection_wick_ratio)) : (updatedLocal.maxRejectionWickRatio || 40),
            hmaMaxRejectionWickRatio: settings.hma_max_rejection_wick_ratio !== undefined ? (parseFloat(settings.hma_max_rejection_wick_ratio) <= 1.0 ? parseFloat(settings.hma_max_rejection_wick_ratio) * 100 : parseFloat(settings.hma_max_rejection_wick_ratio)) : (updatedLocal.hmaMaxRejectionWickRatio || 40),
            hma_max_rejection_wick_ratio: settings.hma_max_rejection_wick_ratio !== undefined ? (parseFloat(settings.hma_max_rejection_wick_ratio) <= 1.0 ? parseFloat(settings.hma_max_rejection_wick_ratio) * 100 : parseFloat(settings.hma_max_rejection_wick_ratio)) : (updatedLocal.hma_max_rejection_wick_ratio || 40),
            dynamicLimitEnable: settings.hma_dynamic_limit_enable !== undefined ? Boolean(settings.hma_dynamic_limit_enable) : (updatedLocal.dynamicLimitEnable ?? true),
            hmaDynamicLimitEnable: settings.hma_dynamic_limit_enable !== undefined ? Boolean(settings.hma_dynamic_limit_enable) : (updatedLocal.hmaDynamicLimitEnable ?? true),
            hma_dynamic_limit_enable: settings.hma_dynamic_limit_enable !== undefined ? Boolean(settings.hma_dynamic_limit_enable) : (updatedLocal.hma_dynamic_limit_enable ?? true),
            limitThresholdPips: settings.hma_limit_threshold_pips !== undefined ? parseFloat(settings.hma_limit_threshold_pips) : (updatedLocal.limitThresholdPips || 60),
            hmaLimitThresholdPips: settings.hma_limit_threshold_pips !== undefined ? parseFloat(settings.hma_limit_threshold_pips) : (updatedLocal.hmaLimitThresholdPips || 60),
            hma_limit_threshold_pips: settings.hma_limit_threshold_pips !== undefined ? parseFloat(settings.hma_limit_threshold_pips) : (updatedLocal.hma_limit_threshold_pips || 60),
            limitPullbackPct: settings.hma_limit_pullback_pct !== undefined ? parseFloat(settings.hma_limit_pullback_pct) : (updatedLocal.limitPullbackPct || 50),
            hmaLimitPullbackPct: settings.hma_limit_pullback_pct !== undefined ? parseFloat(settings.hma_limit_pullback_pct) : (updatedLocal.hmaLimitPullbackPct || 50),
            hma_limit_pullback_pct: settings.hma_limit_pullback_pct !== undefined ? parseFloat(settings.hma_limit_pullback_pct) : (updatedLocal.hma_limit_pullback_pct || 50),
            limitCancelBars: settings.hma_limit_cancel_bars !== undefined ? parseInt(settings.hma_limit_cancel_bars) : (updatedLocal.limitCancelBars || 20),
            hmaLimitCancelBars: settings.hma_limit_cancel_bars !== undefined ? parseInt(settings.hma_limit_cancel_bars) : (updatedLocal.hmaLimitCancelBars || 20),
            hma_limit_cancel_bars: settings.hma_limit_cancel_bars !== undefined ? parseInt(settings.hma_limit_cancel_bars) : (updatedLocal.hma_limit_cancel_bars || 20),
            dynamicBeEnable: settings.hma_dynamic_be_enable !== undefined ? Boolean(settings.hma_dynamic_be_enable) : (updatedLocal.dynamicBeEnable ?? false),
            hmaDynamicBeEnable: settings.hma_dynamic_be_enable !== undefined ? Boolean(settings.hma_dynamic_be_enable) : (updatedLocal.hmaDynamicBeEnable ?? false),
            hma_dynamic_be_enable: settings.hma_dynamic_be_enable !== undefined ? Boolean(settings.hma_dynamic_be_enable) : (updatedLocal.hma_dynamic_be_enable ?? false),
            beTriggerPct: settings.hma_be_trigger_pct !== undefined ? parseFloat(settings.hma_be_trigger_pct) : (updatedLocal.beTriggerPct || 75),
            hmaBeTriggerPct: settings.hma_be_trigger_pct !== undefined ? parseFloat(settings.hma_be_trigger_pct) : (updatedLocal.hmaBeTriggerPct || 75),
            hma_be_trigger_pct: settings.hma_be_trigger_pct !== undefined ? parseFloat(settings.hma_be_trigger_pct) : (updatedLocal.hma_be_trigger_pct || 75),
            beLockPips: settings.hma_be_lock_pips !== undefined ? parseFloat(settings.hma_be_lock_pips) : (updatedLocal.beLockPips || 5),
            hmaBeLockPips: settings.hma_be_lock_pips !== undefined ? parseFloat(settings.hma_be_lock_pips) : (updatedLocal.hmaBeLockPips || 5),
            hma_be_lock_pips: settings.hma_be_lock_pips !== undefined ? parseFloat(settings.hma_be_lock_pips) : (updatedLocal.hma_be_lock_pips || 5),
            nearTpCutEnable: settings.hma_near_tp_cut_enable !== undefined ? Boolean(settings.hma_near_tp_cut_enable) : (updatedLocal.nearTpCutEnable ?? false),
            hmaNearTpCutEnable: settings.hma_near_tp_cut_enable !== undefined ? Boolean(settings.hma_near_tp_cut_enable) : (updatedLocal.hmaNearTpCutEnable ?? false),
            hma_near_tp_cut_enable: settings.hma_near_tp_cut_enable !== undefined ? Boolean(settings.hma_near_tp_cut_enable) : (updatedLocal.hma_near_tp_cut_enable ?? false),
            nearTpThresholdPct: settings.hma_near_tp_threshold_pct !== undefined ? parseFloat(settings.hma_near_tp_threshold_pct) : (updatedLocal.nearTpThresholdPct || 85),
            hmaNearTpThresholdPct: settings.hma_near_tp_threshold_pct !== undefined ? parseFloat(settings.hma_near_tp_threshold_pct) : (updatedLocal.hmaNearTpThresholdPct || 85),
            hma_near_tp_threshold_pct: settings.hma_near_tp_threshold_pct !== undefined ? parseFloat(settings.hma_near_tp_threshold_pct) : (updatedLocal.hma_near_tp_threshold_pct || 85),
            closeOnOpposite: settings.hma_close_on_opposite !== undefined ? Boolean(settings.hma_close_on_opposite) : (updatedLocal.closeOnOpposite ?? true),
            hmaCloseOnOpposite: settings.hma_close_on_opposite !== undefined ? Boolean(settings.hma_close_on_opposite) : (updatedLocal.hmaCloseOnOpposite ?? true),
            hma_close_on_opposite: settings.hma_close_on_opposite !== undefined ? Boolean(settings.hma_close_on_opposite) : (updatedLocal.hma_close_on_opposite ?? true),
            cascadeFilter: settings.hma_cascade_filter !== undefined ? Boolean(settings.hma_cascade_filter) : (updatedLocal.cascadeFilter ?? false),
            hmaCascadeFilter: settings.hma_cascade_filter !== undefined ? Boolean(settings.hma_cascade_filter) : (updatedLocal.hmaCascadeFilter ?? false),
            hma_cascade_filter: settings.hma_cascade_filter !== undefined ? Boolean(settings.hma_cascade_filter) : (updatedLocal.hma_cascade_filter ?? false),
            bearRatioThresh: settings.hma_bear_ratio_thresh !== undefined ? parseFloat(settings.hma_bear_ratio_thresh) : (updatedLocal.bearRatioThresh || 70),
            hmaBearRatioThresh: settings.hma_bear_ratio_thresh !== undefined ? parseFloat(settings.hma_bear_ratio_thresh) : (updatedLocal.hmaBearRatioThresh || 70),
            hma_bear_ratio_thresh: settings.hma_bear_ratio_thresh !== undefined ? parseFloat(settings.hma_bear_ratio_thresh) : (updatedLocal.hma_bear_ratio_thresh || 70),
            sessionFilterEnable: settings.hma_session_filter_enable !== undefined ? Boolean(settings.hma_session_filter_enable) : (updatedLocal.sessionFilterEnable ?? false),
            hmaSessionFilterEnable: settings.hma_session_filter_enable !== undefined ? Boolean(settings.hma_session_filter_enable) : (updatedLocal.hmaSessionFilterEnable ?? false),
            hma_session_filter_enable: settings.hma_session_filter_enable !== undefined ? Boolean(settings.hma_session_filter_enable) : (updatedLocal.hma_session_filter_enable ?? false),
            sessionMode: settings.hma_session_mode || updatedLocal.sessionMode || 'ALL',
            hmaSessionMode: settings.hma_session_mode || updatedLocal.hmaSessionMode || 'ALL',
            hma_session_mode: settings.hma_session_mode || updatedLocal.hma_session_mode || 'ALL',
            sessionStart: settings.hma_session_start || updatedLocal.sessionStart || '06:00',
            hmaSessionStart: settings.hma_session_start || updatedLocal.hmaSessionStart || '06:00',
            hma_session_start: settings.hma_session_start || updatedLocal.hma_session_start || '06:00',
            sessionEnd: settings.hma_session_end || updatedLocal.sessionEnd || '19:30',
            hmaSessionEnd: settings.hma_session_end || updatedLocal.hmaSessionEnd || '19:30',
            hma_session_end: settings.hma_session_end || updatedLocal.hma_session_end || '19:30',
            hma_ema200_sw_filter_enable: settings.hma_ema200_sw_filter_enable !== undefined ? Boolean(settings.hma_ema200_sw_filter_enable) : (updatedLocal.hma_ema200_sw_filter_enable ?? false),
            ema200_sw_filter_enable: settings.hma_ema200_sw_filter_enable !== undefined ? Boolean(settings.hma_ema200_sw_filter_enable) : (updatedLocal.ema200_sw_filter_enable ?? false),
            hmaEma200SwFilterEnable: settings.hma_ema200_sw_filter_enable !== undefined ? Boolean(settings.hma_ema200_sw_filter_enable) : (updatedLocal.hmaEma200SwFilterEnable ?? false),
            hma_ema200_sw_max_dist_pips: settings.hma_ema200_sw_max_dist_pips !== undefined ? parseFloat(settings.hma_ema200_sw_max_dist_pips) : (updatedLocal.hma_ema200_sw_max_dist_pips || 300),
            ema200_sw_max_dist_pips: settings.hma_ema200_sw_max_dist_pips !== undefined ? parseFloat(settings.hma_ema200_sw_max_dist_pips) : (updatedLocal.ema200_sw_max_dist_pips || 300),
            hmaEma200SwMaxDistPips: settings.hma_ema200_sw_max_dist_pips !== undefined ? parseFloat(settings.hma_ema200_sw_max_dist_pips) : (updatedLocal.hmaEma200SwMaxDistPips || 300),
            hma_adaptive_period_enable: settings.hma_adaptive_period_enable !== undefined ? Boolean(settings.hma_adaptive_period_enable) : (updatedLocal.hma_adaptive_period_enable ?? false),
            adaptive_period_enable: settings.hma_adaptive_period_enable !== undefined ? Boolean(settings.hma_adaptive_period_enable) : (updatedLocal.adaptive_period_enable ?? false),
            hmaAdaptivePeriodEnable: settings.hma_adaptive_period_enable !== undefined ? Boolean(settings.hma_adaptive_period_enable) : (updatedLocal.hmaAdaptivePeriodEnable ?? false),
            adaptivePeriodEnable: settings.hma_adaptive_period_enable !== undefined ? Boolean(settings.hma_adaptive_period_enable) : (updatedLocal.adaptivePeriodEnable ?? false),
            hma_adaptive_min_period: settings.hma_adaptive_min_period !== undefined ? parseInt(settings.hma_adaptive_min_period) : (updatedLocal.hma_adaptive_min_period || 21),
            adaptive_min_period: settings.hma_adaptive_min_period !== undefined ? parseInt(settings.hma_adaptive_min_period) : (updatedLocal.adaptive_min_period || 21),
            hmaAdaptiveMinPeriod: settings.hma_adaptive_min_period !== undefined ? parseInt(settings.hma_adaptive_min_period) : (updatedLocal.hmaAdaptiveMinPeriod || 21),
            adaptiveMinPeriod: settings.hma_adaptive_min_period !== undefined ? parseInt(settings.hma_adaptive_min_period) : (updatedLocal.adaptiveMinPeriod || 21),
            hma_adaptive_max_period: settings.hma_adaptive_max_period !== undefined ? parseInt(settings.hma_adaptive_max_period) : (updatedLocal.hma_adaptive_max_period || 35),
            adaptive_max_period: settings.hma_adaptive_max_period !== undefined ? parseInt(settings.hma_adaptive_max_period) : (updatedLocal.adaptive_max_period || 35),
            hmaAdaptiveMaxPeriod: settings.hma_adaptive_max_period !== undefined ? parseInt(settings.hma_adaptive_max_period) : (updatedLocal.hmaAdaptiveMaxPeriod || 35),
            adaptiveMaxPeriod: settings.hma_adaptive_max_period !== undefined ? parseInt(settings.hma_adaptive_max_period) : (updatedLocal.adaptiveMaxPeriod || 35),
            slBufferMode: settings.hma_sl_buffer_mode || updatedLocal.slBufferMode || 'ATR',
            hmaSlBufferMode: settings.hma_sl_buffer_mode || updatedLocal.hmaSlBufferMode || 'ATR',
            hma_sl_buffer_mode: settings.hma_sl_buffer_mode || updatedLocal.hma_sl_buffer_mode || 'ATR',
            slBufferMinPips: settings.hma_sl_buffer_min_pips !== undefined ? parseFloat(settings.hma_sl_buffer_min_pips) : (updatedLocal.slBufferMinPips ?? 5.0),
            hmaSlBufferMinPips: settings.hma_sl_buffer_min_pips !== undefined ? parseFloat(settings.hma_sl_buffer_min_pips) : (updatedLocal.hmaSlBufferMinPips ?? 5.0),
            hma_sl_buffer_min_pips: settings.hma_sl_buffer_min_pips !== undefined ? parseFloat(settings.hma_sl_buffer_min_pips) : (updatedLocal.hma_sl_buffer_min_pips ?? 5.0),
            slBufferMaxPips: settings.hma_sl_buffer_max_pips !== undefined ? parseFloat(settings.hma_sl_buffer_max_pips) : (updatedLocal.slBufferMaxPips ?? 30.0),
            hmaSlBufferMaxPips: settings.hma_sl_buffer_max_pips !== undefined ? parseFloat(settings.hma_sl_buffer_max_pips) : (updatedLocal.hmaSlBufferMaxPips ?? 30.0),
            hma_sl_buffer_max_pips: settings.hma_sl_buffer_max_pips !== undefined ? parseFloat(settings.hma_sl_buffer_max_pips) : (updatedLocal.hma_sl_buffer_max_pips ?? 30.0),
            slAtrMult: settings.hma_sl_atr_mult !== undefined ? parseFloat(settings.hma_sl_atr_mult) : (updatedLocal.slAtrMult ?? 1.0),
            hmaSlAtrMult: settings.hma_sl_atr_mult !== undefined ? parseFloat(settings.hma_sl_atr_mult) : (updatedLocal.hmaSlAtrMult ?? 1.0),
            hma_sl_atr_mult: settings.hma_sl_atr_mult !== undefined ? parseFloat(settings.hma_sl_atr_mult) : (updatedLocal.hma_sl_atr_mult ?? 1.0),
          };
        } else if (activeSection === 'volman_config') {
          updatedLocal = {
            ...updatedLocal,
            volmanEmaPeriod: settings.volman_ema_period !== undefined ? parseInt(settings.volman_ema_period) : (updatedLocal.volmanEmaPeriod || 20),
            volmanPivotLength: settings.volman_pivot_length !== undefined ? parseInt(settings.volman_pivot_length) : (updatedLocal.volmanPivotLength || 3),
            volmanR1: settings.volman_r1 !== undefined ? parseFloat(settings.volman_r1) : (updatedLocal.volmanR1 || 1.5),
            volmanSlBufferPips: settings.volman_sl_buffer_pips !== undefined ? parseFloat(settings.volman_sl_buffer_pips) : (updatedLocal.volmanSlBufferPips ?? 5.0),
            volmanSlFilterEnable: settings.volman_sl_filter_enable !== undefined ? (settings.volman_sl_filter_enable === true || settings.volman_sl_filter_enable === 'true') : (updatedLocal.volmanSlFilterEnable ?? true),
            volmanMinRiskPips: settings.volman_min_risk_pips !== undefined ? parseFloat(settings.volman_min_risk_pips) : (updatedLocal.volmanMinRiskPips ?? 3.0),
            volmanMaxRiskPips: settings.volman_max_risk_pips !== undefined ? parseFloat(settings.volman_max_risk_pips) : (updatedLocal.volmanMaxRiskPips ?? 150.0),
            volmanCooldownBars: settings.volman_cooldown_bars !== undefined ? parseInt(settings.volman_cooldown_bars) : (updatedLocal.volmanCooldownBars || 5),
            volmanEnableBB: settings.volman_enable_bb !== undefined ? Boolean(settings.volman_enable_bb) : (updatedLocal.volmanEnableBB !== false),
            volmanEnableTB: settings.volman_enable_tb !== undefined ? Boolean(settings.volman_enable_tb) : (updatedLocal.volmanEnableTB !== false),
            volmanBreakoutCandleFilter: settings.volman_breakout_candle_filter !== undefined ? Boolean(settings.volman_breakout_candle_filter) : (updatedLocal.volmanBreakoutCandleFilter !== false),
            volmanMinBodyRatio: settings.volman_min_body_ratio !== undefined ? parseFloat(settings.volman_min_body_ratio) : (updatedLocal.volmanMinBodyRatio ?? 50),
            volmanMinBodyBeyondPct: settings.volman_min_body_beyond_pct !== undefined ? parseFloat(settings.volman_min_body_beyond_pct) : (updatedLocal.volmanMinBodyBeyondPct ?? 30),
            volmanMaxRejectionWick: settings.volman_max_rejection_wick !== undefined ? parseFloat(settings.volman_max_rejection_wick) : (updatedLocal.volmanMaxRejectionWick ?? 25),
            volmanShowTrendlines: settings.volman_show_trendlines !== undefined ? Boolean(settings.volman_show_trendlines) : (updatedLocal.volmanShowTrendlines ?? false),
            volmanShowBarriers: settings.volman_show_barriers !== undefined ? Boolean(settings.volman_show_barriers) : (updatedLocal.volmanShowBarriers ?? false),
            volmanShowBuildup: settings.volman_show_buildup !== undefined ? Boolean(settings.volman_show_buildup) : (updatedLocal.volmanShowBuildup ?? false),
            volmanShowEMA: settings.volman_show_ema !== undefined ? Boolean(settings.volman_show_ema) : (updatedLocal.volmanShowEMA ?? true),
          };
        } else if (activeSection === 'orb_config') {
          updatedLocal = {
            ...updatedLocal,
            orbAsiaEnable: settings.orb_asia_enable !== undefined ? Boolean(settings.orb_asia_enable) : (updatedLocal.orbAsiaEnable ?? true),
            orbLondonEnable: settings.orb_london_enable !== undefined ? Boolean(settings.orb_london_enable) : (updatedLocal.orbLondonEnable ?? true),
            orbNyEnable: settings.orb_ny_enable !== undefined ? Boolean(settings.orb_ny_enable) : (updatedLocal.orbNyEnable ?? false),
            orbEntryMode: settings.orb_entry_mode !== undefined ? settings.orb_entry_mode : (updatedLocal.orbEntryMode || 'SMART_HYBRID'),
            orbRr: settings.orb_rr !== undefined ? parseFloat(settings.orb_rr) : (updatedLocal.orbRr || 3.0),
            orbSlBufferMode: settings.orb_sl_buffer_mode !== undefined ? settings.orb_sl_buffer_mode : (updatedLocal.orbSlBufferMode || 'FIXED'),
            orbSlBufferPips: settings.orb_sl_buffer_pips !== undefined ? parseFloat(settings.orb_sl_buffer_pips) : (updatedLocal.orbSlBufferPips ?? 2.0),
            orbSlAtrMult: settings.orb_sl_atr_mult !== undefined ? parseFloat(settings.orb_sl_atr_mult) : (updatedLocal.orbSlAtrMult ?? 1.0),
            orbSlBufferMinPips: settings.orb_sl_buffer_min_pips !== undefined ? parseFloat(settings.orb_sl_buffer_min_pips) : (updatedLocal.orbSlBufferMinPips ?? 2.0),
            orbSlBufferMaxPips: settings.orb_sl_buffer_max_pips !== undefined ? parseFloat(settings.orb_sl_buffer_max_pips) : (updatedLocal.orbSlBufferMaxPips ?? 15.0),
            orbSlFilterEnable: settings.orb_sl_filter_enable !== undefined ? Boolean(settings.orb_sl_filter_enable) : (updatedLocal.orbSlFilterEnable ?? false),
            orbMinSlPips: settings.orb_min_sl_pips !== undefined ? parseFloat(settings.orb_min_sl_pips) : (updatedLocal.orbMinSlPips ?? 3.0),
            orbMaxSlPips: settings.orb_max_sl_pips !== undefined ? parseFloat(settings.orb_max_sl_pips) : (updatedLocal.orbMaxSlPips ?? 150.0),
            orbMaxBreakPips: settings.orb_max_break_pips !== undefined ? parseFloat(settings.orb_max_break_pips) : (updatedLocal.orbMaxBreakPips ?? 15.0),
            orbMaxLimitBars: settings.orb_max_limit_bars !== undefined ? parseInt(settings.orb_max_limit_bars) : (updatedLocal.orbMaxLimitBars ?? 6),
            orbCancelOnTp: settings.orb_cancel_on_tp !== undefined ? Boolean(settings.orb_cancel_on_tp) : (updatedLocal.orbCancelOnTp ?? true),
            orbCancelOnSl: settings.orb_cancel_on_sl !== undefined ? Boolean(settings.orb_cancel_on_sl) : (updatedLocal.orbCancelOnSl ?? true),
            orbEma200Filter: settings.orb_ema200_filter !== undefined ? Boolean(settings.orb_ema200_filter) : (updatedLocal.orbEma200Filter ?? false),
            orbEma200Mode: settings.orb_ema200_mode || (settings.orb_ema200_filter ? 'STRICT' : 'OFF'),
            orbMaxZonePips: settings.orb_max_zone_pips !== undefined ? parseFloat(settings.orb_max_zone_pips) : (updatedLocal.orbMaxZonePips ?? 250.0),
            orbMinZonePips: settings.orb_min_zone_pips !== undefined ? parseFloat(settings.orb_min_zone_pips) : (updatedLocal.orbMinZonePips ?? 10.0),
            orbDynamicBeEnable: settings.orb_dynamic_be_enable !== undefined ? Boolean(settings.orb_dynamic_be_enable) : (updatedLocal.orbDynamicBeEnable ?? false),
            orbBeTriggerPct: settings.orb_be_trigger_pct !== undefined ? parseFloat(settings.orb_be_trigger_pct) : (updatedLocal.orbBeTriggerPct ?? 75.0),
            orbBeLockPips: settings.orb_be_lock_pips !== undefined ? parseFloat(settings.orb_be_lock_pips) : (updatedLocal.orbBeLockPips ?? 5.0),
            orbTimeoutExitEnable: settings.orb_timeout_exit_enable !== undefined ? Boolean(settings.orb_timeout_exit_enable) : (updatedLocal.orbTimeoutExitEnable ?? true),
            orbRevertEnable: settings.orb_revert_enable !== undefined ? Boolean(settings.orb_revert_enable) : (updatedLocal.orbRevertEnable ?? false),
            orbRevertEntryMode: settings.orb_revert_entry_mode !== undefined ? settings.orb_revert_entry_mode : (updatedLocal.orbRevertEntryMode || 'LIMIT_50_BOX'),
            orbRevertRr: settings.orb_revert_rr !== undefined ? parseFloat(settings.orb_revert_rr) : (updatedLocal.orbRevertRr ?? 1.5),
            orbRevertCancelBars: settings.orb_revert_cancel_bars !== undefined ? parseInt(settings.orb_revert_cancel_bars) : (updatedLocal.orbRevertCancelBars ?? 6),
            orbRevertSlBufferPips: settings.orb_revert_sl_buffer_pips !== undefined ? parseFloat(settings.orb_revert_sl_buffer_pips) : (updatedLocal.orbRevertSlBufferPips ?? 2.0),
          };
        } else if (activeSection === 'smc_config') {
          updatedLocal = {
            ...updatedLocal,
            default_mid_same_pct: settings.default_mid_same_pct !== undefined ? parseFloat(settings.default_mid_same_pct) : (updatedLocal.default_mid_same_pct || 50),
            defaultMidSamePct: settings.default_mid_same_pct !== undefined ? parseFloat(settings.default_mid_same_pct) : (updatedLocal.defaultMidSamePct || 50),
            default_entry_mode: settings.default_entry_mode || updatedLocal.default_entry_mode || 'Limit 1',
            defaultEntryMode: settings.default_entry_mode || updatedLocal.defaultEntryMode || 'Limit 1',
            smc_fvg_filter_enable: settings.smc_fvg_filter_enable !== undefined ? Boolean(settings.smc_fvg_filter_enable) : (updatedLocal.smc_fvg_filter_enable ?? false),
            smcFvgFilterEnable: settings.smc_fvg_filter_enable !== undefined ? Boolean(settings.smc_fvg_filter_enable) : (updatedLocal.smcFvgFilterEnable ?? false),
            fvgFilterEnable: settings.smc_fvg_filter_enable !== undefined ? Boolean(settings.smc_fvg_filter_enable) : (updatedLocal.fvgFilterEnable ?? false),
            smc_revert_enable: settings.smc_revert_enable !== undefined ? Boolean(settings.smc_revert_enable) : (updatedLocal.smc_revert_enable ?? false),
            smcRevertEnable: settings.smc_revert_enable !== undefined ? Boolean(settings.smc_revert_enable) : (updatedLocal.smcRevertEnable ?? false),
            smc_revert_entry_mode: settings.smc_revert_entry_mode || updatedLocal.smc_revert_entry_mode || 'MARKET',
            smcRevertEntryMode: settings.smc_revert_entry_mode || updatedLocal.smcRevertEntryMode || 'MARKET',
            smc_revert_rr: settings.smc_revert_rr !== undefined ? parseFloat(settings.smc_revert_rr) : (updatedLocal.smc_revert_rr ?? 1.5),
            smcRevertRr: settings.smc_revert_rr !== undefined ? parseFloat(settings.smc_revert_rr) : (updatedLocal.smcRevertRr ?? 1.5),
            smc_revert_cancel_bars: settings.smc_revert_cancel_bars !== undefined ? parseInt(settings.smc_revert_cancel_bars) : (updatedLocal.smc_revert_cancel_bars ?? 20),
            smcRevertCancelBars: settings.smc_revert_cancel_bars !== undefined ? parseInt(settings.smc_revert_cancel_bars) : (updatedLocal.smcRevertCancelBars ?? 20),
            smc_revert_sl_buffer_pips: settings.smc_revert_sl_buffer_pips !== undefined ? parseFloat(settings.smc_revert_sl_buffer_pips) : (updatedLocal.smc_revert_sl_buffer_pips ?? 10.0),
            smcRevertSlBufferPips: settings.smc_revert_sl_buffer_pips !== undefined ? parseFloat(settings.smc_revert_sl_buffer_pips) : (updatedLocal.smcRevertSlBufferPips ?? 10.0),
          };
        } else if (activeSection === 'sweep_config') {
          updatedLocal = {
            ...updatedLocal,
            sweepM1AnchorM5: settings.sweep_m1_anchor_m5 !== false,
            sweep_m1_anchor_m5: settings.sweep_m1_anchor_m5 !== false,
            sweepLookback: settings.sweep_lookback !== undefined ? parseInt(settings.sweep_lookback) : (updatedLocal.sweepLookback ?? 5),
            sweep_lookback: settings.sweep_lookback !== undefined ? parseInt(settings.sweep_lookback) : (updatedLocal.sweep_lookback ?? 5),
            len: settings.sweep_lookback !== undefined ? parseInt(settings.sweep_lookback) : (updatedLocal.len ?? 5),
            sweepOpt: 'Only Outbreaks & Retest',
            sweep_opt: 'Only Outbreaks & Retest',
            opt: 'Only Outbreaks & Retest',
            sweepExtend: settings.sweep_extend !== undefined ? Boolean(settings.sweep_extend) : (updatedLocal.sweepExtend ?? true),
            sweep_extend: settings.sweep_extend !== undefined ? Boolean(settings.sweep_extend) : (updatedLocal.sweep_extend ?? true),
            sweepMaxBars: settings.sweep_max_bars !== undefined ? parseInt(settings.sweep_max_bars) : (updatedLocal.sweepMaxBars ?? 300),
            sweep_max_bars: settings.sweep_max_bars !== undefined ? parseInt(settings.sweep_max_bars) : (updatedLocal.sweep_max_bars ?? 300),
            sweepEntryMode: settings.sweep_entry_mode || updatedLocal.sweepEntryMode || 'RETEST',
            sweep_entry_mode: settings.sweep_entry_mode || updatedLocal.sweep_entry_mode || 'RETEST',
            sweepRr: settings.sweep_rr !== undefined ? parseFloat(settings.sweep_rr) : (updatedLocal.sweepRr ?? 2.0),
            sweep_rr: settings.sweep_rr !== undefined ? parseFloat(settings.sweep_rr) : (updatedLocal.sweep_rr ?? 2.0),
            sweepSlBufferPips: settings.sweep_sl_buffer_pips !== undefined ? parseFloat(settings.sweep_sl_buffer_pips) : (updatedLocal.sweepSlBufferPips ?? 1.5),
            sweep_sl_buffer_pips: settings.sweep_sl_buffer_pips !== undefined ? parseFloat(settings.sweep_sl_buffer_pips) : (updatedLocal.sweep_sl_buffer_pips ?? 1.5),
            sweepMinRiskPips: settings.sweep_min_risk_pips !== undefined ? parseFloat(settings.sweep_min_risk_pips) : (updatedLocal.sweepMinRiskPips ?? 3.0),
            sweep_min_risk_pips: settings.sweep_min_risk_pips !== undefined ? parseFloat(settings.sweep_min_risk_pips) : (updatedLocal.sweep_min_risk_pips ?? 3.0),
            sweepMaxRiskPips: settings.sweep_max_risk_pips !== undefined ? parseFloat(settings.sweep_max_risk_pips) : (updatedLocal.sweepMaxRiskPips ?? 150.0),
            sweep_max_risk_pips: settings.sweep_max_risk_pips !== undefined ? parseFloat(settings.sweep_max_risk_pips) : (updatedLocal.sweep_max_risk_pips ?? 150.0),
            sweepCooldownBars: settings.sweep_cooldown_bars !== undefined ? parseInt(settings.sweep_cooldown_bars) : (updatedLocal.sweepCooldownBars ?? 3),
            sweep_cooldown_bars: settings.sweep_cooldown_bars !== undefined ? parseInt(settings.sweep_cooldown_bars) : (updatedLocal.sweep_cooldown_bars ?? 3),
            sweepMinSwingPips: settings.sweep_min_swing_pips !== undefined ? parseFloat(settings.sweep_min_swing_pips) : (updatedLocal.sweepMinSwingPips ?? 18.0),
            sweep_min_swing_pips: settings.sweep_min_swing_pips !== undefined ? parseFloat(settings.sweep_min_swing_pips) : (updatedLocal.sweep_min_swing_pips ?? 18.0),
            sweepCompareBars: settings.sweep_compare_bars !== undefined ? parseInt(settings.sweep_compare_bars) : (updatedLocal.sweepCompareBars ?? 25),
            sweep_compare_bars: settings.sweep_compare_bars !== undefined ? parseInt(settings.sweep_compare_bars) : (updatedLocal.sweep_compare_bars ?? 25),
            sweepLimitCancelBars: settings.sweep_limit_cancel_bars !== undefined ? parseInt(settings.sweep_limit_cancel_bars) : (updatedLocal.sweepLimitCancelBars ?? 30),
            sweep_limit_cancel_bars: settings.sweep_limit_cancel_bars !== undefined ? parseInt(settings.sweep_limit_cancel_bars) : (updatedLocal.sweep_limit_cancel_bars ?? 30),
            sweepZoneLimitEnable: settings.sweep_zone_limit_enable !== undefined ? (settings.sweep_zone_limit_enable === true || settings.sweep_zone_limit_enable === 'true') : (updatedLocal.sweepZoneLimitEnable ?? true),
            sweep_zone_limit_enable: settings.sweep_zone_limit_enable !== undefined ? (settings.sweep_zone_limit_enable === true || settings.sweep_zone_limit_enable === 'true') : (updatedLocal.sweep_zone_limit_enable ?? true),
            sweepHybridThresholdPips: settings.sweep_hybrid_threshold_pips !== undefined ? parseFloat(settings.sweep_hybrid_threshold_pips) : (updatedLocal.sweepHybridThresholdPips ?? 80.0),
            sweep_hybrid_threshold_pips: settings.sweep_hybrid_threshold_pips !== undefined ? parseFloat(settings.sweep_hybrid_threshold_pips) : (updatedLocal.sweep_hybrid_threshold_pips ?? 150.0),
            sweepHybridLimitMode: settings.sweep_hybrid_limit_mode || updatedLocal.sweepHybridLimitMode || 'ZONE_50',
            sweep_hybrid_limit_mode: settings.sweep_hybrid_limit_mode || updatedLocal.sweep_hybrid_limit_mode || 'ZONE_50',
            sweepHybridZonePct: settings.sweep_hybrid_zone_pct !== undefined ? parseFloat(settings.sweep_hybrid_zone_pct) : (updatedLocal.sweepHybridZonePct ?? 50.0),
            sweep_hybrid_zone_pct: settings.sweep_hybrid_zone_pct !== undefined ? parseFloat(settings.sweep_hybrid_zone_pct) : (updatedLocal.sweep_hybrid_zone_pct ?? 50.0),
            sweepHybridPullbackPct: settings.sweep_hybrid_pullback_pct !== undefined ? parseFloat(settings.sweep_hybrid_pullback_pct) : (updatedLocal.sweepHybridPullbackPct ?? 50.0),
            sweep_hybrid_pullback_pct: settings.sweep_hybrid_pullback_pct !== undefined ? parseFloat(settings.sweep_hybrid_pullback_pct) : (updatedLocal.sweep_hybrid_pullback_pct ?? 50.0),
            sweepHighestPeakFilter: settings.sweep_highest_peak_filter !== undefined ? Boolean(settings.sweep_highest_peak_filter) : (updatedLocal.sweepHighestPeakFilter ?? false),
            sweep_highest_peak_filter: settings.sweep_highest_peak_filter !== undefined ? Boolean(settings.sweep_highest_peak_filter) : (updatedLocal.sweep_highest_peak_filter ?? false),
            sweepPeakLookback: settings.sweep_peak_lookback !== undefined ? parseInt(settings.sweep_peak_lookback) : (updatedLocal.sweepPeakLookback ?? 30),
            sweep_peak_lookback: settings.sweep_peak_lookback !== undefined ? parseInt(settings.sweep_peak_lookback) : (updatedLocal.sweep_peak_lookback ?? 30),
            sweepMaxWaitBars: settings.sweep_max_wait_bars !== undefined ? parseInt(settings.sweep_max_wait_bars) : (updatedLocal.sweepMaxWaitBars ?? 5),
            sweep_max_wait_bars: settings.sweep_max_wait_bars !== undefined ? parseInt(settings.sweep_max_wait_bars) : (updatedLocal.sweep_max_wait_bars ?? 5),
            sweepTrendFilterEnable: settings.sweep_trend_filter_enable !== undefined ? Boolean(settings.sweep_trend_filter_enable) : (updatedLocal.sweepTrendFilterEnable ?? false),
            sweep_trend_filter_enable: settings.sweep_trend_filter_enable !== undefined ? Boolean(settings.sweep_trend_filter_enable) : (updatedLocal.sweep_trend_filter_enable ?? false),
            sweepTrendEmaPeriod: settings.sweep_trend_ema_period !== undefined ? parseInt(settings.sweep_trend_ema_period) : (updatedLocal.sweepTrendEmaPeriod ?? 200),
            sweep_trend_ema_period: settings.sweep_trend_ema_period !== undefined ? parseInt(settings.sweep_trend_ema_period) : (updatedLocal.sweep_trend_ema_period ?? 200),
            sweepSessionFilterEnable: settings.sweep_session_filter_enable !== undefined ? Boolean(settings.sweep_session_filter_enable) : (updatedLocal.sweepSessionFilterEnable ?? false),
            sweep_session_filter_enable: settings.sweep_session_filter_enable !== undefined ? Boolean(settings.sweep_session_filter_enable) : (updatedLocal.sweep_session_filter_enable ?? false),
            sweepSessionStart: settings.sweep_session_start || updatedLocal.sweepSessionStart || '00:00',
            sweep_session_start: settings.sweep_session_start || updatedLocal.sweep_session_start || '00:00',
            sweepSessionEnd: settings.sweep_session_end || updatedLocal.sweepSessionEnd || '23:59',
            sweep_session_end: settings.sweep_session_end || updatedLocal.sweep_session_end || '23:59',
            sweepCloseOnOpposite: settings.sweep_close_on_opposite !== undefined ? Boolean(settings.sweep_close_on_opposite) : (updatedLocal.sweepCloseOnOpposite ?? false),
            sweep_close_on_opposite: settings.sweep_close_on_opposite !== undefined ? Boolean(settings.sweep_close_on_opposite) : (updatedLocal.sweep_close_on_opposite ?? false),
            sweepDynamicBeEnable: settings.sweep_dynamic_be_enable !== undefined ? Boolean(settings.sweep_dynamic_be_enable) : (updatedLocal.sweepDynamicBeEnable ?? false),
            sweep_dynamic_be_enable: settings.sweep_dynamic_be_enable !== undefined ? Boolean(settings.sweep_dynamic_be_enable) : (updatedLocal.sweep_dynamic_be_enable ?? false),
            sweepBeTriggerMode: settings.sweep_be_trigger_mode || updatedLocal.sweepBeTriggerMode || 'PCT_TP',
            sweep_be_trigger_mode: settings.sweep_be_trigger_mode || updatedLocal.sweep_be_trigger_mode || 'PCT_TP',
            sweepBeTriggerPct: settings.sweep_be_trigger_pct !== undefined ? parseFloat(settings.sweep_be_trigger_pct) : (updatedLocal.sweepBeTriggerPct ?? 50.0),
            sweep_be_trigger_pct: settings.sweep_be_trigger_pct !== undefined ? parseFloat(settings.sweep_be_trigger_pct) : (updatedLocal.sweep_be_trigger_pct ?? 50.0),
            sweepBeTriggerPips: settings.sweep_be_trigger_pips !== undefined ? parseFloat(settings.sweep_be_trigger_pips) : (updatedLocal.sweepBeTriggerPips ?? 40.0),
            sweep_be_trigger_pips: settings.sweep_be_trigger_pips !== undefined ? parseFloat(settings.sweep_be_trigger_pips) : (updatedLocal.sweep_be_trigger_pips ?? 40.0),
            sweepBeLockPips: settings.sweep_be_lock_pips !== undefined ? parseFloat(settings.sweep_be_lock_pips) : (updatedLocal.sweepBeLockPips ?? 2.0),
            sweep_be_lock_pips: settings.sweep_be_lock_pips !== undefined ? parseFloat(settings.sweep_be_lock_pips) : (updatedLocal.sweep_be_lock_pips ?? 2.0),
          };
        }

        localStorage.setItem("minopro_strategy_params", JSON.stringify(updatedLocal));
        try {
          const oldCfg = JSON.parse(localStorage.getItem("minopro_public_config") || "{}");
          localStorage.setItem("minopro_public_config", JSON.stringify({ ...oldCfg, ...payload }));
        } catch (e) {}
      } catch (err) {}

      // Dispatch event to notify App.jsx to refresh config and reload chart
      window.dispatchEvent(new CustomEvent('minopro_admin_settings_updated', { detail: { section: activeSection, settings } }));

      setSettingsMsg({ type: 'success', text: 'Đã lưu cấu hình hệ thống thành công!' });
      setTimeout(() => setSettingsMsg({ type: '', text: '' }), 4000);
    } catch (err) {
      setSettingsMsg({ type: 'error', text: err.response?.data?.detail || 'Lỗi lưu cấu hình.' });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleTestTelegram = async () => {
    setTestingTg(true);
    setTgMsg({ type: '', text: '' });
    try {
      await api.adminUpdateSettings({
        telegram_bot_token: settings.telegram_bot_token,
        telegram_chat_id: settings.telegram_chat_id,
      });
      const res = await api.testTelegram();
      if (res.success) {
        setTgMsg({ type: 'success', text: 'Gửi tin nhắn thử nghiệm Telegram thành công!' });
      } else {
        setTgMsg({ type: 'error', text: res.message || 'Gửi thử thất bại.' });
      }
    } catch (err) {
      setTgMsg({ type: 'error', text: err.message || 'Lỗi kết nối Telegram.' });
    } finally {
      setTestingTg(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    setEmailTestMsg({ type: '', text: '' });
    try {
      await api.adminUpdateSettings({
        smtp_host: settings.smtp_host,
        smtp_port: settings.smtp_port,
        smtp_user: settings.smtp_user,
        smtp_password: settings.smtp_password,
        smtp_sender_name: settings.smtp_sender_name
      });
      const target = testEmailTo.trim() || settings.smtp_user || user?.email;
      if (!target) {
        setEmailTestMsg({ type: 'error', text: 'Vui lòng nhập địa chỉ email nhận thử nghiệm.' });
        return;
      }
      const res = await api.adminTestEmail(target);
      if (res.success) {
        setEmailTestMsg({ type: 'success', text: res.message || 'Gửi email thử nghiệm thành công!' });
      } else {
        setEmailTestMsg({ type: 'error', text: res.message || 'Gửi thử email thất bại.' });
      }
    } catch (err) {
      setEmailTestMsg({ type: 'error', text: err.response?.data?.detail || err.message || 'Lỗi kiểm tra gửi email.' });
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestAIGeneration = async () => {
    setTestingAI(true);
    setAiTestResult(null);
    try {
      await api.adminUpdateSettings({
        gemini_api_key: settings.gemini_api_key,
        default_ai_model: settings.default_ai_model,
      });
      const res = await api.aiAnalyze({
        symbol: 'XAUUSD',
        timeframe: 'M5',
        price: 4410.5,
        rsi: 48.0,
        consensus: 'ĐỒNG THUẬN GIẢM (5/7)',
        bear_count: 5,
        bull_count: 1
      });
      if (res.success) {
        setAiTestResult(res);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setTestingAI(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwdMsg({ type: '', text: '' });

    if (!oldPassword || !newPassword) {
      setPwdMsg({ type: 'error', text: 'Vui lòng nhập đầy đủ mật khẩu cũ và mới.' });
      return;
    }
    if (newPassword.length < 6) {
      setPwdMsg({ type: 'error', text: 'Mật khẩu mới phải có ít nhất 6 ký tự.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdMsg({ type: 'error', text: 'Mật khẩu xác nhận không khớp.' });
      return;
    }

    setPwdLoading(true);
    try {
      await changePassword(oldPassword, newPassword);
      setPwdMsg({ type: 'success', text: 'Đổi mật khẩu thành công!' });
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPwdMsg({ type: '', text: '' }), 4000);
    } catch (err) {
      setPwdMsg({ type: 'error', text: err.response?.data?.detail || err.message || 'Lỗi đổi mật khẩu.' });
    } finally {
      setPwdLoading(false);
    }
  };

  // Metrics
  const totalUsers = users.length;
  const basicCount = users.filter(u => u.tier === 'BASIC').length;
  const proCount = users.filter(u => u.tier === 'PRO').length;
  const adminCount = users.filter(u => u.tier === 'ADMIN').length;

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.username.toLowerCase().includes(userSearch.toLowerCase()) || 
                          u.email.toLowerCase().includes(userSearch.toLowerCase());
    const matchesTier = tierFilter === 'ALL' || u.tier === tierFilter;
    return matchesSearch && matchesTier;
  });

  // EA Filtered & Aggregated Metrics
  const activeAccObj = selectedEaAccount !== 'ALL' ? eaAccounts.find(a => a.id === selectedEaAccount) : null;
  const filteredPositions = eaPositions.filter(p => selectedEaAccount === 'ALL' || p.account_id === selectedEaAccount);
  const filteredOrders = eaOrders.filter(o => selectedEaAccount === 'ALL' || o.account_id === selectedEaAccount);
  const filteredStrategies = eaStrategies.filter(s => selectedEaAccount === 'ALL' || s.account_id === selectedEaAccount);
  const filteredTransactions = eaTransactions.filter(t => (ledgerFilterAcc === 'ALL' || t.account_id === ledgerFilterAcc) && (selectedEaAccount === 'ALL' || t.account_id === selectedEaAccount));

  // Helper to compute start & end timestamps for date filtering
  const getHistoryDateRange = () => {
    const now = new Date();
    if (historyFilterPeriod === 'ALL') {
      return { startTs: 0, endTs: Infinity, label: 'Toàn bộ thời gian' };
    }
    if (historyFilterPeriod === 'TODAY') {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0).getTime() / 1000;
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime() / 1000;
      return { startTs: start, endTs: end, label: `Hôm nay (${now.toLocaleDateString('vi-VN')})` };
    }
    if (historyFilterPeriod === 'YESTERDAY') {
      const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      const start = new Date(y.getFullYear(), y.getMonth(), y.getDate(), 0, 0, 0).getTime() / 1000;
      const end = new Date(y.getFullYear(), y.getMonth(), y.getDate(), 23, 59, 59, 999).getTime() / 1000;
      return { startTs: start, endTs: end, label: `Hôm qua (${y.toLocaleDateString('vi-VN')})` };
    }
    if (historyFilterPeriod === '7D') {
      const start = Math.floor((now.getTime() - 7 * 86400 * 1000) / 1000);
      const end = Math.floor(now.getTime() / 1000) + 86400;
      return { startTs: start, endTs: end, label: '7 ngày qua' };
    }
    if (historyFilterPeriod === '30D') {
      const start = Math.floor((now.getTime() - 30 * 86400 * 1000) / 1000);
      const end = Math.floor(now.getTime() / 1000) + 86400;
      return { startTs: start, endTs: end, label: '30 ngày qua' };
    }
    if (historyFilterPeriod === 'THIS_MONTH') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0).getTime() / 1000;
      const end = Math.floor(now.getTime() / 1000) + 86400;
      return { startTs: start, endTs: end, label: `Tháng ${now.getMonth() + 1}/${now.getFullYear()}` };
    }
    if (historyFilterPeriod === 'CUSTOM') {
      let startTs = 0;
      let endTs = Infinity;
      let label = 'Tùy chỉnh khoảng ngày';
      if (historyStartDate && historyEndDate) {
        startTs = new Date(historyStartDate + 'T00:00:00').getTime() / 1000;
        endTs = new Date(historyEndDate + 'T23:59:59').getTime() / 1000;
        label = `Từ ${historyStartDate} đến ${historyEndDate}`;
      } else if (historyStartDate) {
        startTs = new Date(historyStartDate + 'T00:00:00').getTime() / 1000;
        label = `Từ ${historyStartDate} đến nay`;
      } else if (historyEndDate) {
        endTs = new Date(historyEndDate + 'T23:59:59').getTime() / 1000;
        label = `Trước ngày ${historyEndDate}`;
      }
      return { startTs, endTs, label };
    }
    return { startTs: 0, endTs: Infinity, label: 'Toàn bộ thời gian' };
  };

  const { startTs: histStartTs, endTs: histEndTs, label: histPeriodLabel } = getHistoryDateRange();

  // Filtered orders taking into account: Account, Symbol, Status, and Date/Time range
  const filteredHistoryOrders = eaOrders.filter(o => {
    const matchesAccount = selectedEaAccount === 'ALL' || o.account_id === selectedEaAccount;
    const matchesSymbol = historyFilterSym === 'ALL' || o.symbol === historyFilterSym;
    const matchesStatus = historyFilterStatus === 'ALL' || o.status === historyFilterStatus;
    
    // Check order time (prefer close_time, then open_time, then created_at)
    const orderTime = o.close_time || o.open_time || o.created_at || 0;
    const matchesDate = (orderTime === 0) || (orderTime >= histStartTs && orderTime <= histEndTs);

    return matchesAccount && matchesSymbol && matchesStatus && matchesDate;
  }).sort((a, b) => {
    const timeA = a.close_time || a.open_time || a.created_at || 0;
    const timeB = b.close_time || b.open_time || b.created_at || 0;
    return timeB - timeA;
  });

  // History financial metrics calculated specifically for filteredHistoryOrders
  const historyClosedOrders = filteredHistoryOrders.filter(o => o.status === 'CLOSED');
  const historyWinOrders = historyClosedOrders.filter(o => Number(o.pnl || 0) > 0);
  const historyLossOrders = historyClosedOrders.filter(o => Number(o.pnl || 0) < 0);
  const historyGrossProfit = historyWinOrders.reduce((sum, o) => sum + Number(o.pnl || 0), 0);
  const historyGrossLoss = Math.abs(historyLossOrders.reduce((sum, o) => sum + Number(o.pnl || 0), 0));
  const historyNetPnl = historyClosedOrders.reduce((sum, o) => sum + Number(o.pnl || 0), 0);
  const historyWinRate = historyClosedOrders.length > 0 ? ((historyWinOrders.length / historyClosedOrders.length) * 100).toFixed(1) : '0.0';
  const historyProfitFactor = historyGrossLoss > 0 ? (historyGrossProfit / historyGrossLoss).toFixed(2) : (historyGrossProfit > 0 ? 'MAX' : '0.00');
  const historyMaxWin = historyWinOrders.length > 0 ? Math.max(...historyWinOrders.map(o => Number(o.pnl || 0))) : 0;
  const historyMaxLoss = historyLossOrders.length > 0 ? Math.min(...historyLossOrders.map(o => Number(o.pnl || 0))) : 0;

  // Dynamic top stats bar depending on whether 'ALL' or a specific account is selected
  const displayEaStats = (() => {
    if (selectedEaAccount === 'ALL' || !activeAccObj) {
      return {
        isAll: true,
        label: 'Toàn hệ thống',
        subLabel: `${eaStats.active_accounts || 0} Online / ${eaStats.total_accounts || 0} Tài khoản`,
        totalAccounts: eaStats.total_accounts || 0,
        activeAccounts: eaStats.active_accounts || 0,
        deposit: Number(eaStats.total_deposit || 0),
        withdraw: Number(eaStats.total_withdraw || 0),
        realizedProfit: Number(eaStats.total_account_realized || eaStats.total_realized_pnl || 0),
        balance: Number(eaStats.total_balance || 0),
        equity: Number(eaStats.total_equity || 0),
        freeMargin: Number(eaStats.total_free_margin || 0),
        floatingPnl: Number(eaStats.total_floating_pnl || 0),
        openTrades: eaPositions.length,
      };
    } else {
      const now = Math.floor(Date.now() / 1000);
      const isOnline = activeAccObj.last_ping && (now - activeAccObj.last_ping < 15);
      return {
        isAll: false,
        label: `${activeAccObj.name || 'MT5'}`,
        subLabel: `#${activeAccObj.login} ${activeAccObj.server ? `| ${activeAccObj.server}` : ''}`,
        totalAccounts: 1,
        activeAccounts: isOnline ? 1 : 0,
        deposit: Number(activeAccObj.total_deposit || 0),
        withdraw: Number(activeAccObj.total_withdraw || 0),
        realizedProfit: Number(activeAccObj.realized_profit || 0),
        balance: Number(activeAccObj.balance || 0),
        equity: Number(activeAccObj.equity || 0),
        freeMargin: Number(activeAccObj.free_margin || 0),
        floatingPnl: Number(activeAccObj.floating_pnl || 0),
        openTrades: filteredPositions.length,
      };
    }
  })();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col md:flex-row font-sans selection:bg-purple-500 selection:text-white">
      
      {/* LEFT SIDEBAR NAVIGATION */}
      <aside className="w-full md:w-64 bg-slate-900 border-r border-slate-800 flex flex-col justify-between shrink-0">
        <div>
          {/* Brand Header */}
          <div className="p-5 border-b border-slate-800 flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="MINO TRADING PRO Logo" 
              className="w-9 h-9 rounded-xl object-contain shadow-lg shadow-amber-500/20" 
            />
            <div>
              <h2 className="font-extrabold text-sm text-slate-100 flex items-center gap-1.5">
                MINO TRADING PRO
                <span className="px-1.5 py-0.2 rounded text-[9px] font-black bg-purple-500/20 text-purple-300 border border-purple-500/40">
                  ADMIN
                </span>
              </h2>
              <p className="text-[11px] text-slate-400">Trang Quản Trị Hệ Thống</p>
            </div>
          </div>

          {/* Quick Return to Terminal Button */}
          <div className="p-3">
            <button
              onClick={onBackToTerminal}
              className="w-full py-2.5 px-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-slate-950 font-bold text-xs flex items-center justify-center gap-2 transition cursor-pointer shadow-md shadow-amber-500/20"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Quay Lại Biểu Đồ Trading</span>
            </button>
          </div>

          {/* Nav Items */}
          <nav className="p-3 space-y-1.5">
            <button
              onClick={() => setActiveSection('users')}
              className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition cursor-pointer ${
                activeSection === 'users'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
              }`}
            >
              <Users className="w-4 h-4" />
              <span>Quản Lý Người Dùng</span>
              <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-slate-950/40">
                {totalUsers}
              </span>
            </button>

            <button
              onClick={() => setActiveSection('ea_trading')}
              className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition cursor-pointer relative overflow-hidden ${
                activeSection === 'ea_trading'
                  ? 'bg-gradient-to-r from-amber-500 to-purple-600 text-white shadow-lg shadow-purple-600/30'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/80 border border-amber-500/20 bg-amber-500/5'
              }`}
            >
              <Zap className="w-4 h-4 text-amber-400" />
              <span>Giao Dịch Qua EA (MT5)</span>
              <span className="ml-auto px-1.5 py-0.5 rounded text-[9px] font-black bg-amber-400 text-slate-950 uppercase tracking-tighter">
                AUTO
              </span>
            </button>

            <button
              onClick={() => setActiveSection('connection')}
              className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition cursor-pointer ${
                activeSection === 'connection'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
              }`}
            >
              <Server className="w-4 h-4 text-emerald-400" />
              <span>Nguồn Cấp & Feeder MT5</span>
              <span className="ml-auto flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
            </button>

            <button
              onClick={() => setActiveSection('ai')}
              className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition cursor-pointer ${
                activeSection === 'ai'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
              }`}
            >
              <Sparkles className="w-4 h-4 text-cyan-400" />
              <span>Cấu Hình AI Hệ Thống</span>
            </button>

            <button
              onClick={() => setActiveSection('telegram')}
              className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition cursor-pointer ${
                activeSection === 'telegram'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
              }`}
            >
              <Bot className="w-4 h-4 text-amber-400" />
              <span>Cấu Hình Telegram Bot</span>
            </button>

            <button
              onClick={() => setActiveSection('email_oauth')}
              className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition cursor-pointer ${
                activeSection === 'email_oauth'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
              }`}
            >
              <Mail className="w-4 h-4 text-rose-400" />
              <span>Email OTP & Google OAuth</span>
            </button>

            <button
              onClick={() => setActiveSection('smc_config')}
              className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition cursor-pointer ${
                activeSection === 'smc_config'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
              }`}
            >
              <Settings className="w-4 h-4 text-amber-400" />
              <span>Cấu Hình SMC & Limits</span>
            </button>

            <button
              onClick={() => setActiveSection('hma_config')}
              className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition cursor-pointer ${
                activeSection === 'hma_config'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
              }`}
            >
              <Zap className="w-4 h-4 text-emerald-400" />
              <span>Cấu Hình HMA Breakout</span>
            </button>

            <button
              onClick={() => setActiveSection('volman_config')}
              className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition cursor-pointer ${
                activeSection === 'volman_config'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
              }`}
            >
              <Activity className="w-4 h-4 text-cyan-400" />
              <span>Cấu Hình Bob Volman</span>
            </button>

            <button
              onClick={() => setActiveSection('orb_config')}
              className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition cursor-pointer ${
                activeSection === 'orb_config'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
              }`}
            >
              <Clock className="w-4 h-4 text-amber-400" />
              <span>Cấu Hình ORB Session Pro</span>
            </button>

            <button
              onClick={() => setActiveSection('sweep_config')}
              className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition cursor-pointer ${
                activeSection === 'sweep_config'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
              }`}
            >
              <Zap className="w-4 h-4 text-purple-400" />
              <span>Cấu Hình Liquidity Sweep Pro</span>
            </button>

            <button
              onClick={() => setActiveSection('storage')}
              className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition cursor-pointer ${
                activeSection === 'storage'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
              }`}
            >
              <Database className="w-4 h-4 text-emerald-400" />
              <span>Lưu Trữ Cấu Hình & DB</span>
            </button>

            <button
              onClick={() => setActiveSection('account')}
              className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition cursor-pointer ${
                activeSection === 'account'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
              }`}
            >
              <User className="w-4 h-4 text-purple-300" />
              <span>Tài Khoản & Bảo Mật</span>
            </button>
          </nav>
        </div>

        {/* Sidebar Footer User Info */}
        <div className="p-4 border-t border-slate-800 bg-slate-950/40">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-8 h-8 rounded-lg bg-purple-950 border border-purple-500/40 text-purple-300 font-bold text-xs flex items-center justify-center">
              {user?.username ? user.username.slice(0, 2).toUpperCase() : 'A'}
            </div>
            <div className="overflow-hidden">
              <div className="text-xs font-bold text-slate-200 truncate">{user?.username || 'phuongvuthe'}</div>
              <div className="text-[10px] text-purple-400 font-semibold">SUPER ADMIN ⚡</div>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full py-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-400 text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Đăng Xuất</span>
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <main className="flex-1 flex flex-col min-w-0 overflow-y-auto">
        
        {/* Top Header */}
        <header className="px-6 py-4 border-b border-slate-800 bg-slate-900/60 backdrop-blur sticky top-0 z-30 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-base font-extrabold text-slate-100 flex items-center gap-2">
              {activeSection === 'users' && 'Quản Lý Người Dùng & Phân Quyền RBAC'}
              {activeSection === 'ea_trading' && '⚡ Quản Lý Giao Dịch Tự Động Qua MT5 EA & Khớp Lệnh Realtime'}
              {activeSection === 'connection' && 'Giám Sát Nguồn Cấp Feeder MT5 & Kết Nối Máy Chủ'}
              {activeSection === 'ai' && 'Cấu Hình Trí Tuệ Nhân Tạo AI Toàn Hệ Thống'}
              {activeSection === 'telegram' && 'Cấu Hình Tín Hiệu & Cảnh Báo Telegram Bot'}
              {activeSection === 'email_oauth' && 'Cấu Hình Email OTP Khôi Phục MK & Google OAuth'}
              {activeSection === 'smc_config' && 'Cấu Hình Thuật Toán SMC & Limit Entry (Mặc Định Toàn Hệ Thống)'}
              {activeSection === 'hma_config' && 'Cấu Hình Chiến Lược HMA Breakout Pro (Mặc Định Toàn Hệ Thống)'}
              {activeSection === 'volman_config' && 'Cấu Hình Chiến Lược Bob Volman Scalping (Mặc Định Toàn Hệ Thống)'}
              {activeSection === 'orb_config' && 'Cấu Hình Chiến Lược ORB Session Pro (Mặc Định Toàn Hệ Thống)'}
              {activeSection === 'sweep_config' && 'Cấu Hình Liquidity Sweep Pro (Mặc Định Toàn Hệ Thống)'}
              {activeSection === 'storage' && 'Cơ Chế Lưu Trữ & Đồng Bộ Cấu Hình Người Dùng'}
              {activeSection === 'account' && 'Thông Tin Cá Nhân & Đổi Mật Khẩu'}
            </h1>
            <p className="text-[11px] text-slate-400">
              MINO TRADING PRO Enterprise Management Console
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onBackToTerminal}
              className="px-3.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs flex items-center gap-1.5 transition cursor-pointer border border-slate-700"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Trở Lại Biểu Đồ</span>
            </button>
          </div>
        </header>

        {/* Content Container */}
        <div className="p-6 max-w-7xl w-full mx-auto space-y-6">

          {/* SECTION 1: USERS MANAGEMENT */}
          {activeSection === 'users' && (
            <div className="space-y-6">
              {/* Stat Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-lg flex items-center justify-between">
                  <div>
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">Tổng Người Dùng</span>
                    <span className="text-2xl font-black text-slate-100">{totalUsers}</span>
                  </div>
                  <div className="p-3 rounded-xl bg-purple-500/15 text-purple-400 border border-purple-500/30">
                    <Users className="w-5 h-5" />
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-lg flex items-center justify-between">
                  <div>
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">Thành Viên Basic</span>
                    <span className="text-2xl font-black text-cyan-400">{basicCount}</span>
                  </div>
                  <div className="p-3 rounded-xl bg-cyan-500/15 text-cyan-400 border border-cyan-500/30">
                    <User className="w-5 h-5" />
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-lg flex items-center justify-between">
                  <div>
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">Thành Viên PRO / VIP</span>
                    <span className="text-2xl font-black text-amber-400">{proCount}</span>
                  </div>
                  <div className="p-3 rounded-xl bg-amber-500/15 text-amber-400 border border-amber-500/30">
                    <Crown className="w-5 h-5" />
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-lg flex items-center justify-between">
                  <div>
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">Quản Trị Viên</span>
                    <span className="text-2xl font-black text-purple-300">{adminCount}</span>
                  </div>
                  <div className="p-3 rounded-xl bg-purple-500/20 text-purple-300 border border-purple-500/40">
                    <Shield className="w-5 h-5" />
                  </div>
                </div>
              </div>

              {/* Message Banner */}
              {usersMsg.text && (
                <div className={`p-3.5 rounded-xl border text-xs flex items-center gap-2 ${
                  usersMsg.type === 'success'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                }`}>
                  {usersMsg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  <span>{usersMsg.text}</span>
                </div>
              )}

              {/* Search & Filter Toolbar */}
              <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-lg flex flex-wrap items-center justify-between gap-3">
                <div className="relative flex-1 min-w-[240px]">
                  <Search className="w-4 h-4 absolute left-3.5 top-3 text-slate-500" />
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Tìm kiếm theo Tên người dùng hoặc Email..."
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl pl-10 pr-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <select
                    value={tierFilter}
                    onChange={(e) => setTierFilter(e.target.value)}
                    className="bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-purple-500 cursor-pointer"
                  >
                    <option value="ALL">Tất Cả Cấp Bậc ({totalUsers})</option>
                    <option value="BASIC">Chỉ Basic ({basicCount})</option>
                    <option value="PRO">Chỉ PRO VIP ({proCount})</option>
                    <option value="ADMIN">Chỉ Admin ({adminCount})</option>
                  </select>

                  <button
                    onClick={loadUsers}
                    disabled={loadingUsers}
                    className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold transition flex items-center gap-1.5 cursor-pointer border border-slate-700"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${loadingUsers ? 'animate-spin' : ''}`} />
                    <span>Làm Mới</span>
                  </button>
                </div>
              </div>

              {/* Users Table */}
              <div className="rounded-2xl border border-slate-800 overflow-hidden bg-slate-900/60 shadow-xl">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-950/80 text-slate-400 border-b border-slate-800 text-[10px] uppercase font-bold tracking-wider">
                    <tr>
                      <th className="px-5 py-3.5">Người Dùng</th>
                      <th className="px-4 py-3.5">Phân Quyền (Tier)</th>
                      <th className="px-4 py-3.5">Hạn Sử Dụng PRO / VIP</th>
                      <th className="px-4 py-3.5">Trạng Thái</th>
                      <th className="px-4 py-3.5">Ngày Đăng Ký</th>
                      <th className="px-4 py-3.5">Lần Truy Cập Cuối</th>
                      <th className="px-5 py-3.5 text-right">Thao Tác</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60">
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-5 py-8 text-center text-slate-500">
                          {loadingUsers ? 'Đang tải dữ liệu...' : 'Không tìm thấy người dùng nào phù hợp.'}
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((u) => {
                        const ts = u.last_active_at || u.last_login || u.created_at;
                        const nowTs = Math.floor(Date.now() / 1000);
                        const diffSec = ts ? Math.max(0, nowTs - ts) : null;
                        let statusBadge = null;

                        if (diffSec !== null) {
                          if (diffSec < 180) {
                            statusBadge = (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 animate-pulse">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                                🟢 Đang Online
                              </span>
                            );
                          } else if (diffSec < 3600) {
                            const mins = Math.max(1, Math.floor(diffSec / 60));
                            statusBadge = (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-500/15 text-teal-300 border border-teal-500/30">
                                ⏱️ {mins} phút trước
                              </span>
                            );
                          } else if (diffSec < 86400) {
                            const hours = Math.floor(diffSec / 3600);
                            statusBadge = (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-cyan-500/10 text-cyan-300 border border-cyan-500/30">
                                ⚡ {hours} giờ trước
                              </span>
                            );
                          } else if (diffSec < 86400 * 2) {
                            statusBadge = (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-800 text-slate-300 border border-slate-700">
                                Hôm qua
                              </span>
                            );
                          } else {
                            const days = Math.floor(diffSec / 86400);
                            statusBadge = (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-900 text-slate-400 border border-slate-800">
                                {days} ngày trước
                              </span>
                            );
                          }
                        }

                        const exactTimeStr = ts ? new Date(ts * 1000).toLocaleString('vi-VN') : '--';
                        const loginTimeStr = u.last_login ? new Date(u.last_login * 1000).toLocaleString('vi-VN') : null;

                        return (
                        <tr key={u.id} className="hover:bg-slate-900/80 transition">
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-slate-800 text-slate-300 flex items-center justify-center font-bold text-xs border border-slate-700">
                                {u.username.slice(0, 1).toUpperCase()}
                              </div>
                              <div>
                                <div className="font-bold text-slate-100 flex items-center gap-1.5">
                                  <span>{u.username}</span>
                                  {u.username === 'phuongvuthe' && (
                                    <span className="px-1.5 py-0.2 rounded text-[9px] font-black bg-purple-500/20 text-purple-300 border border-purple-500/40">
                                      CHÍNH
                                    </span>
                                  )}
                                </div>
                                <div className="text-[11px] text-slate-400 font-mono">{u.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            <select
                              value={u.tier}
                              onChange={(e) => {
                                const newT = e.target.value;
                                if (newT === 'PRO' && u.tier !== 'PRO') {
                                  handleUpdateUserTier(u.id, 'PRO', u.status, 30); // Default 30 days
                                } else {
                                  handleUpdateUserTier(u.id, newT, u.status);
                                }
                              }}
                              className={`px-2.5 py-1 rounded-lg text-xs font-bold border bg-slate-950 cursor-pointer focus:outline-none ${
                                u.tier === 'ADMIN'
                                  ? 'text-purple-300 border-purple-500/50'
                                  : u.tier === 'PRO'
                                  ? 'text-amber-300 border-amber-500/50'
                                  : 'text-cyan-300 border-cyan-500/50'
                              }`}
                            >
                              <option value="BASIC">BASIC (Miễn Phí)</option>
                              <option value="PRO">PRO (VIP 👑)</option>
                              <option value="ADMIN">ADMIN (⚡ Quản Trị)</option>
                            </select>
                          </td>
                          <td className="px-4 py-3.5">
                            {u.tier === 'ADMIN' && (
                              <span className="text-[10px] font-bold text-purple-300 bg-purple-500/15 border border-purple-500/30 px-2.5 py-1 rounded-full flex items-center gap-1 w-fit font-mono">
                                ⚡ Vô Thời Hạn
                              </span>
                            )}

                            {u.tier === 'PRO' && (
                              <div className="space-y-1.5 min-w-[180px]">
                                <div>
                                  {u.pro_expires_at ? (
                                    (() => {
                                      const nowTs2 = Math.floor(Date.now() / 1000);
                                      const diffSec2 = u.pro_expires_at - nowTs2;
                                      const daysLeft = Math.ceil(diffSec2 / 86400);
                                      const expDate = new Date(u.pro_expires_at * 1000).toLocaleDateString('vi-VN');

                                      if (daysLeft <= 0) {
                                        return (
                                          <span className="text-[10px] font-bold text-rose-400 bg-rose-500/20 border border-rose-500/40 px-2 py-0.5 rounded-full inline-flex items-center gap-1 font-mono">
                                            ⚠️ Đã Hết Hạn ({expDate})
                                          </span>
                                        );
                                      }
                                      if (daysLeft <= 3) {
                                        return (
                                          <span className="text-[10px] font-bold text-amber-300 bg-amber-500/20 border border-amber-500/40 px-2 py-0.5 rounded-full inline-flex items-center gap-1 font-mono">
                                            ⏳ Còn {daysLeft} ngày ({expDate})
                                          </span>
                                        );
                                      }
                                      return (
                                        <span className="text-[10px] font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 rounded-full inline-flex items-center gap-1 font-mono">
                                          ✅ Còn {daysLeft} ngày ({expDate})
                                        </span>
                                      );
                                    })()
                                  ) : (
                                    <span className="text-[10px] font-bold text-amber-300 bg-amber-500/20 border border-amber-500/40 px-2 py-0.5 rounded-full inline-flex items-center gap-1 font-mono">
                                      👑 Vĩnh Viễn (Lifetime)
                                    </span>
                                  )}
                                </div>

                                <select
                                  value=""
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (val === -1) {
                                      handleUpdateUserTier(u.id, 'BASIC', u.status);
                                    } else {
                                      handleUpdateUserTier(u.id, 'PRO', u.status, val);
                                    }
                                  }}
                                  className="text-[10px] bg-slate-950 border border-slate-700/80 rounded-lg px-2 py-0.5 text-slate-300 focus:outline-none focus:border-amber-400 cursor-pointer w-full"
                                >
                                  <option value="" disabled>⏱️ Gia Hạn / Đổi Hạn...</option>
                                  <option value="7">+7 Ngày (Dùng thử)</option>
                                  <option value="30">+30 Ngày (1 Tháng)</option>
                                  <option value="90">+90 Ngày (3 Tháng)</option>
                                  <option value="180">+180 Ngày (6 Tháng)</option>
                                  <option value="365">+1 Năm (365 Ngày)</option>
                                  <option value="0">👑 Vĩnh Viễn (Không hạn)</option>
                                  <option value="-1">❌ Chuyển về Basic ngay</option>
                                </select>
                              </div>
                            )}

                            {u.tier === 'BASIC' && (
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10.5px] text-slate-500 font-mono">Miễn Phí</span>
                                <button
                                  type="button"
                                  onClick={() => handleUpdateUserTier(u.id, 'PRO', u.status, 30)}
                                  className="text-[10px] font-bold text-amber-300 bg-amber-500/10 hover:bg-amber-500/25 border border-amber-500/30 px-2 py-0.5 rounded-lg transition cursor-pointer flex items-center gap-1"
                                  title="Cấp quyền PRO 30 ngày"
                                >
                                  <Crown className="w-3 h-3" />
                                  <span>+30N PRO</span>
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3.5">
                            <select
                              value={u.status}
                              onChange={(e) => handleUpdateUserTier(u.id, u.tier, e.target.value)}
                              className={`px-2.5 py-1 rounded-lg text-xs font-bold border bg-slate-950 cursor-pointer focus:outline-none ${
                                u.status === 'ACTIVE'
                                  ? 'text-emerald-400 border-emerald-500/40'
                                  : 'text-rose-400 border-rose-500/40'
                              }`}
                            >
                              <option value="ACTIVE">Hoạt Động</option>
                              <option value="BANNED">Khóa Tài Khoản</option>
                            </select>
                          </td>
                          <td className="px-4 py-3.5 text-slate-300 font-mono text-[11px]">
                            {u.created_at ? new Date(u.created_at * 1000).toLocaleDateString('vi-VN') : '--'}
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="space-y-1">
                              <div>{statusBadge}</div>
                              <div className="text-[10px] text-slate-400 font-mono" title={`Truy cập: ${exactTimeStr}${loginTimeStr ? `\nĐăng nhập: ${loginTimeStr}` : ''}`}>
                                {exactTimeStr}
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            {u.username !== 'phuongvuthe' && u.username !== 'admin' && (
                              <button
                                onClick={() => handleDeleteUser(u.id, u.username)}
                                className="p-1.5 rounded-lg hover:bg-rose-500/20 text-slate-500 hover:text-rose-400 transition cursor-pointer"
                                title="Xóa người dùng này"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SECTION: EA AUTOMATED TRADING MANAGEMENT */}
          {activeSection === 'ea_trading' && (
            <div className="space-y-6">
              
              {/* Notification Message */}
              {eaMsg.text && (
                <div className={`p-4 rounded-2xl border text-xs font-bold flex items-center justify-between transition shadow-lg ${
                  eaMsg.type === 'success'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 shadow-emerald-500/10'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-400 shadow-rose-500/10'
                }`}>
                  <div className="flex items-center gap-2.5">
                    {eaMsg.type === 'success' ? <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" /> : <AlertCircle className="w-5 h-5 text-rose-400 shrink-0" />}
                    <span>{eaMsg.text}</span>
                  </div>
                  <button onClick={() => setEaMsg({ type: '', text: '' })} className="p-1 hover:bg-slate-800/60 rounded-lg cursor-pointer">
                    ✕
                  </button>
                </div>
              )}

              {/* Top EA Metrics Bar */}
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                {/* MT5 Accounts Card */}
                <div className="relative p-4 rounded-2xl bg-gradient-to-br from-purple-950/60 to-slate-900/90 border border-purple-500/25 shadow-lg shadow-purple-900/20 overflow-hidden group hover:border-purple-500/50 transition-all duration-300">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="flex items-start justify-between relative z-10">
                    <div>
                      <span className="text-[10px] font-bold text-purple-400/80 uppercase tracking-widest block mb-1.5">
                        {displayEaStats.isAll ? 'Tài Khoản MT5' : 'Tài Khoản Đang Chọn'}
                      </span>
                      <div className="flex items-baseline gap-1">
                        {displayEaStats.isAll ? (
                          <>
                            <span className="text-2xl font-black text-slate-100">{displayEaStats.activeAccounts}</span>
                            <span className="text-[11px] text-slate-500 font-semibold">/ {displayEaStats.totalAccounts}</span>
                          </>
                        ) : (
                          <span className="text-base font-black text-slate-100 truncate max-w-[110px] block" title={displayEaStats.label}>
                            {displayEaStats.label}
                          </span>
                        )}
                      </div>
                      <span className={`text-[10px] font-mono mt-0.5 block truncate max-w-[120px] ${displayEaStats.isAll ? 'text-purple-400/70' : (displayEaStats.activeAccounts > 0 ? 'text-emerald-400 font-bold' : 'text-slate-500')}`}>
                        {displayEaStats.subLabel}
                      </span>
                    </div>
                    <div className="p-2 rounded-xl bg-purple-500/20 text-purple-400 border border-purple-500/30 shadow-inner">
                      <Cpu className="w-4 h-4" />
                    </div>
                  </div>
                </div>

                {/* Total Deposit Card */}
                <div className="relative p-4 rounded-2xl bg-gradient-to-br from-emerald-950/60 to-slate-900/90 border border-emerald-500/25 shadow-lg shadow-emerald-900/20 overflow-hidden group hover:border-emerald-500/50 transition-all duration-300">
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="flex items-start justify-between relative z-10">
                    <div>
                      <span className="text-[10px] font-bold text-emerald-400/80 uppercase tracking-widest block mb-1.5">Tổng Tiền Nạp</span>
                      <span className="text-lg font-black text-emerald-400">
                        ${displayEaStats.deposit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-[10px] text-slate-500 font-mono mt-0.5 block">
                        {displayEaStats.isAll ? 'Toàn hệ thống' : 'Tài khoản này'}
                      </span>
                    </div>
                    <div className="p-2 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 shadow-inner">
                      <TrendingUp className="w-4 h-4" />
                    </div>
                  </div>
                </div>

                {/* Total Withdraw Card */}
                <div className="relative p-4 rounded-2xl bg-gradient-to-br from-rose-950/60 to-slate-900/90 border border-rose-500/25 shadow-lg shadow-rose-900/20 overflow-hidden group hover:border-rose-500/50 transition-all duration-300">
                  <div className="absolute inset-0 bg-gradient-to-br from-rose-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="flex items-start justify-between relative z-10">
                    <div>
                      <span className="text-[10px] font-bold text-rose-400/80 uppercase tracking-widest block mb-1.5">Tổng Tiền Rút</span>
                      <span className="text-lg font-black text-rose-400">
                        ${displayEaStats.withdraw.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-[10px] text-slate-500 font-mono mt-0.5 block">
                        {displayEaStats.isAll ? 'Toàn hệ thống' : 'Tài khoản này'}
                      </span>
                    </div>
                    <div className="p-2 rounded-xl bg-rose-500/20 text-rose-400 border border-rose-500/30 shadow-inner">
                      <TrendingDown className="w-4 h-4" />
                    </div>
                  </div>
                </div>

                {/* Realized PnL Card */}
                <div className={`relative p-4 rounded-2xl bg-gradient-to-br ${displayEaStats.realizedProfit >= 0 ? 'from-emerald-950/60 border-emerald-500/25 shadow-emerald-900/20' : 'from-rose-950/60 border-rose-500/25 shadow-rose-900/20'} to-slate-900/90 border shadow-lg overflow-hidden group hover:border-opacity-50 transition-all duration-300`}>
                  <div className="flex items-start justify-between relative z-10">
                    <div>
                      <span className="text-[10px] font-bold text-slate-400/80 uppercase tracking-widest block mb-1.5">Lãi Đã Chốt</span>
                      <span className={`text-lg font-black ${displayEaStats.realizedProfit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {displayEaStats.realizedProfit >= 0 ? '+' : ''}${displayEaStats.realizedProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-[10px] text-slate-500 font-mono mt-0.5 block">
                        {displayEaStats.isAll ? 'Realized P&L' : 'Tài khoản này'}
                      </span>
                    </div>
                    <div className={`p-2 rounded-xl ${displayEaStats.realizedProfit >= 0 ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-rose-500/20 text-rose-400 border-rose-500/30'} border shadow-inner`}>
                      <DollarSign className="w-4 h-4" />
                    </div>
                  </div>
                </div>

                {/* Equity / Balance Card */}
                <div className="relative p-4 rounded-2xl bg-gradient-to-br from-cyan-950/60 to-slate-900/90 border border-cyan-500/25 shadow-lg shadow-cyan-900/20 overflow-hidden group hover:border-cyan-500/50 transition-all duration-300">
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="flex items-start justify-between relative z-10">
                    <div>
                      <span className="text-[10px] font-bold text-cyan-400/80 uppercase tracking-widest block mb-1.5">Tài Sản / Số Dư</span>
                      <span className="text-lg font-black text-cyan-400">
                        ${displayEaStats.equity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-[10px] text-slate-400/70 font-mono mt-0.5 block">
                        Dư: ${displayEaStats.balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="p-2 rounded-xl bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 shadow-inner">
                      <Database className="w-4 h-4" />
                    </div>
                  </div>
                </div>

                {/* Active Positions Card */}
                <div className="relative p-4 rounded-2xl bg-gradient-to-br from-amber-950/60 to-slate-900/90 border border-amber-500/25 shadow-lg shadow-amber-900/20 overflow-hidden group hover:border-amber-500/50 transition-all duration-300">
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="flex items-start justify-between relative z-10">
                    <div>
                      <span className="text-[10px] font-bold text-amber-400/80 uppercase tracking-widest block mb-1.5">Lệnh Đang Chạy</span>
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-2xl font-black text-amber-400">{displayEaStats.openTrades}</span>
                        <span className={`text-[11px] font-bold font-mono ${displayEaStats.floatingPnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {displayEaStats.floatingPnl >= 0 ? '+' : ''}${displayEaStats.floatingPnl.toFixed(2)}
                        </span>
                      </div>
                      <span className="text-[10px] text-slate-400/70 font-mono mt-0.5 block">
                        Ký quỹ: ${displayEaStats.freeMargin.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    <div className="p-2 rounded-xl bg-amber-500/20 text-amber-400 border border-amber-500/30 shadow-inner">
                      <Activity className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </div>


              {/* EA Download & WebRequest Setup Guide Banner */}
              <div className="relative rounded-2xl overflow-hidden border border-slate-700/60 shadow-xl">
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-purple-500 via-amber-400 to-cyan-500" />
                <div className="p-4 bg-gradient-to-r from-purple-950/50 via-slate-900/95 to-amber-950/30">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-xl bg-amber-500/15 border border-amber-500/30 shrink-0">
                        <Zap className="w-4 h-4 text-amber-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-extrabold text-slate-100">EA Bridge v2.0 — Khớp Lệnh MT5 Realtime</h3>
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-black bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 uppercase tracking-wider">LIVE</span>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          HMA Breakout · Bob Volman · ORB Session · SMC MSB — tín hiệu tự động đẩy đến từng tài khoản MT5 độc lập
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                      <a
                        href={api.adminDownloadEAUrl}
                        download="MinoTradingPro_EA.mq5"
                        className="px-3.5 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-950 text-xs font-black flex items-center gap-1.5 transition cursor-pointer shadow-lg shadow-amber-500/25"
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span>Tải File EA (.mq5)</span>
                      </a>

                      <button
                        type="button"
                        onClick={() => handleCopyUrl(window.location.origin)}
                        className="px-3 py-2 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-200 text-xs font-bold flex items-center gap-1.5 transition cursor-pointer border border-slate-600/60"
                        title="Sao chép URL Server"
                      >
                        {copiedUrl ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-cyan-400" />}
                        <span>{copiedUrl ? 'Đã Sao Chép!' : 'Copy URL WebRequest'}</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setShowGuide(!showGuide)}
                        className="px-3 py-2 rounded-xl bg-purple-500/15 hover:bg-purple-500/25 text-purple-300 text-xs font-bold flex items-center gap-1 transition cursor-pointer border border-purple-500/30"
                      >
                        <Info className="w-3.5 h-3.5" />
                        <span>{showGuide ? 'Ẩn' : 'Hướng Dẫn'}</span>
                        {showGuide ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                    </div>
                  </div>

                  {/* Collapsible MT5 Installation Guide */}
                  {showGuide && (
                    <div className="mt-3 p-4 rounded-xl bg-slate-950/80 border border-slate-800/80 space-y-3 text-xs text-slate-300">
                      <div className="font-bold text-amber-400 flex items-center gap-1.5">
                        <Terminal className="w-4 h-4" />
                        <span>Quy Trình Cài Đặt EA Lên MetaTrader 5:</span>
                      </div>
                      <ol className="list-decimal list-inside space-y-2 pl-1 leading-relaxed text-slate-300">
                        <li>
                          <b>Bước 1:</b> Tải file <code className="text-amber-300 bg-slate-900 px-1.5 py-0.5 rounded border border-slate-800">MinoTradingPro_EA.mq5</code> bằng nút màu vàng ở trên.
                        </li>
                        <li>
                          <b>Bước 2:</b> Trên MT5, chọn menu <b>File</b> ➔ <b>Open Data Folder</b> ➔ Mở thư mục <b>MQL5</b> ➔ <b>Experts</b> ➔ Dán file <code className="text-amber-300">MinoTradingPro_EA.mq5</code> vào đây.
                        </li>
                        <li>
                          <b>Bước 3:</b> Nhấn phím <b>F4</b> để mở MetaEditor ➔ Mở file ➔ Nhấn <b>F7 (Compile)</b> để biên dịch sang <code className="text-emerald-400">.ex5</code>.
                        </li>
                        <li>
                          <b>Bước 4 (Cực Kỳ Quan Trọng):</b> Trên MT5, bấm <b>Ctrl + O (Options)</b> ➔ Chọn tab <b>Expert Advisors</b>:
                          <ul className="list-disc list-inside pl-4 mt-1 space-y-1 text-slate-400">
                            <li>Tích chọn <b className="text-slate-200">Allow algorithmic trading</b>.</li>
                            <li>Tích chọn <b className="text-slate-200">Allow WebRequest for listed URL</b> và thêm 2 địa chỉ:
                              <div className="mt-1 flex items-center gap-2">
                                <code className="text-cyan-300 bg-slate-900 px-2 py-1 rounded border border-slate-800 font-mono select-all">
                                  {window.location.origin}
                                </code>
                                <span className="text-slate-500">và</span>
                                <code className="text-cyan-300 bg-slate-900 px-2 py-1 rounded border border-slate-800 font-mono select-all">
                                  http://103.69.84.102:8000
                                </code>
                              </div>
                            </li>
                          </ul>
                        </li>
                        <li>
                          <b>Bước 5:</b> Kéo EA <code className="text-amber-300">MinoTradingPro_EA</code> vào biểu đồ ➔ Nhập <b>API Key</b> ➔ OK!
                        </li>
                      </ol>
                    </div>
                  )}
                </div>
              </div>


              {/* ==================== GLOBAL ACCOUNT FOCUS & QUICK SWITCH BAR ==================== */}
              <div className="p-3.5 rounded-2xl bg-gradient-to-r from-slate-900/90 via-purple-950/30 to-slate-900/90 border border-purple-500/20 shadow-xl space-y-2.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-400" />
                    <span className="text-xs font-black text-slate-200 uppercase tracking-wider">
                      Theo Dõi Từng Tài Khoản MT5:
                    </span>
                    {selectedEaAccount !== 'ALL' && activeAccObj && (
                      <span className="px-2 py-0.5 rounded-md bg-purple-500/20 border border-purple-500/40 text-purple-300 text-[11px] font-bold flex items-center gap-1.5">
                        <span>Đang lọc: <b>{activeAccObj.name} (#{activeAccObj.login})</b></span>
                        <button
                          type="button"
                          onClick={() => setSelectedEaAccount('ALL')}
                          className="hover:text-white cursor-pointer ml-1 font-bold text-xs"
                          title="Hủy chọn để xem tất cả"
                        >
                          ✕
                        </button>
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-400">
                    Bấm vào tài khoản để lọc toàn bộ Lệnh, Chiến Lược, Lịch Sử & Sổ Cái tương ứng
                  </div>
                </div>

                {/* Scrollable Account Pills */}
                <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-slate-700">
                  {/* ALL ACCOUNTS PILL */}
                  <button
                    type="button"
                    onClick={() => setSelectedEaAccount('ALL')}
                    className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer shrink-0 border ${
                      selectedEaAccount === 'ALL'
                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md shadow-purple-600/30 border-purple-400/40'
                        : 'bg-slate-950/80 hover:bg-slate-800 text-slate-300 border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    <Globe className="w-3.5 h-3.5 text-purple-300" />
                    <span>🌐 Tất Cả Tài Khoản ({eaAccounts.length})</span>
                  </button>

                  {/* INDIVIDUAL ACCOUNT PILLS */}
                  {eaAccounts.map(acc => {
                    const now = Math.floor(Date.now() / 1000);
                    const isOnline = acc.last_ping && (now - acc.last_ping < 15);
                    const isSelected = selectedEaAccount === acc.id;
                    const stratCount = eaStrategies.filter(s => s.account_id === acc.id).length;
                    const openPosCount = eaPositions.filter(p => p.account_id === acc.id).length;

                    return (
                      <button
                        key={acc.id}
                        type="button"
                        onClick={() => setSelectedEaAccount(isSelected ? 'ALL' : acc.id)}
                        className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2.5 cursor-pointer shrink-0 border ${
                          isSelected
                            ? 'bg-gradient-to-r from-purple-700 to-indigo-700 text-white shadow-lg shadow-purple-700/40 border-purple-400'
                            : 'bg-slate-950/80 hover:bg-slate-800 text-slate-300 border-slate-800 hover:border-slate-700'
                        }`}
                      >
                        {/* Online Pulse Indicator */}
                        <span className="relative flex h-2.5 w-2.5 shrink-0">
                          {isOnline ? (
                            <>
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                            </>
                          ) : (
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500" />
                          )}
                        </span>

                        {/* Account Info */}
                        <div className="text-left">
                          <div className="font-black leading-tight flex items-center gap-1.5">
                            <span>{acc.name || 'MT5'}</span>
                            <span className="text-[10px] font-mono text-purple-300 opacity-90">#{acc.login}</span>
                          </div>
                          <div className="text-[10px] text-slate-400 font-mono mt-0.5 flex items-center gap-2">
                            <span>Eq: ${Number(acc.equity || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                            <span className="text-slate-600">|</span>
                            <span>{stratCount} Strats</span>
                          </div>
                        </div>

                        {/* Active Position Badge */}
                        {openPosCount > 0 && (
                          <span className="px-1.5 py-0.5 rounded-md bg-amber-500 text-slate-950 text-[10px] font-black animate-pulse">
                            {openPosCount} Pos
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>


              {/* Sub-Tabs Navigation */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-1 bg-slate-900/70 p-1 rounded-xl border border-slate-800/80 backdrop-blur-sm">
                  <button
                    onClick={() => setEaSubTab('accounts')}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                      eaSubTab === 'accounts'
                        ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                    }`}
                  >
                    <Users className="w-3.5 h-3.5" />
                    <span>Tài Khoản MT5</span>
                    <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-black ${eaSubTab === 'accounts' ? 'bg-white/20 text-white' : 'bg-slate-800 text-slate-400'}`}>{eaAccounts.length}</span>
                  </button>

                  <button
                    onClick={() => setEaSubTab('strategies')}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                      eaSubTab === 'strategies'
                        ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                    }`}
                  >
                    <Sliders className="w-3.5 h-3.5" />
                    <span>Cấu Hình Hệ Thống</span>
                    <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-black ${eaSubTab === 'strategies' ? 'bg-white/20 text-white' : 'bg-slate-800 text-slate-400'}`}>{filteredStrategies.length}</span>
                  </button>

                  <button
                    onClick={() => setEaSubTab('positions')}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                      eaSubTab === 'positions'
                        ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                    }`}
                  >
                    <Activity className="w-3.5 h-3.5" />
                    <span>Lệnh Đang Chạy</span>
                    <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-black ${eaSubTab === 'positions' ? 'bg-white/20 text-white' : 'bg-slate-800 text-slate-400'}`}>{filteredPositions.length}</span>
                    {filteredPositions.length > 0 && (
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
                    )}
                  </button>

                  <button
                    onClick={() => setEaSubTab('history')}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                      eaSubTab === 'history'
                        ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                    }`}
                  >
                    <BarChart2 className="w-3.5 h-3.5" />
                    <span>Lịch Sử & Hiệu Suất</span>
                    <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-black ${eaSubTab === 'history' ? 'bg-white/20 text-white' : 'bg-slate-800 text-slate-400'}`}>{filteredOrders.length}</span>
                  </button>

                  <button
                    onClick={() => setEaSubTab('ledger')}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                      eaSubTab === 'ledger'
                        ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-md shadow-teal-600/30'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                    }`}
                  >
                    <DollarSign className="w-3.5 h-3.5" />
                    <span>Sổ Cái & Lợi Nhuận</span>
                    <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-black ${eaSubTab === 'ledger' ? 'bg-white/20 text-white' : 'bg-slate-800 text-slate-400'}`}>{filteredTransactions.length}</span>
                  </button>
                </div>

                <button
                  onClick={loadEaData}
                  disabled={loadingEa}
                  className="p-2 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white transition cursor-pointer border border-slate-700/80 shadow-sm"
                  title="Làm mới dữ liệu"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingEa ? 'animate-spin' : ''}`} />
                </button>
              </div>


              {/* ==================== SUB-TAB 1: TÀI KHOẢN MT5 ==================== */}
              {eaSubTab === 'accounts' && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <h4 className="text-sm font-bold text-slate-200">Danh Sách Tài Khoản MT5 & Phân Bổ Chiến Lược</h4>
                      <p className="text-[11px] text-slate-400">Quản lý độc lập từng tài khoản, xem nhanh số dư, lãi lỗ, chiến lược được gán và lịch sử lệnh.</p>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* View Mode Switch */}
                      <div className="flex items-center bg-slate-900 border border-slate-800 p-0.5 rounded-xl">
                        <button
                          type="button"
                          onClick={() => setAccountViewMode('cards')}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                            accountViewMode === 'cards'
                              ? 'bg-purple-600 text-white shadow-md'
                              : 'text-slate-400 hover:text-slate-200'
                          }`}
                          title="Hiển thị dạng thẻ chi tiết từng tài khoản"
                        >
                          <LayoutGrid className="w-3.5 h-3.5" />
                          <span>Dạng Thẻ (Cards)</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setAccountViewMode('table')}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                            accountViewMode === 'table'
                              ? 'bg-purple-600 text-white shadow-md'
                              : 'text-slate-400 hover:text-slate-200'
                          }`}
                          title="Hiển thị dạng bảng dữ liệu tổng hợp"
                        >
                          <List className="w-3.5 h-3.5" />
                          <span>Dạng Bảng (Table)</span>
                        </button>
                      </div>

                      <button
                        onClick={() => {
                          setEditingAcc(null);
                          setAccForm({ name: '', login: '', server: '', magic_number: 888888, is_active: true, profit_share_pct: 30.0, client_notes: '' });
                          setShowAddAccModal(true);
                        }}
                        className="px-3.5 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold flex items-center gap-1.5 transition cursor-pointer shadow-lg shadow-purple-600/20"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Thêm Tài Khoản MT5 Mới</span>
                      </button>
                    </div>
                  </div>

                  {/* CARDS VIEW MODE */}
                  {accountViewMode === 'cards' && (
                    <div className="space-y-4">
                      {eaAccounts.length === 0 ? (
                        <div className="p-12 rounded-2xl border border-slate-800 bg-slate-900/40 text-center text-slate-500">
                          <Cpu className="w-10 h-10 mx-auto mb-3 text-slate-600 opacity-40" />
                          <p className="text-sm font-bold text-slate-400">Chưa có tài khoản MT5 nào được đăng ký.</p>
                          <p className="text-xs text-slate-600 mt-1">Bấm nút "Thêm Tài Khoản MT5 Mới" ở góc phải để bắt đầu kết nối.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-5">
                          {eaAccounts.map(acc => {
                            const now = Math.floor(Date.now() / 1000);
                            const lastPingDiff = acc.last_ping ? (now - acc.last_ping) : 999999;
                            const isOnline = lastPingDiff < 15;
                            const isKeyShown = showKeyMap[acc.id];
                            const isSelected = selectedEaAccount === acc.id;

                            const accStrats = eaStrategies.filter(s => s.account_id === acc.id);
                            const accPos = eaPositions.filter(p => p.account_id === acc.id);
                            const accOrders = eaOrders.filter(o => o.account_id === acc.id);

                            return (
                              <div
                                key={acc.id}
                                className={`relative rounded-2xl bg-gradient-to-b from-slate-900/95 to-slate-950/90 border transition-all duration-300 shadow-xl overflow-hidden flex flex-col justify-between ${
                                  isSelected
                                    ? 'border-purple-500 shadow-purple-900/30 ring-1 ring-purple-500/50'
                                    : 'border-slate-800 hover:border-slate-700 hover:shadow-2xl'
                                }`}
                              >
                                {/* Top Accent bar */}
                                <div className={`h-1 w-full ${isOnline ? 'bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-500' : 'bg-slate-700'}`} />

                                <div className="p-5 space-y-4">
                                  {/* 1. Account Header */}
                                  <div className="flex items-start justify-between gap-3">
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <h5 className="text-base font-black text-slate-100 tracking-tight">{acc.name || 'Tài Khoản MT5'}</h5>
                                        {isOnline ? (
                                          <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
                                            <span>ONLINE</span>
                                          </span>
                                        ) : (
                                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-500/15 text-rose-400 border border-rose-500/25">
                                            {acc.last_ping > 0 ? `OFFLINE (${Math.round(lastPingDiff / 60)}p trước)` : 'CHƯA KẾT NỐI'}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2 mt-1 text-[11px] font-mono text-slate-400">
                                        <span>Login: <b className="text-amber-300">{acc.login}</b></span>
                                        <span className="text-slate-600">·</span>
                                        <span>Server: <b className="text-slate-300">{acc.server || 'Exness'}</b></span>
                                        <span className="text-slate-600">·</span>
                                        <span className="text-purple-300 font-bold">#{acc.magic_number || 888888}</span>
                                      </div>
                                    </div>

                                    {/* Auto-Trade Switch */}
                                    <div className="flex flex-col items-end gap-1">
                                      <span className="text-[9px] font-bold uppercase text-slate-500">Tự Động</span>
                                      <button
                                        type="button"
                                        onClick={() => handleToggleAccountActive(acc)}
                                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                          acc.is_active ? 'bg-emerald-500' : 'bg-slate-700'
                                        }`}
                                        title={acc.is_active ? 'Bật giao dịch tự động' : 'Tắt giao dịch tự động'}
                                      >
                                        <span
                                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                                            acc.is_active ? 'translate-x-4' : 'translate-x-0'
                                          }`}
                                        />
                                      </button>
                                    </div>
                                  </div>

                                  {/* 2. API Key Box */}
                                  <div className="p-2.5 rounded-xl bg-slate-950/80 border border-slate-800/80 flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <Key className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                                      <span className="text-[10px] font-bold text-slate-400 shrink-0">API Key:</span>
                                      <code className="text-xs font-mono text-slate-300 truncate">
                                        {isKeyShown ? acc.api_key : `${acc.api_key.slice(0, 10)}••••••••••••`}
                                      </code>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      <button
                                        type="button"
                                        onClick={() => setShowKeyMap(prev => ({ ...prev, [acc.id]: !prev[acc.id] }))}
                                        className="p-1 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-slate-200 transition cursor-pointer"
                                        title={isKeyShown ? 'Ẩn API Key' : 'Hiện API Key'}
                                      >
                                        {isKeyShown ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleCopyKey(acc.api_key)}
                                        className="p-1 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-cyan-400 transition cursor-pointer"
                                        title="Sao chép API Key"
                                      >
                                        {copiedKey === acc.api_key ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleRegenerateKey(acc.id)}
                                        className="p-1 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-amber-400 transition cursor-pointer"
                                        title="Tạo lại API Key"
                                      >
                                        <RefreshCcw className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </div>

                                  {/* 3. Financial Metrics 4-Box Grid */}
                                  <div className="grid grid-cols-2 gap-2.5">
                                    {/* Balance */}
                                    <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800/80">
                                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                                        💼 Số Dư (Balance)
                                      </span>
                                      <div className="text-base font-black text-slate-100 font-mono">
                                        ${Number(acc.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </div>
                                    </div>

                                    {/* Equity */}
                                    <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800/80">
                                      <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400 block mb-1">
                                        💎 Tài Sản (Equity)
                                      </span>
                                      <div className="text-base font-black text-cyan-400 font-mono">
                                        ${Number(acc.equity || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </div>
                                      <div className="text-[9.5px] text-slate-500 font-mono mt-0.5 truncate">
                                        Free: ${Number(acc.free_margin || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                        {acc.margin_level > 0 ? ` · ${Number(acc.margin_level).toFixed(0)}%` : ''}
                                      </div>
                                    </div>

                                    {/* Realized PnL */}
                                    <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800/80">
                                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                                        💰 Lãi Đã Chốt (Realized)
                                      </span>
                                      <div className={`text-base font-black font-mono ${Number(acc.realized_profit || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                        {Number(acc.realized_profit || 0) >= 0 ? '+' : ''}${Number(acc.realized_profit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </div>
                                    </div>

                                    {/* Floating PnL & Positions */}
                                    <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800/80">
                                      <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400 block mb-1">
                                        ⚡ Lệnh Chạy (Floating)
                                      </span>
                                      <div className="flex items-baseline justify-between">
                                        <div className={`text-base font-black font-mono ${Number(acc.floating_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                          {Number(acc.floating_pnl || 0) >= 0 ? '+' : ''}${Number(acc.floating_pnl || 0).toFixed(2)}
                                        </div>
                                        <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[10px] font-bold font-mono">
                                          {acc.open_trades_count || 0} lệnh
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Deposit & Withdraw mini summary */}
                                  <div className="px-3 py-2 rounded-xl bg-slate-950/60 border border-slate-800/60 flex items-center justify-between text-[10.5px] font-mono">
                                    <div className="flex items-center gap-1.5 text-slate-400">
                                      <span>Nạp:</span>
                                      <span className="text-emerald-400 font-bold">
                                        +${Number(acc.total_deposit || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                      </span>
                                    </div>
                                    <span className="text-slate-700">|</span>
                                    <div className="flex items-center gap-1.5 text-slate-400">
                                      <span>Rút:</span>
                                      <span className="text-rose-400 font-bold">
                                        -${Number(acc.total_withdraw || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                      </span>
                                    </div>
                                    <span className="text-slate-700">|</span>
                                    <div className="flex items-center gap-1.5 text-slate-400">
                                      <span>Chia sẻ:</span>
                                      <span className="text-purple-300 font-bold">{acc.profit_share_pct || 30}%</span>
                                    </div>
                                  </div>

                                  {/* 4. Active Strategies Assigned */}
                                  <div className="space-y-1.5">
                                    <div className="flex items-center justify-between text-[11px]">
                                      <span className="font-bold text-slate-300 flex items-center gap-1.5">
                                        <Sliders className="w-3.5 h-3.5 text-purple-400" />
                                        <span>Chiến Lược Hoạt Động ({accStrats.length})</span>
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditingStrat(null);
                                          const defParams = getDefaultStrategyParams('hma');
                                          setStratForm({
                                            account_id: acc.id,
                                            strategy: 'hma',
                                            symbol: 'XAUUSD',
                                            timeframe: 'M1',
                                            lot_mode: 'FIXED',
                                            lot_size: 0.01,
                                            risk_pct: 1.0,
                                            max_spread: 40.0,
                                            tp_mode: 'TP1',
                                            breakeven_enable: true,
                                            breakeven_pips: 15.0,
                                            trailing_enable: false,
                                            trailing_pips: 20.0,
                                            session_enable: false,
                                            session_start: '00:00',
                                            session_end: '23:59',
                                            custom_params: defParams,
                                          });
                                          setShowAddStratModal(true);
                                        }}
                                        className="text-[10px] font-bold text-purple-400 hover:text-purple-300 cursor-pointer"
                                      >
                                        + Gán Thêm
                                      </button>
                                    </div>

                                    <div className="flex flex-wrap gap-1.5">
                                      {accStrats.length === 0 ? (
                                        <span className="text-[11px] text-slate-500 italic">Chưa gán chiến lược nào cho tài khoản này.</span>
                                      ) : (
                                        accStrats.map(s => {
                                          const isHma = s.strategy === 'hma';
                                          const isVolman = s.strategy === 'bob_volman' || s.strategy === 'volman';
                                          const isOrb = s.strategy === 'orb' || s.strategy === 'orb_session';
                                          const isSweep = s.strategy === 'liquidity_sweep' || s.strategy === 'sweep' || s.strategy === 'sweep_pro' || s.strategy === 'liquidity_sweep_pro';
                                          return (
                                            <span
                                              key={s.id}
                                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border text-[10px] font-bold font-mono ${
                                                s.is_active
                                                  ? (isHma ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300' :
                                                     isVolman ? 'bg-indigo-500/10 border-indigo-500/30 text-indigo-300' :
                                                     isOrb ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' :
                                                     isSweep ? 'bg-purple-500/10 border-purple-500/30 text-purple-300' :
                                                     'bg-violet-500/10 border-violet-500/30 text-violet-300')
                                                  : 'bg-slate-800/50 border-slate-700 text-slate-500 line-through'
                                              }`}
                                            >
                                              <span>{isHma ? '⚡ HMA' : isVolman ? '🎯 Volman' : isOrb ? '🌅 ORB' : isSweep ? '💧 Sweep Pro' : '🔮 Matrix'}</span>
                                              <span>{s.symbol}</span>
                                              <span className="opacity-75">{s.timeframe}</span>
                                            </span>
                                          );
                                        })
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* 5. Action Footer & Direct Drilldown Navigation */}
                                <div className="p-3 bg-slate-950/90 border-t border-slate-800/80 flex flex-wrap items-center justify-between gap-2">
                                  {/* Left direct filters */}
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedEaAccount(acc.id);
                                        setEaSubTab('strategies');
                                      }}
                                      className="px-2 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-[10.5px] font-bold transition cursor-pointer flex items-center gap-1"
                                      title="Xem và chỉnh sửa chiến lược của tài khoản này"
                                    >
                                      <Sliders className="w-3 h-3 text-purple-400" />
                                      <span>Chiến Lược ({accStrats.length})</span>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedEaAccount(acc.id);
                                        setEaSubTab('positions');
                                      }}
                                      className="px-2 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-[10.5px] font-bold transition cursor-pointer flex items-center gap-1"
                                      title="Xem lệnh đang chạy của tài khoản này"
                                    >
                                      <Activity className="w-3 h-3 text-emerald-400" />
                                      <span>Lệnh ({accPos.length})</span>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedEaAccount(acc.id);
                                        setEaSubTab('history');
                                      }}
                                      className="px-2 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-[10.5px] font-bold transition cursor-pointer flex items-center gap-1"
                                      title="Xem lịch sử lệnh của tài khoản này"
                                    >
                                      <BarChart2 className="w-3 h-3 text-cyan-400" />
                                      <span>Lịch Sử ({accOrders.length})</span>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedEaAccount(acc.id);
                                        setLedgerFilterAcc(acc.id);
                                        setEaSubTab('ledger');
                                      }}
                                      className="px-2 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-[10.5px] font-bold transition cursor-pointer flex items-center gap-1"
                                      title="Xem sổ cái nạp rút & chia lợi nhuận của tài khoản này"
                                    >
                                      <DollarSign className="w-3 h-3 text-amber-400" />
                                      <span>Sổ Cái</span>
                                    </button>
                                  </div>

                                  {/* Right Edit & Delete */}
                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingAcc(acc);
                                        setAccForm({
                                          name: acc.name,
                                          login: acc.login,
                                          server: acc.server || '',
                                          magic_number: acc.magic_number || 888888,
                                          is_active: Boolean(acc.is_active),
                                          profit_share_pct: acc.profit_share_pct || 30.0,
                                          client_notes: acc.client_notes || ''
                                        });
                                        setShowAddAccModal(true);
                                      }}
                                      className="p-1.5 rounded-lg hover:bg-purple-500/20 text-slate-400 hover:text-purple-300 transition cursor-pointer"
                                      title="Sửa thông tin tài khoản"
                                    >
                                      <Edit3 className="w-4 h-4" />
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => handleDeleteAccount(acc)}
                                      className="p-1.5 rounded-lg hover:bg-rose-500/20 text-slate-500 hover:text-rose-400 transition cursor-pointer"
                                      title="Xóa tài khoản này"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* TABLE VIEW MODE */}
                  {accountViewMode === 'table' && (
                    <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60 shadow-xl">
                      <table className="w-full text-left text-xs text-slate-200 border-collapse">
                        <thead>
                          <tr className="border-b border-slate-800 bg-slate-950/60 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                            <th className="px-3.5 py-3.5">Tài Khoản & Server</th>
                            <th className="px-3 py-3.5">Magic No.</th>
                            <th className="px-3.5 py-3.5">Mã API Key (Bảo Mật)</th>
                            <th className="px-3 py-3.5">Tổng Nạp</th>
                            <th className="px-3 py-3.5">Tổng Rút</th>
                            <th className="px-3 py-3.5">Lãi Đã Chốt</th>
                            <th className="px-3 py-3.5">Số Dư (Balance)</th>
                            <th className="px-3 py-3.5">Tài Sản (Equity)</th>
                            <th className="px-3 py-3.5">Ký Quỹ Khả Dụng</th>
                            <th className="px-3 py-3.5">Floating PnL</th>
                            <th className="px-2.5 py-3.5">Lệnh Mở</th>
                            <th className="px-3 py-3.5">Kết Nối EA</th>
                            <th className="px-3 py-3.5">Tự Động</th>
                            <th className="px-3 py-3.5 text-right">Thao Tác</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/50">
                          {eaAccounts.length === 0 ? (
                            <tr>
                              <td colSpan="14" className="px-4 py-12 text-center text-slate-500">
                                <Cpu className="w-8 h-8 mx-auto mb-2 text-slate-600 opacity-40" />
                                <p className="font-semibold">Chưa có tài khoản MT5 nào được đăng ký.</p>
                                <p className="text-[11px] text-slate-600 mt-1">Bấm nút "Thêm Tài Khoản MT5 Mới" ở trên để bắt đầu kết nối.</p>
                              </td>
                            </tr>
                          ) : (
                            eaAccounts.map(acc => {
                              const now = Math.floor(Date.now() / 1000);
                              const lastPingDiff = acc.last_ping ? (now - acc.last_ping) : 999999;
                              const isOnline = lastPingDiff < 15;
                              const isKeyShown = showKeyMap[acc.id];

                              return (
                                <tr key={acc.id} className="hover:bg-slate-800/40 transition group">
                                  <td className="px-3.5 py-3.5">
                                    <div className="font-bold text-slate-100 flex items-center gap-1.5">
                                      <span>{acc.name || 'MT5 Account'}</span>
                                    </div>
                                    <div className="text-[11px] text-slate-400 font-mono">
                                      Login: <span className="text-amber-300 font-bold">{acc.login}</span> {acc.server ? `| ${acc.server}` : ''}
                                    </div>
                                  </td>
                                  <td className="px-3 py-3.5 font-mono text-purple-300 font-bold">
                                    #{acc.magic_number || 888888}
                                  </td>
                                  <td className="px-3.5 py-3.5">
                                    <div className="flex items-center gap-1.5 bg-slate-950/80 px-2 py-1 rounded-lg border border-slate-800 w-fit">
                                      <span className="font-mono text-[11px] text-slate-300">
                                        {isKeyShown ? acc.api_key : `${acc.api_key.slice(0, 8)}••••••••`}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => setShowKeyMap(prev => ({ ...prev, [acc.id]: !prev[acc.id] }))}
                                        className="p-1 text-slate-500 hover:text-slate-300 transition cursor-pointer"
                                        title={isKeyShown ? 'Ẩn API Key' : 'Hiện API Key'}
                                      >
                                        {isKeyShown ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleCopyKey(acc.api_key)}
                                        className="p-1 text-slate-500 hover:text-cyan-400 transition cursor-pointer"
                                        title="Sao chép API Key"
                                      >
                                        {copiedKey === acc.api_key ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleRegenerateKey(acc.id)}
                                        className="p-1 text-slate-500 hover:text-amber-400 transition cursor-pointer"
                                        title="Tạo lại API Key mới"
                                      >
                                        <RefreshCcw className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </td>
                                  <td className="px-3.5 py-3.5 font-mono font-bold text-emerald-400">
                                    ${Number(acc.total_deposit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="px-3.5 py-3.5 font-mono font-bold text-rose-400">
                                    ${Number(acc.total_withdraw || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="px-3.5 py-3.5 font-mono font-bold">
                                    <span className={Number(acc.realized_profit || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                                      {Number(acc.realized_profit || 0) >= 0 ? '+' : ''}${Number(acc.realized_profit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                  </td>
                                  <td className="px-3.5 py-3.5 font-mono font-bold text-slate-200">
                                    ${Number(acc.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="px-3.5 py-3.5 font-mono font-bold text-cyan-400">
                                    ${Number(acc.equity || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="px-3.5 py-3.5 font-mono">
                                    <div className="font-bold text-slate-200">
                                      ${Number(acc.free_margin || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </div>
                                    {acc.margin_level > 0 && (
                                      <div className="text-[10px] text-slate-400 font-normal">
                                        Margin: <span className={acc.margin_level < 200 ? 'text-amber-400 font-bold' : 'text-emerald-400 font-bold'}>{Number(acc.margin_level).toFixed(1)}%</span>
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-3.5 py-3.5 font-mono font-bold">
                                    <span className={Number(acc.floating_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                                      {Number(acc.floating_pnl || 0) >= 0 ? '+' : ''}${Number(acc.floating_pnl || 0).toFixed(2)}
                                    </span>
                                  </td>
                                  <td className="px-2.5 py-3.5">
                                    <span className="px-2 py-0.5 rounded-md bg-slate-800 text-amber-300 font-bold font-mono text-[11px]">
                                      {acc.open_trades_count || 0}
                                    </span>
                                  </td>
                                  <td className="px-3.5 py-3.5">
                                    {isOnline ? (
                                      <div className="flex items-center gap-1.5 text-emerald-400 font-bold text-[11px]">
                                        <span className="relative flex h-2 w-2">
                                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                        </span>
                                        <span>Online</span>
                                      </div>
                                    ) : acc.last_ping > 0 ? (
                                      <div className="text-[11px] text-slate-400">
                                        <span className="text-rose-400 font-bold">Offline</span>
                                        <span className="text-[10px] text-slate-500 block">
                                          {Math.round(lastPingDiff / 60)}p trước
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-[11px] text-slate-500 italic">Chưa kết nối</span>
                                    )}
                                  </td>
                                  <td className="px-3.5 py-3.5">
                                    <button
                                      type="button"
                                      onClick={() => handleToggleAccountActive(acc)}
                                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                        acc.is_active ? 'bg-emerald-500' : 'bg-slate-700'
                                      }`}
                                    >
                                      <span
                                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                                          acc.is_active ? 'translate-x-4' : 'translate-x-0'
                                        }`}
                                      />
                                    </button>
                                  </td>
                                  <td className="px-4 py-3.5 text-right space-x-1">
                                    <button
                                      onClick={() => {
                                        setEditingAcc(acc);
                                        setAccForm({
                                          name: acc.name,
                                          login: acc.login,
                                          server: acc.server || '',
                                          magic_number: acc.magic_number || 888888,
                                          is_active: Boolean(acc.is_active),
                                          profit_share_pct: acc.profit_share_pct || 30.0,
                                          client_notes: acc.client_notes || ''
                                        });
                                        setShowAddAccModal(true);
                                      }}
                                      className="p-1.5 rounded-lg hover:bg-purple-500/20 text-slate-400 hover:text-purple-300 transition cursor-pointer"
                                      title="Sửa thông tin tài khoản"
                                    >
                                      <Edit3 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteAccount(acc)}
                                      className="p-1.5 rounded-lg hover:bg-rose-500/20 text-slate-500 hover:text-rose-400 transition cursor-pointer"
                                      title="Xóa tài khoản này"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ==================== SUB-TAB 2: CẤU HÌNH HỆ THỐNG GIAO DỊCH ==================== */}
              {eaSubTab === 'strategies' && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <div>
                        <h4 className="text-sm font-bold text-slate-200">Cấu Hình Chiến Lược Khớp Lệnh</h4>
                        <p className="text-[11px] text-slate-400">Thiết lập từng hệ thống, cặp tiền, khung thời gian và quản lý vốn riêng cho từng tài khoản.</p>
                      </div>
                      <select
                        value={selectedEaAccount}
                        onChange={(e) => setSelectedEaAccount(e.target.value)}
                        className="text-xs bg-slate-900 border border-purple-500/40 rounded-xl px-3 py-1.5 text-slate-200 font-bold focus:outline-none focus:border-purple-500 cursor-pointer shadow-sm"
                      >
                        <option value="ALL">🔍 Tất Cả Tài Khoản ({eaAccounts.length})</option>
                        {eaAccounts.map(a => (
                          <option key={a.id} value={a.id}>{a.name} ({a.login})</option>
                        ))}
                      </select>
                    </div>

                    <button
                      onClick={() => {
                        setEditingStrat(null);
                        const defParams = getDefaultStrategyParams('hma');
                        setStratForm({
                          account_id: selectedEaAccount !== 'ALL' ? selectedEaAccount : (eaAccounts[0]?.id || ''),
                          strategy: 'hma',
                          symbol: 'XAUUSD',
                          timeframe: 'M5',
                          lot_mode: 'FIXED',
                          lot_size: 0.01,
                          risk_pct: 1.0,
                          max_spread: 40.0,
                          tp_mode: 'TP1',
                          breakeven_enable: true,
                          breakeven_pips: 15.0,
                          trailing_enable: false,
                          trailing_pips: 20.0,
                          session_enable: false,
                          session_start: '00:00',
                          session_end: '23:59',
                          custom_params: defParams,
                        });
                        setShowAddStratModal(true);
                      }}
                      disabled={eaAccounts.length === 0}
                      className="px-3.5 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1.5 transition cursor-pointer shadow-lg shadow-purple-600/20"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Thêm Cấu Hình Hệ Thống</span>
                    </button>
                  </div>

                  {/* Active Account Filter Notice */}
                  {selectedEaAccount !== 'ALL' && activeAccObj && (
                    <div className="p-3 rounded-xl bg-purple-950/40 border border-purple-500/30 flex items-center justify-between text-xs text-purple-300">
                      <div className="flex items-center gap-2">
                        <Sliders className="w-4 h-4 text-purple-400" />
                        <span>Đang hiển thị chiến lược gán riêng cho: <b>{activeAccObj.name} (#{activeAccObj.login})</b> ({filteredStrategies.length} chiến lược)</span>
                      </div>
                      <button
                        onClick={() => setSelectedEaAccount('ALL')}
                        className="text-xs font-bold text-slate-400 hover:text-white underline cursor-pointer"
                      >
                        Xem tất cả tài khoản
                      </button>
                    </div>
                  )}

                  <div className="overflow-x-auto rounded-2xl border border-slate-800/80 bg-slate-900/50 shadow-xl">
                    <table className="w-full text-left text-xs text-slate-200 border-collapse">
                      <thead>
                        <tr className="border-b border-slate-800/80 bg-slate-950/80 text-slate-500 font-bold uppercase tracking-widest text-[9.5px]">
                          <th className="px-4 py-3">Tài Khoản Áp Dụng</th>
                          <th className="px-4 py-3">Hệ Thống Giao Dịch</th>
                          <th className="px-4 py-3">Cặp Tiền & Khung</th>
                          <th className="px-4 py-3">Khối Lượng / QLRR</th>
                          <th className="px-4 py-3">Mục Tiêu (TP Mode)</th>
                          <th className="px-4 py-3">Hòa Vốn & Trailing</th>
                          <th className="px-4 py-3">Phiên Giao Dịch</th>
                          <th className="px-4 py-3">Bật / Tắt</th>
                          <th className="px-4 py-3 text-right">Thao Tác</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/50">
                        {filteredStrategies.length === 0 ? (
                          <tr>
                            <td colSpan="9" className="px-4 py-12 text-center text-slate-500">
                              <Sliders className="w-8 h-8 mx-auto mb-2 text-slate-600 opacity-40" />
                              <p className="font-semibold">Chưa có cấu hình hệ thống giao dịch nào.</p>
                              <p className="text-[11px] text-slate-600 mt-1">Bấm "Thêm Cấu Hình Hệ Thống" để phân công chiến lược cho từng tài khoản MT5.</p>
                            </td>
                          </tr>
                        ) : (
                          filteredStrategies.map(strat => {
                            return (
                              <tr key={strat.id} className={`hover:bg-slate-800/30 transition-colors group border-l-2 ${strat.is_active ? 'border-l-transparent hover:border-l-purple-500/60' : 'border-l-slate-800'}`}>
                                <td className="px-4 py-3">
                                  <div className="font-bold text-slate-200 text-xs">{strat.account_name || 'MT5 Account'}</div>
                                  <div className="text-[10px] text-slate-500 font-mono mt-0.5">#{strat.account_login}</div>
                                </td>
                                <td className="px-4 py-3">
                                  {strat.strategy === 'hma' && (
                                    <div>
                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-cyan-500/10 border border-cyan-500/25 text-cyan-300 font-bold text-[10px]">
                                      ⚡ HMA Breakout Pro
                                    </span>
                                      {strat.custom_params?.ema200SwFilterEnable !== false && (
                                        <div className="mt-1">
                                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-mono text-[9px]" title={`Lọc Sideway EMA200 M5: ±${strat.custom_params?.ema200SwMaxDistPips ?? 420}p (Chỉ áp dụng M1)`}>
                                            🛡️ EMA200 M5: ±{strat.custom_params?.ema200SwMaxDistPips ?? 420}p
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {strat.strategy === 'smc' && (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-300 font-bold text-[10px]">
                                      🏛️ SMC MSB Dynamic
                                    </span>
                                  )}
                                  {(strat.strategy === 'matrix' || strat.strategy === 'signal') && (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-500/10 border border-purple-500/25 text-purple-300 font-bold text-[10px]">
                                      🔮 Matrix Strategy Pro
                                    </span>
                                  )}
                                  {(strat.strategy === 'bob_volman' || strat.strategy === 'volman') && (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-500/10 border border-indigo-500/25 text-indigo-300 font-bold text-[10px]">
                                      🎯 Bob Volman Scalping
                                    </span>
                                  )}
                                  {(strat.strategy === 'orb' || strat.strategy === 'orb_session' || strat.strategy === 'orb_session_pro') && (
                                    <div className="flex flex-col gap-1 items-start">
                                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-300 font-bold text-[10px]">
                                        🌅 ORB Session Pro
                                      </span>
                                      {Boolean(strat.custom_params?.orbDynamicLimitEnable || strat.custom_params?.orb_dynamic_limit_enable) && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[9px] font-mono border border-amber-500/30">
                                          ⚡ Limit SL &gt; {strat.custom_params?.orbLimitThresholdPips ?? strat.custom_params?.limitThresholdPips ?? 100}p ({strat.custom_params?.orbLimitPullbackPct ?? strat.custom_params?.limitPullbackPct ?? 50}% Zone)
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {(strat.strategy === 'liquidity_sweep' || strat.strategy === 'sweep' || strat.strategy === 'sweep_pro' || strat.strategy === 'liquidity_sweep_pro') && (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-500/10 border border-purple-500/25 text-purple-300 font-bold text-[10px]">
                                      💧 Liquidity Sweep Pro
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-mono font-black text-amber-300 text-xs">{strat.symbol}</span>
                                    <span className="px-1.5 py-0.5 rounded-md bg-slate-800/80 border border-slate-700/60 text-slate-400 font-mono text-[10px]">
                                      {strat.timeframe}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 font-mono">
                                  {strat.lot_mode === 'RISK_PCT' ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-500/10 border border-rose-500/25 text-rose-300 font-bold text-[10px]">
                                      🛡️ {strat.risk_pct}% Rủi Ro
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 font-bold text-[10px]">
                                      📦 {strat.lot_size} Lot Cố Định
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <span className="font-bold text-slate-200 text-xs">{strat.tp_mode || 'TP1'}</span>
                                  <div className="text-[10px] text-slate-500 font-mono mt-0.5">Max Spread: {strat.max_spread || 40} pts</div>
                                </td>
                                <td className="px-4 py-3 text-[10.5px] font-mono space-y-0.5">
                                  <div className="flex items-center gap-1">
                                    <span className="text-slate-500">BE:</span>
                                    <span className={strat.breakeven_enable ? 'text-emerald-400 font-bold' : 'text-slate-600'}>{strat.breakeven_enable ? `BẬT (${strat.breakeven_pips}p)` : 'TẮT'}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-slate-500">Trail:</span>
                                    <span className={strat.trailing_enable ? 'text-cyan-400 font-bold' : 'text-slate-600'}>{strat.trailing_enable ? `BẬT (${strat.trailing_pips}p)` : 'TẮT'}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-[10.5px] font-mono">
                                  {strat.session_enable ? (
                                    <span className="text-amber-300 font-bold">{strat.session_start} - {strat.session_end}</span>
                                  ) : (
                                    <span className="text-slate-500">24/7 (Cả Ngày)</span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <button
                                    type="button"
                                    onClick={() => handleToggleStrategyActive(strat)}
                                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                      strat.is_active ? 'bg-emerald-500' : 'bg-slate-700'
                                    }`}
                                  >
                                    <span
                                      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                                        strat.is_active ? 'translate-x-4' : 'translate-x-0'
                                      }`}
                                    />
                                  </button>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                      onClick={() => {
                                        setEditingStrat(strat);
                                        const stratCustomParams = (strat.custom_params && typeof strat.custom_params === 'object' && Object.keys(strat.custom_params).length > 0)
                                          ? strat.custom_params
                                          : getDefaultStrategyParams(strat.strategy);
                                        setStratForm({
                                          account_id: strat.account_id,
                                          strategy: strat.strategy,
                                          symbol: strat.symbol,
                                          timeframe: strat.timeframe,
                                          lot_mode: strat.lot_mode || 'FIXED',
                                          lot_size: strat.lot_size || 0.01,
                                          risk_pct: strat.risk_pct || 1.0,
                                          max_spread: strat.max_spread || 40.0,
                                          tp_mode: strat.tp_mode || 'TP1',
                                          breakeven_enable: Boolean(strat.breakeven_enable),
                                          breakeven_pips: strat.breakeven_pips || 15.0,
                                          trailing_enable: Boolean(strat.trailing_enable),
                                          trailing_pips: strat.trailing_pips || 20.0,
                                          session_enable: Boolean(strat.session_enable),
                                          session_start: strat.session_start || '00:00',
                                          session_end: strat.session_end || '23:59',
                                          custom_params: stratCustomParams,
                                        });
                                        setShowAddStratModal(true);
                                      }}
                                      className="p-1.5 rounded-lg bg-purple-500/10 hover:bg-purple-500/25 text-purple-400 hover:text-purple-300 transition cursor-pointer border border-purple-500/20"
                                      title="Sửa cấu hình chiến lược"
                                    >
                                      <Edit3 className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteStrategy(strat.id)}
                                      className="p-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/25 text-rose-500 hover:text-rose-400 transition cursor-pointer border border-rose-500/20"
                                      title="Xóa cấu hình này"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                </div>
              )}

              {/* ==================== SUB-TAB 3: LỆNH ĐANG CHẠY REALTIME ==================== */}
              {eaSubTab === 'positions' && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                      </span>
                      <h4 className="text-sm font-bold text-slate-200">
                        {selectedEaAccount !== 'ALL' && activeAccObj
                          ? `Vị Thế Đang Mở: ${activeAccObj.name} (#${activeAccObj.login}) (${filteredPositions.length} Lệnh)`
                          : `Vị Thế Đang Mở Toàn Hệ Thống (${filteredPositions.length} Lệnh)`}
                      </h4>
                    </div>

                    {filteredPositions.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setConfirmCloseAll(true)}
                        className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-black flex items-center gap-1.5 transition cursor-pointer shadow-lg shadow-rose-600/30"
                      >
                        <AlertTriangle className="w-4 h-4" />
                        <span>🚨 Đóng Toàn Bộ Lệnh (Close All)</span>
                      </button>
                    )}
                  </div>

                  <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60 shadow-xl">
                    <table className="w-full text-left text-xs text-slate-200 border-collapse">
                      <thead>
                        <tr className="border-b border-slate-800 bg-slate-950/60 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                          <th className="px-4 py-3.5">Ticket #</th>
                          <th className="px-4 py-3.5">Tài Khoản MT5</th>
                          <th className="px-4 py-3.5">Cặp Tiền & Lệnh</th>
                          <th className="px-4 py-3.5">Khối Lượng</th>
                          <th className="px-4 py-3.5">Giá Vào (Open)</th>
                          <th className="px-4 py-3.5">Dừng Lỗ (SL)</th>
                          <th className="px-4 py-3.5">Chốt Lời (TP)</th>
                          <th className="px-4 py-3.5">Lợi Nhuận Tạm Tính</th>
                          <th className="px-4 py-3.5 text-right">Hành Động Khẩn Cấp</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/50">
                        {filteredPositions.length === 0 ? (
                          <tr>
                            <td colSpan="9" className="px-4 py-12 text-center text-slate-500">
                              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-500 opacity-40" />
                              <p className="font-semibold text-slate-400">Hiện không có lệnh nào đang chạy trên MT5.</p>
                              <p className="text-[11px] text-slate-600 mt-1">Hệ thống sẽ tự động vào lệnh và hiển thị realtime tại đây ngay khi có tín hiệu mới.</p>
                            </td>
                          </tr>
                        ) : (
                          filteredPositions.map((pos, idx) => {
                            const isBuy = pos.type === 'BUY';
                            return (
                              <tr key={pos.ticket || idx} className="hover:bg-slate-800/40 transition">
                                <td className="px-4 py-3.5 font-mono text-purple-300 font-bold">
                                  #{pos.ticket}
                                </td>
                                <td className="px-4 py-3.5">
                                  <div className="font-bold text-slate-200">{pos.account_name || 'MT5 Account'}</div>
                                  <div className="text-[11px] text-slate-500 font-mono">#{pos.account_login}</div>
                                </td>
                                <td className="px-4 py-3.5">
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-mono font-bold text-amber-300">{pos.symbol}</span>
                                    <span className={`px-2 py-0.5 rounded font-black text-[10px] ${
                                      isBuy ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                                    }`}>
                                      {pos.type}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-3.5 font-mono font-bold text-slate-200">
                                  {pos.volume} Lot
                                </td>
                                <td className="px-4 py-3.5 font-mono font-bold text-cyan-300">
                                  {Number(pos.open_price).toFixed(pos.symbol?.includes('JPY') ? 3 : 2)}
                                </td>
                                <td className="px-4 py-3.5 font-mono text-rose-400 font-bold">
                                  {pos.sl > 0 ? Number(pos.sl).toFixed(pos.symbol?.includes('JPY') ? 3 : 2) : '--'}
                                </td>
                                <td className="px-4 py-3.5 font-mono text-emerald-400 font-bold">
                                  {pos.tp > 0 ? Number(pos.tp).toFixed(pos.symbol?.includes('JPY') ? 3 : 2) : '--'}
                                </td>
                                <td className="px-4 py-3.5 font-mono font-black text-sm">
                                  <span className={Number(pos.pnl || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                                    {Number(pos.pnl || 0) >= 0 ? '+' : ''}${Number(pos.pnl || 0).toFixed(2)}
                                  </span>
                                </td>
                                <td className="px-4 py-3.5 text-right">
                                  <button
                                    onClick={() => handleClosePosition(pos.ticket, pos.account_id)}
                                    className="px-3 py-1.5 rounded-lg bg-rose-500/15 hover:bg-rose-500 text-rose-400 hover:text-white border border-rose-500/30 text-xs font-bold transition cursor-pointer"
                                  >
                                    Đóng Lệnh
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ==================== SUB-TAB 4: LỊCH SỬ LỆNH & HIỆU SUẤT ==================== */}
              {eaSubTab === 'history' && (
                <div className="space-y-4">

                  {/* Date & Period Filtering Toolbar */}
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-slate-900/95 via-purple-950/20 to-slate-900/95 border border-purple-500/20 shadow-xl space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-purple-400" />
                        <span className="text-xs font-black text-slate-200 uppercase tracking-wider">
                          Lọc Lịch Sử Theo Khoảng Thời Gian:
                        </span>
                        <span className="px-2.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30 text-[11px] font-bold">
                          {histPeriodLabel} ({filteredHistoryOrders.length} Lệnh)
                        </span>
                      </div>

                      {/* Quick Presets Pills */}
                      <div className="flex flex-wrap items-center gap-1.5">
                        {[
                          { id: 'ALL', label: '🌐 Tất Cả' },
                          { id: 'TODAY', label: '📅 Hôm Nay' },
                          { id: 'YESTERDAY', label: '⏪ Hôm Qua' },
                          { id: '7D', label: '⏱️ 7 Ngày' },
                          { id: '30D', label: '🗓️ 30 Ngày' },
                          { id: 'THIS_MONTH', label: '📆 Tháng Này' },
                          { id: 'CUSTOM', label: '⚙️ Tùy Chọn Ngày' }
                        ].map((btn) => (
                          <button
                            key={btn.id}
                            type="button"
                            onClick={() => setHistoryFilterPeriod(btn.id)}
                            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer border ${
                              historyFilterPeriod === btn.id
                                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md shadow-purple-600/30 border-purple-400'
                                : 'bg-slate-950/80 hover:bg-slate-800 text-slate-400 hover:text-slate-200 border-slate-800'
                            }`}
                          >
                            {btn.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Custom Date Pickers (Shown when CUSTOM or inputs have values) */}
                    {(historyFilterPeriod === 'CUSTOM' || historyStartDate || historyEndDate) && (
                      <div className="pt-3 border-t border-slate-800/80 flex flex-wrap items-center justify-between gap-3 bg-slate-950/40 p-3 rounded-xl">
                        <div className="flex flex-wrap items-center gap-3 text-xs">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-400 font-bold">Từ Ngày:</span>
                            <input
                              type="date"
                              value={historyStartDate}
                              onChange={(e) => {
                                setHistoryStartDate(e.target.value);
                                setHistoryFilterPeriod('CUSTOM');
                              }}
                              className="bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-slate-200 font-mono text-xs focus:outline-none focus:border-purple-400 cursor-pointer"
                            />
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-slate-400 font-bold">Đến Ngày:</span>
                            <input
                              type="date"
                              value={historyEndDate}
                              onChange={(e) => {
                                setHistoryEndDate(e.target.value);
                                setHistoryFilterPeriod('CUSTOM');
                              }}
                              className="bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-slate-200 font-mono text-xs focus:outline-none focus:border-purple-400 cursor-pointer"
                            />
                          </div>
                        </div>

                        {(historyStartDate || historyEndDate) && (
                          <button
                            type="button"
                            onClick={() => {
                              setHistoryStartDate('');
                              setHistoryEndDate('');
                              setHistoryFilterPeriod('ALL');
                            }}
                            className="px-2.5 py-1 rounded-lg bg-rose-500/15 hover:bg-rose-500/25 text-rose-400 border border-rose-500/30 text-xs font-bold transition cursor-pointer"
                          >
                            ✕ Xóa Lọc Ngày
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 5 Financial & Performance Summary Cards for the Selected Period */}
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    
                    {/* Card 1: Tổng Lệnh & Win Rate */}
                    <div className="p-3.5 rounded-2xl bg-gradient-to-br from-purple-950/40 to-slate-900/90 border border-purple-500/20 shadow-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-purple-300 uppercase tracking-wider block">
                          Tổng Lệnh Đã Đóng
                        </span>
                        <BarChart2 className="w-4 h-4 text-purple-400" />
                      </div>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-xl font-black text-slate-100 font-mono">
                          {historyClosedOrders.length}
                        </span>
                        <span className="text-xs font-black text-purple-300 font-mono">
                          (WR: {historyWinRate}%)
                        </span>
                      </div>
                      <div className="text-[10.5px] text-slate-400 font-mono mt-1 flex items-center gap-1.5">
                        <span className="text-emerald-400 font-bold">🟢 {historyWinOrders.length} Thắng</span>
                        <span className="text-slate-600">/</span>
                        <span className="text-rose-400 font-bold">🔴 {historyLossOrders.length} Thua</span>
                      </div>
                    </div>

                    {/* Card 2: Tổng Tiền Thắng (Gross Profit) */}
                    <div className="p-3.5 rounded-2xl bg-gradient-to-br from-emerald-950/40 to-slate-900/90 border border-emerald-500/20 shadow-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider block">
                          Tổng Tiền Ăn (Gross Win)
                        </span>
                        <TrendingUp className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div className="text-xl font-black text-emerald-400 font-mono mt-1">
                        +${historyGrossProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono mt-1 block truncate">
                        Max Win: <b className="text-emerald-300">+${historyMaxWin.toFixed(2)}</b>
                      </span>
                    </div>

                    {/* Card 3: Tổng Tiền Thua (Gross Loss) */}
                    <div className="p-3.5 rounded-2xl bg-gradient-to-br from-rose-950/40 to-slate-900/90 border border-rose-500/20 shadow-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-rose-400 uppercase tracking-wider block">
                          Tổng Tiền Thua (Gross Loss)
                        </span>
                        <TrendingDown className="w-4 h-4 text-rose-400" />
                      </div>
                      <div className="text-xl font-black text-rose-400 font-mono mt-1">
                        -${historyGrossLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono mt-1 block truncate">
                        Max Loss: <b className="text-rose-300">-${Math.abs(historyMaxLoss).toFixed(2)}</b>
                      </span>
                    </div>

                    {/* Card 4: Lợi Nhuận Ròng Thực Tế (Net P&L) */}
                    <div className={`p-3.5 rounded-2xl bg-gradient-to-br ${
                      historyNetPnl >= 0 ? 'from-emerald-950/50 border-emerald-500/30' : 'from-rose-950/50 border-rose-500/30'
                    } to-slate-900/90 border shadow-lg`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-[10px] font-bold uppercase tracking-wider block ${
                          historyNetPnl >= 0 ? 'text-emerald-300' : 'text-rose-300'
                        }`}>
                          Lãi / Lỗ Ròng Thực Tế
                        </span>
                        <DollarSign className={`w-4 h-4 ${historyNetPnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} />
                      </div>
                      <div className={`text-xl font-black font-mono mt-1 ${
                        historyNetPnl >= 0 ? 'text-emerald-400' : 'text-rose-400'
                      }`}>
                        {historyNetPnl >= 0 ? '+' : ''}${historyNetPnl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono mt-1 flex items-center justify-between">
                        <span>Profit Factor (PF):</span>
                        <span className="text-amber-300 font-bold">{historyProfitFactor}</span>
                      </div>
                    </div>

                    {/* Card 5: Vị Thế Đang Mở & Hàng Đợi */}
                    <div className="p-3.5 rounded-2xl bg-gradient-to-br from-amber-950/30 to-slate-900/90 border border-amber-500/20 shadow-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-amber-300 uppercase tracking-wider block">
                          Trạng Thái Hiện Tại
                        </span>
                        <Activity className="w-4 h-4 text-amber-400" />
                      </div>
                      <div className="text-xs font-mono font-bold text-slate-200 mt-1 space-y-0.5">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-400">Đang chạy (OPEN):</span>
                          <span className="text-emerald-400 font-bold">{filteredOrders.filter(o => o.status === 'OPEN').length}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-400">Hàng đợi (PENDING):</span>
                          <span className="text-amber-400 font-bold">{filteredOrders.filter(o => o.status === 'PENDING').length}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Filter Toolbar (Account, Symbol, Status) */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
                    <div>
                      <h4 className="text-sm font-bold text-slate-200 flex items-center gap-2">
                        <span>Lịch Sử Khớp Lệnh Chi Tiết</span>
                        <span className="text-[11px] font-normal text-slate-400 font-mono">
                          (Hiển thị {filteredHistoryOrders.length} / {eaOrders.length} lệnh)
                        </span>
                      </h4>
                      <p className="text-[11px] text-slate-400">
                        {selectedEaAccount !== 'ALL' && activeAccObj
                          ? `Đang xem lịch sử của tài khoản: ${activeAccObj.name} (#${activeAccObj.login})`
                          : 'Xem lịch sử toàn bộ các tài khoản MT5'}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={selectedEaAccount}
                        onChange={(e) => setSelectedEaAccount(e.target.value)}
                        className="text-xs bg-slate-900 border border-slate-700 rounded-xl px-2.5 py-1.5 text-slate-200 font-bold focus:outline-none cursor-pointer"
                      >
                        <option value="ALL">Tài Khoản: Tất Cả ({eaAccounts.length})</option>
                        {eaAccounts.map(a => (
                          <option key={a.id} value={a.id}>{a.name} ({a.login})</option>
                        ))}
                      </select>

                      <select
                        value={historyFilterSym}
                        onChange={(e) => setHistoryFilterSym(e.target.value)}
                        className="text-xs bg-slate-900 border border-slate-700 rounded-xl px-2.5 py-1.5 text-slate-200 font-bold focus:outline-none cursor-pointer"
                      >
                        <option value="ALL">Cặp Tiền: Tất Cả</option>
                        <option value="XAUUSD">XAUUSD</option>
                        <option value="BTCUSD">BTCUSD</option>
                        <option value="EURUSD">EURUSD</option>
                        <option value="GBPUSD">GBPUSD</option>
                        <option value="USDJPY">USDJPY</option>
                      </select>

                      <select
                        value={historyFilterStatus}
                        onChange={(e) => setHistoryFilterStatus(e.target.value)}
                        className="text-xs bg-slate-900 border border-slate-700 rounded-xl px-2.5 py-1.5 text-slate-200 font-bold focus:outline-none cursor-pointer"
                      >
                        <option value="ALL">Trạng Thái: Tất Cả</option>
                        <option value="CLOSED">Đã Đóng (CLOSED)</option>
                        <option value="OPEN">Đang Chạy (OPEN)</option>
                        <option value="PENDING">Hàng Đợi (PENDING)</option>
                        <option value="REJECTED">Từ Chối (REJECTED)</option>
                      </select>
                    </div>
                  </div>

                  {/* Orders Table */}
                  <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60 shadow-xl">
                    <table className="w-full text-left text-xs text-slate-200 border-collapse">
                      <thead>
                        <tr className="border-b border-slate-800 bg-slate-950/60 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                          <th className="px-4 py-3.5">Ticket # / ID</th>
                          <th className="px-4 py-3.5">Tài Khoản</th>
                          <th className="px-4 py-3.5">Cặp Tiền & Lệnh</th>
                          <th className="px-4 py-3.5">Khối Lượng</th>
                          <th className="px-4 py-3.5">Giá Mở / Giá Đóng</th>
                          <th className="px-4 py-3.5">Thời Gian Mở / Đóng</th>
                          <th className="px-4 py-3.5">Lợi Nhuận Thực Tế</th>
                          <th className="px-4 py-3.5 text-right">Trạng Thái</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/50">
                        {filteredHistoryOrders.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="px-4 py-12 text-center text-slate-500">
                              <BarChart2 className="w-8 h-8 mx-auto mb-2 text-slate-600 opacity-40" />
                              <p className="font-semibold">Chưa có lịch sử lệnh nào trong khoảng thời gian đã chọn.</p>
                              <p className="text-[11px] text-slate-600 mt-1">Hãy thử chọn mốc thời gian "Tất Cả" hoặc khoảng ngày khác.</p>
                            </td>
                          </tr>
                        ) : (
                          filteredHistoryOrders.map((ord, idx) => {
                            const isBuy = ord.action === 'BUY';
                            const pnlNum = Number(ord.pnl || 0);
                            const isWin = pnlNum > 0;
                            const isLoss = pnlNum < 0;

                            const openDateStr = ord.open_time || ord.created_at
                              ? new Date((ord.open_time || ord.created_at) * 1000).toLocaleString('vi-VN', {
                                  day: '2-digit', month: '2-digit', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit', second: '2-digit'
                                })
                              : '--';

                            const closeDateStr = ord.close_time
                              ? new Date(ord.close_time * 1000).toLocaleString('vi-VN', {
                                  day: '2-digit', month: '2-digit', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit', second: '2-digit'
                                })
                              : '';

                            return (
                              <tr key={ord.id || ord.order_ticket || idx} className="hover:bg-slate-800/40 transition">
                                <td className="px-4 py-3.5 font-mono">
                                  <div className="font-bold text-purple-300">
                                    {ord.order_ticket > 0 ? `#${ord.order_ticket}` : ord.id}
                                  </div>
                                  <div className="text-[10px] text-slate-500 truncate max-w-[120px]" title={ord.comment || ord.signal_id}>
                                    {ord.comment || ord.signal_id || 'Signal'}
                                  </div>
                                </td>
                                <td className="px-4 py-3.5">
                                  <div className="font-bold text-slate-200">{ord.account_name || 'MT5 Account'}</div>
                                  <div className="text-[11px] text-slate-500 font-mono">#{ord.account_login}</div>
                                </td>
                                <td className="px-4 py-3.5">
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-mono font-bold text-amber-300">{ord.symbol}</span>
                                    <span className={`px-2 py-0.5 rounded font-black text-[10px] ${
                                      isBuy ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                                    }`}>
                                      {ord.action}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-3.5 font-mono font-bold text-slate-200">
                                  {ord.volume} Lot
                                </td>
                                <td className="px-4 py-3.5 font-mono text-[11px]">
                                  <div>Vào: <span className="text-cyan-300 font-bold">{ord.open_price > 0 ? ord.open_price : '--'}</span></div>
                                  <div>Đóng: <span className="text-slate-300 font-bold">{ord.close_price > 0 ? ord.close_price : '--'}</span></div>
                                </td>
                                <td className="px-4 py-3.5 font-mono text-[10.5px] text-slate-300">
                                  <div className="text-slate-300">Vào: {openDateStr}</div>
                                  {closeDateStr && <div className="text-slate-400">Đóng: {closeDateStr}</div>}
                                </td>
                                <td className="px-4 py-3.5 font-mono font-bold">
                                  {ord.status === 'CLOSED' ? (
                                    <div className="flex items-center gap-1.5">
                                      <span className={`text-sm font-black ${isWin ? 'text-emerald-400' : isLoss ? 'text-rose-400' : 'text-slate-400'}`}>
                                        {pnlNum > 0 ? '+' : ''}${pnlNum.toFixed(2)}
                                      </span>
                                      <span className={`px-1.5 py-0.2 rounded text-[9px] font-black ${
                                        isWin ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                                        isLoss ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' :
                                        'bg-slate-700 text-slate-400'
                                      }`}>
                                        {isWin ? 'WIN' : isLoss ? 'LOSS' : 'BE'}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-slate-500 text-xs">--</span>
                                  )}
                                </td>
                                <td className="px-4 py-3.5 text-right">
                                  <span className={`px-2.5 py-1 rounded-lg text-[10.5px] font-bold ${
                                    ord.status === 'CLOSED' ? 'bg-slate-800 text-slate-300' :
                                    ord.status === 'OPEN' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                                    ord.status === 'PENDING' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                                    'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                                  }`}>
                                    {ord.status}
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ==================== SUB-TAB 5: SỔ CÁI NẠP/RÚT & CHIA LỢI NHUẬN ==================== */}
              {eaSubTab === 'ledger' && (
                <div className="space-y-6">
                  {/* Top Financial Ledger Stat Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3.5">
                    <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wider">Tổng Tiền Nạp (Ledger)</span>
                        <TrendingUp className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div className="text-xl font-black text-emerald-400 mt-1">
                        ${displayEaStats.deposit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <span className="text-[10px] text-slate-500 font-mono">
                        {displayEaStats.isAll ? `${eaStats.deposit_transactions_count || 0} lần nạp` : 'Tài khoản đang chọn'}
                      </span>
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wider">Tổng Tiền Rút (Ledger)</span>
                        <TrendingDown className="w-4 h-4 text-rose-400" />
                      </div>
                      <div className="text-xl font-black text-rose-400 mt-1">
                        ${displayEaStats.withdraw.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <span className="text-[10px] text-slate-500 font-mono">
                        {displayEaStats.isAll ? `${eaStats.withdraw_transactions_count || 0} lần rút` : 'Tài khoản đang chọn'}
                      </span>
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wider">Lãi Ròng Thực Tế</span>
                        <DollarSign className="w-4 h-4 text-cyan-400" />
                      </div>
                      <div className={`text-xl font-black ${displayEaStats.realizedProfit >= 0 ? 'text-emerald-400' : 'text-rose-400'} mt-1`}>
                        {displayEaStats.realizedProfit >= 0 ? '+' : ''}${displayEaStats.realizedProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <span className="text-[10px] text-slate-500 font-mono">
                        {displayEaStats.isAll ? 'Tất cả tài khoản' : 'Tài khoản đang chọn'}
                      </span>
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wider">Hoa Hồng Đã Thu</span>
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div className="text-xl font-black text-emerald-400 mt-1">
                        ${Number(eaStats.commission_paid || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <span className="text-[10px] text-slate-500 font-mono">{eaStats.paid_settlements || 0} đợt đã thanh toán</span>
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wider">Hoa Hồng Chờ Thu</span>
                        <AlertCircle className="w-4 h-4 text-amber-400" />
                      </div>
                      <div className="text-xl font-black text-amber-400 mt-1">
                        ${Number(eaStats.commission_pending || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <span className="text-[10px] text-slate-500 font-mono">{eaStats.pending_settlements || 0} đợt chờ thanh toán</span>
                    </div>
                  </div>

                  {/* Section 1: Quản lý Phân Chia Lợi Nhuận Từng Khách Hàng */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-bold text-slate-100 flex items-center gap-2">
                          <Crown className="w-4 h-4 text-amber-400" />
                          <span>1. Danh Sách Khách Hàng & Đối Soát Chia Lợi Nhuận</span>
                        </h4>
                        <p className="text-[11px] text-slate-400">Tự động tính lợi nhuận ròng chưa đối soát và số tiền hoa hồng cần thu theo % đã cấu hình.</p>
                      </div>
                    </div>

                    <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60 shadow-xl">
                      <table className="w-full text-left text-xs text-slate-200 border-collapse">
                        <thead>
                          <tr className="border-b border-slate-800 bg-slate-950/60 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                            <th className="px-4 py-3.5">Khách Hàng & MT5</th>
                            <th className="px-4 py-3.5">Ghi Chú Khách</th>
                            <th className="px-4 py-3.5">% Chia Sẻ</th>
                            <th className="px-4 py-3.5">Lãi Thực Tế (Realized)</th>
                            <th className="px-4 py-3.5">Lãi Đã Chốt Kỳ Trước</th>
                            <th className="px-4 py-3.5 text-amber-300">Lãi Mới Chưa Đối Soát</th>
                            <th className="px-4 py-3.5 text-emerald-400">Tiền Hoa Hồng Cần Thu</th>
                            <th className="px-4 py-3.5 text-right">Thao Tác</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/50">
                          {eaAccounts
                            .filter(a => selectedEaAccount === 'ALL' || a.id === selectedEaAccount)
                            .length === 0 ? (
                            <tr>
                              <td colSpan="8" className="px-4 py-8 text-center text-slate-500">Chưa có tài khoản khách hàng nào.</td>
                            </tr>
                          ) : (
                            eaAccounts
                              .filter(a => selectedEaAccount === 'ALL' || a.id === selectedEaAccount)
                              .map(acc => {
                                const totRealized = Number(acc.realized_profit || 0);
                                const lastSettled = Number(acc.last_settled_profit || 0);
                                const unsettledProfit = Math.max(0, totRealized - lastSettled);
                                const pct = Number(acc.profit_share_pct || 30.0);
                                const commissionDue = roundDec(unsettledProfit * pct / 100.0, 2);

                                function roundDec(val, d) {
                                  return Number(Math.round(val + 'e' + d) + 'e-' + d);
                                }

                                return (
                                  <tr key={acc.id} className="hover:bg-slate-800/40 transition">
                                    <td className="px-4 py-3.5">
                                      <div className="font-bold text-slate-100">{acc.name || 'MT5 Client'}</div>
                                      <div className="text-[11px] text-slate-400 font-mono">
                                        Login: <span className="text-amber-300 font-bold">{acc.login}</span> {acc.server ? `| ${acc.server}` : ''}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3.5 text-slate-300 text-[11px]">
                                      {acc.client_notes ? (
                                        <span className="px-2 py-1 rounded bg-slate-950 border border-slate-800 text-slate-300">{acc.client_notes}</span>
                                      ) : (
                                        <span className="text-slate-600 italic">Chưa có ghi chú</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3.5 font-mono font-bold text-purple-300">
                                      <span className="px-2 py-0.5 rounded bg-purple-500/15 border border-purple-500/30">
                                        {pct}%
                                      </span>
                                    </td>
                                    <td className="px-4 py-3.5 font-mono font-bold">
                                      <span className={totRealized >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                                        {totRealized >= 0 ? '+' : ''}${totRealized.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3.5 font-mono text-slate-400">
                                      ${lastSettled.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-4 py-3.5 font-mono font-black text-amber-300">
                                      ${unsettledProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-4 py-3.5 font-mono font-black text-emerald-400 text-sm">
                                      ${commissionDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-4 py-3.5 text-right space-x-2">
                                      <button
                                        type="button"
                                        onClick={() => handleOpenProfitShareModal(acc)}
                                        className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] font-bold border border-slate-700 transition cursor-pointer"
                                        title="Cài đặt % chia sẻ & thông tin khách"
                                      >
                                        Cài %
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleOpenSettleModal(acc)}
                                        disabled={unsettledProfit <= 0}
                                        className={`px-3 py-1 rounded-lg text-[11px] font-bold transition cursor-pointer ${
                                          unsettledProfit > 0
                                            ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-md shadow-emerald-600/20'
                                            : 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'
                                        }`}
                                      >
                                        Chốt Phí Đợt Này
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Section 2: Sổ Cái Chi Tiết Từng Lần Nạp / Rút Tiền */}
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h4 className="text-sm font-bold text-slate-100 flex items-center gap-2">
                          <Database className="w-4 h-4 text-cyan-400" />
                          <span>2. Sổ Cái Giao Dịch Nạp / Rút (Transaction Ledger)</span>
                        </h4>
                        <p className="text-[11px] text-slate-400">Toàn bộ các giao dịch Nạp và Rút tiền thực tế được EA tự động đồng bộ từ MT5 với mã Deal Ticket duy nhất.</p>
                      </div>

                      <div className="flex items-center gap-2">
                        <select
                          value={ledgerFilterAcc}
                          onChange={e => setLedgerFilterAcc(e.target.value)}
                          className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-1.5 text-xs text-slate-200 focus:outline-none"
                        >
                          <option value="ALL">-- Tất Cả Tài Khoản --</option>
                          {eaAccounts.map(a => (
                            <option key={a.id} value={a.id}>{a.name} ({a.login})</option>
                          ))}
                        </select>

                        <select
                          value={ledgerFilterType}
                          onChange={e => setLedgerFilterType(e.target.value)}
                          className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-1.5 text-xs text-slate-200 focus:outline-none"
                        >
                          <option value="ALL">-- Tất Cả Loại --</option>
                          <option value="DEPOSIT">🟢 Nạp Tiền (Deposit)</option>
                          <option value="WITHDRAWAL">🔴 Rút Tiền (Withdrawal)</option>
                        </select>
                      </div>
                    </div>

                    <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60 shadow-xl max-h-[380px] overflow-y-auto">
                      <table className="w-full text-left text-xs text-slate-200 border-collapse">
                        <thead className="sticky top-0 z-10">
                          <tr className="border-b border-slate-800 bg-slate-950 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                            <th className="px-4 py-3">Thời Gian (GMT+7)</th>
                            <th className="px-4 py-3">Tài Khoản & Server</th>
                            <th className="px-4 py-3">Loại Giao Dịch</th>
                            <th className="px-4 py-3">Số Tiền ($ USD)</th>
                            <th className="px-4 py-3">Deal Ticket MT5</th>
                            <th className="px-4 py-3">Ghi Chú Giao Dịch</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/50">
                          {filteredTransactions.length === 0 ? (
                            <tr>
                              <td colSpan="6" className="px-4 py-8 text-center text-slate-500">
                                Chưa ghi nhận giao dịch Nạp / Rút nào. EA sẽ tự động đồng bộ khi kết nối MT5.
                              </td>
                            </tr>
                          ) : (
                            filteredTransactions
                              .filter(tx => (ledgerFilterType === 'ALL' || tx.type === ledgerFilterType))
                              .map(tx => {
                                const isDep = tx.type === 'DEPOSIT';
                                return (
                                  <tr key={tx.id || tx.deal_ticket} className="hover:bg-slate-800/40 transition font-mono">
                                    <td className="px-4 py-3 text-slate-300">
                                      {tx.timestamp ? new Date(tx.timestamp * 1000).toLocaleString('vi-VN') : '--'}
                                    </td>
                                    <td className="px-4 py-3 font-sans">
                                      <span className="font-bold text-slate-100">{tx.account_name || 'MT5'}</span>
                                      <span className="text-[11px] text-slate-400 font-mono ml-1.5">#{tx.account_login}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className={`px-2.5 py-0.5 rounded-md font-bold text-[10.5px] ${
                                        isDep ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                                      }`}>
                                        {isDep ? '🟢 NẠP TIỀN' : '🔴 RÚT TIỀN'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 font-bold text-sm">
                                      <span className={isDep ? 'text-emerald-400' : 'text-rose-400'}>
                                        {isDep ? '+' : '-'}${Number(tx.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-purple-300 font-bold">
                                      #{tx.deal_ticket}
                                    </td>
                                    <td className="px-4 py-3 text-slate-400 font-sans text-[11px]">
                                      {tx.comment || '--'}
                                    </td>
                                  </tr>
                                );
                              })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Section 3: Lịch Sử Các Đợt Đối Soát & Thu Tiền */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-bold text-slate-100 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-400" />
                          <span>3. Lịch Sử Các Đợt Đối Soát & Thu Phí Chia Lợi Nhuận</span>
                        </h4>
                        <p className="text-[11px] text-slate-400">Theo dõi công nợ, trạng thái thanh toán và lưu trữ hóa đơn phân chia lợi nhuận.</p>
                      </div>
                    </div>

                    <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60 shadow-xl">
                      <table className="w-full text-left text-xs text-slate-200 border-collapse">
                        <thead>
                          <tr className="border-b border-slate-800 bg-slate-950/60 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                            <th className="px-4 py-3.5">Mã Đợt & Tài Khoản</th>
                            <th className="px-4 py-3.5">Kỳ Đối Soát</th>
                            <th className="px-4 py-3.5">Lợi Nhuận Kỳ ($)</th>
                            <th className="px-4 py-3.5">% Phí</th>
                            <th className="px-4 py-3.5">Hoa Hồng Phải Thu ($)</th>
                            <th className="px-4 py-3.5">Trạng Thái</th>
                            <th className="px-4 py-3.5">Ghi Chú</th>
                            <th className="px-4 py-3.5 text-right">Thao Tác</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/50">
                          {eaSettlements
                            .filter(s => selectedEaAccount === 'ALL' || s.account_id === selectedEaAccount)
                            .length === 0 ? (
                            <tr>
                              <td colSpan="8" className="px-4 py-8 text-center text-slate-500">
                                Chưa có đợt đối soát nào được tạo. Bấm "Chốt Phí Đợt Này" ở Bảng 1 để tạo đợt đối soát.
                              </td>
                            </tr>
                          ) : (
                            eaSettlements
                              .filter(s => selectedEaAccount === 'ALL' || s.account_id === selectedEaAccount)
                              .map(stl => {
                                const isPaid = stl.status === 'PAID';
                                return (
                                  <tr key={stl.id} className="hover:bg-slate-800/40 transition">
                                    <td className="px-4 py-3.5">
                                      <div className="font-bold text-slate-100">{stl.account_name || 'MT5 Client'}</div>
                                      <div className="text-[10px] text-slate-500 font-mono">{stl.id}</div>
                                    </td>
                                    <td className="px-4 py-3.5 text-slate-300 font-mono text-[11px]">
                                      {stl.period_start ? new Date(stl.period_start * 1000).toLocaleDateString('vi-VN') : '--'} ➔ {stl.period_end ? new Date(stl.period_end * 1000).toLocaleDateString('vi-VN') : '--'}
                                    </td>
                                    <td className="px-4 py-3.5 font-mono font-bold text-cyan-400">
                                      ${Number(stl.period_profit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-4 py-3.5 font-mono font-bold text-purple-300">
                                      {stl.profit_share_pct}%
                                    </td>
                                    <td className="px-4 py-3.5 font-mono font-black text-emerald-400 text-sm">
                                      ${Number(stl.commission_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-4 py-3.5">
                                      <span className={`px-2.5 py-1 rounded-lg text-[10.5px] font-bold ${
                                        isPaid ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                      }`}>
                                        {isPaid ? '🟢 ĐÃ THANH TOÁN' : '🟡 CHỜ THANH TOÁN'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3.5 text-slate-400 text-[11px]">
                                      {stl.payment_note || '--'}
                                    </td>
                                    <td className="px-4 py-3.5 text-right space-x-1.5">
                                      {!isPaid && (
                                        <button
                                          type="button"
                                          onClick={() => handleSettlePaymentStatus(stl.id, 'PAID')}
                                          className="px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-[11px] shadow-sm transition cursor-pointer"
                                        >
                                          Đã Nhận Tiền
                                        </button>
                                      )}
                                      {isPaid && (
                                        <button
                                          type="button"
                                          onClick={() => handleSettlePaymentStatus(stl.id, 'PENDING')}
                                          className="px-2 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 text-[10px] transition cursor-pointer"
                                          title="Chuyển về trạng thái chờ"
                                        >
                                          Hoàn tác
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* MODAL 1: THÊM / SỬA TÀI KHOẢN MT5 */}
              {showAddAccModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
                  <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/60">
                      <h3 className="font-extrabold text-sm text-slate-100 flex items-center gap-2">
                        <Cpu className="w-4 h-4 text-purple-400" />
                        <span>{editingAcc ? 'Cập Nhật Tài Khoản MT5' : 'Thêm Tài Khoản MT5 Mới'}</span>
                      </h3>
                      <button onClick={() => setShowAddAccModal(false)} className="text-slate-500 hover:text-slate-300 cursor-pointer">
                        ✕
                      </button>
                    </div>

                    <form onSubmit={handleSaveAccount} className="p-5 space-y-4">
                      <div>
                        <label className="text-xs font-bold text-slate-300 block mb-1">Tên Mô Tả / Khách Hàng</label>
                        <input
                          type="text"
                          value={accForm.name}
                          onChange={e => setAccForm({ ...accForm, name: e.target.value })}
                          placeholder="Ví dụ: Anh Nam VIP, Exness Real 01..."
                          className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-100 focus:outline-none focus:border-purple-500"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-bold text-slate-300 block mb-1">Mã Login MT5 *</label>
                          <input
                            type="text"
                            required
                            value={accForm.login}
                            onChange={e => setAccForm({ ...accForm, login: e.target.value })}
                            placeholder="Ví dụ: 416296152"
                            className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-100 font-mono focus:outline-none focus:border-purple-500"
                          />
                        </div>

                        <div>
                          <label className="text-xs font-bold text-slate-300 block mb-1">Server MT5</label>
                          <input
                            type="text"
                            value={accForm.server}
                            onChange={e => setAccForm({ ...accForm, server: e.target.value })}
                            placeholder="Ví dụ: Exness-MT5Real9"
                            className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-100 focus:outline-none focus:border-purple-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-bold text-slate-300 block mb-1">Magic Number</label>
                          <input
                            type="number"
                            value={accForm.magic_number}
                            onChange={e => setAccForm({ ...accForm, magic_number: parseInt(e.target.value) || 888888 })}
                            placeholder="888888"
                            className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-100 font-mono focus:outline-none focus:border-purple-500"
                          />
                        </div>

                        <div>
                          <label className="text-xs font-bold text-purple-300 block mb-1">% Chia Sẻ Lợi Nhuận</label>
                          <input
                            type="number"
                            step="0.1"
                            value={accForm.profit_share_pct}
                            onChange={e => setAccForm({ ...accForm, profit_share_pct: parseFloat(e.target.value) || 0 })}
                            placeholder="30"
                            className="w-full bg-slate-950 border border-purple-500/50 rounded-xl px-3 py-2 text-xs text-purple-200 font-mono font-bold focus:outline-none focus:border-purple-400"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-slate-300 block mb-1">Ghi Chú Khách Hàng (SĐT, Telegram, v.v.)</label>
                        <input
                          type="text"
                          value={accForm.client_notes}
                          onChange={e => setAccForm({ ...accForm, client_notes: e.target.value })}
                          placeholder="Ví dụ: Telegram @john, SĐT 0909..., Vốn 5000$"
                          className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-100 focus:outline-none focus:border-purple-500"
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 rounded-xl bg-slate-950/60 border border-slate-800">
                        <div>
                          <span className="text-xs font-bold text-slate-200 block">Kích Hoạt Tự Động Giao Dịch</span>
                          <span className="text-[10px] text-slate-500">Cho phép EA nhận tín hiệu và mở lệnh.</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={accForm.is_active}
                          onChange={e => setAccForm({ ...accForm, is_active: e.target.checked })}
                          className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
                        />
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
                        <button
                          type="button"
                          onClick={() => setShowAddAccModal(false)}
                          className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition cursor-pointer"
                        >
                          Hủy
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition cursor-pointer shadow-lg shadow-purple-600/20"
                        >
                          {editingAcc ? 'Lưu Thay Đổi' : 'Tạo Tài Khoản & Cấp API Key'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* MODAL 4: TẠO ĐỢT ĐỐI SOÁT & CHỐT HOA HỒNG */}
              {showSettleModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
                  <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/60">
                      <h3 className="font-extrabold text-sm text-slate-100 flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-emerald-400" />
                        <span>Tạo Đợt Đối Soát Chia Lợi Nhuận</span>
                      </h3>
                      <button onClick={() => setShowSettleModal(false)} className="text-slate-500 hover:text-slate-300 cursor-pointer">
                        ✕
                      </button>
                    </div>

                    <form onSubmit={handleSaveSettlement} className="p-5 space-y-4">
                      <div>
                        <span className="text-xs text-slate-400 block">Tài khoản đối soát:</span>
                        <span className="text-sm font-black text-slate-100">{settleForm.account_name}</span>
                      </div>

                      <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 space-y-2">
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-slate-400">Lợi nhuận ròng đợt này:</span>
                          <span className="font-mono font-bold text-amber-300">${Number(settleForm.period_profit).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-slate-400">Tỉ lệ chia sẻ lợi nhuận:</span>
                          <span className="font-mono font-bold text-purple-300">{settleForm.profit_share_pct}%</span>
                        </div>
                        <div className="border-t border-slate-800 pt-2 flex justify-between items-center">
                          <span className="text-xs font-bold text-slate-200">Hoa hồng cần thu:</span>
                          <span className="font-mono font-black text-emerald-400 text-base">
                            ${Number(settleForm.period_profit * settleForm.profit_share_pct / 100).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-slate-300 block mb-1">Ghi Chú Đợt Đối Soát</label>
                        <input
                          type="text"
                          value={settleForm.payment_note}
                          onChange={e => setSettleForm({ ...settleForm, payment_note: e.target.value })}
                          className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-100 focus:outline-none focus:border-emerald-500"
                        />
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
                        <button
                          type="button"
                          onClick={() => setShowSettleModal(false)}
                          className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition cursor-pointer"
                        >
                          Hủy
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-bold transition cursor-pointer shadow-lg shadow-emerald-600/20"
                        >
                          Xác Nhận Tạo Đợt Đối Soát
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* MODAL 5: CẬP NHẬT CẤU HÌNH % CHIA SẺ LỢI NHUẬN */}
              {showProfitShareModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
                  <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/60">
                      <h3 className="font-extrabold text-sm text-slate-100 flex items-center gap-2">
                        <Crown className="w-4 h-4 text-amber-400" />
                        <span>Cài Đặt % Chia Sẻ Lợi Nhuận Khách Hàng</span>
                      </h3>
                      <button onClick={() => setShowProfitShareModal(false)} className="text-slate-500 hover:text-slate-300 cursor-pointer">
                        ✕
                      </button>
                    </div>

                    <form onSubmit={handleSaveProfitShareConfig} className="p-5 space-y-4">
                      <div>
                        <span className="text-xs text-slate-400 block">Tài khoản:</span>
                        <span className="text-sm font-black text-slate-100">{profitShareForm.account_name}</span>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-slate-300 block mb-1">Tỉ Lệ Chia Sẻ Lợi Nhuận (%)</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="0.5"
                            required
                            value={profitShareForm.profit_share_pct}
                            onChange={e => setProfitShareForm({ ...profitShareForm, profit_share_pct: parseFloat(e.target.value) || 0 })}
                            className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-xs text-purple-300 font-mono font-bold focus:outline-none focus:border-purple-500"
                          />
                          <span className="text-xs text-slate-400 font-bold">%</span>
                        </div>
                        <span className="text-[10.5px] text-slate-500 mt-1 block">Ví dụ: 25% hoặc 30% lợi nhuận thuộc về người quản lý/chủ bot.</span>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-slate-300 block mb-1">Ghi Chú Khách Hàng</label>
                        <textarea
                          rows="2"
                          value={profitShareForm.client_notes}
                          onChange={e => setProfitShareForm({ ...profitShareForm, client_notes: e.target.value })}
                          placeholder="Thông tin liên hệ, Telegram, tài khoản nhận tiền..."
                          className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-100 focus:outline-none focus:border-purple-500"
                        />
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
                        <button
                          type="button"
                          onClick={() => setShowProfitShareModal(false)}
                          className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition cursor-pointer"
                        >
                          Hủy
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition cursor-pointer shadow-lg shadow-purple-600/20"
                        >
                          Lưu Cấu Hình
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* MODAL 2: THÊM / SỬA CẤU HÌNH CHIẾN LƯỢC */}
              {showAddStratModal && (() => {
                const currentStratKey = stratForm.strategy || 'hma';
                const p = stratForm.custom_params || getDefaultStrategyParams(currentStratKey);
                const setP = (key, val) => {
                  setStratForm(prev => ({
                    ...prev,
                    custom_params: {
                      ...(prev.custom_params || getDefaultStrategyParams(prev.strategy)),
                      [key]: val
                    }
                  }));
                };

                return (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
                  <div className="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
                    <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/80 shrink-0">
                      <div className="flex items-center gap-2.5">
                        <div className="p-2 rounded-xl bg-purple-500/20 text-purple-400 border border-purple-500/30">
                          <Sliders className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                          <h3 className="font-extrabold text-sm text-slate-100 flex items-center gap-2">
                            <span>{editingStrat ? 'Cập Nhật Cấu Hình Hệ Thống EA' : 'Thêm Cấu Hình Hệ Thống Giao Dịch EA'}</span>
                          </h3>
                          <p className="text-[11px] text-slate-400">Cấu hình chi tiết bộ lọc, tín hiệu và quản trị rủi ro khớp lệnh MT5</p>
                        </div>
                      </div>
                      <button onClick={() => setShowAddStratModal(false)} className="text-slate-500 hover:text-slate-300 p-1.5 rounded-lg hover:bg-slate-800 transition cursor-pointer">
                        ✕
                      </button>
                    </div>

                    <form onSubmit={handleSaveStrategy} className="p-5 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
                      {/* 1. Account & Strategy Core */}
                      <div className="p-3.5 rounded-xl bg-slate-950/80 border border-slate-800/90 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-bold text-purple-400 uppercase tracking-wider block">
                            1. Phân Bổ Tài Khoản & Chiến Lược
                          </span>
                          <span className="text-[10px] text-slate-500 font-mono">Core Settings</span>
                        </div>

                        <div>
                          <label className="text-xs font-bold text-slate-300 block mb-1">Chọn Tài Khoản MT5 Áp Dụng *</label>
                          <select
                            required
                            value={stratForm.account_id}
                            onChange={e => setStratForm({ ...stratForm, account_id: e.target.value })}
                            className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-100 font-bold focus:outline-none focus:border-purple-500 cursor-pointer"
                          >
                            {eaAccounts.map(a => (
                              <option key={a.id} value={a.id}>{a.name} (Login: {a.login})</option>
                            ))}
                          </select>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="text-xs font-bold text-slate-300 block mb-1">Hệ Thống Giao Dịch</label>
                            <select
                              value={stratForm.strategy}
                              onChange={e => {
                                const newStrat = e.target.value;
                                setStratForm({
                                  ...stratForm,
                                  strategy: newStrat,
                                  custom_params: getDefaultStrategyParams(newStrat)
                                });
                              }}
                              className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-cyan-300 font-bold focus:outline-none focus:border-purple-500 cursor-pointer"
                            >
                              <option value="matrix">🔮 Matrix Strategy Pro</option>
                              <option value="hma">⚡ HMA Breakout Pro</option>
                              <option value="smc">🏛️ SMC MSB Dynamic</option>
                              <option value="bob_volman">🎯 Bob Volman Scalping</option>
                              <option value="orb">🌅 ORB Session Pro</option>
                              <option value="liquidity_sweep">💧 Liquidity Sweep Pro</option>
                            </select>
                          </div>

                          <div>
                            <label className="text-xs font-bold text-slate-300 block mb-1">Cặp Tiền Áp Dụng</label>
                            <select
                              value={stratForm.symbol}
                              onChange={e => setStratForm({ ...stratForm, symbol: e.target.value })}
                              className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-amber-300 font-bold focus:outline-none focus:border-purple-500 cursor-pointer"
                            >
                              <option value="XAUUSD">XAUUSD (Vàng)</option>
                              <option value="BTCUSD">BTCUSD (Bitcoin)</option>
                              <option value="EURUSD">EURUSD</option>
                              <option value="GBPUSD">GBPUSD</option>
                              <option value="USDJPY">USDJPY</option>
                              <option value="US30">US30 (Dow Jones)</option>
                              <option value="NAS100">NAS100 (Nasdaq)</option>
                              <option value="ALL">🌐 TẤT CẢ CẶP TIỀN (ALL)</option>
                            </select>
                          </div>

                          <div>
                            <label className="text-xs font-bold text-slate-300 block mb-1">Khung Thời Gian</label>
                            <select
                              value={stratForm.timeframe}
                              onChange={e => setStratForm({ ...stratForm, timeframe: e.target.value })}
                              className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-100 font-bold focus:outline-none focus:border-purple-500 cursor-pointer"
                            >
                              <option value="M1">M1 (1 Phút)</option>
                              <option value="M5">M5 (5 Phút)</option>
                              <option value="M15">M15 (15 Phút)</option>
                              <option value="M30">M30 (30 Phút)</option>
                              <option value="H1">H1 (1 Giờ)</option>
                              <option value="H4">H4 (4 Giờ)</option>
                              <option value="ALL">🌐 TẤT CẢ KHUNG (ALL)</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* 2. Strategy Specific Full Parameters Panel */}
                      <div className="p-3.5 rounded-xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border border-cyan-500/30 space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-1 border-b border-slate-800">
                          <span className="text-[11px] font-bold text-cyan-400 uppercase tracking-wider flex items-center gap-1.5">
                            <Sliders className="w-3.5 h-3.5 text-cyan-400" />
                            2. Cấu Hình Tín Hiệu & Chỉ Báo Chi Tiết ({currentStratKey.toUpperCase()})
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                const defaults = getDefaultStrategyParams(stratForm.strategy);
                                setStratForm(prev => ({
                                  ...prev,
                                  custom_params: defaults
                                }));
                              }}
                              className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/40 transition cursor-pointer flex items-center gap-1"
                              title="Tải lại toàn bộ thông số mặc định từ bảng Cài Đặt Admin"
                            >
                              <RefreshCw className="w-3 h-3" />
                              <span>🔄 Đồng Bộ Từ Cài Đặt Admin</span>
                            </button>
                            <span className="text-[10px] text-emerald-400 font-mono bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                              Full Parameters
                            </span>
                          </div>
                        </div>

                        {/* ===================== MATRIX PARAMS ===================== */}
                        {(currentStratKey === 'matrix' || currentStratKey === 'signal') && (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Fast EMA</label>
                                <input
                                  type="number"
                                  value={p.emaFast ?? 9}
                                  onChange={e => setP('emaFast', parseInt(e.target.value) || 9)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Slow EMA</label>
                                <input
                                  type="number"
                                  value={p.emaSlow ?? 21}
                                  onChange={e => setP('emaSlow', parseInt(e.target.value) || 21)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">RSI Period</label>
                                <input
                                  type="number"
                                  value={p.rsiPeriod ?? 14}
                                  onChange={e => setP('rsiPeriod', parseInt(e.target.value) || 14)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Min Confidence (%)</label>
                                <input
                                  type="number"
                                  value={p.minConfidence ?? 35}
                                  onChange={e => setP('minConfidence', parseInt(e.target.value) || 35)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">TP Target (R)</label>
                                <input
                                  type="number"
                                  step="0.1"
                                  value={p.tpMult ?? 1.8}
                                  onChange={e => setP('tpMult', parseFloat(e.target.value) || 1.8)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-emerald-400 font-mono font-bold"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">SL Mult (R)</label>
                                <input
                                  type="number"
                                  step="0.1"
                                  value={p.slMult ?? 1.2}
                                  onChange={e => setP('slMult', parseFloat(e.target.value) || 1.2)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-rose-400 font-mono font-bold"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Cooldown (Nến)</label>
                                <input
                                  type="number"
                                  value={p.cooldownBars ?? 25}
                                  onChange={e => setP('cooldownBars', parseInt(e.target.value) || 25)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Vào Lệnh (Entry)</label>
                                <select
                                  value={p.entryMode ?? 'Limit 1'}
                                  onChange={e => setP('entryMode', e.target.value)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-cyan-300 font-mono"
                                >
                                  <option value="Limit 1">Limit 1 (Tối Ưu)</option>
                                  <option value="Market">Market (Khớp Ngay)</option>
                                </select>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">SL Min (Pips)</label>
                                <input
                                  type="number"
                                  value={p.slMin ?? 20}
                                  onChange={e => setP('slMin', parseFloat(e.target.value) || 20)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">SL Max (Pips)</label>
                                <input
                                  type="number"
                                  value={p.slMax ?? 150}
                                  onChange={e => setP('slMax', parseFloat(e.target.value) || 150)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono"
                                />
                              </div>
                              <div className="flex items-center justify-between pt-4">
                                <label className="text-[10.5px] font-bold text-slate-300">Lọc SL Min/Max</label>
                                <input
                                  type="checkbox"
                                  checked={p.slFilterEnable !== false}
                                  onChange={e => setP('slFilterEnable', e.target.checked)}
                                  className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
                                />
                              </div>
                              <div className="flex items-center justify-between pt-4">
                                <label className="text-[10.5px] font-bold text-slate-300">Chống Chuỗi Thua</label>
                                <input
                                  type="checkbox"
                                  checked={Boolean(p.antiStreakEnable)}
                                  onChange={e => setP('antiStreakEnable', e.target.checked)}
                                  className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
                                />
                              </div>
                            </div>

                            {/* EMA 200 Filters explicitly shown */}
                            <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-[11px] font-bold text-slate-300 block">Lọc Xu Hướng EMA 200</span>
                                  <span className="text-[9.5px] text-slate-500">Chỉ BUY trên EMA200 / SELL dưới EMA200</span>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={Boolean(p.ema200FilterEnable)}
                                  onChange={e => setP('ema200FilterEnable', e.target.checked)}
                                  className="w-4 h-4 rounded text-cyan-500 focus:ring-0 cursor-pointer"
                                />
                              </div>
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-[11px] font-bold text-slate-300 block">Lọc Khoảng Cách EMA 200</span>
                                  <span className="text-[9.5px] text-slate-500">Chặn vào lệnh khi quá xa/quá gần</span>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={Boolean(p.ema200DistFilterEnable)}
                                  onChange={e => setP('ema200DistFilterEnable', e.target.checked)}
                                  className="w-4 h-4 rounded text-cyan-500 focus:ring-0 cursor-pointer"
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* ===================== HMA PARAMS ===================== */}
                        {currentStratKey === 'hma' && (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Chu Kỳ HMA</label>
                                <input
                                  type="number"
                                  value={p.period ?? (settings.hma_period || 24)}
                                  onChange={e => setP('period', parseInt(e.target.value) || 24)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-cyan-300 font-mono font-bold"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Lookback (Nến)</label>
                                <input
                                  type="number"
                                  value={p.lookbackBars ?? (settings.hma_lookback_bars || 100)}
                                  onChange={e => setP('lookbackBars', parseInt(e.target.value) || 100)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Max Bars Cực Trị</label>
                                <input
                                  type="number"
                                  value={p.maxBarsFromExtreme ?? (settings.hma_max_bars_from_extreme || 35)}
                                  onChange={e => setP('maxBarsFromExtreme', parseInt(e.target.value) || 35)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">HMA Cooldown (Nến)</label>
                                <input
                                  type="number"
                                  value={p.hmaCooldownBars ?? (settings.hma_cooldown_bars || 5)}
                                  onChange={e => setP('hmaCooldownBars', parseInt(e.target.value) || 5)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-emerald-300 font-mono"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">TP1 Target (R1)</label>
                                <input
                                  type="number"
                                  step="0.1"
                                  value={p.r1 ?? (settings.hma_r1 || 1.5)}
                                  onChange={e => setP('r1', parseFloat(e.target.value) || 1.5)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-emerald-400 font-mono font-bold"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">TP2 Max (R2)</label>
                                <input
                                  type="number"
                                  step="0.1"
                                  value={p.r2 ?? (settings.hma_r2 || 2.0)}
                                  onChange={e => setP('r2', parseFloat(e.target.value) || 2.0)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-emerald-300 font-mono font-bold"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">SL Buffer Mode</label>
                                <select
                                  value={p.slBufferMode ?? (settings.hma_sl_buffer_mode || "ATR")}
                                  onChange={e => setP('slBufferMode', e.target.value)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-cyan-300 font-mono"
                                >
                                  <option value="ATR">Dynamic ATR (Min-Max)</option>
                                  <option value="FIXED">Fixed Pips</option>
                                </select>
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">
                                  {p.slBufferMode === 'FIXED' ? 'SL Buffer (Pips)' : 'SL Buffer (Min-Max pips)'}
                                </label>
                                {p.slBufferMode === 'FIXED' ? (
                                  <input
                                    type="number"
                                    value={p.slBufferPips ?? (settings.hma_sl_buffer_pips || 5.0)}
                                    onChange={e => setP('slBufferPips', parseFloat(e.target.value) || 5.0)}
                                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-rose-400 font-mono"
                                  />
                                ) : (
                                  <div className="flex items-center gap-1 font-mono text-xs">
                                    <input
                                      type="number"
                                      value={p.slBufferMinPips ?? (settings.hma_sl_buffer_min_pips || 5.0)}
                                      onChange={e => setP('slBufferMinPips', parseFloat(e.target.value) || 5.0)}
                                      className="w-1/2 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-cyan-300 font-bold"
                                      placeholder="Min"
                                    />
                                    <span className="text-slate-500">-</span>
                                    <input
                                      type="number"
                                      value={p.slBufferMaxPips ?? (settings.hma_sl_buffer_max_pips || 30.0)}
                                      onChange={e => setP('slBufferMaxPips', parseFloat(e.target.value) || 30.0)}
                                      className="w-1/2 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-amber-300 font-bold"
                                      placeholder="Max"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">SL Min Risk (Pips)</label>
                                <input
                                  type="number"
                                  value={p.minRiskPips ?? (settings.hma_min_risk_pips || 3.0)}
                                  onChange={e => setP('minRiskPips', parseFloat(e.target.value) || 3.0)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">SL Max Risk (Pips)</label>
                                <input
                                  type="number"
                                  value={p.maxRiskPips ?? (settings.hma_max_risk_pips || 80.0)}
                                  onChange={e => setP('maxRiskPips', parseFloat(e.target.value) || 80.0)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-rose-400 font-mono font-bold"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Tỷ Lệ Vượt HMA (% Body)</label>
                                <input
                                  type="number"
                                  value={p.minBodyPct ?? (settings.hma_min_body_pct || 60)}
                                  onChange={e => setP('minBodyPct', parseFloat(e.target.value) || 60)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-purple-300 font-mono font-bold"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Max HMA Slope</label>
                                <input
                                  type="number"
                                  step="0.5"
                                  value={p.maxHmaSlope ?? (settings.hma_max_slope || 3.0)}
                                  onChange={e => setP('maxHmaSlope', parseFloat(e.target.value) || 3.0)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-cyan-300 font-mono font-bold"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2.5">
                              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-950 border border-slate-800">
                                <div>
                                  <label className="text-[10.5px] font-bold text-slate-300 block">Lọc Độ Dốc Slope</label>
                                  <span className="text-[9px] text-slate-500">Chặn tín hiệu khi HMA quá dốc</span>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={p.hmaSlopeFilter !== false}
                                  onChange={e => setP('hmaSlopeFilter', e.target.checked)}
                                  className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
                                />
                              </div>
                              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-950 border border-slate-800">
                                <div>
                                  <label className="text-[10.5px] font-bold text-slate-300 block">Nến Floating Breakout</label>
                                  <span className="text-[9px] text-slate-500">Chấp nhận nến mở/đóng ngoài HMA</span>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={Boolean(p.allowFloatingBreakout)}
                                  onChange={e => setP('allowFloatingBreakout', e.target.checked)}
                                  className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
                                />
                              </div>
                            </div>

                            {/* Card: Candle Momentum & Rejection Wick Filter */}
                            <div className="p-3.5 rounded-xl bg-purple-500/10 border border-purple-500/30 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Zap className="w-4 h-4 text-purple-400" />
                                  <span className="text-xs font-bold text-purple-200">Lọc Cường Lực Nến Phá Vỡ (Candle Momentum Filter)</span>
                                </div>
                                <span className="text-[10px] text-purple-400 font-mono font-bold bg-purple-950/60 border border-purple-800/60 px-2 py-0.5 rounded">
                                  High-Momentum
                                </span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Bộ Lọc Cường Lực Nến</label>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setP('candleStrengthFilter', p.candleStrengthFilter === false ? true : false)}
                                    className={`w-full py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                      p.candleStrengthFilter !== false
                                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/40 shadow-sm'
                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                    }`}
                                  >
                                    {p.candleStrengthFilter !== false ? 'ĐANG BẬT ✓' : 'ĐANG TẮT ✕'}
                                  </button>
                                  <p className="text-[9.5px] text-slate-400 leading-tight">
                                    {p.candleStrengthFilter !== false ? 'Bật: Chỉ nhận nến đặc thân, loại nến rút râu.' : 'Tắt: Nhận mọi hình thái nến.'}
                                  </p>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Thân Nến Tối Thiểu</label>
                                    <span className="text-[10px] font-mono font-bold text-purple-400 bg-purple-500/15 px-1.5 py-0.5 rounded border border-purple-500/30">
                                      {p.minCandleBodyRatio ?? (settings.hma_min_candle_body_ratio || 35)}%
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="10"
                                      max="90"
                                      step="5"
                                      disabled={p.candleStrengthFilter === false}
                                      value={p.minCandleBodyRatio ?? (settings.hma_min_candle_body_ratio || 35)}
                                      onChange={e => setP('minCandleBodyRatio', parseFloat(e.target.value) || 35)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-purple-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">%</span>
                                  </div>
                                  <p className="text-[9.5px] text-slate-500 leading-tight">Tối thiểu % thân nến trên tổng chiều dài.</p>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Râu Ngược Hướng Max</label>
                                    <span className="text-[10px] font-mono font-bold text-rose-400 bg-rose-500/15 px-1.5 py-0.5 rounded border border-rose-500/30">
                                      {p.maxRejectionWickRatio ?? (settings.hma_max_rejection_wick_ratio || 40)}%
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="10"
                                      max="70"
                                      step="5"
                                      disabled={p.candleStrengthFilter === false}
                                      value={p.maxRejectionWickRatio ?? (settings.hma_max_rejection_wick_ratio || 40)}
                                      onChange={e => setP('maxRejectionWickRatio', parseFloat(e.target.value) || 40)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-rose-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">%</span>
                                  </div>
                                  <p className="text-[9.5px] text-slate-500 leading-tight">Tối đa % râu nến dội ngược cản trở xu hướng.</p>
                                </div>
                              </div>
                            </div>

                            {/* Card: Dynamic Limit Entry on Large SL Breakouts */}
                            <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-amber-400" />
                                  <span className="text-xs font-bold text-amber-200">Vào Lệnh Chờ Limit Khi Nến Phá Vỡ Quá Lớn (Dynamic Limit Entry)</span>
                                </div>
                                <span className="text-[10px] text-amber-400 font-mono font-bold bg-amber-950/60 border border-amber-800/60 px-2 py-0.5 rounded">
                                  Retest Pullback Engine
                                </span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5">
                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Chế Độ Lệnh Chờ Limit</label>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setP('dynamicLimitEnable', p.dynamicLimitEnable === false ? true : false)}
                                    className={`w-full py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                      p.dynamicLimitEnable !== false
                                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40 shadow-sm'
                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                    }`}
                                  >
                                    {p.dynamicLimitEnable !== false ? 'ĐANG BẬT ✓' : 'ĐANG TẮT ✕'}
                                  </button>
                                  <p className="text-[9.5px] text-slate-400 leading-tight">
                                    {p.dynamicLimitEnable !== false ? 'Bật: SL lớn sẽ đặt Limit chờ hồi.' : 'Tắt: Luôn vào Market ngay.'}
                                  </p>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Ngưỡng Kích Hoạt Limit</label>
                                    <span className="text-[10px] font-mono font-bold text-amber-400 bg-amber-500/15 px-1.5 py-0.5 rounded border border-amber-500/30">
                                      {p.limitThresholdPips ?? (settings.hma_limit_threshold_pips || 60)}p
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="20"
                                      max="200"
                                      step="5"
                                      disabled={p.dynamicLimitEnable === false}
                                      value={p.limitThresholdPips ?? (settings.hma_limit_threshold_pips || 60)}
                                      onChange={e => setP('limitThresholdPips', parseFloat(e.target.value) || 60)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-amber-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">pips</span>
                                  </div>
                                  <p className="text-[9.5px] text-slate-500 leading-tight">Nếu SL ban đầu &gt; mức này sẽ chuyển sang đặt lệnh Limit.</p>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Tỷ Lệ Hồi Retest Zone</label>
                                    <span className="text-[10px] font-mono font-bold text-amber-400 bg-amber-500/15 px-1.5 py-0.5 rounded border border-amber-500/30">
                                      {p.limitPullbackPct ?? (settings.hma_limit_pullback_pct || 50)}%
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="10"
                                      max="90"
                                      step="5"
                                      disabled={p.dynamicLimitEnable === false}
                                      value={p.limitPullbackPct ?? (settings.hma_limit_pullback_pct || 50)}
                                      onChange={e => setP('limitPullbackPct', parseFloat(e.target.value) || 50)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-amber-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">%</span>
                                  </div>
                                  <p className="text-[9.5px] text-slate-500 leading-tight">Mức hồi quy từ giá đóng cửa về mốc phá vỡ (khuyên dùng 50%).</p>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Số Nến Tự Hủy (Timeout)</label>
                                    <span className="text-[10px] font-mono font-bold text-amber-400 bg-amber-500/15 px-1.5 py-0.5 rounded border border-amber-500/30">
                                      {p.limitCancelBars ?? (settings.hma_limit_cancel_bars || 20)} nến
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="5"
                                      max="100"
                                      step="1"
                                      disabled={p.dynamicLimitEnable === false}
                                      value={p.limitCancelBars ?? (settings.hma_limit_cancel_bars || 20)}
                                      onChange={e => setP('limitCancelBars', parseInt(e.target.value) || 20)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-amber-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">nến</span>
                                  </div>
                                  <p className="text-[9.5px] text-slate-500 leading-tight">Tự động hủy lệnh nếu sau số nến này giá không hồi về khớp.</p>
                                </div>
                              </div>
                            </div>

                            {/* Card: Dynamic Break-Even */}
                            <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Shield className="w-4 h-4 text-emerald-400" />
                                  <span className="text-xs font-bold text-emerald-200">Dời SL Về Hòa Vốn Khi Đạt % TP (Early Break-Even Lock)</span>
                                </div>
                                <span className="text-[10px] text-emerald-400 font-mono font-bold bg-emerald-950/60 border border-emerald-800/60 px-2 py-0.5 rounded">
                                  Capital Protection
                                </span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Chế Độ Dời BE Sớm</label>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setP('dynamicBeEnable', !p.dynamicBeEnable)}
                                    className={`w-full py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                      p.dynamicBeEnable
                                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 shadow-sm'
                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                    }`}
                                  >
                                    {p.dynamicBeEnable ? 'ĐANG BẬT ✓' : 'ĐANG TẮT ✕'}
                                  </button>
                                  <p className="text-[9.5px] text-slate-400 leading-tight">
                                    {p.dynamicBeEnable ? 'Bật: Đạt % TP tự dời SL về BE+.' : 'Tắt: Giữ nguyên SL ban đầu.'}
                                  </p>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Ngưỡng Kích Hoạt (% TP)</label>
                                    <span className="text-[10px] font-mono font-bold text-emerald-400 bg-emerald-500/15 px-1.5 py-0.5 rounded border border-emerald-500/30">
                                      {p.beTriggerPct ?? (settings.hma_be_trigger_pct || 75)}% TP
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="30"
                                      max="95"
                                      step="5"
                                      disabled={!p.dynamicBeEnable}
                                      value={p.beTriggerPct ?? (settings.hma_be_trigger_pct || 75)}
                                      onChange={e => setP('beTriggerPct', parseFloat(e.target.value) || 75)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-emerald-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">%</span>
                                  </div>
                                  <p className="text-[9.5px] text-slate-500 leading-tight">Mức giá đạt được tới TP (khuyên dùng 70 - 80%).</p>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Pips Khóa Lời (BE+)</label>
                                    <span className="text-[10px] font-mono font-bold text-emerald-400 bg-emerald-500/15 px-1.5 py-0.5 rounded border border-emerald-500/30">
                                      +{p.beLockPips ?? (settings.hma_be_lock_pips || 5)}p
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="0"
                                      max="30"
                                      step="1"
                                      disabled={!p.dynamicBeEnable}
                                      value={p.beLockPips ?? (settings.hma_be_lock_pips || 5)}
                                      onChange={e => setP('beLockPips', parseFloat(e.target.value) || 0)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-emerald-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">pips</span>
                                  </div>
                                  <p className="text-[9.5px] text-slate-500 leading-tight">Số pips dịch qua entry khóa lãi (khuyên dùng +5p).</p>
                                </div>
                              </div>
                            </div>

                            {/* Card: Close on Opposite Signal */}
                            <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <RefreshCw className="w-4 h-4 text-amber-400" />
                                  <span className="text-xs font-bold text-amber-200">Cắt Lệnh Khi Xuất Hiện Lệnh Ngược Chiều (Close on Opposite)</span>
                                </div>
                                <span className="text-[10px] text-amber-400 font-mono font-bold bg-amber-950/60 border border-amber-800/60 px-2 py-0.5 rounded">
                                  Opposite Exit
                                </span>
                              </div>
                              <div className="flex items-center justify-between p-2.5 rounded-lg bg-slate-950/80 border border-slate-800">
                                <div className="space-y-0.5">
                                  <label className="text-[10.5px] font-bold text-slate-300 block">Tự Động Đóng Lệnh Ngược Chiều</label>
                                  <p className="text-[9.5px] text-slate-400 leading-tight">
                                    {p.closeOnOpposite !== false ? 'Bật: Đang giữ SELL mà có BUY mới -> Cắt hết SELL cũ để theo BUY mới.' : 'Tắt: Giữ cả BUY và SELL chạy độc lập.'}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setP('closeOnOpposite', p.closeOnOpposite === false ? true : false)}
                                  className={`px-3 py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                    p.closeOnOpposite !== false
                                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40 shadow-sm'
                                      : 'bg-slate-800 text-slate-400 border border-slate-700'
                                  }`}
                                >
                                  {p.closeOnOpposite !== false ? 'ĐANG BẬT ✓' : 'ĐANG TẮT ✕'}
                                </button>
                              </div>
                            </div>

                            {/* Card: Anti-Cascade Dump Protection */}
                            <div className="p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/30 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Shield className="w-4 h-4 text-rose-400" />
                                  <span className="text-xs font-bold text-rose-200">Lọc Chống Ép Giảm / Thác Đổ (Cascade Dump Protection)</span>
                                </div>
                                <span className="text-[10px] text-rose-400 font-mono font-bold bg-rose-950/60 border border-rose-800/60 px-2 py-0.5 rounded">
                                  Anti-Cascade
                                </span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Trạng Thái Lọc Thác Đổ</label>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setP('cascadeFilter', !p.cascadeFilter)}
                                    className={`w-full py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                      p.cascadeFilter
                                        ? 'bg-rose-500/20 text-rose-400 border border-rose-500/40 shadow-sm'
                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                    }`}
                                  >
                                    {p.cascadeFilter ? 'ĐANG BẬT ✓' : 'ĐANG TẮT ✕'}
                                  </button>
                                  <p className="text-[9.5px] text-slate-400 leading-tight">
                                    {p.cascadeFilter ? 'Bật: Khóa lệnh khi gặp chuỗi nến ép xả thác đổ / gom mua dồn dập.' : 'Tắt: Vào lệnh bình thường.'}
                                  </p>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Ngưỡng Nến Đè Áp Đảo</label>
                                    <span className="text-[10px] font-mono font-bold text-rose-400 bg-rose-500/15 px-1.5 py-0.5 rounded border border-rose-500/30">
                                      {p.bearRatioThresh ?? (settings.hma_bear_ratio_thresh || 70)}%
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="50"
                                      max="90"
                                      step="10"
                                      disabled={!p.cascadeFilter}
                                      value={p.bearRatioThresh ?? (settings.hma_bear_ratio_thresh || 70)}
                                      onChange={e => setP('bearRatioThresh', parseFloat(e.target.value) || 70)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-rose-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">%</span>
                                  </div>
                                  <p className="text-[9.5px] text-slate-500 leading-tight">Tối thiểu % nến cùng màu trong 10 nến gần nhất (khuyên dùng 70%).</p>
                                </div>
                              </div>
                            </div>

                            {/* Card: Trading Session & Hours Filter */}
                            <div className="p-3.5 rounded-xl bg-cyan-500/10 border border-cyan-500/30 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-cyan-400" />
                                  <span className="text-xs font-bold text-cyan-200">Lọc Khung Giờ / Phiên Giao Dịch (Trading Session Filter)</span>
                                </div>
                                <span className="text-[10px] text-cyan-400 font-mono font-bold bg-cyan-950/60 border border-cyan-800/60 px-2 py-0.5 rounded">
                                  Session Filter
                                </span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <label className="text-[10.5px] font-bold text-slate-300 block">Trạng Thái Bộ Lọc</label>
                                  <button
                                    type="button"
                                    onClick={() => setP('sessionFilterEnable', !p.sessionFilterEnable)}
                                    className={`w-full py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                      p.sessionFilterEnable
                                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40 shadow-sm'
                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                    }`}
                                  >
                                    {p.sessionFilterEnable ? 'ĐANG BẬT ✓' : 'ĐANG TẮT (24/24) ✕'}
                                  </button>
                                  <p className="text-[9.5px] text-slate-400 leading-tight">
                                    {p.sessionFilterEnable ? 'Bật: Chỉ phát lệnh trong phiên chọn.' : 'Tắt: Bắn lệnh 24/24 cả ngày.'}
                                  </p>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5 sm:col-span-2">
                                  <label className="text-[10.5px] font-bold text-slate-300 block">Chọn Phiên (Giờ VN - UTC+7)</label>
                                  <select
                                    disabled={!p.sessionFilterEnable}
                                    value={p.sessionMode || 'ALL'}
                                    onChange={e => {
                                      const m = e.target.value;
                                      let sStart = p.sessionStart || '06:00';
                                      let sEnd = p.sessionEnd || '19:30';
                                      if (m === 'ASIA') { sStart = '06:00'; sEnd = '14:00'; }
                                      else if (m === 'LONDON') { sStart = '14:00'; sEnd = '21:00'; }
                                      else if (m === 'NY') { sStart = '19:30'; sEnd = '03:30'; }
                                      else if (m === 'ASIA_LONDON') { sStart = '06:00'; sEnd = '19:30'; }
                                      else if (m === 'LONDON_NY') { sStart = '14:00'; sEnd = '03:30'; }
                                      setP('sessionMode', m);
                                      setP('sessionStart', sStart);
                                      setP('sessionEnd', sEnd);
                                    }}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-cyan-300 font-bold disabled:opacity-40"
                                  >
                                    <option value="ALL">🌐 Tất Cả Phiên (24/24)</option>
                                    <option value="ASIA">🌏 Chỉ Phiên Á (06:00 - 14:00 VN)</option>
                                    <option value="LONDON">🏰 Chỉ Phiên Âu (14:00 - 21:00 VN)</option>
                                    <option value="NY">🗽 Chỉ Phiên Mỹ (19:30 - 03:30 VN)</option>
                                    <option value="ASIA_LONDON">⭐ Phiên Á + Âu (BỎ PHIÊN MỸ: 06:00 - 19:30 VN)</option>
                                    <option value="LONDON_NY">⚡ Phiên Âu + Mỹ (14:00 - 03:30 VN)</option>
                                    <option value="CUSTOM">🔧 Tùy Chỉnh Giờ (Start - End)</option>
                                  </select>

                                  {(p.sessionMode === 'CUSTOM' || p.sessionFilterEnable) && (
                                    <div className="flex items-center gap-2 pt-1">
                                      <input
                                        type="text"
                                        disabled={!p.sessionFilterEnable}
                                        value={p.sessionStart || '06:00'}
                                        onChange={e => setP('sessionStart', e.target.value)}
                                        placeholder="06:00"
                                        className="w-1/2 bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-center text-cyan-300 font-mono text-xs font-bold disabled:opacity-40"
                                      />
                                      <span className="text-slate-500">-</span>
                                      <input
                                        type="text"
                                        disabled={!p.sessionFilterEnable}
                                        value={p.sessionEnd || '19:30'}
                                        onChange={e => setP('sessionEnd', e.target.value)}
                                        placeholder="19:30"
                                        className="w-1/2 bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-center text-amber-300 font-mono text-xs font-bold disabled:opacity-40"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Card: Lọc Vùng Sideway Theo EMA 200 M5 (Chỉ áp dụng nến M1 - M5 tự động không lọc) */}
                            <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Activity className="w-4 h-4 text-emerald-400" />
                                  <span className="text-xs font-bold text-emerald-200">Lọc Vùng Sideway Theo EMA 200 M5 (Chỉ áp dụng nến M1 - M5 tự động không lọc)</span>
                                </div>
                                <span className="text-[10px] text-emerald-400 font-mono font-bold bg-emerald-950/60 border border-emerald-800/60 px-2 py-0.5 rounded">
                                  Sideway Range Protection
                                </span>
                              </div>

                              <div className="p-2.5 rounded-lg bg-emerald-950/30 border border-emerald-500/20 text-[11px] text-emerald-300 leading-relaxed">
                                <strong className="block mb-0.5 text-emerald-300 font-bold">🎯 Giới hạn giao dịch trong vùng Sideway quanh EMA 200 (Khung M5)</strong>
                                <p className="text-[10.5px] text-emerald-300/80">
                                  Chỉ bắn lệnh BUY/SELL khi khoảng cách từ giá tới EMA 200 M5 không quá <b>{p.ema200SwMaxDistPips ?? (settings.hma_ema200_sw_max_dist_pips || 420)} pips</b>. Tự động đứng ngoài khi bão xu hướng xả thác/rocket pump. Chuyển sang nến M5 hệ thống sẽ tự động tắt lọc.
                                </p>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                {/* Toggle */}
                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Trạng Thái Lọc Sideway EMA200</label>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const currentVal = p.ema200SwFilterEnable !== undefined ? p.ema200SwFilterEnable : (settings.hma_ema200_sw_filter_enable !== false);
                                      setP('ema200SwFilterEnable', !currentVal);
                                    }}
                                    className={`w-full py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                      (p.ema200SwFilterEnable !== undefined ? p.ema200SwFilterEnable : (settings.hma_ema200_sw_filter_enable !== false))
                                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 shadow-sm'
                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                    }`}
                                  >
                                    {(p.ema200SwFilterEnable !== undefined ? p.ema200SwFilterEnable : (settings.hma_ema200_sw_filter_enable !== false)) ? 'ĐANG BẬT ✓' : 'ĐANG TẮT ✕'}
                                  </button>
                                  <p className="text-[9.5px] text-slate-400 leading-tight">
                                    {(p.ema200SwFilterEnable !== undefined ? p.ema200SwFilterEnable : (settings.hma_ema200_sw_filter_enable !== false))
                                      ? 'Đang Bật: Chỉ bắn lệnh BUY/SELL khi giá nằm trong biên độ Sideway quanh EMA 200 M5.'
                                      : 'Đang Tắt: Bắn lệnh HMA tự do không giới hạn khoảng cách so với EMA 200.'}
                                  </p>
                                </div>

                                {/* Max distance input & presets */}
                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Khoảng Cách Tối Đa Tới EMA 200 M5</label>
                                    <span className="text-[10px] font-mono font-bold text-emerald-400 bg-emerald-500/15 px-1.5 py-0.5 rounded border border-emerald-500/30">
                                      ±{p.ema200SwMaxDistPips ?? (settings.hma_ema200_sw_max_dist_pips || 420)} pips
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="50"
                                      max="2000"
                                      step="10"
                                      disabled={!(p.ema200SwFilterEnable !== undefined ? p.ema200SwFilterEnable : (settings.hma_ema200_sw_filter_enable !== false))}
                                      value={p.ema200SwMaxDistPips ?? (settings.hma_ema200_sw_max_dist_pips || 420)}
                                      onChange={e => setP('ema200SwMaxDistPips', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-emerald-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">pips</span>
                                  </div>
                                  <div className="flex items-center gap-1 pt-0.5">
                                    {[150, 200, 250, 300, 400, 420, 500].map(dist => (
                                      <button
                                        key={dist}
                                        type="button"
                                        disabled={!(p.ema200SwFilterEnable !== undefined ? p.ema200SwFilterEnable : (settings.hma_ema200_sw_filter_enable !== false))}
                                        onClick={() => setP('ema200SwMaxDistPips', dist)}
                                        className={`flex-1 py-0.5 text-[9.5px] rounded border transition font-mono ${
                                          (p.ema200SwMaxDistPips ?? (settings.hma_ema200_sw_max_dist_pips || 420)) === dist
                                            ? 'bg-emerald-500/25 text-emerald-300 border border-emerald-500/50 font-bold'
                                            : 'bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800'
                                        }`}
                                      >
                                        {dist}p
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>

                          </div>
                        )}

                        {/* ===================== SMC PARAMS ===================== */}
                        {currentStratKey === 'smc' && (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Window Bars</label>
                                <input
                                  type="number"
                                  value={p.windowBars ?? 100}
                                  onChange={e => setP('windowBars', parseInt(e.target.value) || 100)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Pivot Length PH/PL</label>
                                <input
                                  type="number"
                                  value={p.lenPH ?? 3}
                                  onChange={e => {
                                    const val = parseInt(e.target.value) || 3;
                                    setP('lenPH', val);
                                    setP('lenPL', val);
                                  }}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">TP1 (R1)</label>
                                <input
                                  type="number"
                                  step="0.1"
                                  value={p.r1 ?? 1.5}
                                  onChange={e => setP('r1', parseFloat(e.target.value) || 1.5)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-emerald-400 font-mono font-bold"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">TP2 (R2)</label>
                                <input
                                  type="number"
                                  step="0.1"
                                  value={p.r2 ?? 2.0}
                                  onChange={e => setP('r2', parseFloat(e.target.value) || 2.0)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-emerald-300 font-mono font-bold"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Kiểu Vào Lệnh</label>
                                <select
                                  value={p.entryMode ?? 'Limit 1'}
                                  onChange={e => setP('entryMode', e.target.value)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-cyan-300 font-mono"
                                >
                                  <option value="Limit 1">Limit 1 (Tối Ưu)</option>
                                  <option value="Half Break">Half Break (50% Range)</option>
                                  <option value="Market">Market (Khớp Ngay)</option>
                                </select>
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">SL Min (Pips)</label>
                                <input
                                  type="number"
                                  value={p.slMin ?? 10}
                                  onChange={e => setP('slMin', parseFloat(e.target.value) || 10)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">SL Max (Pips)</label>
                                <input
                                  type="number"
                                  value={p.slMax ?? 150}
                                  onChange={e => setP('slMax', parseFloat(e.target.value) || 150)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">SL Extra (Pips)</label>
                                <input
                                  type="number"
                                  value={p.slExtraVal ?? 5}
                                  onChange={e => setP('slExtraVal', parseFloat(e.target.value) || 5)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-rose-400 font-mono"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2.5">
                              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-950 border border-slate-800">
                                <label className="text-[10.5px] font-bold text-slate-300">Đảo Chiều Reversal MSB</label>
                                <input
                                  type="checkbox"
                                  checked={p.revEnable !== false}
                                  onChange={e => setP('revEnable', e.target.checked)}
                                  className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
                                />
                              </div>
                              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-950 border border-slate-800">
                                <label className="text-[10.5px] font-bold text-slate-300">Lọc Khung Lớn (HTF Auto)</label>
                                <input
                                  type="checkbox"
                                  checked={p.htfFilterEnable !== false}
                                  onChange={e => setP('htfFilterEnable', e.target.checked)}
                                  className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
                                />
                              </div>
                              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-950 border border-slate-800 col-span-2">
                                <div>
                                  <label className="text-[10.5px] font-bold text-slate-300 block">Lọc Nến Breakout Phải Tạo FVG</label>
                                  <span className="text-[9px] text-slate-500">Chỉ mở lệnh khi nến break tạo khoảng trống FVG Imbalance</span>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={Boolean(p.fvgFilterEnable ?? p.smcFvgFilterEnable ?? p.smc_fvg_filter_enable)}
                                  onChange={e => {
                                    setP('fvgFilterEnable', e.target.checked);
                                    setP('smcFvgFilterEnable', e.target.checked);
                                    setP('smc_fvg_filter_enable', e.target.checked);
                                  }}
                                  className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
                                />
                              </div>
                              <div className="p-2.5 rounded-lg bg-slate-950 border border-slate-800 col-span-2 space-y-2">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <label className="text-[10.5px] font-bold text-slate-300 block">🔄 Đảo Chiều Fakeout Khi Dính SL (Revert)</label>
                                    <span className="text-[9px] text-slate-500">Tự động bắn lệnh ngược lại khi cấu trúc SMC bị phá vỡ dính SL</span>
                                  </div>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(p.smcRevertEnable ?? p.smc_revert_enable)}
                                    onChange={e => {
                                      setP('smcRevertEnable', e.target.checked);
                                      setP('smc_revert_enable', e.target.checked);
                                    }}
                                    className="w-4 h-4 rounded text-orange-600 focus:ring-0 cursor-pointer"
                                  />
                                </div>
                                {Boolean(p.smcRevertEnable ?? p.smc_revert_enable) && (
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 border-t border-slate-900">
                                    <div>
                                      <label className="text-[9px] text-slate-400 block mb-0.5">Entry Mode</label>
                                      <select
                                        value={p.smcRevertEntryMode || p.smc_revert_entry_mode || 'MARKET'}
                                        onChange={e => {
                                          setP('smcRevertEntryMode', e.target.value);
                                          setP('smc_revert_entry_mode', e.target.value);
                                        }}
                                        className="w-full bg-slate-900 border border-slate-800 rounded px-1.5 py-1 text-[10px] text-slate-200"
                                      >
                                        <option value="MARKET">Market</option>
                                        <option value="LIMIT_MID">Limit 50%</option>
                                        <option value="LIMIT_ENTRY">Limit Entry</option>
                                        <option value="SMART_HYBRID">Smart Hybrid</option>
                                      </select>
                                    </div>
                                    <div>
                                      <label className="text-[9px] text-slate-400 block mb-0.5">Target RR</label>
                                      <input
                                        type="number"
                                        step="0.1"
                                        value={p.smcRevertRr ?? p.smc_revert_rr ?? 1.5}
                                        onChange={e => {
                                          const val = parseFloat(e.target.value) || 1.5;
                                          setP('smcRevertRr', val);
                                          setP('smc_revert_rr', val);
                                        }}
                                        className="w-full bg-slate-900 border border-slate-800 rounded px-1.5 py-1 text-[10px] text-orange-400 font-bold"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-[9px] text-slate-400 block mb-0.5">Hủy Limit (nến)</label>
                                      <input
                                        type="number"
                                        value={p.smcRevertCancelBars ?? p.smc_revert_cancel_bars ?? 20}
                                        onChange={e => {
                                          const val = parseInt(e.target.value) || 20;
                                          setP('smcRevertCancelBars', val);
                                          setP('smc_revert_cancel_bars', val);
                                        }}
                                        className="w-full bg-slate-900 border border-slate-800 rounded px-1.5 py-1 text-[10px] text-slate-200"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-[9px] text-slate-400 block mb-0.5">SL Buffer (p)</label>
                                      <input
                                        type="number"
                                        step="0.5"
                                        value={p.smcRevertSlBufferPips ?? p.smc_revert_sl_buffer_pips ?? 10.0}
                                        onChange={e => {
                                          const val = parseFloat(e.target.value) || 10.0;
                                          setP('smcRevertSlBufferPips', val);
                                          setP('smc_revert_sl_buffer_pips', val);
                                        }}
                                        className="w-full bg-slate-900 border border-slate-800 rounded px-1.5 py-1 text-[10px] text-slate-200"
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* ===================== BOB VOLMAN PARAMS ===================== */}
                        {(currentStratKey === 'bob_volman' || currentStratKey === 'volman') && (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Pivot Length (L/R)</label>
                                <input
                                  type="number"
                                  min="2"
                                  max="10"
                                  value={p.volmanPivotLength ?? (settings.volman_pivot_length || 3)}
                                  onChange={e => setP('volmanPivotLength', parseInt(e.target.value) || 3)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-cyan-400 font-mono font-bold"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">EMA Period</label>
                                <input
                                  type="number"
                                  value={p.volmanEmaPeriod ?? (settings.volman_ema_period || 20)}
                                  onChange={e => setP('volmanEmaPeriod', parseInt(e.target.value) || 20)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">TP Target (R:R)</label>
                                <input
                                  type="number"
                                  step="0.1"
                                  value={p.volmanR1 ?? (settings.volman_r1 || 1.5)}
                                  onChange={e => setP('volmanR1', parseFloat(e.target.value) || 1.5)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-emerald-400 font-mono font-bold"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Cooldown (Nến)</label>
                                <input
                                  type="number"
                                  value={p.volmanCooldownBars ?? (settings.volman_cooldown_bars || 5)}
                                  onChange={e => setP('volmanCooldownBars', parseInt(e.target.value) || 5)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">SL Buffer (Pips)</label>
                                <input
                                  type="number"
                                  value={p.volmanSlBufferPips ?? (settings.volman_sl_buffer_pips || 5.0)}
                                  onChange={e => setP('volmanSlBufferPips', parseFloat(e.target.value) || 5.0)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-rose-400 font-mono font-bold"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Min Risk (Pips)</label>
                                <input
                                  type="number"
                                  value={p.volmanMinRiskPips ?? (settings.volman_min_risk_pips || 3.0)}
                                  onChange={e => setP('volmanMinRiskPips', parseFloat(e.target.value) || 3.0)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-mono"
                                />
                              </div>
                              <div>
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Max Risk (Pips)</label>
                                <input
                                  type="number"
                                  value={p.volmanMaxRiskPips ?? (settings.volman_max_risk_pips || 150.0)}
                                  onChange={e => setP('volmanMaxRiskPips', parseFloat(e.target.value) || 150.0)}
                                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-rose-400 font-mono font-bold"
                                />
                              </div>
                              <div className="flex items-center justify-between pt-4">
                                <label className="text-[10.5px] font-bold text-slate-300">Lọc SL Min/Max</label>
                                <input
                                  type="checkbox"
                                  checked={p.volmanSlFilterEnable !== false}
                                  onChange={e => setP('volmanSlFilterEnable', e.target.checked)}
                                  className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-2.5 pt-1">
                              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-950 border border-slate-800">
                                <label className="text-[10.5px] font-bold text-slate-300">Setup BB (Block Break)</label>
                                <input
                                  type="checkbox"
                                  checked={p.volmanEnableBB !== false}
                                  onChange={e => setP('volmanEnableBB', e.target.checked)}
                                  className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
                                />
                              </div>
                              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-950 border border-slate-800">
                                <label className="text-[10.5px] font-bold text-slate-300">Setup TB (Trendline Break)</label>
                                <input
                                  type="checkbox"
                                  checked={p.volmanEnableTB !== false}
                                  onChange={e => setP('volmanEnableTB', e.target.checked)}
                                  className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
                                />
                              </div>
                              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-950 border border-slate-800">
                                <label className="text-[10.5px] font-bold text-slate-300">Setup SB (Squeeze Break)</label>
                                <input
                                  type="checkbox"
                                  checked={Boolean(p.volmanEnableSB)}
                                  onChange={e => setP('volmanEnableSB', e.target.checked)}
                                  className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
                                />
                              </div>
                            </div>

                            {/* Card: Breakout Candle Filter */}
                            <div className="p-3.5 rounded-xl bg-purple-500/10 border border-purple-500/30 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Zap className="w-4 h-4 text-purple-400" />
                                  <span className="text-xs font-bold text-purple-200">Lọc Nến Phá Vỡ Cản (Breakout Candle Filter)</span>
                                </div>
                                <span className="text-[10px] text-purple-400 font-mono font-bold bg-purple-950/60 border border-purple-800/60 px-2 py-0.5 rounded">
                                  Price Action Filter
                                </span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5">
                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <label className="text-[10.5px] font-bold text-slate-300 block">Lọc Nến Breakout</label>
                                  <button
                                    type="button"
                                    onClick={() => setP('volmanBreakoutCandleFilter', p.volmanBreakoutCandleFilter === false ? true : false)}
                                    className={`w-full py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                      p.volmanBreakoutCandleFilter !== false
                                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/40 shadow-sm'
                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                    }`}
                                  >
                                    {p.volmanBreakoutCandleFilter !== false ? 'ĐANG BẬT ✓' : 'ĐANG TẮT ✕'}
                                  </button>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Thân Nến Tối Thiểu</label>
                                    <span className="text-[10px] font-mono font-bold text-purple-400 bg-purple-500/15 px-1.5 py-0.5 rounded border border-purple-500/30">
                                      {p.volmanMinBodyRatio ?? (settings.volman_min_body_ratio || 50)}%
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="20"
                                      max="80"
                                      step="5"
                                      disabled={p.volmanBreakoutCandleFilter === false}
                                      value={p.volmanMinBodyRatio ?? (settings.volman_min_body_ratio || 50)}
                                      onChange={e => setP('volmanMinBodyRatio', parseFloat(e.target.value) || 50)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-purple-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">%</span>
                                  </div>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Vượt Cản Tối Thiểu</label>
                                    <span className="text-[10px] font-mono font-bold text-cyan-400 bg-cyan-500/15 px-1.5 py-0.5 rounded border border-cyan-500/30">
                                      {p.volmanMinBodyBeyondPct ?? (settings.volman_min_body_beyond_pct || 30)}%
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="10"
                                      max="80"
                                      step="5"
                                      disabled={p.volmanBreakoutCandleFilter === false}
                                      value={p.volmanMinBodyBeyondPct ?? (settings.volman_min_body_beyond_pct || 30)}
                                      onChange={e => setP('volmanMinBodyBeyondPct', parseFloat(e.target.value) || 30)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-cyan-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">%</span>
                                  </div>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Râu Dội Lại Max</label>
                                    <span className="text-[10px] font-mono font-bold text-rose-400 bg-rose-500/15 px-1.5 py-0.5 rounded border border-rose-500/30">
                                      {p.volmanMaxRejectionWick ?? (settings.volman_max_rejection_wick || 25)}%
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="10"
                                      max="50"
                                      step="5"
                                      disabled={p.volmanBreakoutCandleFilter === false}
                                      value={p.volmanMaxRejectionWick ?? (settings.volman_max_rejection_wick || 25)}
                                      onChange={e => setP('volmanMaxRejectionWick', parseFloat(e.target.value) || 25)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-rose-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">%</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Card: Dynamic Limit Entry */}
                            <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-amber-400" />
                                  <span className="text-xs font-bold text-amber-200">Vào Lệnh Chờ Limit Khi SL Quá Lớn (Dynamic Limit Entry)</span>
                                </div>
                                <span className="text-[10px] text-amber-400 font-mono font-bold bg-amber-950/60 border border-amber-800/60 px-2 py-0.5 rounded">
                                  Retest Pullback
                                </span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5">
                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <label className="text-[10.5px] font-bold text-slate-300 block">Chế Độ Chờ Limit</label>
                                  <button
                                    type="button"
                                    onClick={() => setP('volmanDynamicLimitEnable', !p.volmanDynamicLimitEnable)}
                                    className={`w-full py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                      p.volmanDynamicLimitEnable
                                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40 shadow-sm'
                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                    }`}
                                  >
                                    {p.volmanDynamicLimitEnable ? 'ĐANG BẬT ✓' : 'ĐANG TẮT ✕'}
                                  </button>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Ngưỡng Kích Hoạt</label>
                                    <span className="text-[10px] font-mono font-bold text-amber-400 bg-amber-500/15 px-1.5 py-0.5 rounded border border-amber-500/30">
                                      {p.volmanLimitThresholdPips ?? (settings.volman_limit_threshold_pips || 80)}p
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="20"
                                      max="200"
                                      step="5"
                                      disabled={!p.volmanDynamicLimitEnable}
                                      value={p.volmanLimitThresholdPips ?? (settings.volman_limit_threshold_pips || 80)}
                                      onChange={e => setP('volmanLimitThresholdPips', parseFloat(e.target.value) || 80)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-amber-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">pips</span>
                                  </div>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Tỷ Lệ Hồi Pullback</label>
                                    <span className="text-[10px] font-mono font-bold text-amber-400 bg-amber-500/15 px-1.5 py-0.5 rounded border border-amber-500/30">
                                      {p.volmanLimitPullbackPct ?? (settings.volman_limit_pullback_pct || 40)}%
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="10"
                                      max="90"
                                      step="5"
                                      disabled={!p.volmanDynamicLimitEnable}
                                      value={p.volmanLimitPullbackPct ?? (settings.volman_limit_pullback_pct || 40)}
                                      onChange={e => setP('volmanLimitPullbackPct', parseFloat(e.target.value) || 40)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-amber-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">%</span>
                                  </div>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Số Nến Tự Hủy</label>
                                    <span className="text-[10px] font-mono font-bold text-amber-400 bg-amber-500/15 px-1.5 py-0.5 rounded border border-amber-500/30">
                                      {p.volmanLimitCancelBars ?? (settings.volman_limit_cancel_bars || 20)} nến
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="5"
                                      max="100"
                                      step="1"
                                      disabled={!p.volmanDynamicLimitEnable}
                                      value={p.volmanLimitCancelBars ?? (settings.volman_limit_cancel_bars || 20)}
                                      onChange={e => setP('volmanLimitCancelBars', parseInt(e.target.value) || 20)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-amber-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">nến</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Card: MA Trend Filter */}
                            <div className="p-3.5 rounded-xl bg-cyan-500/10 border border-cyan-500/30 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <TrendingUp className="w-4 h-4 text-cyan-400" />
                                  <span className="text-xs font-bold text-cyan-200">Lọc Xu Hướng Đường MA (MA Trend Filter)</span>
                                </div>
                                <span className="text-[10px] text-cyan-400 font-mono font-bold bg-cyan-950/60 border border-cyan-800/60 px-2 py-0.5 rounded">
                                  Trend Filter
                                </span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <label className="text-[10.5px] font-bold text-slate-300 block">Lọc Xu Hướng MA</label>
                                  <button
                                    type="button"
                                    onClick={() => setP('volmanMaFilterEnable', !p.volmanMaFilterEnable)}
                                    className={`w-full py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                      p.volmanMaFilterEnable
                                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40 shadow-sm'
                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                    }`}
                                  >
                                    {p.volmanMaFilterEnable ? 'ĐANG BẬT ✓' : 'ĐANG TẮT ✕'}
                                  </button>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <label className="text-[10.5px] font-bold text-slate-300 block">Loại Đường MA</label>
                                  <select
                                    disabled={!p.volmanMaFilterEnable}
                                    value={p.volmanMaType || settings.volman_ma_type || 'EMA'}
                                    onChange={e => setP('volmanMaType', e.target.value)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-cyan-300 font-bold focus:outline-none focus:border-cyan-500 disabled:opacity-40"
                                  >
                                    <option value="EMA">EMA (Hàm mũ)</option>
                                    <option value="SMA">SMA (Đơn giản)</option>
                                  </select>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Chu kỳ MA</label>
                                    <span className="text-[10px] font-mono font-bold text-cyan-400 bg-cyan-500/15 px-1.5 py-0.5 rounded border border-cyan-500/30">
                                      {p.volmanMaPeriod ?? (settings.volman_ma_period || 200)}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="5"
                                      max="1000"
                                      step="5"
                                      disabled={!p.volmanMaFilterEnable}
                                      value={p.volmanMaPeriod ?? (settings.volman_ma_period || 200)}
                                      onChange={e => setP('volmanMaPeriod', parseInt(e.target.value) || 200)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-cyan-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">nến</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* ===================== ORB SESSION PRO PARAMS ===================== */}
                        {(currentStratKey === 'orb' || currentStratKey === 'orb_session' || currentStratKey === 'orb_session_pro') && (
                          <div className="space-y-3">
                            {/* Card 1: Session Selection */}
                            <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-amber-400" />
                                  <span className="text-xs font-bold text-amber-200">Chọn Phiên Giao Dịch ORB Breakout</span>
                                </div>
                                <span className="text-[10px] text-amber-400 font-mono font-bold bg-amber-950/60 border border-amber-800/60 px-2 py-0.5 rounded">
                                  Session Filter
                                </span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Phiên Á (Asia)</label>
                                    <span className="text-[9.5px] text-cyan-400 font-mono">08:30 VN</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setP('orbAsiaEnable', p.orbAsiaEnable === false ? true : false)}
                                    className={`w-full py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                      p.orbAsiaEnable !== false
                                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40 shadow-sm'
                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                    }`}
                                  >
                                    {p.orbAsiaEnable !== false ? 'BẬT (08:30) ✓' : 'ĐANG TẮT ✕'}
                                  </button>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Phiên Âu (London)</label>
                                    <span className="text-[9.5px] text-cyan-400 font-mono">14:30 VN</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setP('orbLondonEnable', p.orbLondonEnable === false ? true : false)}
                                    className={`w-full py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                      p.orbLondonEnable !== false
                                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40 shadow-sm'
                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                    }`}
                                  >
                                    {p.orbLondonEnable !== false ? 'BẬT (14:30) ✓' : 'ĐANG TẮT ✕'}
                                  </button>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Phiên Mỹ (NY)</label>
                                    <span className="text-[9.5px] text-cyan-400 font-mono">19:30 VN</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setP('orbNyEnable', !p.orbNyEnable)}
                                    className={`w-full py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                      Boolean(p.orbNyEnable)
                                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40 shadow-sm'
                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                    }`}
                                  >
                                    {Boolean(p.orbNyEnable) ? 'BẬT (19:30) ✓' : 'ĐANG TẮT ✕'}
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Card 2: Entry Mode & Breakout Targets */}
                            <div className="p-3.5 rounded-xl bg-slate-950/80 border border-slate-800 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Zap className="w-4 h-4 text-cyan-400" />
                                  <span className="text-xs font-bold text-cyan-200">Cơ Chế Khớp Lệnh & Thông Số Vào Lệnh (Entry Engine)</span>
                                </div>
                                <span className="text-[10px] text-cyan-400 font-mono font-bold bg-cyan-950/60 border border-cyan-800/60 px-2 py-0.5 rounded">
                                  Entry Mode
                                </span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                <div className="sm:col-span-2">
                                  <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Kiểu Khớp Lệnh (Entry Mode)</label>
                                  <select
                                    value={p.orbEntryMode || 'SMART_HYBRID'}
                                    onChange={e => setP('orbEntryMode', e.target.value)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-amber-300 font-bold focus:outline-none focus:border-amber-400 cursor-pointer"
                                  >
                                    <option value="SMART_HYBRID">⚡ Smart Hybrid (Nến ngắn Market, Nến dài Limit 50%)</option>
                                    <option value="MARKET_BREAK">🚀 Market Breakout (Khớp ngay khi đóng nến)</option>
                                    <option value="LIMIT_EDGE">🎯 Limit Edge (Chờ hồi mép biên hộp)</option>
                                    <option value="LIMIT_50_BREAK">📊 Limit 50% Breakout (Hồi 50% nến break)</option>
                                  </select>
                                </div>

                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <label className="text-[10.5px] font-bold text-slate-400">TP Target (R:R)</label>
                                    <span className="text-[10px] text-emerald-400 font-mono font-bold bg-emerald-500/15 px-1 rounded border border-emerald-500/30">
                                      {p.orbRr ?? (settings.orb_rr || 3.0)}R
                                    </span>
                                  </div>
                                  <input
                                    type="number"
                                    step="0.1"
                                    min="1.0"
                                    max="10.0"
                                    value={p.orbRr ?? (settings.orb_rr || 3.0)}
                                    onChange={e => setP('orbRr', parseFloat(e.target.value) || 3.0)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-emerald-400 font-mono font-bold text-right"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                                <div>
                                  <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Max Break (Pips)</label>
                                  <input
                                    type="number"
                                    step="1"
                                    value={p.orbMaxBreakPips ?? (settings.orb_max_break_pips || 15.0)}
                                    onChange={e => setP('orbMaxBreakPips', parseFloat(e.target.value) || 15.0)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-cyan-400 font-mono font-bold"
                                  />
                                </div>

                                <div>
                                  <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Nến Chờ Limit Hủy</label>
                                  <input
                                    type="number"
                                    value={p.orbMaxLimitBars ?? (settings.orb_max_limit_bars || 6)}
                                    onChange={e => setP('orbMaxLimitBars', parseInt(e.target.value) || 6)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-200 font-mono"
                                  />
                                </div>

                                <div className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800">
                                  <label className="text-[10px] font-bold text-slate-300">Hủy Limit Nếu Chạm TP</label>
                                  <input
                                    type="checkbox"
                                    checked={p.orbCancelOnTp !== false}
                                    onChange={e => setP('orbCancelOnTp', e.target.checked)}
                                    className="w-4 h-4 rounded text-emerald-600 focus:ring-0 cursor-pointer"
                                  />
                                </div>

                                <div className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800">
                                  <label className="text-[10px] font-bold text-slate-300">Hủy Limit Nếu Chạm SL</label>
                                  <input
                                    type="checkbox"
                                    checked={p.orbCancelOnSl !== false}
                                    onChange={e => setP('orbCancelOnSl', e.target.checked)}
                                    className="w-4 h-4 rounded text-rose-600 focus:ring-0 cursor-pointer"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Card 3: SL Buffer & SL Risk Filter */}
                            <div className="p-3.5 rounded-xl bg-slate-950/80 border border-slate-800 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Shield className="w-4 h-4 text-rose-400" />
                                  <span className="text-xs font-bold text-rose-200">Quản Trị Rủi Ro Cắt Lỗ SL & Khoảng Đệm (SL Buffer)</span>
                                </div>
                                <span className="text-[10px] text-rose-400 font-mono font-bold bg-rose-950/60 border border-rose-800/60 px-2 py-0.5 rounded">
                                  SL Risk & Buffer
                                </span>
                              </div>

                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                                <div>
                                  <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Chế Độ Đệm SL</label>
                                  <select
                                    value={p.orbSlBufferMode || 'FIXED'}
                                    onChange={e => setP('orbSlBufferMode', e.target.value)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-200 font-mono cursor-pointer"
                                  >
                                    <option value="FIXED">Cố Định (Fixed Pips)</option>
                                    <option value="ATR">Động Biến Động (ATR)</option>
                                  </select>
                                </div>

                                {p.orbSlBufferMode === 'ATR' ? (
                                  <>
                                    <div>
                                      <label className="text-[10.5px] font-bold text-slate-400 block mb-1">ATR Multiplier</label>
                                      <input
                                        type="number"
                                        step="0.1"
                                        value={p.orbSlAtrMult ?? (settings.orb_sl_atr_mult || 1.0)}
                                        onChange={e => setP('orbSlAtrMult', parseFloat(e.target.value) || 1.0)}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-rose-400 font-mono font-bold"
                                      />
                                    </div>
                                    <div className="col-span-2">
                                      <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Min/Max Buffer (Pips)</label>
                                      <div className="flex items-center gap-1">
                                        <input
                                          type="number"
                                          value={p.orbSlBufferMinPips ?? (settings.orb_sl_buffer_min_pips || 2.0)}
                                          onChange={e => setP('orbSlBufferMinPips', parseFloat(e.target.value) || 2.0)}
                                          className="w-1/2 bg-slate-900 border border-slate-700 rounded-lg px-1.5 py-1 text-xs text-slate-200 font-mono text-center"
                                          placeholder="Min"
                                        />
                                        <span className="text-slate-500">-</span>
                                        <input
                                          type="number"
                                          value={p.orbSlBufferMaxPips ?? (settings.orb_sl_buffer_max_pips || 15.0)}
                                          onChange={e => setP('orbSlBufferMaxPips', parseFloat(e.target.value) || 15.0)}
                                          className="w-1/2 bg-slate-900 border border-slate-700 rounded-lg px-1.5 py-1 text-xs text-slate-200 font-mono text-center"
                                          placeholder="Max"
                                        />
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <div>
                                    <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Đệm SL (Pips)</label>
                                    <input
                                      type="number"
                                      step="0.5"
                                      value={p.orbSlBufferPips ?? (settings.orb_sl_buffer_pips || 2.0)}
                                      onChange={e => setP('orbSlBufferPips', parseFloat(e.target.value) || 2.0)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-rose-400 font-mono font-bold"
                                    />
                                  </div>
                                )}

                                <div className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800">
                                  <label className="text-[10.5px] font-bold text-slate-300">Lọc Khoảng Cách SL</label>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(p.orbSlFilterEnable)}
                                    onChange={e => setP('orbSlFilterEnable', e.target.checked)}
                                    className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
                                  />
                                </div>
                              </div>

                              {p.orbSlFilterEnable && (
                                <div className="grid grid-cols-2 gap-2.5 pt-2 border-t border-slate-800">
                                  <div className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800 font-mono text-xs">
                                    <span className="text-slate-400 text-[10.5px]">SL Tối thiểu:</span>
                                    <div className="flex items-center gap-1">
                                      <input
                                        type="number"
                                        step="1"
                                        min="1"
                                        max="50"
                                        value={p.orbMinSlPips ?? (settings.orb_min_sl_pips || 3.0)}
                                        onChange={e => setP('orbMinSlPips', parseFloat(e.target.value) || 3.0)}
                                        className="w-16 bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 text-right text-slate-200 font-bold"
                                      />
                                      <span className="text-slate-400 text-[10px]">pips</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800 font-mono text-xs">
                                    <span className="text-slate-400 text-[10.5px]">SL Tối đa (Max Risk):</span>
                                    <div className="flex items-center gap-1">
                                      <input
                                        type="number"
                                        step="5"
                                        min="20"
                                        max="500"
                                        value={p.orbMaxSlPips ?? (settings.orb_max_sl_pips || 150.0)}
                                        onChange={e => setP('orbMaxSlPips', parseFloat(e.target.value) || 150.0)}
                                        className="w-16 bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 text-right text-rose-400 font-bold"
                                      />
                                      <span className="text-slate-400 text-[10px]">pips</span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Card: ORB Dynamic Limit Entry on Large SL Breakouts */}
                            <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-amber-400" />
                                  <span className="text-xs font-bold text-amber-200">Vào Lệnh Chờ Limit Khi SL Quá Lớn (ORB Dynamic Limit Entry)</span>
                                </div>
                                <span className="text-[10px] text-amber-400 font-mono font-bold bg-amber-950/60 border border-amber-800/60 px-2 py-0.5 rounded">
                                  Pullback 40-50% Zone
                                </span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5">
                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Chế Độ Chờ Limit</label>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setP('orbDynamicLimitEnable', !Boolean(p.orbDynamicLimitEnable))}
                                    className={`w-full py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                      Boolean(p.orbDynamicLimitEnable)
                                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40 shadow-sm'
                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                    }`}
                                  >
                                    {Boolean(p.orbDynamicLimitEnable) ? 'ĐANG BẬT ✓' : 'ĐANG TẮT ✕'}
                                  </button>
                                  <p className="text-[9.5px] text-slate-400 leading-tight">
                                    {Boolean(p.orbDynamicLimitEnable) ? 'Bật: SL lớn sẽ tự động chuyển sang Limit chờ hồi.' : 'Tắt: Luôn vào lệnh theo Entry Mode.'}
                                  </p>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Ngưỡng Kích Hoạt Limit</label>
                                    <span className="text-[10px] font-mono font-bold text-amber-400 bg-amber-500/15 px-1.5 py-0.5 rounded border border-amber-500/30">
                                      {p.orbLimitThresholdPips ?? (settings.orb_limit_threshold_pips || 100)}p
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="20"
                                      max="300"
                                      step="5"
                                      disabled={!Boolean(p.orbDynamicLimitEnable)}
                                      value={p.orbLimitThresholdPips !== undefined && p.orbLimitThresholdPips !== '' ? p.orbLimitThresholdPips : (settings.orb_limit_threshold_pips ?? 100)}
                                      onChange={e => setP('orbLimitThresholdPips', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-amber-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">pips</span>
                                  </div>
                                  <p className="text-[9.5px] text-slate-400 leading-tight">
                                    SL ban đầu &gt; ngưỡng này (vd: 100-120p) sẽ tự chuyển sang Limit.
                                  </p>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Tỷ Lệ Hồi (Pullback Zone)</label>
                                    <span className="text-[10px] font-mono font-bold text-amber-400 bg-amber-500/15 px-1.5 py-0.5 rounded border border-amber-500/30">
                                      {p.orbLimitPullbackPct ?? (settings.orb_limit_pullback_pct || 50)}%
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="10"
                                      max="90"
                                      step="5"
                                      disabled={!Boolean(p.orbDynamicLimitEnable)}
                                      value={p.orbLimitPullbackPct !== undefined && p.orbLimitPullbackPct !== '' ? p.orbLimitPullbackPct : (settings.orb_limit_pullback_pct ?? 50)}
                                      onChange={e => setP('orbLimitPullbackPct', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-amber-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">%</span>
                                  </div>
                                  <p className="text-[9.5px] text-slate-400 leading-tight">
                                    Đặt Limit hồi 40-50% khoảng cách Breakout-SL để giảm rủi ro.
                                  </p>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Hủy Limit Sau (Bars)</label>
                                    <span className="text-[10px] font-mono font-bold text-amber-400 bg-amber-500/15 px-1.5 py-0.5 rounded border border-amber-500/30">
                                      {p.orbLimitCancelBars ?? (settings.orb_limit_cancel_bars || 10)} nến
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="1"
                                      max="50"
                                      step="1"
                                      disabled={!Boolean(p.orbDynamicLimitEnable)}
                                      value={p.orbLimitCancelBars !== undefined && p.orbLimitCancelBars !== '' ? p.orbLimitCancelBars : (settings.orb_limit_cancel_bars ?? 10)}
                                      onChange={e => setP('orbLimitCancelBars', e.target.value === '' ? '' : (parseInt(e.target.value) || 0))}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-amber-300 font-bold font-mono text-right disabled:opacity-40"
                                    />
                                    <span className="text-[11px] font-bold text-slate-400">nến</span>
                                  </div>
                                  <p className="text-[9.5px] text-slate-400 leading-tight">
                                    Hủy lệnh Limit nếu giá không hồi khớp sau N nến.
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Card 4: EMA 200 Trend Filter & Dual Trend Mode */}
                            <div className="p-3.5 rounded-xl bg-purple-500/10 border border-purple-500/30 space-y-2.5">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <div>
                                  <span className="text-purple-300 text-xs font-bold block flex items-center gap-1.5">
                                    <span>📈 Bộ Lọc Xu Hướng EMA 200:</span>
                                  </span>
                                  <span className="text-[10.5px] text-slate-400">
                                    Chọn phong cách lọc xu hướng hoặc giao dịch chế độ kép (Dual Trend)
                                  </span>
                                </div>
                                <select
                                  value={p.orbEma200Mode || (p.orbEma200Filter ? 'STRICT' : 'OFF')}
                                  onChange={e => {
                                    const mode = e.target.value;
                                    setP('orbEma200Mode', mode);
                                    setP('orbEma200Filter', mode !== 'OFF');
                                  }}
                                  className="bg-slate-900 border border-purple-500/50 rounded-lg px-2.5 py-1 text-xs text-purple-300 font-bold focus:outline-none focus:border-purple-400 cursor-pointer"
                                >
                                  <option value="OFF">⚪ 1. Tắt lọc MA (Đánh 2 đầu tự do)</option>
                                  <option value="STRICT">🔒 2. Lọc Thuận Trend (Trên BUY / Dưới SELL)</option>
                                  <option value="DUAL_TREND">⚡ 3. Chế độ Kép Thông Minh (Giao dịch cả 2 style)</option>
                                </select>
                              </div>
                              <div className="text-[11px] text-slate-300 leading-relaxed bg-slate-950/70 p-2.5 rounded-lg border border-slate-800">
                                {(p.orbEma200Mode || (p.orbEma200Filter ? 'STRICT' : 'OFF')) === 'DUAL_TREND' && (
                                  <span className="text-amber-300">
                                    ⚡ <strong>Chế độ Kép Thông Minh (Combined):</strong> Giao dịch kết hợp đồng thời cả 2 chiến lược (Lọc MA nghiêm ngặt + Đánh tự do không lọc). Hệ thống giữ trọn vẹn toàn bộ các cơ hội breakout & revert tự do, đồng thời bổ sung thêm các cơ hội bắt trend độc lập của hệ thống lọc MA.
                                  </span>
                                )}
                                {(p.orbEma200Mode || (p.orbEma200Filter ? 'STRICT' : 'OFF')) === 'STRICT' && (
                                  <span className="text-cyan-300">
                                    🔒 <strong>Lọc Thuận Trend Tuyệt Đối:</strong> Khi giá trên EMA 200 chỉ cho phép BUY (bỏ qua SELL). Khi giá dưới EMA 200 chỉ cho phép SELL (bỏ qua BUY).
                                  </span>
                                )}
                                {(p.orbEma200Mode || (p.orbEma200Filter ? 'STRICT' : 'OFF')) === 'OFF' && (
                                  <span className="text-slate-400">
                                    ⚪ <strong>Tắt lọc MA:</strong> Đánh tự do cả 2 đầu phá vỡ của hộp phiên (Breakout LÊN = BUY, Breakout XUỐNG = SELL) bất kể vị trí EMA 200.
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Card 5: Dynamic Break-Even */}
                            <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Shield className="w-4 h-4 text-emerald-400" />
                                  <span className="text-xs font-bold text-emerald-200">Dời SL Hòa Vốn Tự Động (Dynamic Break-Even)</span>
                                </div>
                                <span className="text-[10px] text-emerald-400 font-mono font-bold bg-emerald-950/60 border border-emerald-800/60 px-2 py-0.5 rounded">
                                  Risk-Free Engine
                                </span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <label className="text-[10.5px] font-bold text-slate-300 block">Chế Độ Hòa Vốn</label>
                                  <button
                                    type="button"
                                    onClick={() => setP('orbDynamicBeEnable', !p.orbDynamicBeEnable)}
                                    className={`w-full py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                      p.orbDynamicBeEnable
                                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 shadow-sm'
                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                    }`}
                                  >
                                    {p.orbDynamicBeEnable ? 'ĐANG BẬT ✓' : 'ĐANG TẮT ✕'}
                                  </button>
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Ngưỡng Kích Hoạt BE</label>
                                    <span className="text-[10px] font-mono font-bold text-emerald-400 bg-emerald-500/15 px-1.5 py-0.5 rounded border border-emerald-500/30">
                                      {p.orbBeTriggerPct ?? (settings.orb_be_trigger_pct || 75)}% TP
                                    </span>
                                  </div>
                                  <input
                                    type="number"
                                    min="20"
                                    max="95"
                                    step="5"
                                    disabled={!p.orbDynamicBeEnable}
                                    value={p.orbBeTriggerPct ?? (settings.orb_be_trigger_pct || 75)}
                                    onChange={e => setP('orbBeTriggerPct', parseFloat(e.target.value) || 75)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-emerald-300 font-bold font-mono text-right disabled:opacity-40"
                                  />
                                </div>

                                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10.5px] font-bold text-slate-300">Khóa Lãi Bảo Vệ (Pips)</label>
                                    <span className="text-[10px] font-mono font-bold text-emerald-400 bg-emerald-500/15 px-1.5 py-0.5 rounded border border-emerald-500/30">
                                      +{p.orbBeLockPips ?? (settings.orb_be_lock_pips || 5)}p
                                    </span>
                                  </div>
                                  <input
                                    type="number"
                                    min="0"
                                    max="50"
                                    step="1"
                                    disabled={!p.orbDynamicBeEnable}
                                    value={p.orbBeLockPips ?? (settings.orb_be_lock_pips || 5)}
                                    onChange={e => setP('orbBeLockPips', parseFloat(e.target.value) || 5)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-emerald-300 font-bold font-mono text-right disabled:opacity-40"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Card 6: Session Timeout Exit */}
                            <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-slate-200 text-xs font-semibold block">Hết Phiên Ép Đóng Lệnh (Session Timeout Exit):</span>
                                  <span className="text-[10px] text-slate-400">
                                    {p.orbTimeoutExitEnable !== false 
                                      ? "BẬT: Tự động đóng lệnh khi hết phiên nếu chưa chạm TP/SL" 
                                      : "TẮT: Giữ lệnh chạy xuyên phiên thuần TP hoặc SL"}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setP('orbTimeoutExitEnable', p.orbTimeoutExitEnable === false ? true : false)}
                                  className={`px-2.5 py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                    p.orbTimeoutExitEnable !== false
                                      ? "bg-amber-500/20 text-amber-400 border border-amber-500/40 shadow-sm"
                                      : "bg-slate-800 text-slate-400 border border-slate-700"
                                  }`}
                                >
                                  {p.orbTimeoutExitEnable !== false ? "ĐANG BẬT (Ép cắt hết phiên) ✓" : "ĐANG TẮT (Thuần TP/SL) ✕"}
                                </button>
                              </div>
                            </div>

                            {/* Card 7: Revert Strategy (Đảo chiều sau Fakeout / Quét SL) */}
                            <div className="p-3.5 rounded-xl bg-slate-950/80 border border-orange-500/30 space-y-3">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-orange-400 text-xs font-bold flex items-center gap-1.5">
                                    <span>🔄 Đảo Chiều Sau Fakeout (Revert Strategy):</span>
                                  </span>
                                  <span className="text-[10.5px] text-slate-400 block mt-0.5">
                                    Khi lệnh chính bị dính SL (bị quét Fakeout), hệ thống tự động kích hoạt lệnh đảo chiều ngược lại
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setP('orbRevertEnable', !p.orbRevertEnable)}
                                  className={`px-2.5 py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                    p.orbRevertEnable
                                      ? "bg-orange-500/25 text-orange-300 border border-orange-500/50 shadow-sm"
                                      : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-200"
                                  }`}
                                >
                                  {p.orbRevertEnable ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                                </button>
                              </div>

                              {p.orbRevertEnable && (
                                <div className="space-y-3 pt-2 border-t border-slate-800">
                                  <div>
                                    <span className="text-slate-300 text-xs font-semibold block mb-1">Kiểu Vào Lệnh Revert (Entry Mode):</span>
                                    <select
                                      value={p.orbRevertEntryMode || "LIMIT_50_BOX"}
                                      onChange={e => setP('orbRevertEntryMode', e.target.value)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-medium focus:border-orange-500 focus:ring-1 focus:ring-orange-500 cursor-pointer"
                                    >
                                      <option value="LIMIT_50_BOX">Limit tại 50% Thân Hộp Phiên (Midpoint Box)</option>
                                      <option value="SMART_HYBRID">Smart Hybrid (Market nến ngắn &le; {p.orbMaxBreakPips ?? (settings.orb_max_break_pips || 15)}p / Limit hồi 50%)</option>
                                      <option value="MARKET">Vào Lệnh Market Ngay Khi Quét SL</option>
                                      <option value="LIMIT_50_BREAK">Limit Hồi 50% Cây Nến Chạm SL</option>
                                      <option value="LIMIT_EDGE">Limit Tại Mép Hộp Đối Diện</option>
                                    </select>
                                  </div>

                                  <div className="grid grid-cols-3 gap-2.5">
                                    <div className="bg-slate-900/90 border border-slate-800 rounded-lg p-2">
                                      <span className="text-[11px] text-slate-400 block mb-1">Target RR Revert:</span>
                                      <div className="flex items-center gap-1">
                                        <span className="text-xs text-orange-400 font-bold">1:</span>
                                        <input
                                          type="number"
                                          step="0.1"
                                          min="0.1"
                                          max="50"
                                          value={p.orbRevertRr ?? (settings.orb_revert_rr || 1.5)}
                                          onChange={e => setP('orbRevertRr', parseFloat(e.target.value) || 1.5)}
                                          className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 font-bold focus:border-orange-500 text-right"
                                        />
                                      </div>
                                    </div>

                                    <div className="bg-slate-900/90 border border-slate-800 rounded-lg p-2">
                                      <span className="text-[11px] text-slate-400 block mb-1">Số Nến Hủy Limit:</span>
                                      <input
                                        type="number"
                                        step="1"
                                        min="1"
                                        max="200"
                                        value={p.orbRevertCancelBars ?? (settings.orb_revert_cancel_bars || 6)}
                                        onChange={e => setP('orbRevertCancelBars', parseInt(e.target.value) || 6)}
                                        className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 font-bold focus:border-orange-500 text-right"
                                      />
                                    </div>

                                    <div className="bg-slate-900/90 border border-slate-800 rounded-lg p-2">
                                      <span className="text-[11px] text-slate-400 block mb-1">SL Buffer (pips):</span>
                                      <input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        max="500"
                                        value={p.orbRevertSlBufferPips ?? (settings.orb_revert_sl_buffer_pips || 2.0)}
                                        onChange={e => setP('orbRevertSlBufferPips', parseFloat(e.target.value) || 0)}
                                        className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 font-bold focus:border-orange-500 text-right"
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {/* ===================== LIQUIDITY SWEEP PRO PARAMS ===================== */}
                        {(currentStratKey === 'liquidity_sweep' || currentStratKey === 'sweep' || currentStratKey === 'sweep_pro' || currentStratKey === 'liquidity_sweep_pro') && (
                          <div className="space-y-3">
                            {/* Card 1: Entry Mode & Sweep Opt */}
                            <div className="p-3.5 rounded-xl bg-purple-500/10 border border-purple-500/30 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Zap className="w-4 h-4 text-purple-400" />
                                  <span className="text-xs font-bold text-purple-200">Cơ Chế Quét & Vào Lệnh (Liquidity Sweep Engine)</span>
                                </div>
                                <span className="text-[10px] text-purple-400 font-mono font-bold bg-purple-950/60 border border-purple-800/60 px-2 py-0.5 rounded">
                                  Sweep Core
                                </span>
                              </div>

                              <div className="space-y-2">
                                <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Chế Độ Vào Lệnh (Entry Mode)</label>
                                <select
                                  value={p.sweepEntryMode === 'MARKET' ? 'MARKET' : 'RETEST'}
                                  onChange={e => setP('sweepEntryMode', e.target.value)}
                                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-purple-300 font-bold focus:outline-none focus:border-purple-400 cursor-pointer"
                                >
                                  <option value="RETEST">🎯 Limit</option>
                                  <option value="MARKET">⚡ Market</option>
                                </select>

                                {/* Over-threshold Limit Zone Sub-config for EA */}
                                {p.sweepEntryMode !== 'MARKET' && (
                                  <div className="p-3 rounded-lg bg-purple-950/20 border border-purple-800/40 space-y-2.5 mt-2">
                                    <div className="flex items-center justify-between">
                                      <span className="text-[11px] font-bold text-purple-300 flex items-center gap-1.5">
                                        ⚡ Tự động chuyển sang Limit Zone khi nến quét vượt ngưỡng pips
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => setP('sweepZoneLimitEnable', !(p.sweepZoneLimitEnable !== false))}
                                        className={`px-2 py-0.5 text-[10.5px] font-bold rounded-lg transition cursor-pointer ${
                                          p.sweepZoneLimitEnable !== false
                                            ? "bg-purple-500/20 text-purple-400 border border-purple-500/40 shadow-sm"
                                            : "bg-slate-800 text-slate-400 border border-slate-700"
                                        }`}
                                      >
                                        {p.sweepZoneLimitEnable !== false ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                                      </button>
                                    </div>

                                    {p.sweepZoneLimitEnable !== false ? (
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1 border-t border-purple-800/30">
                                        <div>
                                          <div className="flex items-center justify-between mb-1">
                                            <label className="text-[10.5px] font-bold text-purple-200">Ngưỡng Pips Chuyển Limit Zone:</label>
                                            <span className="text-[10px] font-bold text-purple-300 font-mono bg-purple-500/15 px-1.5 py-0.5 rounded border border-purple-500/30">
                                              {p.sweepHybridThresholdPips ?? 80.0} pips ({(p.sweepHybridThresholdPips ?? 80.0) / 10} giá)
                                            </span>
                                          </div>
                                          <input
                                            type="number" min="10" max="500" step="5"
                                            value={p.sweepHybridThresholdPips ?? 80.0}
                                            onChange={e => setP('sweepHybridThresholdPips', parseFloat(e.target.value) || 80.0)}
                                            className="w-full bg-slate-900 border border-purple-700/50 rounded-lg px-2 py-1 text-xs text-purple-300 font-bold font-mono text-right focus:outline-none"
                                          />
                                          <p className="text-[9.5px] text-slate-400 mt-0.5">SL &le; ngưỡng: Limit mép cản; SL &gt; ngưỡng: Limit ở Zone</p>
                                        </div>
                                        <div>
                                          <div className="flex items-center justify-between mb-1">
                                            <label className="text-[10.5px] font-bold text-purple-200 block">Vị Trí Đặt Limit (% Zone tự điền):</label>
                                            <span className="text-[10px] font-bold text-purple-300 font-mono bg-purple-500/15 px-1.5 py-0.5 rounded border border-purple-500/30">
                                              {p.sweepHybridZonePct ?? 50}% Zone
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-1.5">
                                            <div className="relative flex-1">
                                              <input
                                                type="number" min="5" max="95" step="5"
                                                value={p.sweepHybridZonePct ?? 50}
                                                onChange={e => {
                                                  const val = e.target.value === '' ? '' : (parseFloat(e.target.value) || 0);
                                                  setP('sweepHybridZonePct', val);
                                                  setP('sweepHybridLimitMode', 'ZONE_PCT');
                                                }}
                                                className="w-full bg-slate-900 border border-purple-700/50 rounded-lg px-2 py-1 text-xs text-purple-300 font-bold font-mono text-right focus:outline-none pr-6"
                                              />
                                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-purple-400 pointer-events-none">%</span>
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => { setP('sweepHybridZonePct', 40); setP('sweepHybridLimitMode', 'ZONE_40'); }}
                                              className={`px-2 py-1 text-[10.5px] font-bold rounded-lg transition border cursor-pointer ${
                                                Number(p.sweepHybridZonePct) === 40 ? 'bg-purple-600 text-white border-purple-400' : 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
                                              }`}
                                            >
                                              40%
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => { setP('sweepHybridZonePct', 50); setP('sweepHybridLimitMode', 'ZONE_50'); }}
                                              className={`px-2 py-1 text-[10.5px] font-bold rounded-lg transition border cursor-pointer ${
                                                Number(p.sweepHybridZonePct) === 50 ? 'bg-purple-600 text-white border-purple-400' : 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
                                              }`}
                                            >
                                              50%
                                            </button>
                                          </div>
                                          <p className="text-[9.5px] text-slate-400 mt-0.5">Tự điền % vùng cản quét đón lệnh Limit (Mặc định: 50% hoặc 40%).</p>
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="text-[10px] text-slate-400 italic pt-1 border-t border-purple-800/30">
                                        Đang TẮT: Hệ thống sẽ luôn đặt Limit cố định tại mép đỉnh/đáy bị quét, bất kể khoảng cách nến lớn hay nhỏ.
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Major Structure Peak Filter */}
                              <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 space-y-2">
                                <div className="flex items-center justify-between">
                                  <label className="text-[10.5px] font-bold text-slate-300">🏔️ Chỉ Quét Đỉnh Cao Nhất / Đáy Thấp Nhất Đoạn (Major Swing)</label>
                                  <button
                                    type="button"
                                    onClick={() => setP('sweepHighestPeakFilter', !p.sweepHighestPeakFilter)}
                                    className={`px-3 py-0.5 text-xs font-bold rounded-lg transition cursor-pointer ${
                                      Boolean(p.sweepHighestPeakFilter)
                                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40 shadow-sm'
                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                    }`}
                                  >
                                    {Boolean(p.sweepHighestPeakFilter) ? 'BẬT ✓' : 'TẮT ✕'}
                                  </button>
                                </div>
                                {p.sweepHighestPeakFilter && (
                                  <div className="flex items-center justify-between pt-1 border-t border-slate-800/80">
                                    <span className="text-[10px] text-slate-400">Peak Lookback:</span>
                                    <div className="flex items-center gap-1 font-mono text-xs">
                                      <input
                                        type="number" min="10" max="200" step="5"
                                        value={p.sweepPeakLookback ?? 30}
                                        onChange={e => setP('sweepPeakLookback', parseInt(e.target.value) || 30)}
                                        className="w-20 bg-slate-900 border border-slate-700 rounded px-2 py-0.5 text-right text-amber-300 font-bold"
                                      />
                                      <span className="text-slate-400 text-xs">nến</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Card 2: Risk & Order Execution */}
                            <div className="p-3.5 rounded-xl bg-slate-950/80 border border-slate-800 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Sliders className="w-4 h-4 text-emerald-400" />
                                  <span className="text-xs font-bold text-emerald-200">Quản Trị Rủi Ro & Khớp Lệnh (Risk & Execution)</span>
                                </div>
                                <span className="text-[10px] text-emerald-400 font-mono font-bold bg-emerald-950/60 border border-emerald-800/60 px-2 py-0.5 rounded">
                                  Execution
                                </span>
                              </div>

                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                                <div>
                                  <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Tỷ Lệ R:R</label>
                                  <input
                                    type="number" min="0.5" max="10" step="0.1"
                                    value={p.sweepRr ?? 2.0}
                                    onChange={e => setP('sweepRr', parseFloat(e.target.value) || 2.0)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-emerald-300 font-bold font-mono text-right"
                                  />
                                </div>
                                <div>
                                  <label className="text-[10.5px] font-bold text-slate-400 block mb-1">SL Buffer (pips)</label>
                                  <input
                                    type="number" min="0" max="30" step="0.5"
                                    value={p.sweepSlBufferPips ?? 1.5}
                                    onChange={e => setP('sweepSlBufferPips', parseFloat(e.target.value) || 1.5)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-rose-300 font-bold font-mono text-right"
                                  />
                                </div>
                                <div>
                                  <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Cooldown (nến)</label>
                                  <input
                                    type="number" min="1" max="50" step="1"
                                    value={p.sweepCooldownBars ?? 3}
                                    onChange={e => setP('sweepCooldownBars', parseInt(e.target.value) || 3)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-slate-200 font-bold font-mono text-right"
                                  />
                                </div>
                                <div>
                                  <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Hủy Limit sau (nến)</label>
                                  <input
                                    type="number" min="5" max="100" step="1"
                                    value={p.sweepLimitCancelBars ?? 30}
                                    onChange={e => setP('sweepLimitCancelBars', parseInt(e.target.value) || 30)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-amber-300 font-bold font-mono text-right"
                                  />
                                </div>
                              </div>

                              <div className="flex items-center justify-between p-2.5 rounded-lg bg-slate-900 border border-slate-800">
                                <div>
                                  <span className="text-[10.5px] font-bold text-slate-200 block">Đóng lệnh khi có tín hiệu ngược chiều (Close on Opposite)</span>
                                  <span className="text-[9.5px] text-slate-400">Tự hủy Limit và đóng lệnh mở nếu quét ngược chiều</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setP('sweepCloseOnOpposite', !p.sweepCloseOnOpposite)}
                                  className={`px-3 py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                                    Boolean(p.sweepCloseOnOpposite)
                                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                                      : 'bg-slate-800 text-slate-400 border border-slate-700'
                                  }`}
                                >
                                  {Boolean(p.sweepCloseOnOpposite) ? 'BẬT ✓' : 'TẮT ✕'}
                                </button>
                              </div>
                            </div>

                            {/* Card 3: Dynamic Break-Even */}
                            <div className="p-3.5 rounded-xl bg-slate-950/80 border border-slate-800 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Shield className="w-4 h-4 text-emerald-400" />
                                  <span className="text-xs font-bold text-emerald-200">Dời SL Về Hòa Vốn (Dynamic Break-Even)</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setP('sweepDynamicBeEnable', !p.sweepDynamicBeEnable)}
                                  className={`px-3 py-0.5 text-xs font-bold rounded-lg transition cursor-pointer ${
                                    Boolean(p.sweepDynamicBeEnable)
                                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 shadow-sm'
                                      : 'bg-slate-800 text-slate-400 border border-slate-700'
                                  }`}
                                >
                                  {Boolean(p.sweepDynamicBeEnable) ? 'BẬT ✓' : 'TẮT ✕'}
                                </button>
                              </div>

                              {p.sweepDynamicBeEnable && (
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-2 border-t border-slate-800">
                                  <div>
                                    <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Kiểu Kích Hoạt</label>
                                    <select
                                      value={p.sweepBeTriggerMode || 'PCT_TP'}
                                      onChange={e => setP('sweepBeTriggerMode', e.target.value)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-emerald-300 font-bold"
                                    >
                                      <option value="PCT_TP">Theo % TP</option>
                                      <option value="PIPS">Theo số Pips</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="text-[10.5px] font-bold text-slate-400 block mb-1">
                                      {p.sweepBeTriggerMode === 'PIPS' ? 'Ngưỡng Pips' : 'Ngưỡng % TP'}
                                    </label>
                                    <input
                                      type="number"
                                      value={p.sweepBeTriggerMode === 'PIPS' ? (p.sweepBeTriggerPips ?? 40.0) : (p.sweepBeTriggerPct ?? 50.0)}
                                      onChange={e => {
                                        if (p.sweepBeTriggerMode === 'PIPS') {
                                          setP('sweepBeTriggerPips', parseFloat(e.target.value) || 40.0);
                                        } else {
                                          setP('sweepBeTriggerPct', parseFloat(e.target.value) || 50.0);
                                        }
                                      }}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-emerald-300 font-bold font-mono text-right"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Khóa Lãi (Pips)</label>
                                    <input
                                      type="number" min="0" max="20" step="0.5"
                                      value={p.sweepBeLockPips ?? 2.0}
                                      onChange={e => setP('sweepBeLockPips', parseFloat(e.target.value) || 2.0)}
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-emerald-300 font-bold font-mono text-right"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                      </div>

                      {/* 3. Money Management */}
                      <div className="p-3.5 rounded-xl bg-slate-950/80 border border-slate-800 space-y-3">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                          3. Quản Lý Khối Lượng & Quản Trị Rủi Ro
                        </span>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="text-xs font-bold text-slate-300 block mb-1">Chế Độ Tính Lot</label>
                            <select
                              value={stratForm.lot_mode}
                              onChange={e => setStratForm({ ...stratForm, lot_mode: e.target.value })}
                              className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-100 font-bold focus:outline-none focus:border-purple-500 cursor-pointer"
                            >
                              <option value="FIXED">📦 Lot Cố Định (Fixed)</option>
                              <option value="RISK_PCT">🛡️ % Rủi Ro Tài Khoản (% Risk)</option>
                            </select>
                          </div>

                          {stratForm.lot_mode === 'FIXED' ? (
                            <div>
                              <label className="text-xs font-bold text-slate-300 block mb-1">Khối Lượng (Lots)</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                max="50.0"
                                value={stratForm.lot_size}
                                onChange={e => setStratForm({ ...stratForm, lot_size: parseFloat(e.target.value) || 0.01 })}
                                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-100 font-mono focus:outline-none focus:border-purple-500"
                              />
                            </div>
                          ) : (
                            <div>
                              <label className="text-xs font-bold text-slate-300 block mb-1">% Rủi Ro / Lệnh (%)</label>
                              <input
                                type="number"
                                step="0.1"
                                min="0.1"
                                max="10.0"
                                value={stratForm.risk_pct}
                                onChange={e => setStratForm({ ...stratForm, risk_pct: parseFloat(e.target.value) || 1.0 })}
                                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-rose-400 font-mono font-bold focus:outline-none focus:border-purple-500"
                              />
                            </div>
                          )}

                          <div>
                            <label className="text-xs font-bold text-slate-300 block mb-1">Mục Tiêu TP Khớp Lệnh</label>
                            <select
                              value={stratForm.tp_mode}
                              onChange={e => setStratForm({ ...stratForm, tp_mode: e.target.value })}
                              className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-100 font-bold focus:outline-none focus:border-purple-500 cursor-pointer"
                            >
                              <option value="TP1">TP1 (An Toàn / Mặc Định)</option>
                              <option value="TP2">TP2 (Mở Rộng)</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="text-xs font-bold text-slate-300 block mb-1">Giới Hạn Spread Tối Đa (Points)</label>
                          <input
                            type="number"
                            step="1"
                            value={stratForm.max_spread}
                            onChange={e => setStratForm({ ...stratForm, max_spread: parseFloat(e.target.value) || 40.0 })}
                            placeholder="40"
                            className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-100 font-mono focus:outline-none focus:border-purple-500"
                          />
                        </div>
                      </div>

                      {/* 4. Break-Even & Trailing Stop */}
                      <div className="p-3.5 rounded-xl bg-slate-950/80 border border-slate-800 space-y-3">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                          4. Tự Động Hòa Vốn (BE) & Dời Lỗ (Trailing)
                        </span>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <label className="text-xs font-bold text-slate-300">Break-Even (Hòa Vốn)</label>
                              <input
                                type="checkbox"
                                checked={stratForm.breakeven_enable}
                                onChange={e => setStratForm({ ...stratForm, breakeven_enable: e.target.checked })}
                                className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
                              />
                            </div>
                            {stratForm.breakeven_enable && (
                              <input
                                type="number"
                                step="1"
                                value={stratForm.breakeven_pips}
                                onChange={e => setStratForm({ ...stratForm, breakeven_pips: parseFloat(e.target.value) || 15.0 })}
                                placeholder="Pips kích hoạt (15)"
                                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-1.5 text-xs text-slate-100 font-mono"
                              />
                            )}
                          </div>

                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <label className="text-xs font-bold text-slate-300">Trailing Stop</label>
                              <input
                                type="checkbox"
                                checked={stratForm.trailing_enable}
                                onChange={e => setStratForm({ ...stratForm, trailing_enable: e.target.checked })}
                                className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
                              />
                            </div>
                            {stratForm.trailing_enable && (
                              <input
                                type="number"
                                step="1"
                                value={stratForm.trailing_pips}
                                onChange={e => setStratForm({ ...stratForm, trailing_pips: parseFloat(e.target.value) || 30.0 })}
                                placeholder="Khoảng cách Pips (20)"
                                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-1.5 text-xs text-slate-100 font-mono"
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* 5. Session Timing */}
                      <div className="p-3.5 rounded-xl bg-slate-950/80 border border-slate-800 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-xs font-bold text-slate-200 block">5. Bộ Lọc Khung Giờ Giao Dịch</span>
                            <span className="text-[10px] text-slate-500">Chỉ mở lệnh trong khoảng thời gian quy định.</span>
                          </div>
                          <input
                            type="checkbox"
                            checked={stratForm.session_enable}
                            onChange={e => setStratForm({ ...stratForm, session_enable: e.target.checked })}
                            className="w-4 h-4 rounded text-purple-600 focus:ring-0 cursor-pointer"
                          />
                        </div>

                        {stratForm.session_enable && (
                          <div className="grid grid-cols-2 gap-3 pt-1">
                            <div>
                              <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Giờ Bắt Đầu</label>
                              <input
                                type="time"
                                value={stratForm.session_start}
                                onChange={e => setStratForm({ ...stratForm, session_start: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-1.5 text-xs text-slate-100 font-mono"
                              />
                            </div>
                            <div>
                              <label className="text-[10.5px] font-bold text-slate-400 block mb-1">Giờ Kết Thúc</label>
                              <input
                                type="time"
                                value={stratForm.session_end}
                                onChange={e => setStratForm({ ...stratForm, session_end: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-1.5 text-xs text-slate-100 font-mono"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-800 sticky bottom-0 bg-slate-900 pb-1">
                        <button
                          type="button"
                          onClick={() => setShowAddStratModal(false)}
                          className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition cursor-pointer"
                        >
                          Hủy
                        </button>
                        <button
                          type="submit"
                          className="px-5 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition cursor-pointer shadow-lg shadow-purple-600/20 flex items-center gap-1.5"
                        >
                          <Save className="w-3.5 h-3.5" />
                          <span>{editingStrat ? 'Lưu Cấu Hình Toàn Bộ' : 'Thêm Cấu Hình Ngay'}</span>
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
                );
              })()}

              {/* MODAL 3: XÁC NHẬN ĐÓNG TOÀN BỘ LỆNH */}
              {confirmCloseAll && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
                  <div className="w-full max-w-md bg-slate-900 border border-rose-500/40 rounded-2xl shadow-2xl p-5 space-y-4">
                    <div className="flex items-center gap-3 text-rose-400">
                      <div className="p-3 rounded-xl bg-rose-500/20 border border-rose-500/40">
                        <AlertTriangle className="w-6 h-6 text-rose-400" />
                      </div>
                      <div>
                        <h3 className="font-extrabold text-sm text-slate-100">Xác Nhận Đóng Toàn Bộ Lệnh Khẩn Cấp!</h3>
                        <p className="text-xs text-rose-300">Thao tác này sẽ gửi lệnh đóng tất cả các vị thế đang mở tới MT5 EA.</p>
                      </div>
                    </div>

                    <p className="text-xs text-slate-300 leading-relaxed bg-slate-950/80 p-3 rounded-xl border border-slate-800">
                      Bạn có chắc chắn muốn đóng ngay lập tức <b>{eaPositions.length} vị thế</b> đang chạy trên tất cả các tài khoản MT5 không?
                    </p>

                    <div className="flex items-center justify-end gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => setConfirmCloseAll(false)}
                        className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition cursor-pointer"
                      >
                        Hủy Bỏ
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCloseAllPositions(null)}
                        className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-black transition cursor-pointer shadow-lg shadow-rose-600/30"
                      >
                        Đồng Ý Đóng Toàn Bộ
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}

          {/* SECTION: CONNECTION & GATEWAY FEEDER */}
          {activeSection === 'connection' && (
            <div className="space-y-6">
              {connMsg.text && (
                <div className={`p-3.5 rounded-xl border text-xs flex items-center gap-2 ${
                  connMsg.type === 'success'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                }`}>
                  {connMsg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  <span>{connMsg.text}</span>
                </div>
              )}

              {/* Card 1: 24/7 Live Stream Feeder Status */}
              <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-xl space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 pb-4">
                  <div>
                    <h3 className="text-sm font-bold text-slate-100 flex items-center gap-2">
                      <Radio className="w-4 h-4 text-emerald-400 animate-pulse" />
                      <span>Trạng Thái Nguồn Cấp Dữ Liệu Thị Trường Realtime (Gateway Feeder 24/7)</span>
                    </h3>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Dữ liệu giá và nến được bơm liên tục từ Windows VPS MT5 Feeder sang máy chủ Web
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 rounded-lg text-xs font-bold font-mono bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 flex items-center gap-1.5 shadow-sm">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                      ĐÃ KẾT NỐI LUỒNG LIVE
                    </span>
                  </div>
                </div>

                {/* Status Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-4 rounded-xl bg-slate-950/70 border border-slate-800/80 space-y-1">
                    <span className="text-[10.5px] font-semibold text-slate-400 uppercase tracking-wider block">Máy Chủ Exness MT5</span>
                    <span className="text-sm font-bold font-mono text-cyan-300">
                      {systemStatus?.server || 'Exness-MT5Trial14'}
                    </span>
                  </div>

                  <div className="p-4 rounded-xl bg-slate-950/70 border border-slate-800/80 space-y-1">
                    <span className="text-[10.5px] font-semibold text-slate-400 uppercase tracking-wider block">Mã Tài Khoản Nguồn</span>
                    <span className="text-sm font-bold font-mono text-amber-300">
                      {systemStatus?.account || '416296152'}
                    </span>
                  </div>

                  <div className="p-4 rounded-xl bg-slate-950/70 border border-slate-800/80 space-y-1">
                    <span className="text-[10.5px] font-semibold text-slate-400 uppercase tracking-wider block">Cặp Tiền & Khung Đồng Bộ</span>
                    <span className="text-sm font-bold font-mono text-emerald-400">
                      8 Cặp • M1 — D1 (100% Khớp)
                    </span>
                  </div>

                  <div className="p-4 rounded-xl bg-slate-950/70 border border-slate-800/80 space-y-1">
                    <span className="text-[10.5px] font-semibold text-slate-400 uppercase tracking-wider block">Địa Chỉ VPS Feeder</span>
                    <span className="text-sm font-bold font-mono text-purple-300">
                      103.176.20.221 (Windows MT5)
                    </span>
                  </div>
                </div>

                {/* Action Bar */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                  <div className="text-xs text-slate-400 flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span>Hệ thống hoạt động tự động 24/7. Khách vào trang web không cần phải đăng nhập MT5.</span>
                  </div>

                  <button
                    type="button"
                    onClick={handleTriggerResync}
                    disabled={resyncing}
                    className="px-4 py-2 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300 font-bold text-xs transition cursor-pointer flex items-center gap-2 shadow-sm"
                  >
                    <RefreshCcw className={`w-4 h-4 ${resyncing ? 'animate-spin' : ''}`} />
                    <span>{resyncing ? 'Đang Gửi Yêu Cầu...' : 'Kích Hoạt Đồng Bộ Lại Lịch Sử Nến (Force Resync)'}</span>
                  </button>
                </div>
              </div>

              {/* Card 2: Manual Direct MT5 Connector */}
              <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-xl space-y-4">
                <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                  <div>
                    <h3 className="text-sm font-bold text-slate-100 flex items-center gap-2">
                      <Server className="w-4 h-4 text-cyan-400" />
                      <span>Cấu Hình Kết Nối MT5 Thủ Công / Dự Phòng (Dành Cho Admin)</span>
                    </h3>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Chỉ sử dụng khi muốn Admin chuyển đổi sang tài khoản MT5 nội bộ khác
                    </p>
                  </div>
                  {systemStatus?.connected && (
                    <button
                      type="button"
                      onClick={handleDisconnectMT5}
                      className="px-3 py-1.5 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/40 text-rose-300 font-bold text-xs transition cursor-pointer flex items-center gap-1.5"
                    >
                      <Power className="w-3.5 h-3.5" />
                      <span>Ngắt Kết Nối MT5</span>
                    </button>
                  )}
                </div>

                <form onSubmit={handleConnectMT5} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                      Số Tài Khoản (MT5 Login ID)
                    </label>
                    <input
                      type="text"
                      value={mt5Form.account}
                      onChange={(e) => setMt5Form({ ...mt5Form, account: e.target.value })}
                      placeholder="Ví dụ: 14598213"
                      className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500 font-mono"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                      Mật Khẩu Giao Dịch
                    </label>
                    <input
                      type="password"
                      value={mt5Form.password}
                      onChange={(e) => setMt5Form({ ...mt5Form, password: e.target.value })}
                      placeholder="••••••••"
                      className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500 font-mono"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                      Server Exness
                    </label>
                    <input
                      type="text"
                      value={mt5Form.server}
                      onChange={(e) => setMt5Form({ ...mt5Form, server: e.target.value })}
                      placeholder="Exness-MT5Real hoặc Exness-MT5Trial14"
                      className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500 font-mono"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                      Đường Dẫn terminal64.exe (Tùy Chọn)
                    </label>
                    <input
                      type="text"
                      value={mt5Form.path}
                      onChange={(e) => setMt5Form({ ...mt5Form, path: e.target.value })}
                      placeholder="C:\Program Files\MetaTrader 5\terminal64.exe"
                      className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500 font-mono"
                    />
                  </div>

                  <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={connectingMT5}
                      className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs transition cursor-pointer flex items-center gap-2 shadow-lg shadow-emerald-600/20"
                    >
                      <Wifi className={`w-4 h-4 ${connectingMT5 ? 'animate-spin' : ''}`} />
                      <span>{connectingMT5 ? 'Đang Kết Nối...' : 'Kết Nối Trực Tiếp MT5'}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* SECTION 2: AI SETTINGS */}
          {activeSection === 'ai' && (
            <div className="space-y-6">
              {settingsMsg.text && (
                <div className={`p-3.5 rounded-xl border text-xs flex items-center gap-2 ${
                  settingsMsg.type === 'success'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                }`}>
                  {settingsMsg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  <span>{settingsMsg.text}</span>
                </div>
              )}

              <div className="p-4 rounded-2xl bg-cyan-950/20 border border-cyan-500/30 text-xs text-cyan-300 space-y-1.5">
                <strong className="text-cyan-200 block text-sm font-bold flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Cơ Chế Phân Tích AI Tập Trung Của Hệ Thống
                </strong>
                <p className="text-slate-300 leading-relaxed">
                  Admin cấu hình 1 Google Gemini API Key tại đây. Khi người dùng bấm <strong>"AI Live"</strong> trên biểu đồ hoặc xem dự báo, hệ thống sẽ tự động sử dụng cấu hình AI này để trả lời và phân tích thị trường cho người dùng mà người dùng không cần đăng ký API Key riêng.
                </p>
              </div>

              <form onSubmit={handleSaveSettings} className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-xl space-y-5">
                {/* Gemini API Key */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                    Google Gemini API Key (Khuyên dùng)
                  </label>
                  <div className="relative">
                    <input
                      type={showGeminiKey ? "text" : "password"}
                      value={settings.gemini_api_key}
                      onChange={(e) => setSettings({ ...settings, gemini_api_key: e.target.value })}
                      placeholder="AIzaSy..."
                      className="w-full bg-slate-950 border border-slate-700/80 rounded-xl pl-3.5 pr-10 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowGeminiKey(!showGeminiKey)}
                      className="absolute right-3.5 top-2.5 text-slate-400 hover:text-slate-200 cursor-pointer"
                    >
                      {showGeminiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-400">
                    Đăng ký và lấy key miễn phí tại: <a href="https://aistudio.google.com/" target="_blank" rel="noreferrer" className="text-cyan-400 underline">Google AI Studio</a>.
                  </p>
                </div>

                {/* Model Selector */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                    Mô Hình AI Mặc Định
                  </label>
                  <select
                    value={settings.default_ai_model}
                    onChange={(e) => setSettings({ ...settings, default_ai_model: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 focus:outline-none focus:border-purple-500 cursor-pointer"
                  >
                    <option value="gemini-1.5-flash">Gemini 1.5 Flash (Tốc độ siêu nhanh & Chuẩn xác - Khuyên dùng)</option>
                    <option value="gemini-1.5-pro">Gemini 1.5 Pro (Mô hình suy luận sâu nhất)</option>
                  </select>
                </div>

                {/* OpenAI API Key (Optional) */}
                <div className="space-y-1.5 pt-3 border-t border-slate-800">
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                    OpenAI API Key (Dự phòng tùy chọn)
                  </label>
                  <div className="relative">
                    <input
                      type={showOpenAIKey ? "text" : "password"}
                      value={settings.openai_api_key}
                      onChange={(e) => setSettings({ ...settings, openai_api_key: e.target.value })}
                      placeholder="sk-proj-..."
                      className="w-full bg-slate-950 border border-slate-700/80 rounded-xl pl-3.5 pr-10 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowOpenAIKey(!showOpenAIKey)}
                      className="absolute right-3.5 top-2.5 text-slate-400 hover:text-slate-200 cursor-pointer"
                    >
                      {showOpenAIKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleTestAIGeneration}
                    disabled={testingAI}
                    className="px-4 py-2.5 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 text-cyan-300 font-bold text-xs transition cursor-pointer flex items-center gap-2"
                  >
                    <Sparkles className={`w-4 h-4 ${testingAI ? 'animate-spin' : ''}`} />
                    <span>{testingAI ? 'Đang Kiểm Tra AI...' : 'Chạy Thử Nghiệm AI Phân Tích'}</span>
                  </button>

                  <button
                    type="submit"
                    disabled={savingSettings}
                    className="px-6 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs transition cursor-pointer flex items-center gap-2 shadow-lg shadow-purple-600/20"
                  >
                    <Save className="w-4 h-4" />
                    <span>{savingSettings ? 'Đang Lưu...' : 'Lưu Cấu Hình AI'}</span>
                  </button>
                </div>
              </form>

              {/* AI Test Output Preview */}
              {aiTestResult && (
                <div className="p-5 rounded-2xl bg-slate-900/90 border border-cyan-500/40 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-cyan-300 uppercase tracking-wider flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Kết Quả Phân Tích Thử Nghiệm Từ AI ({aiTestResult.source})
                    </h4>
                    <span className="text-[10px] text-slate-400">Trạng thái: OK 200</span>
                  </div>
                  <pre className="p-3 bg-slate-950 rounded-xl text-xs text-slate-300 font-mono overflow-x-auto border border-slate-800">
                    {JSON.stringify(aiTestResult.analysis, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* SECTION 3: TELEGRAM BOT */}
          {activeSection === 'telegram' && (
            <div className="space-y-6">
              {tgMsg.text && (
                <div className={`p-3.5 rounded-xl border text-xs flex items-center gap-2 ${
                  tgMsg.type === 'success'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                }`}>
                  {tgMsg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  <span>{tgMsg.text}</span>
                </div>
              )}

              <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-xl space-y-5">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                    Telegram Bot Token
                  </label>
                  <input
                    type="text"
                    value={settings.telegram_bot_token}
                    onChange={(e) => setSettings({ ...settings, telegram_bot_token: e.target.value })}
                    placeholder="123456789:ABCdefGHIjklMNOpqrs..."
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500 font-mono"
                  />
                  <p className="text-[11px] text-slate-400">Lấy token từ BotFather trên Telegram.</p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                    Telegram Chat ID / Channel ID
                  </label>
                  <input
                    type="text"
                    value={settings.telegram_chat_id}
                    onChange={(e) => setSettings({ ...settings, telegram_chat_id: e.target.value })}
                    placeholder="-1001234567890 hoặc @ten_channel"
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500 font-mono"
                  />
                  <p className="text-[11px] text-slate-400">ID của nhóm, kênh hoặc tài khoản cá nhân nhận cảnh báo.</p>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleTestTelegram}
                    disabled={testingTg || !settings.telegram_bot_token || !settings.telegram_chat_id}
                    className="px-4 py-2.5 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300 font-bold text-xs transition cursor-pointer flex items-center gap-2"
                  >
                    <Send className={`w-4 h-4 ${testingTg ? 'animate-pulse' : ''}`} />
                    <span>{testingTg ? 'Đang Gửi Thử...' : 'Gửi Thử Tin Nhắn Telegram'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                    className="px-6 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs transition cursor-pointer flex items-center gap-2 shadow-lg shadow-purple-600/20"
                  >
                    <Save className="w-4 h-4" />
                    <span>Lưu Cấu Hình Bot</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* SECTION: EMAIL OTP & GOOGLE OAUTH */}
          {activeSection === 'email_oauth' && (
            <div className="space-y-6">
              {emailTestMsg.text && (
                <div className={`p-3.5 rounded-xl border text-xs flex items-center gap-2 animate-fadeIn ${
                  emailTestMsg.type === 'success'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                }`}>
                  {emailTestMsg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  <span>{emailTestMsg.text}</span>
                </div>
              )}

              {settingsMsg.text && (
                <div className={`p-3.5 rounded-xl border text-xs flex items-center gap-2 animate-fadeIn ${
                  settingsMsg.type === 'success'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                }`}>
                  {settingsMsg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  <span>{settingsMsg.text}</span>
                </div>
              )}

              {/* Card 1: Google OAuth 2.0 Client ID */}
              <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-xl space-y-4">
                <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                  <h3 className="text-sm font-bold text-slate-100 flex items-center gap-2">
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                    </svg>
                    <span>Cấu Hình Đăng Nhập Google (Google OAuth 2.0 SSO)</span>
                  </h3>
                  <span className="text-[10px] text-cyan-400 font-mono font-bold bg-cyan-950/60 border border-cyan-800/60 px-2 py-0.5 rounded">
                    One-Tap & SSO Button
                  </span>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                    Google OAuth Client ID
                  </label>
                  <input
                    type="text"
                    value={settings.google_client_id}
                    onChange={(e) => setSettings({ ...settings, google_client_id: e.target.value })}
                    placeholder="729606869400-xxxxxxxx.apps.googleusercontent.com"
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500 font-mono"
                  />
                  <p className="text-[11px] text-slate-400">
                    Lấy Client ID từ <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="text-cyan-400 underline">Google Cloud Console</a> (Thêm Authorized Javascript Origins: <code className="text-slate-300">https://minotradingpro.com</code>).
                  </p>
                </div>
              </div>

              {/* Card 2: SMTP Email Service Configuration */}
              <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-xl space-y-4">
                <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                  <h3 className="text-sm font-bold text-slate-100 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-rose-400" />
                    <span>Cấu Hình Máy Chủ Gửi Email SMTP (Gửi Mã OTP Đặt Lại Mật Khẩu)</span>
                  </h3>
                  <span className="text-[10px] text-emerald-400 font-mono font-bold bg-emerald-950/60 border border-emerald-800/60 px-2 py-0.5 rounded">
                    Gmail / Hostinger / Custom SMTP
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                      SMTP Host (Máy Chủ Gửi Mail)
                    </label>
                    <input
                      type="text"
                      value={settings.smtp_host}
                      onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                      placeholder="smtp.gmail.com"
                      className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500 font-mono"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                      SMTP Port (Cổng Kết Nối)
                    </label>
                    <input
                      type="text"
                      value={settings.smtp_port}
                      onChange={(e) => setSettings({ ...settings, smtp_port: e.target.value })}
                      placeholder="587 hoặc 465"
                      className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500 font-mono"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                      Tài Khoản Email Gửi (SMTP User)
                    </label>
                    <input
                      type="email"
                      value={settings.smtp_user}
                      onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
                      placeholder="your-bot@gmail.com"
                      className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500 font-mono"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                      Mật Khẩu Ứng Dụng (App Password)
                    </label>
                    <div className="relative">
                      <input
                        type={showSmtpPwd ? "text" : "password"}
                        value={settings.smtp_password}
                        onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                        placeholder="16 chữ cái mật khẩu ứng dụng Gmail"
                        className="w-full bg-slate-950 border border-slate-700/80 rounded-xl pl-3.5 pr-10 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500 font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSmtpPwd(!showSmtpPwd)}
                        className="absolute right-3.5 top-2.5 text-slate-400 hover:text-slate-200 cursor-pointer"
                      >
                        {showSmtpPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                      Tên Hiển Thị Người Gửi (Sender Name)
                    </label>
                    <input
                      type="text"
                      value={settings.smtp_sender_name}
                      onChange={(e) => setSettings({ ...settings, smtp_sender_name: e.target.value })}
                      placeholder="MINO TRADING PRO"
                      className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                      Email Người Gửi (From Email)
                    </label>
                    <input
                      type="email"
                      value={settings.smtp_from_email}
                      onChange={(e) => setSettings({ ...settings, smtp_from_email: e.target.value })}
                      placeholder="admin@minotradingpro.com hoặc noreply@minotradingpro.com"
                      className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500 font-mono"
                    />
                  </div>

                  <div className="md:col-span-2 text-[11px] text-slate-400 bg-slate-950/40 p-3 rounded-xl border border-slate-800/80 space-y-1">
                    <p className="text-cyan-300 font-semibold">💡 Hướng dẫn cấu hình Resend / Brevo / Gmail:</p>
                    <p>• <strong>Resend:</strong> Host: <code className="text-slate-200">smtp.resend.com</code> | Port: <code className="text-slate-200">587</code> | User: <code className="text-slate-200">resend</code> | Pass: <code className="text-slate-200">re_...</code> (API Key) | From: <code className="text-slate-200">admin@minotradingpro.com</code></p>
                    <p>• <strong>Gmail:</strong> Host: <code className="text-slate-200">smtp.gmail.com</code> | Port: <code className="text-slate-200">587</code> | User: <code className="text-slate-200">your-email@gmail.com</code> | Pass: <code className="text-slate-200">Mật khẩu ứng dụng 16 số</code></p>
                  </div>
                </div>

                {/* Actions & Test Email */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-800">
                  <div className="flex items-center gap-2 flex-1 min-w-[260px]">
                    <input
                      type="email"
                      value={testEmailTo}
                      onChange={(e) => setTestEmailTo(e.target.value)}
                      placeholder="Nhập email nhận thử nghiệm..."
                      className="bg-slate-950 border border-slate-700/80 rounded-xl px-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-rose-400 font-mono flex-1"
                    />
                    <button
                      type="button"
                      onClick={handleTestEmail}
                      disabled={testingEmail || !settings.smtp_user || !settings.smtp_password}
                      className="px-4 py-2 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/40 text-rose-300 font-bold text-xs transition cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
                    >
                      <Send className={`w-3.5 h-3.5 ${testingEmail ? 'animate-pulse' : ''}`} />
                      <span>{testingEmail ? 'Đang Gửi...' : 'Gửi Thử Email'}</span>
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                    className="px-6 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs transition cursor-pointer flex items-center gap-2 shadow-lg shadow-purple-600/20 whitespace-nowrap"
                  >
                    <Save className="w-4 h-4" />
                    <span>Lưu Toàn Bộ Cấu Hình</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* SECTION: SMC & LIMIT ENTRY SYSTEM CONFIGURATION */}
          {activeSection === 'smc_config' && (
            <div className="space-y-5">
              {/* Top Strategy Action Header */}
              <div className="p-5 rounded-2xl bg-gradient-to-r from-slate-900 via-slate-900 to-slate-900/90 border border-slate-800 shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 shadow-md shadow-amber-500/10 shrink-0">
                    <Settings className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-extrabold text-slate-100 tracking-tight">Cấu Hình Thuật Toán SMC & Limit Entry</h2>
                      <span className="text-[10px] text-amber-400 font-mono font-bold bg-amber-950/70 border border-amber-800/60 px-2 py-0.5 rounded-full">
                        SMC MSB Engine v2.5
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Cấu hình tỷ lệ Limit Mid-Same • Bộ lọc khoảng giá FVG • Chiến lược Revert bẫy giá • Đồng bộ toàn hệ thống
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 shrink-0">
                  <button
                    type="button"
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                    className="px-5 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 active:scale-95 text-white font-bold text-xs transition-all cursor-pointer flex items-center gap-2 shadow-lg shadow-amber-600/25 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    <span>{savingSettings ? "Đang Lưu Cấu Hình..." : "Lưu Cấu Hình SMC"}</span>
                  </button>
                </div>
              </div>

              {/* Status Alert Message */}
              {settingsMsg.text && (
                <div className={`p-3.5 rounded-xl border text-xs flex items-center gap-2.5 transition-all ${
                  settingsMsg.type === 'success'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                }`}>
                  {settingsMsg.type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
                  <span className="font-medium">{settingsMsg.text}</span>
                </div>
              )}

              {/* TWO-COLUMN WORKSTATION GRID */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
                
                {/* === LEFT COLUMN: MID-SAME & ENTRY MODE === */}
                <div className="space-y-5">
                  
                  {/* Card 1: Default Mid-Same % & Entry Mode */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
                          <Settings className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Cấu Hình Tỷ Lệ Limit Mid-Same</h3>
                          <p className="text-[11px] text-slate-400">Tỷ lệ thoái lui nến và chế độ khớp lệnh mặc định</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-amber-400 font-mono font-bold bg-amber-950/60 border border-amber-800/60 px-2 py-0.5 rounded">
                        Mid-Same Engine
                      </span>
                    </div>

                    <div className="space-y-3.5">
                      {/* Mid-Same % Input */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Tỷ Lệ Hồi Quy Mid-Same Mặc Định</label>
                          <span className="text-xs font-bold text-amber-400 font-mono px-2 py-0.5 rounded bg-amber-500/15 border border-amber-500/30">
                            {settings.default_mid_same_pct || 50}%
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            max="99"
                            step="0.5"
                            value={settings.default_mid_same_pct !== undefined ? settings.default_mid_same_pct : 50}
                            onChange={(e) => setSettings({ ...settings, default_mid_same_pct: parseFloat(e.target.value) || 50 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs text-amber-300 font-bold focus:border-amber-500 focus:outline-none font-mono"
                          />
                          <span className="text-xs font-bold text-slate-400 font-mono">%</span>
                        </div>
                        <div className="flex items-center gap-1.5 pt-1 flex-wrap">
                          <span className="text-[10px] text-slate-500 mr-1">Mốc chuẩn:</span>
                          {[30, 40, 50, 60, 70].map((pct) => (
                            <button
                              key={pct}
                              type="button"
                              onClick={() => setSettings({ ...settings, default_mid_same_pct: pct })}
                              className={`px-2.5 py-1 text-[11px] font-mono rounded-lg transition cursor-pointer font-bold ${
                                (settings.default_mid_same_pct || 50) === pct
                                  ? "bg-amber-500/25 text-amber-300 border border-amber-500/50"
                                  : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800"
                              }`}
                            >
                              {pct}%
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Entry Mode Selector */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <label className="text-xs font-bold text-slate-300 block">Chế Độ Vào Lệnh Mặc Định Toàn Hệ Thống</label>
                        <select
                          value={settings.default_entry_mode || "LIMIT_MID"}
                          onChange={(e) => setSettings({ ...settings, default_entry_mode: e.target.value })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-2 text-xs text-slate-100 font-bold focus:border-amber-500 focus:outline-none"
                        >
                          <option value="MARKET">Vào Lệnh Thị Trường Ngay Lập Tức (Market Instant)</option>
                          <option value="LIMIT_FVG">Limit 1: Phiên Mỹ FVG + Thoái Lui Mid-Same</option>
                          <option value="LIMIT_MID">Limit 2: Thuần Mid-Same % (Cố Định Theo Tỷ Lệ Cài Đặt)</option>
                          <option value="LIMIT_ZONE">Limit 3: Vùng Đỉnh/Đáy Cụm Nến (Zone Retest)</option>
                          <option value="SMART_HYBRID">Smart Hybrid: Tự Động Chọn Giữa Market & Limit</option>
                        </select>
                        <p className="text-[11px] text-slate-400 leading-relaxed pt-1">
                          Áp dụng làm giá trị mặc định chuẩn cho toàn bộ người dùng mới và EA MT5 khi mở biểu đồ SMC MSB.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: FVG Filter */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                          <Zap className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Bộ Lọc Vùng Khuyết Khoảng Giá FVG</h3>
                          <p className="text-[11px] text-slate-400">Fair Value Gap Retest Engine</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, smc_fvg_filter_enable: !settings.smc_fvg_filter_enable })}
                        className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition cursor-pointer ${
                          settings.smc_fvg_filter_enable
                            ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/40"
                            : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                        }`}
                      >
                        {settings.smc_fvg_filter_enable ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                      </button>
                    </div>

                    <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800">
                      <p className="text-xs text-slate-300 leading-relaxed">
                        {settings.smc_fvg_filter_enable
                          ? "Đang bật: Bắt buộc nến kích hoạt phải tạo khoảng trống mất cân bằng cung cầu (Fair Value Gap) hợp lệ trước khi xem xét tín hiệu phá vỡ cấu trúc MSB."
                          : "Đang tắt: Chấp nhận mọi tín hiệu phá vỡ cấu trúc đỉnh/đáy SMC bất kể có xuất hiện khoảng trống FVG hay không."}
                      </p>
                    </div>
                  </div>

                </div>

                {/* === RIGHT COLUMN: REVERT STRATEGY & EXITS === */}
                <div className="space-y-5">
                  
                  {/* Card 3: Revert Strategy After SL */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center justify-center text-orange-400">
                          <RefreshCw className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Chiến Lược Đảo Chiều Sau Dính SL (Revert Trap)</h3>
                          <p className="text-[11px] text-slate-400">Đảo chiều ngược lại ngay khi lệnh chính bị quét cắt lỗ (Bẫy thanh khoản SMC)</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, smc_revert_enable: !settings.smc_revert_enable })}
                        className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition cursor-pointer ${
                          settings.smc_revert_enable
                            ? "bg-orange-500/20 text-orange-400 border border-orange-500/40"
                            : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                        }`}
                      >
                        {settings.smc_revert_enable ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                      </button>
                    </div>

                    <p className="text-xs text-slate-400 leading-relaxed">
                      Tự động kích hoạt lệnh đảo ngược xu hướng khi lệnh SMC ban đầu bị dính Stop Loss nhằm khai thác triệt để các pha bẫy thanh khoản (Liquidity Trap).
                    </p>

                    {settings.smc_revert_enable ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
                        <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <label className="text-[11px] font-bold text-slate-300 block">Kiểu Vào Lệnh (Entry Mode)</label>
                          <select
                            value={settings.smc_revert_entry_mode || "MARKET"}
                            onChange={(e) => setSettings({ ...settings, smc_revert_entry_mode: e.target.value })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-orange-500 focus:outline-none"
                          >
                            <option value="MARKET">Market (Vào Ngay Khi Dính SL)</option>
                            <option value="LIMIT_MID">Limit 50% (Retest Invalidation)</option>
                            <option value="LIMIT_ENTRY">Limit Entry (Retest Entry Gốc)</option>
                            <option value="SMART_HYBRID">Smart Hybrid</option>
                          </select>
                        </div>

                        <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-[11px] font-bold text-slate-300">Target RR Revert</label>
                            <span className="text-[10px] text-orange-400 font-mono font-bold">{settings.smc_revert_rr ?? 1.5}R</span>
                          </div>
                          <input
                            type="number"
                            step="0.1"
                            min="0.5"
                            max="10"
                            value={settings.smc_revert_rr ?? 1.5}
                            onChange={(e) => setSettings({ ...settings, smc_revert_rr: parseFloat(e.target.value) || 1.5 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-orange-400 font-bold focus:border-orange-500 focus:outline-none"
                          />
                        </div>

                        <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-[11px] font-bold text-slate-300">Số Nến Hủy Limit</label>
                            <span className="text-[10px] text-orange-400 font-mono font-bold">{settings.smc_revert_cancel_bars ?? 20} nến</span>
                          </div>
                          <input
                            type="number"
                            step="1"
                            min="1"
                            max="100"
                            value={settings.smc_revert_cancel_bars ?? 20}
                            onChange={(e) => setSettings({ ...settings, smc_revert_cancel_bars: parseInt(e.target.value) || 20 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-orange-500 focus:outline-none"
                          />
                        </div>

                        <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-[11px] font-bold text-slate-300">SL Buffer (Pips)</label>
                            <span className="text-[10px] text-orange-400 font-mono font-bold">+{settings.smc_revert_sl_buffer_pips ?? 10.0}p</span>
                          </div>
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            max="200"
                            value={settings.smc_revert_sl_buffer_pips ?? 10.0}
                            onChange={(e) => setSettings({ ...settings, smc_revert_sl_buffer_pips: parseFloat(e.target.value) || 10.0 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-orange-500 focus:outline-none"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 rounded-xl bg-slate-950/40 border border-slate-800/60 text-center text-xs text-slate-500">
                        Chế độ Revert hiện đang tắt. Nhấn nút "ĐANG TẮT" ở trên để kích hoạt bẫy thanh khoản SMC.
                      </div>
                    )}
                  </div>

                </div>
              </div>

              {/* Bottom Sticky Save Bar */}
              <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 shadow-xl flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>Cấu hình SMC MSB & Limit Entry sẽ được lưu vào cơ sở dữ liệu và áp dụng làm mặc định toàn hệ thống.</span>
                </div>
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 active:scale-95 text-white font-bold text-xs transition-all cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-amber-600/20 disabled:opacity-50 shrink-0"
                >
                  <Save className="w-4 h-4" />
                  <span>{savingSettings ? "Đang Lưu Cấu Hình..." : "Lưu Toàn Bộ Cấu Hình SMC Mặc Định"}</span>
                </button>
              </div>

            </div>
          )}

          {/* SECTION: HMA BREAKOUT PRO SYSTEM CONFIGURATION */}
          {activeSection === 'hma_config' && (
            <div className="space-y-5">
              {/* Top Strategy Action Header */}
              <div className="p-5 rounded-2xl bg-gradient-to-r from-slate-900 via-slate-900 to-slate-900/90 border border-slate-800 shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shadow-md shadow-emerald-500/10 shrink-0">
                    <Zap className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-extrabold text-slate-100 tracking-tight">Cấu Hình Chiến Lược HMA Breakout Pro</h2>
                      <span className="text-[10px] text-emerald-400 font-mono font-bold bg-emerald-950/70 border border-emerald-800/60 px-2 py-0.5 rounded-full">
                        HMA Engine v2.8
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Cài đặt thuật toán chuẩn toàn hệ thống • Tự động đồng bộ sang EA MT5 và biểu đồ người dùng
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 shrink-0">
                  <button
                    type="button"
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                    className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 active:scale-95 text-white font-bold text-xs transition-all cursor-pointer flex items-center gap-2 shadow-lg shadow-emerald-600/25 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    <span>{savingSettings ? "Đang Lưu Cấu Hình..." : "Lưu Cấu Hình HMA"}</span>
                  </button>
                </div>
              </div>

              {/* Status Alert Message */}
              {settingsMsg.text && (
                <div className={`p-3.5 rounded-xl border text-xs flex items-center gap-2.5 transition-all ${
                  settingsMsg.type === 'success'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                }`}>
                  {settingsMsg.type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
                  <span className="font-medium">{settingsMsg.text}</span>
                </div>
              )}

              {/* TWO-COLUMN WORKSTATION GRID */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
                
                {/* === LEFT COLUMN: CORE ALGORITHM & TREND REGIME === */}
                <div className="space-y-5">
                  
                  {/* Card 1: HMA Core Engine & Slope Filter */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                          <Zap className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Thuật Toán Lõi HMA & Bộ Lọc Góc Dốc</h3>
                          <p className="text-[11px] text-slate-400">Chu kỳ đường HMA, góc dốc động lượng và điều kiện thân nến cắt qua</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-emerald-400 font-mono font-bold bg-emerald-950/60 border border-emerald-800/60 px-2 py-0.5 rounded">
                        Core Engine
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* 1. Chu kỳ HMA */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Chu Kỳ HMA (Period)</label>
                          <span className="text-[11px] text-emerald-400 font-mono font-bold">{settings.hma_period || 24} nến</span>
                        </div>
                        <input
                          type="number"
                          step="1"
                          min="5"
                          max="200"
                          value={settings.hma_period !== undefined ? settings.hma_period : 24}
                          onChange={(e) => setSettings({ ...settings, hma_period: parseInt(e.target.value) || 24 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none"
                        />
                        <div className="flex items-center gap-1 pt-0.5">
                          <span className="text-[10px] text-slate-500 mr-1">Nhanh:</span>
                          {[14, 20, 24, 34, 50].map((p) => (
                            <button
                              key={p}
                              type="button"
                              onClick={() => setSettings({ ...settings, hma_period: p })}
                              className={`px-2 py-0.5 text-[10px] font-mono rounded transition cursor-pointer ${
                                (settings.hma_period || 24) === p
                                  ? "bg-emerald-500/25 text-emerald-300 border border-emerald-500/50 font-bold"
                                  : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800"
                              }`}
                            >
                              {p}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* 2. Bộ lọc góc dốc HMA */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Góc Dốc (Max Slope)</label>
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, hma_slope_filter: !settings.hma_slope_filter })}
                            className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                              settings.hma_slope_filter
                                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                                : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                            }`}
                          >
                            {settings.hma_slope_filter ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                          </button>
                        </div>
                        <input
                          type="number"
                          step="0.5"
                          min="0.5"
                          max="20"
                          disabled={!settings.hma_slope_filter}
                          value={settings.hma_max_slope !== undefined ? settings.hma_max_slope : 3.0}
                          onChange={(e) => setSettings({ ...settings, hma_max_slope: parseFloat(e.target.value) || 3.0 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none disabled:opacity-40"
                        />
                        <div className="flex items-center gap-1 pt-0.5">
                          <span className="text-[10px] text-slate-500 mr-1">Nhanh:</span>
                          {[1.5, 2, 3, 4, 5].map((s) => (
                            <button
                              key={s}
                              type="button"
                              disabled={!settings.hma_slope_filter}
                              onClick={() => setSettings({ ...settings, hma_max_slope: s })}
                              className={`px-2 py-0.5 text-[10px] font-mono rounded transition cursor-pointer ${
                                (settings.hma_max_slope || 3.0) === s
                                  ? "bg-emerald-500/25 text-emerald-300 border border-emerald-500/50 font-bold"
                                  : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800 disabled:opacity-40"
                              }`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* 3. Tỷ lệ thân nến qua HMA */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Vượt HMA (% Body Cross)</label>
                          <span className="text-[11px] text-purple-400 font-mono font-bold">{settings.hma_min_body_pct !== undefined ? settings.hma_min_body_pct : 40}%</span>
                        </div>
                        <input
                          type="number"
                          step="5"
                          min="10"
                          max="90"
                          value={settings.hma_min_body_pct !== undefined ? settings.hma_min_body_pct : 40}
                          onChange={(e) => setSettings({ ...settings, hma_min_body_pct: parseFloat(e.target.value) || 40 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs text-slate-100 font-bold focus:border-purple-500 focus:outline-none"
                        />
                        <div className="flex items-center gap-1 pt-0.5">
                          <span className="text-[10px] text-slate-500 mr-1">Nhanh:</span>
                          {[30, 40, 50, 60, 70].map((pct) => (
                            <button
                              key={pct}
                              type="button"
                              onClick={() => setSettings({ ...settings, hma_min_body_pct: pct })}
                              className={`px-1.5 py-0.5 text-[10px] font-mono rounded transition cursor-pointer ${
                                (settings.hma_min_body_pct || 40) === pct
                                  ? "bg-purple-500/25 text-purple-300 border border-purple-500/50 font-bold"
                                  : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800"
                              }`}
                            >
                              {pct}%
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* 4. Nến Breakout Ngoài HMA */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 flex flex-col justify-between space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Breakout Ngoài HMA</label>
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, hma_allow_floating_breakout: !(settings.hma_allow_floating_breakout !== false) })}
                            className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                              settings.hma_allow_floating_breakout !== false
                                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                                : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                            }`}
                          >
                            {settings.hma_allow_floating_breakout !== false ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                          </button>
                        </div>
                        <p className="text-[11px] text-slate-400 leading-relaxed">
                          {settings.hma_allow_floating_breakout !== false
                            ? "Bật: Cho phép nến mở và đóng hoàn toàn ngoài HMA vẫn tính là tín hiệu phá vỡ hợp lệ."
                            : "Tắt: Bắt buộc nến breakout phải cắt qua đường HMA và đạt tối thiểu % thân nến."}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Candle Momentum & Rejection Wick Filter */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400">
                          <TrendingUp className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Sức Mạnh Nến Phá Vỡ (Momentum & Wick Filter)</h3>
                          <p className="text-[11px] text-slate-400">Loại bỏ nến rút râu đối kháng (Inverted Hammer / Shooting Star) và nến thân mỏng Doji</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, hma_candle_strength_filter: !(settings.hma_candle_strength_filter !== false) })}
                        className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition cursor-pointer ${
                          settings.hma_candle_strength_filter !== false
                            ? "bg-purple-500/20 text-purple-400 border border-purple-500/40"
                            : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                        }`}
                      >
                        {settings.hma_candle_strength_filter !== false ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* Min Candle Body Ratio */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Thân Nến Tối Thiểu (Min Body)</label>
                          <span className="text-[11px] text-purple-400 font-mono font-bold">{settings.hma_min_candle_body_ratio !== undefined ? settings.hma_min_candle_body_ratio : 35}%</span>
                        </div>
                        <input
                          type="number"
                          step="5"
                          min="10"
                          max="90"
                          disabled={settings.hma_candle_strength_filter === false}
                          value={settings.hma_min_candle_body_ratio !== undefined ? settings.hma_min_candle_body_ratio : 35}
                          onChange={(e) => setSettings({ ...settings, hma_min_candle_body_ratio: parseFloat(e.target.value) || 35 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs text-slate-100 font-bold focus:border-purple-500 focus:outline-none disabled:opacity-40"
                        />
                        <div className="flex items-center gap-1 pt-0.5">
                          <span className="text-[10px] text-slate-500 mr-1">Nhanh:</span>
                          {[20, 30, 35, 45, 55].map((pct) => (
                            <button
                              key={pct}
                              type="button"
                              disabled={settings.hma_candle_strength_filter === false}
                              onClick={() => setSettings({ ...settings, hma_min_candle_body_ratio: pct })}
                              className={`px-1.5 py-0.5 text-[10px] font-mono rounded transition cursor-pointer ${
                                (settings.hma_min_candle_body_ratio || 35) === pct
                                  ? "bg-purple-500/25 text-purple-300 border border-purple-500/50 font-bold"
                                  : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800 disabled:opacity-40"
                              }`}
                            >
                              {pct}%
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Max Rejection Wick Ratio */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Râu Đối Kháng Tối Đa (Max Wick)</label>
                          <span className="text-[11px] text-rose-400 font-mono font-bold">{settings.hma_max_rejection_wick_ratio !== undefined ? settings.hma_max_rejection_wick_ratio : 40}%</span>
                        </div>
                        <input
                          type="number"
                          step="5"
                          min="10"
                          max="90"
                          disabled={settings.hma_candle_strength_filter === false}
                          value={settings.hma_max_rejection_wick_ratio !== undefined ? settings.hma_max_rejection_wick_ratio : 40}
                          onChange={(e) => setSettings({ ...settings, hma_max_rejection_wick_ratio: parseFloat(e.target.value) || 40 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs text-slate-100 font-bold focus:border-rose-500 focus:outline-none disabled:opacity-40"
                        />
                        <div className="flex items-center gap-1 pt-0.5">
                          <span className="text-[10px] text-slate-500 mr-1">Nhanh:</span>
                          {[25, 35, 40, 50, 60].map((pct) => (
                            <button
                              key={pct}
                              type="button"
                              disabled={settings.hma_candle_strength_filter === false}
                              onClick={() => setSettings({ ...settings, hma_max_rejection_wick_ratio: pct })}
                              className={`px-1.5 py-0.5 text-[10px] font-mono rounded transition cursor-pointer ${
                                (settings.hma_max_rejection_wick_ratio || 40) === pct
                                  ? "bg-rose-500/25 text-rose-300 border border-rose-500/50 font-bold"
                                  : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800 disabled:opacity-40"
                              }`}
                            >
                              {pct}%
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="p-2.5 rounded-lg bg-purple-950/30 border border-purple-900/40 text-[11px] text-purple-300 flex items-center justify-between">
                      <span>BUY: Thân tăng &ge; <b>{settings.hma_min_candle_body_ratio || 35}%</b> &bull; Râu trên &le; <b>{settings.hma_max_rejection_wick_ratio || 40}%</b></span>
                      <span>SELL: Thân giảm &ge; <b>{settings.hma_min_candle_body_ratio || 35}%</b> &bull; Râu dưới &le; <b>{settings.hma_max_rejection_wick_ratio || 40}%</b></span>
                    </div>
                  </div>

                  {/* Card 3: Macro & Regime Protection (Anti-Cascade + EMA 200 Sideway) */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-rose-500/10 border border-rose-500/30 flex items-center justify-center text-rose-400">
                          <Shield className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Bảo Vệ Thị Trường & Chống Bão Giá (Macro Shield)</h3>
                          <p className="text-[11px] text-slate-400">Chặn bắt dao rơi khi thác đổ Waterfall và hạn chế giao dịch ngoài biên độ Sideway</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-rose-400 font-mono font-bold bg-rose-950/60 border border-rose-800/60 px-2 py-0.5 rounded">
                        Regime Protection
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* Sub-Card A: Cascade Dump Filter */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2.5">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Chống Ép Giảm / Thác Đổ</label>
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, hma_cascade_filter: !Boolean(settings.hma_cascade_filter) })}
                            className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                              settings.hma_cascade_filter
                                ? "bg-rose-500/20 text-rose-400 border border-rose-500/40"
                                : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                            }`}
                          >
                            {settings.hma_cascade_filter ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                          </button>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-[11px]">
                            <span className="text-slate-400">Ngưỡng nến đè áp đảo:</span>
                            <span className="text-rose-400 font-mono font-bold">{settings.hma_bear_ratio_thresh !== undefined ? settings.hma_bear_ratio_thresh : 70}%</span>
                          </div>
                          <input
                            type="number"
                            step="5"
                            min="50"
                            max="100"
                            disabled={!settings.hma_cascade_filter}
                            value={settings.hma_bear_ratio_thresh !== undefined ? settings.hma_bear_ratio_thresh : 70}
                            onChange={(e) => setSettings({ ...settings, hma_bear_ratio_thresh: e.target.value === '' ? '' : (parseFloat(e.target.value) || 70) })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs text-slate-100 font-bold focus:border-rose-500 focus:outline-none disabled:opacity-40"
                          />
                        </div>
                        <div className="flex items-center gap-1 pt-0.5">
                          <span className="text-[10px] text-slate-500 mr-1">Nhanh:</span>
                          {[60, 70, 80, 90].map((pct) => (
                            <button
                              key={pct}
                              type="button"
                              disabled={!settings.hma_cascade_filter}
                              onClick={() => setSettings({ ...settings, hma_bear_ratio_thresh: pct })}
                              className={`px-1.5 py-0.5 text-[10px] font-mono rounded transition cursor-pointer ${
                                (settings.hma_bear_ratio_thresh || 70) === pct
                                  ? "bg-rose-500/25 text-rose-300 border border-rose-500/50 font-bold"
                                  : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800 disabled:opacity-40"
                              }`}
                            >
                              {pct}%
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Sub-Card B: EMA 200 Sideway Filter */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2.5">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Lọc Vùng Sideway EMA 200</label>
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, hma_ema200_sw_filter_enable: !Boolean(settings.hma_ema200_sw_filter_enable) })}
                            className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                              settings.hma_ema200_sw_filter_enable
                                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                                : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                            }`}
                          >
                            {settings.hma_ema200_sw_filter_enable ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                          </button>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-[11px]">
                            <span className="text-slate-400">Khoảng cách tối đa:</span>
                            <span className="text-emerald-400 font-mono font-bold">&plusmn;{settings.hma_ema200_sw_max_dist_pips !== undefined ? settings.hma_ema200_sw_max_dist_pips : 300} pips</span>
                          </div>
                          <input
                            type="number"
                            step="10"
                            min="50"
                            max="1000"
                            disabled={!settings.hma_ema200_sw_filter_enable}
                            value={settings.hma_ema200_sw_max_dist_pips !== undefined ? settings.hma_ema200_sw_max_dist_pips : 300}
                            onChange={(e) => setSettings({ ...settings, hma_ema200_sw_max_dist_pips: e.target.value === '' ? '' : (parseFloat(e.target.value) || 300) })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none disabled:opacity-40"
                          />
                        </div>
                        <div className="flex items-center gap-1 pt-0.5 overflow-x-auto">
                          <span className="text-[10px] text-slate-500 mr-1">Nhanh:</span>
                          {[150, 200, 250, 300, 400, 500].map((dist) => (
                            <button
                              key={dist}
                              type="button"
                              disabled={!settings.hma_ema200_sw_filter_enable}
                              onClick={() => setSettings({ ...settings, hma_ema200_sw_max_dist_pips: dist })}
                              className={`px-1 py-0.5 text-[9px] font-mono rounded transition cursor-pointer ${
                                (settings.hma_ema200_sw_max_dist_pips || 300) === dist
                                  ? "bg-emerald-500/25 text-emerald-300 border border-emerald-500/50 font-bold"
                                  : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800 disabled:opacity-40"
                              }`}
                            >
                              {dist}p
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 4: Volatility-Adaptive HMA Period */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                          <Activity className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Chu Kỳ HMA Co Giãn Tự Động (Adaptive Period)</h3>
                          <p className="text-[11px] text-slate-400">Tự động co ngắn chu kỳ khi thị trường bão mạnh và giãn dài khi biến động yếu</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setSettings({
                            ...settings,
                            hma_adaptive_period_enable: !settings.hma_adaptive_period_enable,
                          })
                        }
                        className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition cursor-pointer ${
                          settings.hma_adaptive_period_enable
                            ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/40"
                            : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                        }`}
                      >
                        {settings.hma_adaptive_period_enable ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* Min Period */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Chu Kỳ Tối Thiểu (Min Period)</label>
                          <span className="text-[11px] text-cyan-400 font-mono font-bold">HMA {settings.hma_adaptive_min_period !== undefined ? settings.hma_adaptive_min_period : 21}</span>
                        </div>
                        <input
                          type="number"
                          step="1"
                          min="10"
                          max="50"
                          disabled={!settings.hma_adaptive_period_enable}
                          value={settings.hma_adaptive_min_period !== undefined ? settings.hma_adaptive_min_period : 21}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              hma_adaptive_min_period: parseInt(e.target.value) || 21,
                            })
                          }
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs text-slate-100 font-bold focus:border-cyan-500 focus:outline-none disabled:opacity-40"
                        />
                      </div>

                      {/* Max Period */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Chu Kỳ Tối Đa (Max Period)</label>
                          <span className="text-[11px] text-cyan-400 font-mono font-bold">HMA {settings.hma_adaptive_max_period !== undefined ? settings.hma_adaptive_max_period : 35}</span>
                        </div>
                        <input
                          type="number"
                          step="1"
                          min="20"
                          max="100"
                          disabled={!settings.hma_adaptive_period_enable}
                          value={settings.hma_adaptive_max_period !== undefined ? settings.hma_adaptive_max_period : 35}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              hma_adaptive_max_period: parseInt(e.target.value) || 35,
                            })
                          }
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs text-slate-100 font-bold focus:border-cyan-500 focus:outline-none disabled:opacity-40"
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-1 flex-wrap">
                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Bộ cấu hình sẵn:</span>
                      <button
                        type="button"
                        disabled={!settings.hma_adaptive_period_enable}
                        onClick={() =>
                          setSettings({
                            ...settings,
                            hma_adaptive_min_period: 21,
                            hma_adaptive_max_period: 35,
                          })
                        }
                        className={`px-2.5 py-1 text-[10px] rounded-lg transition cursor-pointer font-bold ${
                          settings.hma_adaptive_min_period === 21 && settings.hma_adaptive_max_period === 35
                            ? "bg-cyan-500/25 text-cyan-300 border border-cyan-500/50"
                            : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800 disabled:opacity-40"
                        }`}
                      >
                        ⚡ Năng động (21 - 35 nến)
                      </button>
                      <button
                        type="button"
                        disabled={!settings.hma_adaptive_period_enable}
                        onClick={() =>
                          setSettings({
                            ...settings,
                            hma_adaptive_min_period: 24,
                            hma_adaptive_max_period: 35,
                          })
                        }
                        className={`px-2.5 py-1 text-[10px] rounded-lg transition cursor-pointer font-bold ${
                          settings.hma_adaptive_min_period === 24 && settings.hma_adaptive_max_period === 35
                            ? "bg-cyan-500/25 text-cyan-300 border border-cyan-500/50"
                            : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800 disabled:opacity-40"
                        }`}
                      >
                        🛡️ Tiêu chuẩn (24 - 35 nến)
                      </button>
                    </div>
                  </div>
                </div>

                {/* === RIGHT COLUMN: RISK MANAGEMENT, EXECUTION & EXITS === */}
                <div className="space-y-5">
                  
                  {/* Card 5: Wave Lookback & Risk Management */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                          <Layers className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Cửa Sổ Quét Sóng Đáy/Đỉnh & Quản Trị Rủi Ro SL</h3>
                          <p className="text-[11px] text-slate-400">Bộ thông số định vị đỉnh/đáy sóng trước đó và giới hạn cắt lỗ an toàn</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-cyan-400 font-mono font-bold bg-cyan-950/60 border border-cyan-800/60 px-2 py-0.5 rounded">
                        Risk & Swing Engine
                      </span>
                    </div>

                    {/* Section 1: Swing Lookback & Cooldown (3 balanced columns) */}
                    <div className="space-y-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Định vị sóng Swing & Giãn cách nến</span>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <label className="text-[11px] font-bold text-slate-300 block">Cửa Sổ Quét (Lookback)</label>
                          <div className="relative">
                            <input
                              type="number"
                              step="5"
                              min="10"
                              max="300"
                              value={settings.hma_lookback_bars !== undefined ? settings.hma_lookback_bars : 100}
                              onChange={(e) => setSettings({ ...settings, hma_lookback_bars: parseInt(e.target.value) || 100 })}
                              className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-cyan-500 focus:outline-none"
                            />
                            <span className="absolute right-2.5 top-1.5 text-[10px] text-slate-400 font-mono">nến</span>
                          </div>
                          <span className="text-[10px] text-slate-500 block truncate">Số nến lùi tìm đỉnh/đáy</span>
                        </div>

                        <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <label className="text-[11px] font-bold text-slate-300 block">Nến Tối Đa Từ Đỉnh/Đáy</label>
                          <div className="relative">
                            <input
                              type="number"
                              step="1"
                              min="3"
                              max="50"
                              value={settings.hma_max_bars_from_extreme !== undefined ? settings.hma_max_bars_from_extreme : 15}
                              onChange={(e) => setSettings({ ...settings, hma_max_bars_from_extreme: parseInt(e.target.value) || 15 })}
                              className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-cyan-500 focus:outline-none"
                            />
                            <span className="absolute right-2.5 top-1.5 text-[10px] text-slate-400 font-mono">nến</span>
                          </div>
                          <span className="text-[10px] text-slate-500 block truncate">Bắt đảo chiều V-Shape</span>
                        </div>

                        <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <label className="text-[11px] font-bold text-slate-300 block">Giãn Cách Lệnh (Cooldown)</label>
                          <div className="relative">
                            <input
                              type="number"
                              step="1"
                              min="1"
                              max="50"
                              value={settings.hma_cooldown_bars !== undefined ? settings.hma_cooldown_bars : 5}
                              onChange={(e) => setSettings({ ...settings, hma_cooldown_bars: parseInt(e.target.value) || 5 })}
                              className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-cyan-500 focus:outline-none"
                            />
                            <span className="absolute right-2.5 top-1.5 text-[10px] text-slate-400 font-mono">nến</span>
                          </div>
                          <span className="text-[10px] text-slate-500 block truncate">Khoảng nghỉ giữa 2 lệnh</span>
                        </div>
                      </div>
                    </div>

                    {/* Section 2: SL Risk Distance & Buffer (2 balanced columns) */}
                    <div className="space-y-2 pt-1 border-t border-slate-800/60">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Kiểm soát khoảng cách Cắt Lỗ SL</span>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-[11px] font-bold text-slate-300">Khoảng Cách SL Tối Đa (Max Risk)</label>
                            <span className="text-[10px] text-rose-400 font-mono font-bold">{settings.hma_max_risk_pips || 160} pips</span>
                          </div>
                          <div className="relative">
                            <input
                              type="number"
                              step="5"
                              min="20"
                              max="500"
                              value={settings.hma_max_risk_pips !== undefined ? settings.hma_max_risk_pips : 160}
                              onChange={(e) => setSettings({ ...settings, hma_max_risk_pips: parseFloat(e.target.value) || 160 })}
                              className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-rose-500 focus:outline-none"
                            />
                            <span className="absolute right-2.5 top-1.5 text-[10px] text-slate-400 font-mono">pips</span>
                          </div>
                          <span className="text-[10px] text-slate-500 block">Nếu SL vượt mức này, hệ thống sẽ bỏ qua tín hiệu</span>
                        </div>

                        <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-[11px] font-bold text-slate-300">Đệm SL An Toàn (SL Buffer)</label>
                            <span className="text-[10px] text-cyan-400 font-mono font-bold">+{settings.hma_sl_buffer_pips || 30} pips</span>
                          </div>
                          <div className="relative">
                            <input
                              type="number"
                              step="1"
                              min="0"
                              max="100"
                              value={settings.hma_sl_buffer_pips !== undefined ? settings.hma_sl_buffer_pips : 30}
                              onChange={(e) => setSettings({ ...settings, hma_sl_buffer_pips: parseFloat(e.target.value) || 30 })}
                              className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-cyan-500 focus:outline-none"
                            />
                            <span className="absolute right-2.5 top-1.5 text-[10px] text-slate-400 font-mono">pips</span>
                          </div>
                          <span className="text-[10px] text-slate-500 block">Khoảng cách cộng đệm ngoài đỉnh/đáy sóng</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 6: Dynamic Limit Order on Large Breakout Candles */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
                          <Clock className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Vào Lệnh Chờ Limit Khi Nến Phá Vỡ Quá Lớn</h3>
                          <p className="text-[11px] text-slate-400">Tự động chuyển từ Market sang Limit đón sóng hồi (Pullback) để tối ưu hóa tỷ lệ R:R</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, hma_dynamic_limit_enable: !Boolean(settings.hma_dynamic_limit_enable) })}
                        className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition cursor-pointer ${
                          settings.hma_dynamic_limit_enable
                            ? "bg-amber-500/20 text-amber-400 border border-amber-500/40"
                            : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                        }`}
                      >
                        {settings.hma_dynamic_limit_enable ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">Ngưỡng Kích Hoạt</label>
                          <span className="text-[10px] text-amber-400 font-mono font-bold">{settings.hma_limit_threshold_pips || 100}p</span>
                        </div>
                        <input
                          type="number"
                          step="5"
                          min="20"
                          max="300"
                          disabled={!Boolean(settings.hma_dynamic_limit_enable)}
                          value={settings.hma_limit_threshold_pips !== undefined && settings.hma_limit_threshold_pips !== '' ? settings.hma_limit_threshold_pips : 100}
                          onChange={(e) => setSettings({ ...settings, hma_limit_threshold_pips: e.target.value === '' ? '' : (parseFloat(e.target.value) || 100) })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-amber-500 focus:outline-none disabled:opacity-40"
                        />
                        <span className="text-[10px] text-slate-500 block truncate">Kích hoạt khi SL &ge; N pips</span>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">Tỷ Lệ Hồi (Pullback)</label>
                          <span className="text-[10px] text-amber-400 font-mono font-bold">{settings.hma_limit_pullback_pct || 50}%</span>
                        </div>
                        <input
                          type="number"
                          step="5"
                          min="10"
                          max="80"
                          disabled={!Boolean(settings.hma_dynamic_limit_enable)}
                          value={settings.hma_limit_pullback_pct !== undefined && settings.hma_limit_pullback_pct !== '' ? settings.hma_limit_pullback_pct : 50}
                          onChange={(e) => setSettings({ ...settings, hma_limit_pullback_pct: e.target.value === '' ? '' : (parseFloat(e.target.value) || 50) })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-amber-500 focus:outline-none disabled:opacity-40"
                        />
                        <span className="text-[10px] text-slate-500 block truncate">Đặt Limit ở % sóng phá vỡ</span>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">Hủy Limit Sau (Nến)</label>
                          <span className="text-[10px] text-amber-400 font-mono font-bold">{settings.hma_limit_cancel_bars || 10} nến</span>
                        </div>
                        <input
                          type="number"
                          step="1"
                          min="1"
                          max="100"
                          disabled={!Boolean(settings.hma_dynamic_limit_enable)}
                          value={settings.hma_limit_cancel_bars !== undefined && settings.hma_limit_cancel_bars !== '' ? settings.hma_limit_cancel_bars : 10}
                          onChange={(e) => setSettings({ ...settings, hma_limit_cancel_bars: e.target.value === '' ? '' : (parseInt(e.target.value) || 10) })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-amber-500 focus:outline-none disabled:opacity-40"
                        />
                        <span className="text-[10px] text-slate-500 block truncate">Tự hủy lệnh nếu không khớp</span>
                      </div>
                    </div>
                  </div>

                  {/* Card 7: Capital Protection, Exits & Take Profit R:R Target */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                          <Target className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Bảo Vệ Vốn & Mục Tiêu Chốt Lời (Take Profit & Exits)</h3>
                          <p className="text-[11px] text-slate-400">Dời SL hòa vốn, cắt sớm sát TP, đóng khi xuất hiện tín hiệu ngược chiều và tỷ lệ R:R</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-emerald-400 font-mono font-bold bg-emerald-950/60 border border-emerald-800/60 px-2 py-0.5 rounded">
                        Profit & Protection
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* Sub-Card A: Dynamic BE */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Dời Hòa Vốn (Dynamic BE)</label>
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, dynamic_be_enable: !Boolean(settings.dynamic_be_enable) })}
                            className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                              settings.dynamic_be_enable
                                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                                : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                            }`}
                          >
                            {settings.dynamic_be_enable ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="text-[10px] text-slate-400 block mb-1">Ngưỡng % TP:</span>
                            <input
                              type="number"
                              step="5"
                              min="10"
                              max="95"
                              disabled={!Boolean(settings.dynamic_be_enable)}
                              value={settings.be_trigger_pct !== undefined ? settings.be_trigger_pct : 85}
                              onChange={(e) => setSettings({ ...settings, be_trigger_pct: parseFloat(e.target.value) || 85 })}
                              className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none disabled:opacity-40"
                            />
                          </div>
                          <div>
                            <span className="text-[10px] text-slate-400 block mb-1">Khóa Lãi (Pips):</span>
                            <input
                              type="number"
                              step="1"
                              min="0"
                              max="50"
                              disabled={!Boolean(settings.dynamic_be_enable)}
                              value={settings.be_lock_pips !== undefined ? settings.be_lock_pips : 5}
                              onChange={(e) => setSettings({ ...settings, be_lock_pips: parseFloat(e.target.value) || 5 })}
                              className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none disabled:opacity-40"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Sub-Card B: Near-TP Cut & Close Opposite */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2.5">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Cắt Lệnh Sát TP & Ngược Chiều</label>
                        </div>
                        <div className="flex items-center justify-between pt-0.5">
                          <span className="text-[11px] text-slate-400">Cắt sớm sát TP ({settings.near_tp_threshold_pct || 90}%):</span>
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, near_tp_cut_enable: !Boolean(settings.near_tp_cut_enable) })}
                            className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                              settings.near_tp_cut_enable
                                ? "bg-purple-500/20 text-purple-400 border border-purple-500/40"
                                : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                            }`}
                          >
                            {settings.near_tp_cut_enable ? "BẬT ✓" : "TẮT ✕"}
                          </button>
                        </div>
                        <div className="flex items-center justify-between pt-0.5 border-t border-slate-800/60">
                          <span className="text-[11px] text-slate-400">Đóng khi có tín hiệu ngược:</span>
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, hma_close_on_opposite: settings.hma_close_on_opposite === false ? true : false })}
                            className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                              settings.hma_close_on_opposite !== false
                                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                                : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                            }`}
                          >
                            {settings.hma_close_on_opposite !== false ? "BẬT ✓" : "TẮT ✕"}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Take Profit Target R:R */}
                    <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-bold text-slate-300">Tỷ Lệ Mục Tiêu Chốt Lời (Take Profit Target R:R)</label>
                        <span className="text-[11px] text-emerald-400 font-mono font-bold">{settings.hma_r1 !== undefined ? settings.hma_r1 : 1.5}R</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          step="0.1"
                          min="0.5"
                          max="10"
                          value={settings.hma_r1 !== undefined ? settings.hma_r1 : 1.5}
                          onChange={(e) => setSettings({ ...settings, hma_r1: parseFloat(e.target.value) || 1.5 })}
                          className="w-32 bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none"
                        />
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] text-slate-500">Mục tiêu:</span>
                          {[1.0, 1.2, 1.5, 1.8, 2.0, 2.5, 3.0].map((r) => (
                            <button
                              key={r}
                              type="button"
                              onClick={() => setSettings({ ...settings, hma_r1: r })}
                              className={`px-2.5 py-1 text-[11px] rounded-lg transition cursor-pointer font-medium ${
                                (settings.hma_r1 || 1.5) === r
                                  ? "bg-emerald-500/25 text-emerald-300 border border-emerald-500/50 font-bold"
                                  : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800"
                              }`}
                            >
                              {r}R
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 8: Trading Sessions & Algorithm Privacy */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
                          <Sliders className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Phiên Giao Dịch & Bảo Mật Thuật Toán</h3>
                          <p className="text-[11px] text-slate-400">Khung giờ giao dịch hoạt động và bảo mật các chỉ báo kỹ thuật trên biểu đồ</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-indigo-400 font-mono font-bold bg-indigo-950/60 border border-indigo-800/60 px-2 py-0.5 rounded">
                        Session & Privacy
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* Session Filter */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2.5">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Khung Giờ Giao Dịch</label>
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, hma_session_filter_enable: !Boolean(settings.hma_session_filter_enable) })}
                            className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                              settings.hma_session_filter_enable
                                ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/40"
                                : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                            }`}
                          >
                            {settings.hma_session_filter_enable ? "ĐANG BẬT ✓" : "24/24 TẮT ✕"}
                          </button>
                        </div>
                        <select
                          disabled={!settings.hma_session_filter_enable}
                          value={settings.hma_session_mode || 'ALL'}
                          onChange={(e) => {
                            const mode = e.target.value;
                            let sStart = settings.hma_session_start || '06:00';
                            let sEnd = settings.hma_session_end || '19:30';
                            if (mode === 'LONDON_NY') {
                              sStart = '14:00';
                              sEnd = '23:00';
                            } else if (mode === 'NY_ONLY') {
                              sStart = '19:30';
                              sEnd = '23:00';
                            }
                            setSettings({
                              ...settings,
                              hma_session_mode: mode,
                              hma_session_start: sStart,
                              hma_session_end: sEnd
                            });
                          }}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-cyan-500 focus:outline-none disabled:opacity-40"
                        >
                          <option value="ALL">Cả ngày (06:00 - 23:00)</option>
                          <option value="LONDON_NY">Âu + Mỹ (14:00 - 23:00)</option>
                          <option value="NY_ONLY">Phiên Mỹ (19:30 - 23:00)</option>
                          <option value="CUSTOM">Tùy Chỉnh Giờ</option>
                        </select>
                        {(settings.hma_session_mode === 'CUSTOM' || settings.hma_session_filter_enable) && (
                          <div className="grid grid-cols-2 gap-2 pt-1">
                            <div>
                              <span className="text-[10px] text-slate-500 block mb-0.5">Từ:</span>
                              <input
                                type="text"
                                disabled={!settings.hma_session_filter_enable}
                                value={settings.hma_session_start || '06:00'}
                                onChange={(e) => setSettings({ ...settings, hma_session_start: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2 py-1 text-xs text-slate-100 font-mono focus:border-cyan-500 focus:outline-none disabled:opacity-40"
                              />
                            </div>
                            <div>
                              <span className="text-[10px] text-slate-500 block mb-0.5">Đến:</span>
                              <input
                                type="text"
                                disabled={!settings.hma_session_filter_enable}
                                value={settings.hma_session_end || '19:30'}
                                onChange={(e) => setSettings({ ...settings, hma_session_end: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2 py-1 text-xs text-slate-100 font-mono focus:border-cyan-500 focus:outline-none disabled:opacity-40"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Algorithm Privacy */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2.5">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Bảo Mật Thuật Toán (Anti-Reverse)</label>
                        </div>
                        <div className="flex items-center justify-between pt-0.5">
                          <span className="text-[11px] text-slate-400">Đường HMA trên Chart:</span>
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, hma_show_line: !(settings.hma_show_line !== false) })}
                            className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                              settings.hma_show_line !== false
                                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                                : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                            }`}
                          >
                            {settings.hma_show_line !== false ? "HIỆN ✓" : "ẨN ✕"}
                          </button>
                        </div>
                        <div className="flex items-center justify-between pt-0.5 border-t border-slate-800/60">
                          <span className="text-[11px] text-slate-400">Hộp Base Box tích lũy:</span>
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, hma_show_base_box: !(settings.hma_show_base_box !== false) })}
                            className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                              settings.hma_show_base_box !== false
                                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                                : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                            }`}
                          >
                            {settings.hma_show_base_box !== false ? "HIỆN ✓" : "ẨN ✕"}
                          </button>
                        </div>
                        <p className="text-[10px] text-slate-500 leading-tight pt-0.5">
                          Khi ẩn, người dùng thường chỉ thấy Mũi tên vào lệnh và Đường Cắt Lỗ/Chốt Lời mà không biết chu kỳ HMA.
                        </p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Bottom Sticky Save Bar */}
              <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 shadow-xl flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Cấu hình HMA Breakout Pro sẽ được lưu vào cơ sở dữ liệu và áp dụng ngay lập tức cho các máy client.</span>
                </div>
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 active:scale-95 text-white font-bold text-xs transition-all cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/20 disabled:opacity-50 shrink-0"
                >
                  <Save className="w-4 h-4" />
                  <span>{savingSettings ? "Đang Lưu Cấu Hình..." : "Lưu Toàn Bộ Cấu Hình HMA Mặc Định"}</span>
                </button>
              </div>

            </div>
          )}

          {/* SECTION: BOB VOLMAN PRICE ACTION SCALPING CONFIGURATION */}
          {activeSection === 'volman_config' && (
            <div className="space-y-5">
              {/* Top Strategy Action Header */}
              <div className="p-5 rounded-2xl bg-gradient-to-r from-slate-900 via-slate-900 to-slate-900/90 border border-slate-800 shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400 shadow-md shadow-indigo-500/10 shrink-0">
                    <Bot className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-extrabold text-slate-100 tracking-tight">Cấu Hình Chiến Lược Bob Volman Scalping</h2>
                      <span className="text-[10px] text-indigo-400 font-mono font-bold bg-indigo-950/70 border border-indigo-800/60 px-2 py-0.5 rounded-full">
                        Volman Engine v2.0
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Price Action Scalping • 20 EMA Buildup • Breakout Block (BB) & Trendline Break (TB) • Dynamic Pullback
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 shrink-0">
                  <button
                    type="button"
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                    className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 active:scale-95 text-white font-bold text-xs transition-all cursor-pointer flex items-center gap-2 shadow-lg shadow-indigo-600/25 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    <span>{savingSettings ? "Đang Lưu Cấu Hình..." : "Lưu Cấu Hình Volman"}</span>
                  </button>
                </div>
              </div>

              {/* Status Alert Message */}
              {settingsMsg.text && (
                <div className={`p-3.5 rounded-xl border text-xs flex items-center gap-2.5 transition-all ${
                  settingsMsg.type === 'success'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                }`}>
                  {settingsMsg.type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
                  <span className="font-medium">{settingsMsg.text}</span>
                </div>
              )}

              {/* TWO-COLUMN WORKSTATION GRID */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
                
                {/* === LEFT COLUMN: CORE PATTERNS & TREND REGIME === */}
                <div className="space-y-5">
                  
                  {/* Card 1: Breakout Candle Filter & Rejection Wick Protection */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
                          <TrendingUp className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Bộ Lọc Nến Phá Vỡ (Candle Momentum)</h3>
                          <p className="text-[11px] text-slate-400">Loại bỏ nến rút râu đối kháng và đảm bảo lực đẩy vượt rào chắn</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, volman_breakout_candle_filter: !settings.volman_breakout_candle_filter })}
                        className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition cursor-pointer ${
                          settings.volman_breakout_candle_filter
                            ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/40"
                            : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                        }`}
                      >
                        {settings.volman_breakout_candle_filter ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* Min Body Ratio */}
                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">Thân Nến Tối Thiểu</label>
                          <span className="text-[10px] text-indigo-400 font-mono font-bold">{settings.volman_min_body_ratio !== undefined ? settings.volman_min_body_ratio : 35}%</span>
                        </div>
                        <input
                          type="number"
                          step="5"
                          min="10"
                          max="90"
                          disabled={!settings.volman_breakout_candle_filter}
                          value={settings.volman_min_body_ratio !== undefined ? settings.volman_min_body_ratio : 35}
                          onChange={(e) => setSettings({ ...settings, volman_min_body_ratio: parseFloat(e.target.value) || 35 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-indigo-500 focus:outline-none disabled:opacity-40"
                        />
                        <div className="flex items-center gap-1 pt-0.5 overflow-x-auto">
                          {[20, 30, 35, 45, 55].map((pct) => (
                            <button
                              key={pct}
                              type="button"
                              disabled={!settings.volman_breakout_candle_filter}
                              onClick={() => setSettings({ ...settings, volman_min_body_ratio: pct })}
                              className={`px-1 py-0.5 text-[9px] font-mono rounded transition cursor-pointer ${
                                (settings.volman_min_body_ratio || 35) === pct
                                  ? "bg-indigo-500/25 text-indigo-300 border border-indigo-500/50 font-bold"
                                  : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800 disabled:opacity-40"
                              }`}
                            >
                              {pct}%
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Max Rejection Wick */}
                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">Râu Đối Kháng Tối Đa</label>
                          <span className="text-[10px] text-rose-400 font-mono font-bold">{settings.volman_max_rejection_wick !== undefined ? settings.volman_max_rejection_wick : 40}%</span>
                        </div>
                        <input
                          type="number"
                          step="5"
                          min="10"
                          max="90"
                          disabled={!settings.volman_breakout_candle_filter}
                          value={settings.volman_max_rejection_wick !== undefined ? settings.volman_max_rejection_wick : 40}
                          onChange={(e) => setSettings({ ...settings, volman_max_rejection_wick: parseFloat(e.target.value) || 40 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-rose-500 focus:outline-none disabled:opacity-40"
                        />
                        <div className="flex items-center gap-1 pt-0.5 overflow-x-auto">
                          {[25, 35, 40, 50, 60].map((pct) => (
                            <button
                              key={pct}
                              type="button"
                              disabled={!settings.volman_breakout_candle_filter}
                              onClick={() => setSettings({ ...settings, volman_max_rejection_wick: pct })}
                              className={`px-1 py-0.5 text-[9px] font-mono rounded transition cursor-pointer ${
                                (settings.volman_max_rejection_wick || 40) === pct
                                  ? "bg-rose-500/25 text-rose-300 border border-rose-500/50 font-bold"
                                  : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800 disabled:opacity-40"
                              }`}
                            >
                              {pct}%
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Min Body Beyond Barrier */}
                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">Thân Vượt Cản Tối Thiểu</label>
                          <span className="text-[10px] text-indigo-400 font-mono font-bold">{settings.volman_min_body_beyond_pct !== undefined ? settings.volman_min_body_beyond_pct : 15}%</span>
                        </div>
                        <input
                          type="number"
                          step="5"
                          min="5"
                          max="50"
                          disabled={!settings.volman_breakout_candle_filter}
                          value={settings.volman_min_body_beyond_pct !== undefined ? settings.volman_min_body_beyond_pct : 15}
                          onChange={(e) => setSettings({ ...settings, volman_min_body_beyond_pct: parseFloat(e.target.value) || 15 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-indigo-500 focus:outline-none disabled:opacity-40"
                        />
                        <div className="flex items-center gap-1 pt-0.5 overflow-x-auto">
                          {[10, 15, 20, 25, 30].map((pct) => (
                            <button
                              key={pct}
                              type="button"
                              disabled={!settings.volman_breakout_candle_filter}
                              onClick={() => setSettings({ ...settings, volman_min_body_beyond_pct: pct })}
                              className={`px-1 py-0.5 text-[9px] font-mono rounded transition cursor-pointer ${
                                (settings.volman_min_body_beyond_pct || 15) === pct
                                  ? "bg-indigo-500/25 text-indigo-300 border border-indigo-500/50 font-bold"
                                  : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800 disabled:opacity-40"
                              }`}
                            >
                              {pct}%
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: 20 EMA & Setup Models (BB & TB) */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                          <Activity className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Đường 20 EMA & Kích Hoạt Mô Hình (BB & TB)</h3>
                          <p className="text-[11px] text-slate-400">Đường trục giá trị và các mẫu hình phá vỡ nén nến của Bob Volman</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-cyan-400 font-mono font-bold bg-cyan-950/60 border border-cyan-800/60 px-2 py-0.5 rounded">
                        Pattern Models
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* EMA Period */}
                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">Chu Kỳ Đường EMA</label>
                          <span className="text-[10px] text-cyan-400 font-mono font-bold">EMA {settings.volman_ema_period || 20}</span>
                        </div>
                        <input
                          type="number"
                          step="1"
                          min="5"
                          max="200"
                          value={settings.volman_ema_period !== undefined ? settings.volman_ema_period : 20}
                          onChange={(e) => setSettings({ ...settings, volman_ema_period: parseInt(e.target.value) || 20 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-cyan-500 focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500 block truncate">Chuẩn Bob Volman: 20 EMA</span>
                      </div>

                      {/* TB Model Toggle */}
                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 flex flex-col justify-between space-y-2">
                        <div>
                          <span className="text-[11px] font-bold text-slate-300 block">Trendline Break (TB)</span>
                          <span className="text-[10px] text-slate-400 block mt-0.5">Phá vỡ đường xu hướng chéo</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, volman_enable_tb: !settings.volman_enable_tb })}
                          className={`w-full py-1.5 rounded-lg text-xs font-bold transition cursor-pointer flex items-center justify-center gap-1.5 ${
                            settings.volman_enable_tb
                              ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40"
                              : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                          }`}
                        >
                          {settings.volman_enable_tb ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                        </button>
                      </div>

                      {/* BB Model Toggle */}
                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 flex flex-col justify-between space-y-2">
                        <div>
                          <span className="text-[11px] font-bold text-slate-300 block">Breakout Block (BB)</span>
                          <span className="text-[10px] text-slate-400 block mt-0.5">Phá vỡ khối nén tích lũy</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, volman_enable_bb: !settings.volman_enable_bb })}
                          className={`w-full py-1.5 rounded-lg text-xs font-bold transition cursor-pointer flex items-center justify-center gap-1.5 ${
                            settings.volman_enable_bb
                              ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40"
                              : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                          }`}
                        >
                          {settings.volman_enable_bb ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: MA Trend Filter & Swing Pivot */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400">
                          <Layers className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Bộ Lọc Xu Hướng MA Lớn & Định Vị Pivot</h3>
                          <p className="text-[11px] text-slate-400">Độ dài tìm kiếm điểm xoay Swing Pivot và lọc xu hướng dài hạn</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-purple-400 font-mono font-bold bg-purple-950/60 border border-purple-800/60 px-2 py-0.5 rounded">
                        Trend & Pivot
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* Pivot Length */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Độ Dài Swing Pivot</label>
                          <span className="text-[11px] text-purple-400 font-mono font-bold">{settings.volman_pivot_length || 3} nến</span>
                        </div>
                        <input
                          type="number"
                          step="1"
                          min="1"
                          max="20"
                          value={settings.volman_pivot_length !== undefined ? settings.volman_pivot_length : 3}
                          onChange={(e) => setSettings({ ...settings, volman_pivot_length: parseInt(e.target.value) || 3 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-purple-500 focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500 block">Số nến trái/phải để xác nhận đỉnh/đáy pivot</span>
                      </div>

                      {/* MA Filter */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Bộ Lọc Đường MA Lớn</label>
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, volman_ma_filter_enable: !(settings.volman_ma_filter_enable !== false) })}
                            className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                              settings.volman_ma_filter_enable !== false
                                ? "bg-purple-500/20 text-purple-400 border border-purple-500/40"
                                : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                            }`}
                          >
                            {settings.volman_ma_filter_enable !== false ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <select
                            disabled={settings.volman_ma_filter_enable === false}
                            value={settings.volman_ma_type || "EMA"}
                            onChange={(e) => setSettings({ ...settings, volman_ma_type: e.target.value })}
                            className="bg-slate-900 border border-slate-700/80 rounded-lg px-2 py-1 text-xs text-slate-100 font-bold focus:border-purple-500 focus:outline-none disabled:opacity-40"
                          >
                            <option value="EMA">EMA</option>
                            <option value="SMA">SMA</option>
                          </select>
                          <input
                            type="number"
                            step="5"
                            min="10"
                            max="500"
                            disabled={settings.volman_ma_filter_enable === false}
                            value={settings.volman_ma_period !== undefined ? settings.volman_ma_period : 200}
                            onChange={(e) => setSettings({ ...settings, volman_ma_period: parseInt(e.target.value) || 200 })}
                            className="bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1 text-xs text-slate-100 font-bold focus:border-purple-500 focus:outline-none disabled:opacity-40"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                </div>

                {/* === RIGHT COLUMN: RISK, DYNAMIC LIMIT & VISUALS === */}
                <div className="space-y-5">
                  
                  {/* Card 4: Risk Management & Target R:R */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                          <Target className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Quản Trị Rủi Ro SL & Mục Tiêu Chốt Lời</h3>
                          <p className="text-[11px] text-slate-400">Thiết lập tỷ lệ R:R chốt lời và khoảng đệm Stop Loss</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-emerald-400 font-mono font-bold bg-emerald-950/60 border border-emerald-800/60 px-2 py-0.5 rounded">
                        Target & Risk
                      </span>
                    </div>

                    <div className="space-y-3.5">
                      {/* Take Profit R:R */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Tỷ Lệ Mục Tiêu Chốt Lời (Take Profit R:R)</label>
                          <span className="text-[11px] text-emerald-400 font-mono font-bold">{settings.volman_r1 !== undefined ? settings.volman_r1 : 1.5}R</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            step="0.1"
                            min="0.5"
                            max="10"
                            value={settings.volman_r1 !== undefined ? settings.volman_r1 : 1.5}
                            onChange={(e) => setSettings({ ...settings, volman_r1: parseFloat(e.target.value) || 1.5 })}
                            className="w-32 bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none"
                          />
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] text-slate-500">Mục tiêu:</span>
                            {[1.0, 1.2, 1.5, 1.8, 2.0, 2.5, 3.0].map((r) => (
                              <button
                                key={r}
                                type="button"
                                onClick={() => setSettings({ ...settings, volman_r1: r })}
                                className={`px-2 py-0.5 text-[10px] font-mono rounded transition cursor-pointer font-medium ${
                                  (settings.volman_r1 || 1.5) === r
                                    ? "bg-emerald-500/25 text-emerald-300 border border-emerald-500/50 font-bold"
                                    : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800"
                                }`}
                              >
                                {r}R
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Cooldown & SL Buffer */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-bold text-slate-300">Giãn Cách Lệnh (Cooldown)</label>
                            <span className="text-[11px] text-emerald-400 font-mono font-bold">{settings.volman_cooldown_bars !== undefined ? settings.volman_cooldown_bars : 3} nến</span>
                          </div>
                          <input
                            type="number"
                            step="1"
                            min="1"
                            max="50"
                            value={settings.volman_cooldown_bars !== undefined ? settings.volman_cooldown_bars : 3}
                            onChange={(e) => setSettings({ ...settings, volman_cooldown_bars: parseInt(e.target.value) || 3 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none"
                          />
                          <span className="text-[10px] text-slate-500 block">Khoảng nghỉ giữa 2 lần vào lệnh</span>
                        </div>

                        <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-bold text-slate-300">Đệm SL An Toàn (SL Buffer)</label>
                            <span className="text-[11px] text-emerald-400 font-mono font-bold">+{settings.volman_sl_buffer_pips !== undefined ? settings.volman_sl_buffer_pips : 15.0}p</span>
                          </div>
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            max="100"
                            value={settings.volman_sl_buffer_pips !== undefined ? settings.volman_sl_buffer_pips : 15.0}
                            onChange={(e) => setSettings({ ...settings, volman_sl_buffer_pips: parseFloat(e.target.value) || 15.0 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none"
                          />
                          <span className="text-[10px] text-slate-500 block">Khoảng cách cộng đệm ngoài điểm cản</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 5: Max & Min SL Filter */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-rose-500/10 border border-rose-500/30 flex items-center justify-center text-rose-400">
                          <Shield className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Bộ Lọc Khoảng Cách SL (Max & Min Risk)</h3>
                          <p className="text-[11px] text-slate-400">Bỏ qua tín hiệu nếu khoảng cách Stop Loss quá xa hoặc quá hẹp</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, volman_sl_filter_enable: !(settings.volman_sl_filter_enable !== false) })}
                        className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition cursor-pointer ${
                          settings.volman_sl_filter_enable !== false
                            ? "bg-rose-500/20 text-rose-400 border border-rose-500/40"
                            : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                        }`}
                      >
                        {settings.volman_sl_filter_enable !== false ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Khoảng Cách SL Tối Đa</label>
                          <span className="text-[10px] text-rose-400 font-mono font-bold">{settings.volman_max_risk_pips !== undefined ? settings.volman_max_risk_pips : 200}p</span>
                        </div>
                        <input
                          type="number"
                          step="5"
                          min="10"
                          max="500"
                          disabled={settings.volman_sl_filter_enable === false}
                          value={settings.volman_max_risk_pips !== undefined ? settings.volman_max_risk_pips : 200}
                          onChange={(e) => setSettings({ ...settings, volman_max_risk_pips: parseFloat(e.target.value) || 200 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-rose-500 focus:outline-none disabled:opacity-40"
                        />
                        <span className="text-[10px] text-slate-500 block truncate">SL &gt; mức này &rarr; Bỏ qua</span>
                      </div>

                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Khoảng Cách SL Tối Thiểu</label>
                          <span className="text-[10px] text-rose-400 font-mono font-bold">{settings.volman_min_risk_pips !== undefined ? settings.volman_min_risk_pips : 10}p</span>
                        </div>
                        <input
                          type="number"
                          step="1"
                          min="1"
                          max="50"
                          disabled={settings.volman_sl_filter_enable === false}
                          value={settings.volman_min_risk_pips !== undefined ? settings.volman_min_risk_pips : 10}
                          onChange={(e) => setSettings({ ...settings, volman_min_risk_pips: parseFloat(e.target.value) || 10 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-rose-500 focus:outline-none disabled:opacity-40"
                        />
                        <span className="text-[10px] text-slate-500 block truncate">SL &lt; mức này &rarr; Bỏ qua</span>
                      </div>
                    </div>
                  </div>

                  {/* Card 6: Dynamic Limit Entry on Large Breakouts */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
                          <Clock className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Vào Lệnh Chờ Limit Khi Nến Phá Quá Lớn</h3>
                          <p className="text-[11px] text-slate-400">Tự động chuyển từ Market sang Limit chờ hồi sóng (Pullback)</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, volman_dynamic_limit_enable: !(settings.volman_dynamic_limit_enable !== false) })}
                        className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition cursor-pointer ${
                          settings.volman_dynamic_limit_enable !== false
                            ? "bg-amber-500/20 text-amber-400 border border-amber-500/40"
                            : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                        }`}
                      >
                        {settings.volman_dynamic_limit_enable !== false ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">Ngưỡng Kích Hoạt</label>
                          <span className="text-[10px] text-amber-400 font-mono font-bold">{settings.volman_limit_threshold_pips || 100}p</span>
                        </div>
                        <input
                          type="number"
                          step="5"
                          min="20"
                          max="300"
                          disabled={settings.volman_dynamic_limit_enable === false}
                          value={settings.volman_limit_threshold_pips !== undefined ? settings.volman_limit_threshold_pips : 100}
                          onChange={(e) => setSettings({ ...settings, volman_limit_threshold_pips: parseFloat(e.target.value) || 100 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-amber-500 focus:outline-none disabled:opacity-40"
                        />
                        <span className="text-[10px] text-slate-500 block truncate">Kích hoạt khi SL &ge; N pips</span>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">Tỷ Lệ Hồi (Pullback)</label>
                          <span className="text-[10px] text-amber-400 font-mono font-bold">{settings.volman_limit_pullback_pct || 50}%</span>
                        </div>
                        <input
                          type="number"
                          step="5"
                          min="10"
                          max="80"
                          disabled={settings.volman_dynamic_limit_enable === false}
                          value={settings.volman_limit_pullback_pct !== undefined ? settings.volman_limit_pullback_pct : 50}
                          onChange={(e) => setSettings({ ...settings, volman_limit_pullback_pct: parseFloat(e.target.value) || 50 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-amber-500 focus:outline-none disabled:opacity-40"
                        />
                        <span className="text-[10px] text-slate-500 block truncate">Đặt Limit ở % sóng phá vỡ</span>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">Hủy Limit Sau (Nến)</label>
                          <span className="text-[10px] text-amber-400 font-mono font-bold">{settings.volman_limit_cancel_bars || 10} nến</span>
                        </div>
                        <input
                          type="number"
                          step="1"
                          min="1"
                          max="100"
                          disabled={settings.volman_dynamic_limit_enable === false}
                          value={settings.volman_limit_cancel_bars !== undefined ? settings.volman_limit_cancel_bars : 10}
                          onChange={(e) => setSettings({ ...settings, volman_limit_cancel_bars: parseInt(e.target.value) || 10 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-amber-500 focus:outline-none disabled:opacity-40"
                        />
                        <span className="text-[10px] text-slate-500 block truncate">Tự hủy lệnh nếu không khớp</span>
                      </div>
                    </div>
                  </div>

                  {/* Card 7: Chart Overlays & Visual Privacy */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
                          <Eye className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Hiển Thị Lớp Phủ Biểu Đồ (Chart Overlays)</h3>
                          <p className="text-[11px] text-slate-400">Tùy chọn ẩn/hiện các thành phần kỹ thuật Bob Volman trên chart</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-indigo-400 font-mono font-bold bg-indigo-950/60 border border-indigo-800/60 px-2 py-0.5 rounded">
                        Visual Overlays
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-300">Đường 20 EMA:</span>
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, volman_show_ema: !(settings.volman_show_ema !== false) })}
                          className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                            settings.volman_show_ema !== false
                              ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/40"
                              : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                          }`}
                        >
                          {settings.volman_show_ema !== false ? "HIỆN ✓" : "ẨN ✕"}
                        </button>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-300">Rào Chắn (Barriers):</span>
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, volman_show_barriers: !(settings.volman_show_barriers !== false) })}
                          className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                            settings.volman_show_barriers !== false
                              ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/40"
                              : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                          }`}
                        >
                          {settings.volman_show_barriers !== false ? "HIỆN ✓" : "ẨN ✕"}
                        </button>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-300">Trendlines:</span>
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, volman_show_trendlines: !(settings.volman_show_trendlines !== false) })}
                          className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                            settings.volman_show_trendlines !== false
                              ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/40"
                              : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                          }`}
                        >
                          {settings.volman_show_trendlines !== false ? "HIỆN ✓" : "ẨN ✕"}
                        </button>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-300">Khối Nén Buildup:</span>
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, volman_show_buildup: !(settings.volman_show_buildup !== false) })}
                          className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                            settings.volman_show_buildup !== false
                              ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/40"
                              : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                          }`}
                        >
                          {settings.volman_show_buildup !== false ? "HIỆN ✓" : "ẨN ✕"}
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Bottom Sticky Save Bar */}
              <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 shadow-xl flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle className="w-4 h-4 text-indigo-400 shrink-0" />
                  <span>Cấu hình Bob Volman Scalping sẽ được lưu vào cơ sở dữ liệu và đồng bộ tới toàn bộ người dùng & EA.</span>
                </div>
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 active:scale-95 text-white font-bold text-xs transition-all cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/20 disabled:opacity-50 shrink-0"
                >
                  <Save className="w-4 h-4" />
                  <span>{savingSettings ? "Đang Lưu Cấu Hình..." : "Lưu Toàn Bộ Cấu Hình Volman Mặc Định"}</span>
                </button>
              </div>

            </div>
          )}

          {/* SECTION: ORB SESSION PRO SYSTEM CONFIG */}
          {activeSection === 'orb_config' && (
            <div className="space-y-5">
              {/* Top Strategy Action Header */}
              <div className="p-5 rounded-2xl bg-gradient-to-r from-slate-900 via-slate-900 to-slate-900/90 border border-slate-800 shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 shadow-md shadow-amber-500/10 shrink-0">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-extrabold text-slate-100 tracking-tight">Cấu Hình Chiến Lược ORB Session Pro</h2>
                      <span className="text-[10px] text-amber-400 font-mono font-bold bg-amber-950/70 border border-amber-800/60 px-2 py-0.5 rounded-full">
                        ORB Engine v3.0
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Opening Range Breakout • Hỗ trợ Dynamic Limit 50% • Đa phiên Á/Âu/Mỹ • Đồng bộ EA Realtime
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 shrink-0">
                  <button
                    type="button"
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                    className="px-5 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 active:scale-95 text-white font-bold text-xs transition-all cursor-pointer flex items-center gap-2 shadow-lg shadow-amber-600/25 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    <span>{savingSettings ? "Đang Lưu Cấu Hình..." : "Lưu Cấu Hình ORB"}</span>
                  </button>
                </div>
              </div>

              {/* Status Alert Message */}
              {settingsMsg.text && (
                <div className={`p-3.5 rounded-xl border text-xs flex items-center gap-2.5 transition-all ${
                  settingsMsg.type === 'success'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                }`}>
                  {settingsMsg.type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
                  <span className="font-medium">{settingsMsg.text}</span>
                </div>
              )}

              {/* TWO-COLUMN WORKSTATION GRID */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
                
                {/* === LEFT COLUMN: SESSIONS & ENTRY EXECUTION === */}
                <div className="space-y-5">
                  
                  {/* Card 1: Session Selector */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
                          <Clock className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Các Phiên Giao Dịch Mở Cửa (ORB Sessions)</h3>
                          <p className="text-[11px] text-slate-400">Chọn các phiên giao dịch quét tín hiệu phá vỡ đỉnh/đáy hộp tích lũy mở phiên</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-amber-400 font-mono font-bold bg-amber-950/60 border border-amber-800/60 px-2 py-0.5 rounded">
                        Active Sessions
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* Asia Session */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 flex flex-col justify-between space-y-2.5">
                        <div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-slate-200">Phiên Á (Tokyo)</span>
                            <span className="text-[10px] text-slate-500 font-mono">06:00-14:00</span>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1">Đo hộp tích lũy sáng sớm</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, orb_asia_enable: settings.orb_asia_enable === false ? true : false })}
                          className={`w-full py-1.5 rounded-lg text-xs font-bold transition cursor-pointer flex items-center justify-center gap-1.5 ${
                            settings.orb_asia_enable !== false
                              ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                              : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                          }`}
                        >
                          {settings.orb_asia_enable !== false ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                        </button>
                      </div>

                      {/* London Session */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 flex flex-col justify-between space-y-2.5">
                        <div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-slate-200">Phiên Âu (London)</span>
                            <span className="text-[10px] text-slate-500 font-mono">14:00-19:30</span>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1">Sóng bùng nổ London</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, orb_london_enable: settings.orb_london_enable === false ? true : false })}
                          className={`w-full py-1.5 rounded-lg text-xs font-bold transition cursor-pointer flex items-center justify-center gap-1.5 ${
                            settings.orb_london_enable !== false
                              ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                              : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                          }`}
                        >
                          {settings.orb_london_enable !== false ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                        </button>
                      </div>

                      {/* New York Session */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 flex flex-col justify-between space-y-2.5">
                        <div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-slate-200">Phiên Mỹ (NY)</span>
                            <span className="text-[10px] text-slate-500 font-mono">19:30-23:00</span>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1">Biến động tin tức Mỹ</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, orb_ny_enable: !settings.orb_ny_enable })}
                          className={`w-full py-1.5 rounded-lg text-xs font-bold transition cursor-pointer flex items-center justify-center gap-1.5 ${
                            Boolean(settings.orb_ny_enable)
                              ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                              : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                          }`}
                        >
                          {Boolean(settings.orb_ny_enable) ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Entry Mode & EMA 200 Trend Filter */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                          <Zap className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Cơ Chế Khớp Lệnh & Bộ Lọc Nến (Entry Mode Engine)</h3>
                          <p className="text-[11px] text-slate-400">Cách thức khớp lệnh khi nến đóng vượt biên trên/dưới hộp phiên</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-cyan-400 font-mono font-bold bg-cyan-950/60 border border-cyan-800/60 px-2 py-0.5 rounded">
                        Execution Mode
                      </span>
                    </div>

                    <div className="space-y-3.5">
                      {/* Entry Mode Selector */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <label className="text-xs font-bold text-slate-300 block">Chế Độ Vào Lệnh (Entry Mode)</label>
                        <select
                          value={settings.orb_entry_mode || 'SMART_HYBRID'}
                          onChange={(e) => setSettings({ ...settings, orb_entry_mode: e.target.value })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-2 text-xs text-slate-100 font-bold focus:border-cyan-500 focus:outline-none"
                        >
                          <option value="BREAKOUT_INSTANT">1. Vào Lệnh Ngay Khi Đóng Nến (Market Instant)</option>
                          <option value="RETEST_LIMIT">2. Chờ Retest Biên Hộp (Pure Limit Retest)</option>
                          <option value="SMART_HYBRID">3. Smart Hybrid (Market nếu nến ngắn, Limit nếu nến dài)</option>
                        </select>
                      </div>

                      {/* 2 Balanced Columns for Threshold & Limit Wait */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-bold text-slate-300">Biên Nến Phá Tối Đa</label>
                            <span className="text-[11px] text-cyan-400 font-mono font-bold">{settings.orb_max_break_pips !== undefined ? settings.orb_max_break_pips : 15.0}p</span>
                          </div>
                          <div className="relative">
                            <input
                              type="number"
                              step="0.5"
                              min="1"
                              max="100"
                              value={settings.orb_max_break_pips !== undefined ? settings.orb_max_break_pips : 15.0}
                              onChange={(e) => setSettings({ ...settings, orb_max_break_pips: parseFloat(e.target.value) || 15.0 })}
                              className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-cyan-500 focus:outline-none"
                            />
                            <span className="absolute right-2.5 top-1.5 text-[10px] text-slate-400 font-mono">pips</span>
                          </div>
                          <span className="text-[10px] text-slate-500 block">Nến vượt quá số pips này sẽ đổi sang Limit</span>
                        </div>

                        <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-bold text-slate-300">Chờ Khớp Limit Tối Đa</label>
                            <span className="text-[11px] text-cyan-400 font-mono font-bold">{settings.orb_max_limit_bars !== undefined ? settings.orb_max_limit_bars : 6} nến</span>
                          </div>
                          <div className="relative">
                            <input
                              type="number"
                              step="1"
                              min="1"
                              max="50"
                              value={settings.orb_max_limit_bars !== undefined ? settings.orb_max_limit_bars : 6}
                              onChange={(e) => setSettings({ ...settings, orb_max_limit_bars: parseInt(e.target.value) || 6 })}
                              className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-cyan-500 focus:outline-none"
                            />
                            <span className="absolute right-2.5 top-1.5 text-[10px] text-slate-400 font-mono">nến</span>
                          </div>
                          <span className="text-[10px] text-slate-500 block">Hủy lệnh chờ nếu sau N nến chưa chạm</span>
                        </div>
                      </div>

                      {/* EMA 200 Trend Filter */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <label className="text-xs font-bold text-slate-300 block">Bộ Lọc Xu Hướng EMA 200 (M5)</label>
                        <select
                          value={settings.orb_ema200_mode || (settings.orb_ema200_filter ? 'STRICT' : 'OFF')}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSettings({
                              ...settings,
                              orb_ema200_mode: val,
                              orb_ema200_filter: val !== 'OFF'
                            });
                          }}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-2 text-xs text-slate-100 font-bold focus:border-cyan-500 focus:outline-none"
                        >
                          <option value="OFF">TẮT BỘ LỌC (Cho phép BUY/SELL tự do theo biên hộp)</option>
                          <option value="STRICT">STRICT (Chỉ BUY khi Giá & EMA200 &gt; Hộp, Chỉ SELL khi Giá & EMA200 &lt; Hộp)</option>
                          <option value="DUAL_TREND">DUAL-TREND THÔNG MINH (Chỉ xét vị thế Giá so với EMA 200)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Revert Strategy After Fakeout */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center justify-center text-orange-400">
                          <RefreshCw className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Đảo Chiều Sau Fakeout (Revert Strategy)</h3>
                          <p className="text-[11px] text-slate-400">Tự động kích hoạt lệnh ngược lại khi lệnh chính bị dính Stop Loss do bẫy thanh khoản</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, orb_revert_enable: !settings.orb_revert_enable })}
                        className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition cursor-pointer ${
                          settings.orb_revert_enable
                            ? "bg-orange-500/20 text-orange-400 border border-orange-500/40"
                            : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                        }`}
                      >
                        {settings.orb_revert_enable ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                      </button>
                    </div>

                    {settings.orb_revert_enable && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
                        <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <label className="text-[11px] font-bold text-slate-300 block">Cơ Chế Khớp Lệnh Revert</label>
                          <select
                            value={settings.orb_revert_entry_mode || 'LIMIT_50_BOX'}
                            onChange={(e) => setSettings({ ...settings, orb_revert_entry_mode: e.target.value })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-orange-500 focus:outline-none"
                          >
                            <option value="LIMIT_50_BOX">Limit 50% Hộp Phiên (Khuyên dùng)</option>
                            <option value="SMART_HYBRID">Smart Hybrid (&le; {settings.orb_max_break_pips ?? 15}p Market)</option>
                            <option value="MARKET_INSTANT">Market Ngay Lập Tức</option>
                          </select>
                        </div>

                        <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-[11px] font-bold text-slate-300">Mục Tiêu R:R Revert</label>
                            <span className="text-[10px] text-orange-400 font-mono font-bold">{settings.orb_revert_rr ?? 1.5}R</span>
                          </div>
                          <input
                            type="number"
                            step="0.1"
                            min="0.5"
                            max="10"
                            value={settings.orb_revert_rr ?? 1.5}
                            onChange={(e) => setSettings({ ...settings, orb_revert_rr: parseFloat(e.target.value) || 1.5 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-orange-500 focus:outline-none"
                          />
                        </div>

                        <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-[11px] font-bold text-slate-300">Hủy Sau (Nến)</label>
                            <span className="text-[10px] text-orange-400 font-mono font-bold">{settings.orb_revert_cancel_bars ?? 6} nến</span>
                          </div>
                          <input
                            type="number"
                            step="1"
                            min="1"
                            max="200"
                            value={settings.orb_revert_cancel_bars ?? 6}
                            onChange={(e) => setSettings({ ...settings, orb_revert_cancel_bars: parseInt(e.target.value) || 6 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-orange-500 focus:outline-none"
                          />
                        </div>

                        <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-[11px] font-bold text-slate-300">Đệm SL Revert (Pips)</label>
                            <span className="text-[10px] text-orange-400 font-mono font-bold">+{settings.orb_revert_sl_buffer_pips ?? 2.0}p</span>
                          </div>
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            max="500"
                            value={settings.orb_revert_sl_buffer_pips ?? 2.0}
                            onChange={(e) => setSettings({ ...settings, orb_revert_sl_buffer_pips: parseFloat(e.target.value) || 0 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-orange-500 focus:outline-none"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                </div>

                {/* === RIGHT COLUMN: RISK MANAGEMENT, LIMIT RETEST & EXITS === */}
                <div className="space-y-5">
                  
                  {/* Card 4: Target R:R & Stop Loss Buffer */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                          <Target className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Quản Trị Rủi Ro SL & Mục Tiêu Chốt Lời TP</h3>
                          <p className="text-[11px] text-slate-400">Thiết lập tỷ lệ R:R chốt lời và cơ chế cộng đệm SL an toàn</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-emerald-400 font-mono font-bold bg-emerald-950/60 border border-emerald-800/60 px-2 py-0.5 rounded">
                        Target & Risk
                      </span>
                    </div>

                    <div className="space-y-3.5">
                      {/* Take Profit Target R:R */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Tỷ Lệ Mục Tiêu Chốt Lời (Take Profit R:R)</label>
                          <span className="text-[11px] text-emerald-400 font-mono font-bold">{settings.orb_rr !== undefined ? settings.orb_rr : 3.0}R</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            step="0.1"
                            min="0.5"
                            max="10"
                            value={settings.orb_rr !== undefined ? settings.orb_rr : 3.0}
                            onChange={(e) => setSettings({ ...settings, orb_rr: parseFloat(e.target.value) || 3.0 })}
                            className="w-32 bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none"
                          />
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] text-slate-500">Mục tiêu:</span>
                            {[1.5, 2.0, 2.5, 3.0, 3.5, 4.0].map((r) => (
                              <button
                                key={r}
                                type="button"
                                onClick={() => setSettings({ ...settings, orb_rr: r })}
                                className={`px-2 py-0.5 text-[10px] font-mono rounded transition cursor-pointer font-medium ${
                                  (settings.orb_rr || 3.0) === r
                                    ? "bg-emerald-500/25 text-emerald-300 border border-emerald-500/50 font-bold"
                                    : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800"
                                }`}
                              >
                                {r}R
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* SL Buffer Mode & Value */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <label className="text-xs font-bold text-slate-300 block">Cơ Chế Đệm SL</label>
                          <select
                            value={settings.orb_sl_buffer_mode || 'FIXED'}
                            onChange={(e) => setSettings({ ...settings, orb_sl_buffer_mode: e.target.value })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-2 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none"
                          >
                            <option value="FIXED">Cố Định Pips (Fixed Pips)</option>
                            <option value="ATR">Biến Động Nến (ATR Multiplier)</option>
                          </select>
                        </div>

                        <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          {settings.orb_sl_buffer_mode === 'ATR' ? (
                            <>
                              <div className="flex items-center justify-between">
                                <label className="text-xs font-bold text-slate-300">Hệ Số Nhân ATR</label>
                                <span className="text-[11px] text-emerald-400 font-mono font-bold">{settings.orb_sl_atr_mult ?? 1.0}x</span>
                              </div>
                              <input
                                type="number"
                                step="0.1"
                                min="0.1"
                                max="5.0"
                                value={settings.orb_sl_atr_mult ?? 1.0}
                                onChange={(e) => setSettings({ ...settings, orb_sl_atr_mult: parseFloat(e.target.value) || 1.0 })}
                                className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none"
                              />
                            </>
                          ) : (
                            <>
                              <div className="flex items-center justify-between">
                                <label className="text-xs font-bold text-slate-300">Khoảng Đệm SL Cố Định</label>
                                <span className="text-[11px] text-emerald-400 font-mono font-bold">+{settings.orb_sl_buffer_pips !== undefined ? settings.orb_sl_buffer_pips : 2.0} pips</span>
                              </div>
                              <input
                                type="number"
                                step="0.5"
                                min="0"
                                max="20"
                                value={settings.orb_sl_buffer_pips !== undefined ? settings.orb_sl_buffer_pips : 2.0}
                                onChange={(e) => setSettings({ ...settings, orb_sl_buffer_pips: parseFloat(e.target.value) || 0 })}
                                className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none"
                              />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 5: Max SL & Session Range Protection */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-rose-500/10 border border-rose-500/30 flex items-center justify-center text-rose-400">
                          <Shield className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Bộ Lọc Cắt Lỗ SL Tối Đa (Max Risk Protection)</h3>
                          <p className="text-[11px] text-slate-400">Tự động bỏ qua tín hiệu nếu khoảng cách SL hoặc biên độ phiên vượt quá ngưỡng</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, orb_sl_filter_enable: !settings.orb_sl_filter_enable })}
                        className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition cursor-pointer ${
                          Boolean(settings.orb_sl_filter_enable)
                            ? "bg-rose-500/20 text-rose-400 border border-rose-500/40"
                            : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                        }`}
                      >
                        {Boolean(settings.orb_sl_filter_enable) ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">SL Tối Đa (Max SL)</label>
                          <span className="text-[10px] text-rose-400 font-mono font-bold">{settings.orb_max_sl_pips !== undefined ? settings.orb_max_sl_pips : 150}p</span>
                        </div>
                        <input
                          type="number"
                          step="5"
                          min="10"
                          max="500"
                          disabled={!settings.orb_sl_filter_enable}
                          value={settings.orb_max_sl_pips !== undefined && settings.orb_max_sl_pips !== '' ? settings.orb_max_sl_pips : 150}
                          onChange={(e) => setSettings({ ...settings, orb_max_sl_pips: e.target.value === '' ? '' : (parseFloat(e.target.value) || 150) })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-rose-500 focus:outline-none disabled:opacity-40"
                        />
                        <span className="text-[10px] text-slate-500 block truncate">SL &gt; mức này &rarr; Bỏ qua</span>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">SL Tối Thiểu (Min SL)</label>
                          <span className="text-[10px] text-rose-400 font-mono font-bold">{settings.orb_min_sl_pips !== undefined ? settings.orb_min_sl_pips : 3.0}p</span>
                        </div>
                        <input
                          type="number"
                          step="0.5"
                          min="1"
                          max="50"
                          disabled={!settings.orb_sl_filter_enable}
                          value={settings.orb_min_sl_pips !== undefined && settings.orb_min_sl_pips !== '' ? settings.orb_min_sl_pips : 3.0}
                          onChange={(e) => setSettings({ ...settings, orb_min_sl_pips: e.target.value === '' ? '' : (parseFloat(e.target.value) || 3.0) })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-rose-500 focus:outline-none disabled:opacity-40"
                        />
                        <span className="text-[10px] text-slate-500 block truncate">SL &lt; mức này &rarr; Bỏ qua</span>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">Biên Độ Phiên Tối Đa</label>
                          <span className="text-[10px] text-rose-400 font-mono font-bold">{settings.orb_max_zone_pips !== undefined ? settings.orb_max_zone_pips : 250}p</span>
                        </div>
                        <input
                          type="number"
                          step="10"
                          min="20"
                          max="1000"
                          value={settings.orb_max_zone_pips !== undefined && settings.orb_max_zone_pips !== '' ? settings.orb_max_zone_pips : 250}
                          onChange={(e) => setSettings({ ...settings, orb_max_zone_pips: e.target.value === '' ? '' : (parseFloat(e.target.value) || 250) })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-rose-500 focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500 block truncate">Hộp quá rộng &rarr; Bỏ qua</span>
                      </div>
                    </div>
                  </div>

                  {/* Card 6: ORB Dynamic Limit Retest Engine */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
                          <Clock className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Vào Lệnh Chờ Limit Khi SL Quá Lớn (ORB Dynamic Limit)</h3>
                          <p className="text-[11px] text-slate-400">Tự động chuyển từ Market sang Limit chờ hồi 40-50% Zone khi SL ban đầu vượt quá ngưỡng</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, orb_dynamic_limit_enable: !Boolean(settings.orb_dynamic_limit_enable) })}
                        className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition cursor-pointer ${
                          Boolean(settings.orb_dynamic_limit_enable)
                            ? "bg-amber-500/20 text-amber-400 border border-amber-500/40"
                            : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                        }`}
                      >
                        {Boolean(settings.orb_dynamic_limit_enable) ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">Ngưỡng SL Kích Hoạt</label>
                          <span className="text-[10px] text-amber-400 font-mono font-bold">{settings.orb_limit_threshold_pips !== undefined ? settings.orb_limit_threshold_pips : 100}p</span>
                        </div>
                        <input
                          type="number"
                          step="5"
                          min="20"
                          max="300"
                          disabled={!Boolean(settings.orb_dynamic_limit_enable)}
                          value={settings.orb_limit_threshold_pips !== undefined && settings.orb_limit_threshold_pips !== '' ? settings.orb_limit_threshold_pips : 100}
                          onChange={(e) => setSettings({ ...settings, orb_limit_threshold_pips: e.target.value === '' ? '' : (parseFloat(e.target.value) || 100) })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-amber-500 focus:outline-none disabled:opacity-40"
                        />
                        <span className="text-[10px] text-slate-500 block truncate">Khuyên dùng 100-120 pips</span>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">Tỷ Lệ Hồi (Pullback)</label>
                          <span className="text-[10px] text-amber-400 font-mono font-bold">{settings.orb_limit_pullback_pct !== undefined ? settings.orb_limit_pullback_pct : 50}%</span>
                        </div>
                        <input
                          type="number"
                          step="5"
                          min="10"
                          max="80"
                          disabled={!Boolean(settings.orb_dynamic_limit_enable)}
                          value={settings.orb_limit_pullback_pct !== undefined && settings.orb_limit_pullback_pct !== '' ? settings.orb_limit_pullback_pct : 50}
                          onChange={(e) => setSettings({ ...settings, orb_limit_pullback_pct: e.target.value === '' ? '' : (parseFloat(e.target.value) || 50) })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-amber-500 focus:outline-none disabled:opacity-40"
                        />
                        <span className="text-[10px] text-slate-500 block truncate">Hồi 40-50% từ phá vỡ về SL</span>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">Hủy Limit Sau (Nến)</label>
                          <span className="text-[10px] text-amber-400 font-mono font-bold">{settings.orb_limit_cancel_bars !== undefined ? settings.orb_limit_cancel_bars : 10} nến</span>
                        </div>
                        <input
                          type="number"
                          step="1"
                          min="1"
                          max="50"
                          disabled={!Boolean(settings.orb_dynamic_limit_enable)}
                          value={settings.orb_limit_cancel_bars !== undefined && settings.orb_limit_cancel_bars !== '' ? settings.orb_limit_cancel_bars : 10}
                          onChange={(e) => setSettings({ ...settings, orb_limit_cancel_bars: e.target.value === '' ? '' : (parseInt(e.target.value) || 10) })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-amber-500 focus:outline-none disabled:opacity-40"
                        />
                        <span className="text-[10px] text-slate-500 block truncate">Tự hủy lệnh nếu không khớp</span>
                      </div>
                    </div>
                  </div>

                  {/* Card 7: Dynamic Break-Even & Session Timeout Exit */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                          <CheckCircle className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Dời Hòa Vốn & Hết Phiên Ép Đóng Lệnh</h3>
                          <p className="text-[11px] text-slate-400">Khóa lợi nhuận tự động và tùy chọn đóng lệnh khi kết thúc phiên giao dịch</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-emerald-400 font-mono font-bold bg-emerald-950/60 border border-emerald-800/60 px-2 py-0.5 rounded">
                        Risk-Free Engine
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* Dynamic BE */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2.5">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Dời SL Về Hòa Vốn</label>
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, orb_dynamic_be_enable: !settings.orb_dynamic_be_enable })}
                            className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                              Boolean(settings.orb_dynamic_be_enable)
                                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                                : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                            }`}
                          >
                            {Boolean(settings.orb_dynamic_be_enable) ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="text-[10px] text-slate-400 block mb-1">Ngưỡng % TP:</span>
                            <input
                              type="number"
                              step="5"
                              min="10"
                              max="95"
                              disabled={!settings.orb_dynamic_be_enable}
                              value={settings.orb_be_trigger_pct !== undefined ? settings.orb_be_trigger_pct : 75}
                              onChange={(e) => setSettings({ ...settings, orb_be_trigger_pct: parseFloat(e.target.value) || 75 })}
                              className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none disabled:opacity-40"
                            />
                          </div>
                          <div>
                            <span className="text-[10px] text-slate-400 block mb-1">Khóa Lãi (Pips):</span>
                            <input
                              type="number"
                              step="1"
                              min="0"
                              max="50"
                              disabled={!settings.orb_dynamic_be_enable}
                              value={settings.orb_be_lock_pips !== undefined ? settings.orb_be_lock_pips : 5}
                              onChange={(e) => setSettings({ ...settings, orb_be_lock_pips: parseFloat(e.target.value) || 5 })}
                              className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none disabled:opacity-40"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Session Timeout Exit */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 flex flex-col justify-between space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Hết Phiên Ép Đóng Lệnh</label>
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, orb_timeout_exit_enable: settings.orb_timeout_exit_enable === false ? true : false })}
                            className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                              settings.orb_timeout_exit_enable !== false
                                ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                                : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                            }`}
                          >
                            {settings.orb_timeout_exit_enable !== false ? "ĐANG BẬT (Ép cắt) ✓" : "ĐANG TẮT (Thả TP/SL) ✕"}
                          </button>
                        </div>
                        <p className="text-[11px] text-slate-400 leading-relaxed">
                          {settings.orb_timeout_exit_enable !== false
                            ? "Bật: Các lệnh còn đang chạy khi phiên kết thúc sẽ được hệ thống đóng tại giá đóng nến của phiên (Timeout)."
                            : "Tắt: Giữ lệnh chạy qua phiên tiếp theo cho đến khi tự chạm TP hoặc SL."}
                        </p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Bottom Sticky Save Bar */}
              <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 shadow-xl flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>Cấu hình ORB Session Pro sẽ được lưu vào cơ sở dữ liệu và đồng bộ tới toàn bộ người dùng & EA.</span>
                </div>
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 active:scale-95 text-white font-bold text-xs transition-all cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-amber-600/20 disabled:opacity-50 shrink-0"
                >
                  <Save className="w-4 h-4" />
                  <span>{savingSettings ? "Đang Lưu Cấu Hình..." : "Lưu Toàn Bộ Cấu Hình ORB Session Pro Mặc Định"}</span>
                </button>
              </div>

            </div>
          )}

          {/* SECTION: LIQUIDITY SWEEP PRO SYSTEM CONFIG */}
          {activeSection === 'sweep_config' && (
            <div className="space-y-5">
              {/* Top Strategy Action Header */}
              <div className="p-5 rounded-2xl bg-gradient-to-r from-slate-900 via-slate-900 to-slate-900/90 border border-slate-800 shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400 shadow-md shadow-purple-500/10 shrink-0">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-extrabold text-slate-100 tracking-tight">Cấu Hình Chiến Lược Liquidity Sweep Pro</h2>
                      <span className="text-[10px] text-purple-400 font-mono font-bold bg-purple-950/70 border border-purple-800/60 px-2 py-0.5 rounded-full">
                        Sweep Engine v3.2
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Quét thanh khoản đỉnh/đáy • Smart Hybrid Limit 50% • Quản trị rủi ro đa khung M1-M5 • Dynamic BE
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 shrink-0">
                  <button
                    type="button"
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                    className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 active:scale-95 text-white font-bold text-xs transition-all cursor-pointer flex items-center gap-2 shadow-lg shadow-purple-600/25 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    <span>{savingSettings ? "Đang Lưu Cấu Hình..." : "Lưu Cấu Hình Sweep"}</span>
                  </button>
                </div>
              </div>

              {/* Status Alert Message */}
              {settingsMsg.text && (
                <div className={`p-3.5 rounded-xl border text-xs flex items-center gap-2.5 transition-all ${
                  settingsMsg.type === 'success'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                }`}>
                  {settingsMsg.type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
                  <span className="font-medium">{settingsMsg.text}</span>
                </div>
              )}

              {/* TWO-COLUMN WORKSTATION GRID */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
                
                {/* === LEFT COLUMN: ENTRY MODES, STRUCTURE & FILTERS === */}
                <div className="space-y-5">
                  
                  {/* Card 1: Entry Mode & Smart Hybrid Engine */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400">
                          <Zap className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Chế Độ Vào Lệnh (Entry Mode & Hybrid)</h3>
                          <p className="text-[11px] text-slate-400">Cấu hình cách thức kích hoạt lệnh khi xuất hiện nến quét thanh khoản</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-purple-400 font-mono font-bold bg-purple-950/60 border border-purple-800/60 px-2 py-0.5 rounded">
                        Execution Mode
                      </span>
                    </div>

                    <div className="space-y-3.5">
                      {/* Entry Mode Selector */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <label className="text-xs font-bold text-slate-300 block">Kiểu Khớp Lệnh (Entry Mode)</label>
                        <select
                          value={settings.sweep_entry_mode || 'SMART_HYBRID'}
                          onChange={(e) => setSettings({ ...settings, sweep_entry_mode: e.target.value })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-2 text-xs text-slate-100 font-bold focus:border-purple-500 focus:outline-none"
                        >
                          <option value="MARKET">Vào Lệnh Thị Trường Ngay Lập Tức (Market Instant)</option>
                          <option value="LIMIT_50">Limit 50% Thân Nến Quét (Mid-Body Pullback)</option>
                          <option value="LIMIT_ZONE">Limit Vùng Quét Đỉnh/Đáy Cụm (Zone Retest)</option>
                          <option value="SMART_HYBRID">Smart Hybrid (Tự động chuyển Market sang Limit khi giá chạy xa)</option>
                          <option value="PENDING_REVERSE">Lệnh Chờ Đảo Chiều Đón Đầu (Pending Reverse)</option>
                        </select>
                      </div>

                      {/* 3 Balanced Columns for Hybrid Retest */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-[11px] font-bold text-slate-300">Ngưỡng Chuyển Limit</label>
                            <span className="text-[10px] text-purple-400 font-mono font-bold">{settings.sweep_hybrid_threshold_pips !== undefined ? settings.sweep_hybrid_threshold_pips : 30}p</span>
                          </div>
                          <input
                            type="number"
                            step="1"
                            min="5"
                            max="200"
                            value={settings.sweep_hybrid_threshold_pips !== undefined ? settings.sweep_hybrid_threshold_pips : 30}
                            onChange={(e) => setSettings({ ...settings, sweep_hybrid_threshold_pips: parseFloat(e.target.value) || 30 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-purple-500 focus:outline-none"
                          />
                          <span className="text-[10px] text-slate-500 block truncate">Giá lệch &gt; N pips &rarr; Limit</span>
                        </div>

                        <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-[11px] font-bold text-slate-300">Tỷ Lệ Hồi Vùng Zone</label>
                            <span className="text-[10px] text-purple-400 font-mono font-bold">{settings.sweep_hybrid_zone_pct !== undefined ? settings.sweep_hybrid_zone_pct : 50}%</span>
                          </div>
                          <input
                            type="number"
                            step="5"
                            min="10"
                            max="90"
                            value={settings.sweep_hybrid_zone_pct !== undefined ? settings.sweep_hybrid_zone_pct : 50}
                            onChange={(e) => setSettings({ ...settings, sweep_hybrid_zone_pct: parseFloat(e.target.value) || 50 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-purple-500 focus:outline-none"
                          />
                          <span className="text-[10px] text-slate-500 block truncate">Vị trí đặt Limit trong vùng</span>
                        </div>

                        <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-[11px] font-bold text-slate-300">Hủy Limit Sau (Nến)</label>
                            <span className="text-[10px] text-purple-400 font-mono font-bold">{settings.sweep_limit_cancel_bars !== undefined ? settings.sweep_limit_cancel_bars : 10} nến</span>
                          </div>
                          <input
                            type="number"
                            step="1"
                            min="1"
                            max="50"
                            value={settings.sweep_limit_cancel_bars !== undefined ? settings.sweep_limit_cancel_bars : 10}
                            onChange={(e) => setSettings({ ...settings, sweep_limit_cancel_bars: parseInt(e.target.value) || 10 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-purple-500 focus:outline-none"
                          />
                          <span className="text-[10px] text-slate-500 block truncate">Hủy lệnh nếu không khớp</span>
                        </div>
                      </div>

                      {/* 3 Action Toggles */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-1">
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, sweep_m1_anchor_m5: settings.sweep_m1_anchor_m5 === false ? true : false })}
                          className={`p-2 rounded-xl text-[11px] font-bold border transition text-center ${
                            settings.sweep_m1_anchor_m5 !== false
                              ? "bg-purple-500/15 text-purple-300 border-purple-500/30"
                              : "bg-slate-950/60 text-slate-500 border-slate-800"
                          }`}
                        >
                          Neo M1 theo M5: {settings.sweep_m1_anchor_m5 !== false ? "BẬT ✓" : "TẮT ✕"}
                        </button>

                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, sweep_extend: !(settings.sweep_extend === true || settings.sweep_extend === undefined) })}
                          className={`p-2 rounded-xl text-[11px] font-bold border transition text-center ${
                            settings.sweep_extend === true || settings.sweep_extend === undefined
                              ? "bg-purple-500/15 text-purple-300 border-purple-500/30"
                              : "bg-slate-950/60 text-slate-500 border-slate-800"
                          }`}
                        >
                          Kéo dài vùng quét: {settings.sweep_extend === true || settings.sweep_extend === undefined ? "BẬT ✓" : "TẮT ✕"}
                        </button>

                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, sweep_zone_limit_enable: !(settings.sweep_zone_limit_enable === true || settings.sweep_zone_limit_enable === undefined) })}
                          className={`p-2 rounded-xl text-[11px] font-bold border transition text-center ${
                            settings.sweep_zone_limit_enable === true || settings.sweep_zone_limit_enable === undefined
                              ? "bg-purple-500/15 text-purple-300 border-purple-500/30"
                              : "bg-slate-950/60 text-slate-500 border-slate-800"
                          }`}
                        >
                          Limit Vùng Quét: {settings.sweep_zone_limit_enable === true || settings.sweep_zone_limit_enable === undefined ? "BẬT ✓" : "TẮT ✕"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Swing Structure Detection */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                          <Layers className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Định Vị Cấu Trúc Sóng Quét (Wave Structure)</h3>
                          <p className="text-[11px] text-slate-400">Thông số xác định đỉnh/đáy thanh khoản và biên độ nến quét</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-cyan-400 font-mono font-bold bg-cyan-950/60 border border-cyan-800/60 px-2 py-0.5 rounded">
                        Swing Detection
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1">
                        <span className="text-[11px] text-slate-400 block">Lookback Sóng:</span>
                        <input
                          type="number"
                          step="5"
                          min="10"
                          max="200"
                          value={settings.sweep_lookback !== undefined ? settings.sweep_lookback : 50}
                          onChange={(e) => setSettings({ ...settings, sweep_lookback: parseInt(e.target.value) || 50 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1 text-xs text-slate-100 font-bold focus:border-cyan-500 focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500 block">Số nến tìm đỉnh/đáy</span>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1">
                        <span className="text-[11px] text-slate-400 block">Nến Tối Đa:</span>
                        <input
                          type="number"
                          step="1"
                          min="3"
                          max="50"
                          value={settings.sweep_max_bars !== undefined ? settings.sweep_max_bars : 15}
                          onChange={(e) => setSettings({ ...settings, sweep_max_bars: parseInt(e.target.value) || 15 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1 text-xs text-slate-100 font-bold focus:border-cyan-500 focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500 block">Tối đa từ đáy/đỉnh</span>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1">
                        <span className="text-[11px] text-slate-400 block">Đỉnh Cục Bộ:</span>
                        <input
                          type="number"
                          step="1"
                          min="3"
                          max="30"
                          value={settings.sweep_peak_lookback !== undefined ? settings.sweep_peak_lookback : 10}
                          onChange={(e) => setSettings({ ...settings, sweep_peak_lookback: parseInt(e.target.value) || 10 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1 text-xs text-slate-100 font-bold focus:border-cyan-500 focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500 block">Số nến đỉnh lân cận</span>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1">
                        <span className="text-[11px] text-slate-400 block">Sóng Tối Thiểu:</span>
                        <input
                          type="number"
                          step="1"
                          min="1"
                          max="100"
                          value={settings.sweep_min_swing_pips !== undefined ? settings.sweep_min_swing_pips : 10.0}
                          onChange={(e) => setSettings({ ...settings, sweep_min_swing_pips: parseFloat(e.target.value) || 10.0 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1 text-xs text-slate-100 font-bold focus:border-cyan-500 focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500 block">Khoảng cách pips sóng</span>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Advanced Filters (Trend EMA, Session, Opposite Exit) */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
                          <Sliders className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Bộ Lọc Nâng Cao (Xu Hướng & Phiên)</h3>
                          <p className="text-[11px] text-slate-400">Lọc xu hướng EMA, khung giờ giao dịch và đóng lệnh khi có tín hiệu ngược</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-indigo-400 font-mono font-bold bg-indigo-950/60 border border-indigo-800/60 px-2 py-0.5 rounded">
                        Advanced Filters
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* Trend EMA Filter */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Bộ Lọc Xu Hướng EMA</label>
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, sweep_trend_filter_enable: !Boolean(settings.sweep_trend_filter_enable) })}
                            className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                              Boolean(settings.sweep_trend_filter_enable)
                                ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/40"
                                : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                            }`}
                          >
                            {Boolean(settings.sweep_trend_filter_enable) ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-slate-400">Chu kỳ EMA:</span>
                          <input
                            type="number"
                            step="10"
                            min="20"
                            max="500"
                            disabled={!Boolean(settings.sweep_trend_filter_enable)}
                            value={settings.sweep_trend_ema_period !== undefined ? settings.sweep_trend_ema_period : 200}
                            onChange={(e) => setSettings({ ...settings, sweep_trend_ema_period: parseInt(e.target.value) || 200 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1 text-xs text-slate-100 font-bold focus:border-indigo-500 focus:outline-none disabled:opacity-40"
                          />
                        </div>
                      </div>

                      {/* Session Hours Filter */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Khung Giờ Giao Dịch</label>
                          <button
                            type="button"
                            onClick={() => setSettings({ ...settings, sweep_session_filter_enable: !Boolean(settings.sweep_session_filter_enable) })}
                            className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                              Boolean(settings.sweep_session_filter_enable)
                                ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/40"
                                : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                            }`}
                          >
                            {Boolean(settings.sweep_session_filter_enable) ? "ĐANG BẬT ✓" : "24/24 TẮT ✕"}
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            disabled={!Boolean(settings.sweep_session_filter_enable)}
                            value={settings.sweep_session_start || "06:00"}
                            onChange={(e) => setSettings({ ...settings, sweep_session_start: e.target.value })}
                            className="bg-slate-900 border border-slate-700/80 rounded-lg px-2 py-1 text-xs text-slate-100 font-mono focus:border-indigo-500 focus:outline-none disabled:opacity-40 text-center"
                          />
                          <input
                            type="text"
                            disabled={!Boolean(settings.sweep_session_filter_enable)}
                            value={settings.sweep_session_end || "23:00"}
                            onChange={(e) => setSettings({ ...settings, sweep_session_end: e.target.value })}
                            className="bg-slate-900 border border-slate-700/80 rounded-lg px-2 py-1 text-xs text-slate-100 font-mono focus:border-indigo-500 focus:outline-none disabled:opacity-40 text-center"
                          />
                        </div>
                      </div>

                      {/* Highest Peak Filter */}
                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-300">Lọc Đỉnh Cao Nhất:</span>
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, sweep_highest_peak_filter: !(settings.sweep_highest_peak_filter !== false) })}
                          className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                            settings.sweep_highest_peak_filter !== false
                              ? "bg-purple-500/20 text-purple-300 border border-purple-500/40"
                              : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                          }`}
                        >
                          {settings.sweep_highest_peak_filter !== false ? "BẬT ✓" : "TẮT ✕"}
                        </button>
                      </div>

                      {/* Close On Opposite */}
                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-300">Đóng Lệnh Ngược Chiều:</span>
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, sweep_close_on_opposite: !(settings.sweep_close_on_opposite !== false) })}
                          className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                            settings.sweep_close_on_opposite !== false
                              ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                              : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                          }`}
                        >
                          {settings.sweep_close_on_opposite !== false ? "BẬT ✓" : "TẮT ✕"}
                        </button>
                      </div>
                    </div>
                  </div>

                </div>

                {/* === RIGHT COLUMN: RISK MANAGEMENT & DYNAMIC BE === */}
                <div className="space-y-5">
                  
                  {/* Card 4: Risk Management & Target R:R */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                          <Target className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Quản Trị Rủi Ro SL & Mục Tiêu Chốt Lời</h3>
                          <p className="text-[11px] text-slate-400">Thiết lập tỷ lệ R:R chốt lời và khoảng cách cắt lỗ an toàn</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-emerald-400 font-mono font-bold bg-emerald-950/60 border border-emerald-800/60 px-2 py-0.5 rounded">
                        Target & Risk
                      </span>
                    </div>

                    <div className="space-y-3.5">
                      {/* Take Profit Target R:R */}
                      <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-slate-300">Tỷ Lệ Mục Tiêu Chốt Lời (Take Profit R:R)</label>
                          <span className="text-[11px] text-emerald-400 font-mono font-bold">{settings.sweep_rr !== undefined ? settings.sweep_rr : 2.0}R</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            step="0.1"
                            min="0.5"
                            max="10"
                            value={settings.sweep_rr !== undefined ? settings.sweep_rr : 2.0}
                            onChange={(e) => setSettings({ ...settings, sweep_rr: parseFloat(e.target.value) || 2.0 })}
                            className="w-32 bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none"
                          />
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] text-slate-500">Mục tiêu:</span>
                            {[1.0, 1.2, 1.5, 2.0, 2.5, 3.0].map((r) => (
                              <button
                                key={r}
                                type="button"
                                onClick={() => setSettings({ ...settings, sweep_rr: r })}
                                className={`px-2 py-0.5 text-[10px] font-mono rounded transition cursor-pointer font-medium ${
                                  (settings.sweep_rr || 2.0) === r
                                    ? "bg-emerald-500/25 text-emerald-300 border border-emerald-500/50 font-bold"
                                    : "bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800"
                                }`}
                              >
                                {r}R
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* SL Bounds & Cooldown */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-bold text-slate-300">Khoảng Cách SL Tối Đa</label>
                            <span className="text-[10px] text-rose-400 font-mono font-bold">{settings.sweep_max_risk_pips || 150}p</span>
                          </div>
                          <input
                            type="number"
                            step="5"
                            min="20"
                            max="500"
                            value={settings.sweep_max_risk_pips !== undefined ? settings.sweep_max_risk_pips : 150}
                            onChange={(e) => setSettings({ ...settings, sweep_max_risk_pips: parseFloat(e.target.value) || 150 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-rose-500 focus:outline-none"
                          />
                          <span className="text-[10px] text-slate-500 block truncate">SL &gt; mức này &rarr; Bỏ qua</span>
                        </div>

                        <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-bold text-slate-300">Khoảng Cách SL Tối Thiểu</label>
                            <span className="text-[10px] text-rose-400 font-mono font-bold">{settings.sweep_min_risk_pips || 3.0}p</span>
                          </div>
                          <input
                            type="number"
                            step="0.5"
                            min="1"
                            max="50"
                            value={settings.sweep_min_risk_pips !== undefined ? settings.sweep_min_risk_pips : 3.0}
                            onChange={(e) => setSettings({ ...settings, sweep_min_risk_pips: parseFloat(e.target.value) || 3.0 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-rose-500 focus:outline-none"
                          />
                          <span className="text-[10px] text-slate-500 block truncate">SL &lt; mức này &rarr; Bỏ qua</span>
                        </div>

                        <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-bold text-slate-300">Đệm SL An Toàn (Buffer)</label>
                            <span className="text-[10px] text-emerald-400 font-mono font-bold">+{settings.sweep_sl_buffer_pips || 1.0}p</span>
                          </div>
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            max="50"
                            value={settings.sweep_sl_buffer_pips !== undefined ? settings.sweep_sl_buffer_pips : 1.0}
                            onChange={(e) => setSettings({ ...settings, sweep_sl_buffer_pips: parseFloat(e.target.value) || 1.0 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none"
                          />
                          <span className="text-[10px] text-slate-500 block truncate">Cộng đệm ngoài râu nến quét</span>
                        </div>

                        <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-bold text-slate-300">Giãn Cách Lệnh (Cooldown)</label>
                            <span className="text-[10px] text-emerald-400 font-mono font-bold">{settings.sweep_cooldown_bars || 5} nến</span>
                          </div>
                          <input
                            type="number"
                            step="1"
                            min="1"
                            max="50"
                            value={settings.sweep_cooldown_bars !== undefined ? settings.sweep_cooldown_bars : 5}
                            onChange={(e) => setSettings({ ...settings, sweep_cooldown_bars: parseInt(e.target.value) || 5 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none"
                          />
                          <span className="text-[10px] text-slate-500 block truncate">Khoảng nghỉ giữa 2 lệnh</span>
                        </div>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-300">Số nến chờ xác nhận tối đa:</span>
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number"
                            step="1"
                            min="1"
                            max="50"
                            value={settings.sweep_max_wait_bars !== undefined ? settings.sweep_max_wait_bars : 12}
                            onChange={(e) => setSettings({ ...settings, sweep_max_wait_bars: parseInt(e.target.value) || 12 })}
                            className="w-20 bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none text-right"
                          />
                          <span className="text-xs text-slate-400 font-mono">nến</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 5: Dynamic Break-Even Engine */}
                  <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800/90 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                          <CheckCircle className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-100">Dời SL Về Hòa Vốn Thông Minh (Dynamic BE)</h3>
                          <p className="text-[11px] text-slate-400">Tự động khóa lợi nhuận an toàn khi giá đạt tiến độ mục tiêu</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, sweep_dynamic_be_enable: !(settings.sweep_dynamic_be_enable !== false) })}
                        className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition cursor-pointer ${
                          settings.sweep_dynamic_be_enable !== false
                            ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                            : "bg-slate-800/80 text-slate-500 border border-slate-700/50"
                        }`}
                      >
                        {settings.sweep_dynamic_be_enable !== false ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <label className="text-[11px] font-bold text-slate-300 block">Cơ Chế Kích Hoạt</label>
                        <select
                          disabled={settings.sweep_dynamic_be_enable === false}
                          value={settings.sweep_be_trigger_mode || 'PCT_TP'}
                          onChange={(e) => setSettings({ ...settings, sweep_be_trigger_mode: e.target.value })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none disabled:opacity-40"
                        >
                          <option value="PCT_TP">% Mục Tiêu TP</option>
                          <option value="FIXED_PIPS">Số Pips Cố Định</option>
                          <option value="RR_TARGET">Mốc R:R Target</option>
                        </select>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">Ngưỡng Kích Hoạt</label>
                          <span className="text-[10px] text-emerald-400 font-mono font-bold">
                            {(settings.sweep_be_trigger_mode || 'PCT_TP') === 'FIXED_PIPS' ? `${settings.sweep_be_trigger_pips || 20}p` : `${settings.sweep_be_trigger_pct || 80}%`}
                          </span>
                        </div>
                        {(settings.sweep_be_trigger_mode || 'PCT_TP') === 'FIXED_PIPS' ? (
                          <input
                            type="number"
                            step="1"
                            min="5"
                            max="200"
                            disabled={settings.sweep_dynamic_be_enable === false}
                            value={settings.sweep_be_trigger_pips !== undefined ? settings.sweep_be_trigger_pips : 20}
                            onChange={(e) => setSettings({ ...settings, sweep_be_trigger_pips: parseFloat(e.target.value) || 20 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none disabled:opacity-40"
                          />
                        ) : (
                          <input
                            type="number"
                            step="5"
                            min="10"
                            max="95"
                            disabled={settings.sweep_dynamic_be_enable === false}
                            value={settings.sweep_be_trigger_pct !== undefined ? settings.sweep_be_trigger_pct : 80}
                            onChange={(e) => setSettings({ ...settings, sweep_be_trigger_pct: parseFloat(e.target.value) || 80 })}
                            className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none disabled:opacity-40"
                          />
                        )}
                        <span className="text-[10px] text-slate-500 block truncate">Tiến độ để kích hoạt dời SL</span>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-slate-300">Khóa Lãi Bảo Vệ</label>
                          <span className="text-[10px] text-emerald-400 font-mono font-bold">+{settings.sweep_be_lock_pips !== undefined ? settings.sweep_be_lock_pips : 2}p</span>
                        </div>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          max="50"
                          disabled={settings.sweep_dynamic_be_enable === false}
                          value={settings.sweep_be_lock_pips !== undefined ? settings.sweep_be_lock_pips : 2}
                          onChange={(e) => setSettings({ ...settings, sweep_be_lock_pips: parseFloat(e.target.value) || 2 })}
                          className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-bold focus:border-emerald-500 focus:outline-none disabled:opacity-40"
                        />
                        <span className="text-[10px] text-slate-500 block truncate">Dời vượt entry bù spread</span>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Bottom Sticky Save Bar */}
              <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 shadow-xl flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle className="w-4 h-4 text-purple-400 shrink-0" />
                  <span>Cấu hình Liquidity Sweep Pro sẽ được lưu vào cơ sở dữ liệu và đồng bộ tới toàn bộ người dùng & EA.</span>
                </div>
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 active:scale-95 text-white font-bold text-xs transition-all cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-purple-600/20 disabled:opacity-50 shrink-0"
                >
                  <Save className="w-4 h-4" />
                  <span>{savingSettings ? "Đang Lưu Cấu Hình..." : "Lưu Toàn Bộ Cấu Hình Sweep Mặc Định"}</span>
                </button>
              </div>

            </div>
          )}

          {/* SECTION 4: STORAGE & DB MANAGEMENT */}
          {activeSection === 'storage' && (
            <div className="space-y-6">
              <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-xl space-y-4">
                <h3 className="text-sm font-bold text-slate-100 flex items-center gap-2">
                  <Database className="w-4 h-4 text-emerald-400" />
                  <span>Cơ Chế Lưu Trữ Phân Tán Theo User</span>
                </h3>
                
                <p className="text-xs text-slate-300 leading-relaxed">
                  Hệ thống tự động lưu trữ cấu hình riêng biệt cho từng người dùng trong cơ sở dữ liệu SQLite (<code className="text-cyan-400">backend/data_storage/users.db</code>).
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs font-mono">
                  <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800">
                    <span className="text-slate-400 block mb-1">Cấu Hình Biểu Đồ & Múi Giờ</span>
                    <span className="font-bold text-emerald-400">Tự động đồng bộ</span>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800">
                    <span className="text-slate-400 block mb-1">Thông Số SMC & Order Block</span>
                    <span className="font-bold text-cyan-400">Lưu riêng từng user</span>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800">
                    <span className="text-slate-400 block mb-1">Bản Vẽ & Fibonacci Trên Chart</span>
                    <span className="font-bold text-amber-400">Lưu theo cặp tiền & User ID</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 5: PERSONAL ACCOUNT & SECURITY */}
          {activeSection === 'account' && (
            <div className="max-w-xl space-y-6">
              {pwdMsg.text && (
                <div className={`p-3.5 rounded-xl border text-xs flex items-center gap-2 ${
                  pwdMsg.type === 'success'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                }`}>
                  {pwdMsg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  <span>{pwdMsg.text}</span>
                </div>
              )}

              {/* Account Info Card */}
              <div className="p-5 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-xl space-y-3">
                <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Thông Tin Tài Khoản</h3>
                <div className="flex items-center justify-between text-xs border-b border-slate-800 pb-2">
                  <span className="text-slate-400">Tên người dùng:</span>
                  <span className="font-bold text-slate-100">{user?.username}</span>
                </div>
                <div className="flex items-center justify-between text-xs border-b border-slate-800 pb-2">
                  <span className="text-slate-400">Email:</span>
                  <span className="font-mono text-slate-200">{user?.email}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">Cấp bậc:</span>
                  <span className="font-black text-purple-300">SUPER ADMIN ⚡</span>
                </div>
              </div>

              {/* Change Password Form */}
              <form onSubmit={handlePasswordSubmit} className="p-5 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-xl space-y-4">
                <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Đổi Mật Khẩu Admin</h3>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">Mật Khẩu Hiện Tại</label>
                  <input
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    placeholder="Nhập mật khẩu đang dùng"
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">Mật Khẩu Mới</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Tối thiểu 6 ký tự"
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">Xác Nhận Mật Khẩu Mới</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Nhập lại mật khẩu mới"
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500"
                  />
                </div>

                <button
                  type="submit"
                  disabled={pwdLoading}
                  className="w-full py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs transition cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-purple-600/20"
                >
                  <Lock className="w-4 h-4" />
                  <span>{pwdLoading ? 'Đang Cập Nhật...' : 'Lưu Mật Khẩu Mới'}</span>
                </button>
              </form>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
